import type { Controller, ControlSelf, PlayerInput, WorldSense, Vec as CVec } from './PlayerControl';
import { LocalController, createLocalRaw, type LocalRaw } from './LocalController';
import { BotController } from './BotController';
import { buildPathGrid, pathStep as gridPathStep, type PathGrid } from './pathGrid';
import messeSprueche from './data/messe_sprueche.json';
import ausstellerData from './data/aussteller.json';
import kaffeeData from './data/kaffee_sprueche.json';
import kaffeeStandData from './data/kaffee.json';
import personasData from './data/personas.json';
import nervensaegeData from './data/nervensaege.json';
import hungerFailData from './data/hunger_fail.json';
import durchsagenData from './data/durchsagen.json';
import geschenkeData from './data/geschenke.json';
import standKollision from './data/standkollision.json';
import securityData from './data/security.json';
import reinigungData from './data/reinigung.json';
import demoData from './data/demo.json';
import pipiFailData from './data/pipi_fail.json';
import wcNutzloseData from './data/wc_nutzlose.json';
import kundeWegData from './data/kunde_weg.json';
import { sfx, speak, playAllSfx, installAudioUnlock, toggleAudio, audioPrefs, audioInfo, setSong } from './audio/sfx';
import { loadExhibits, exhibitSize, exhibitImage, exhibitStatus, loadStandImages, standImage, standImageStatus, loadPlaceImage, placeImage } from './exhibits';
import standtalkData from './data/standtalk.json';
import restaurantData from './data/restaurant.json';
import toiletteData from './data/toilette.json';
import {
  loadSprites,
  drawSprite,
  tickAnim,
  spriteKeyForPerson,
  spriteKeyForPlayer,
  spriteLoadStatus,
  getSpriteMeta,
  type AnimState,
  type DirRow,
} from './sprites';

const canvas = document.getElementById('game') as HTMLCanvasElement;
const ctx = canvas.getContext('2d')!;
const stage = document.getElementById('stage') as HTMLElement;
const touchUi = document.getElementById('touch-ui') as HTMLElement;
const joystickEl = document.getElementById('joystick') as HTMLElement;
const joyKnob = document.getElementById('joy-knob') as HTMLElement;
const fireBtn = document.getElementById('fire-btn') as HTMLButtonElement;
const switchBtn = document.getElementById('switch-btn') as HTMLButtonElement;
const menuEl = document.getElementById('menu') as HTMLElement;
const optCompany = document.getElementById('opt-company') as HTMLSelectElement;
const optBooths = document.getElementById('opt-booths') as HTMLInputElement;
const optDensity = document.getElementById('opt-density') as HTMLInputElement;
const optCustomers = document.getElementById('opt-customers') as HTMLInputElement;
const optNobodys = document.getElementById('opt-nobodys') as HTMLInputElement;
const optVips = document.getElementById('opt-vips') as HTMLInputElement;
const optRivals = document.getElementById('opt-rivals') as HTMLInputElement;
const optZoom = document.getElementById('opt-zoom') as HTMLInputElement;
const valZoom = document.getElementById('val-zoom') as HTMLElement;
const valBooths = document.getElementById('val-booths') as HTMLElement;
const valDensity = document.getElementById('val-density') as HTMLElement;
const valCustomers = document.getElementById('val-customers') as HTMLElement;
const valNobodys = document.getElementById('val-nobodys') as HTMLElement;
const valVips = document.getElementById('val-vips') as HTMLElement;
const valRivals = document.getElementById('val-rivals') as HTMLElement;
const btnStart = document.getElementById('btn-start') as HTMLButtonElement;
const optHall = document.getElementById('opt-hall') as HTMLInputElement;
const valHall = document.getElementById('val-hall') as HTMLElement;
const optBoothSize = document.getElementById('opt-boothsize') as HTMLInputElement;
const valBoothSize = document.getElementById('val-boothsize') as HTMLElement;
const HALL_LABELS = ['klein (Test)', 'mittel', 'groß'];
function syncHallUi(): void {
  hallSizeIdx = Math.max(0, Math.min(2, Number(optHall?.value ?? 1)));
  boothScale = Math.max(0.75, Math.min(1.5, Number(optBoothSize?.value ?? 100) / 100));
  if (valHall) valHall.textContent = HALL_LABELS[hallSizeIdx]!;
  if (valBoothSize) valBoothSize.textContent = `${boothScale.toFixed(2).replace('.', ',')}×`;
}
optHall?.addEventListener('input', syncHallUi);
optBoothSize?.addEventListener('input', syncHallUi);

/** Große Messehalle (Hallenplan-Stil) */
let WORLD_W = 7200;
let WORLD_H = 5400;
/** Hallengröße 0=klein,1=mittel,2=groß; Standgröße 0.75–1.5 */
let hallSizeIdx = 1;
let boothScale = 1;
const EXH_SCALE = 1.25;
const HALL_GRID: [number, number][] = [
  [3, 2],
  [5, 3],
  [8, 5],
];
function blockDims(): { bw: number; bh: number } {
  return { bw: Math.round(840 * boothScale), bh: Math.round(720 * boothScale) };
}
function configureHall(): void {
  const [cols, rows] = HALL_GRID[hallSizeIdx] ?? HALL_GRID[1]!;
  const { bw, bh } = blockDims();
  WORLD_W = 80 * 2 + cols * (MAIN_AISLE + bw) + MAIN_AISLE + 4;
  WORLD_H = 140 + SVC_STRIP_H + rows * (MAIN_AISLE + bh) + MAIN_AISLE + 24 + ENTRANCE_H;
  HALL_DIAG = Math.hypot(WORLD_W, WORLD_H);
  MIN_SPAWN_DIST = HALL_DIAG * 0.4;
}
/** Basis-Kamerafenster (Zoom 1×). Default-Zoom 1,5× → ~427×280. */
const CAM_BASE_W = 640;
const CAM_BASE_H = 420;
const ZOOM_MIN = 1;
const ZOOM_MAX = 2.5;
const ZOOM_DEFAULT = 1.5;
/** Zusätzlicher Zoom-Faktor im Hochformat (Touch). */
const ZOOM_PORTRAIT_BONUS = 1.12;
let camZoom = ZOOM_DEFAULT;
let CAM_W = CAM_BASE_W / ZOOM_DEFAULT;
let CAM_H = CAM_BASE_H / ZOOM_DEFAULT;
const PLAYER_R = 14;
const PERSON_R = 14;
const PLAYER_SPEED = 240;
const PERSON_SPEED = 95;
const PEN_SPEED = 500;
const PEN_LEN = 14;
const PEN_W = 3;
const FLYER_SPEED = 280;
const FLYER_RANGE = 220;
const FLYER_HIT_R = 22;
const FLYER_DMG = 10;
const PEN_DMG = 1;
const FIRE_COOLDOWN = 0.18;
const JOY_RADIUS = 48;
const BUBBLE_TIME = 1.2;
const MAX_PENS = 20;
const MAX_FLYERS = 5;
const REFILL_PAD = 48;
const EMPTY_MSG_TIME = 1.1;
const TALK_TIME = 2.5;
const IMMUNE_TIME = 1.5;
const PEN_REFILL_EVERY = 1.0;
const FLYER_REFILL_EVERY = 5.0;
const SPAWN_INTERVAL = 1.2;
const ENTRANCE_SPAWN_MIN = 0.4;
const ENTRANCE_SPAWN_MAX = 1.3;
const VISITOR_MIN_SEP = 140;
const VISITOR_PLAYER_SEP = 220;
const VISIT_MIN = 28;
const VISIT_MAX = 55;
const HASH = 160;
const MAIN_AISLE = 130;
/** Servicestreifen am oberen Hallenrand (WC, Restaurants). */
const SVC_STRIP_H = 380;
const SIDE_AISLE = 64;
const ENTRANCE_H = 480;
const COFFEE_COOLDOWN = 20;
/** Kaffee-Ausdauer */
const STAMINA_MAX = 100;
const STAMINA_DRAIN = STAMINA_MAX / 150; // ~150 s bis leer (in Bewegung ~100 s)
const STAMINA_BUSY_MULT = 1.5;
const STAMINA_EMPTY_SPEED = 0.5;
const DRINK_COOLDOWN = 18;
const YAWN_EVERY = 5;
const KAFFEE_LEER: string[] = (kaffeeData as { leer: string[] }).leer;
const KAFFEE_GETRUNKEN: string[] = (kaffeeData as { getrunken: string[] }).getrunken;
function staminaSpeedFactor(st: number): number {
  return st <= 0 ? STAMINA_EMPTY_SPEED : 1;
}
const COFFEE_POINTS_NORMAL = 50;
const COFFEE_POINTS_HART = 75;
const COFFEE_POINTS_GROSS = 120;
const COFFEE_DELIVER_R = 90;
const SMOKE_REST_MIN = 4;
const SMOKE_REST_MAX = 9;
const SMOKE_HP_PER_SEC = 2.5;
const RESTAURANT_LINES = [
  'Ich esse gerade!',
  'Gleich nach dem Essen …',
  'Nicht während der Pause!',
  'Erst der Nachtisch!',
  'Hände weg vom Schnitzel!',
];
const COFFEE_THANKS = [
  'Danke für den Kaffee!',
  'Genau was ich brauchte!',
  'Mmm, starker Espresso!',
  'Du hast mich überzeugt!',
];

type BoothKind = 'row' | 'corner' | 'peninsula' | 'island' | 'own';
type Exhibit = {
  key?: string;
  label: string;
  x: number;
  y: number;
  w: number;
  h: number;
  furniture?: boolean;
};
type Booth = {
  x: number;
  y: number;
  w: number;
  h: number;
  label: string;
  standNo: string;
  kind: BoothKind;
  own?: boolean;
  openN?: boolean;
  openS?: boolean;
  openE?: boolean;
  openW?: boolean;
  /** Begehbarer Stand: nur Wände (geschlossene Seiten) + Exponate kollidieren */
  walls?: Rect[];
  exhibits?: Exhibit[];
  slogan?: string;
  gag?: string;
  featured?: string;
  talkKey?: string;
  color?: string;
  ownerPlayerId?: number | null;
  stockPens?: number;
  refillCd?: number;
  stockFlyers?: number;
  /** Geglättete Anzeige (Welt-Ladeleiste) */
  dispPens?: number;
  dispFlyers?: number;
  /** Diebstahl-Cooldown: thiefId → Restzeit */
  stealCd?: Record<number, number>;
};
type Prop = { x: number; y: number; w: number; h: number; shape: 'rect' | 'tank' | 'truck' | 'barrier' };
type FloorMark = { x: number; y: number; text: string; big?: boolean };
type HallPlaceKind = 'smoke' | 'coffee' | 'restaurant' | 'toilet';
/** Begehbare Zonen (keine Collider) — Raucher, Kaffee, Restaurant. */
type HallPlace = {
  kind: HallPlaceKind;
  x: number;
  y: number;
  w: number;
  h: number;
  label: string;
  /** Trink-Cooldown je Spieler-ID (Restsekunden) */
  drinkCd?: Record<number, number>;
  /** Kaffee: Rest-Cooldown */
  cooldown: number;
};
type Vec = { x: number; y: number };
type Kind = 'customer' | 'nobody' | 'vip';
type CustTier = 'normal' | 'hart' | 'gross';
type AmmoKind = 'pen' | 'flyer';
type Person = {
  id: number;
  x: number;
  y: number;
  vx: number;
  vy: number;
  kind: Kind;
  tier: CustTier;
  hp: number;
  maxHp: number;
  r: number;
  points: number;
  bubble: string | null;
  bubbleT: number;
  becomeNobody: boolean;
  lifeT: number;
  leaving: boolean;
  /** Entourage follows this VIP id */
  followVipId: number | null;
  entourageN: number;
  /** VIP-Route (Weltpunkte); leer bei Nicht-VIPs */
  route: Vec[];
  routeIdx: number;
  /** Raucherbereich: Restpause */
  restT: number;
  /** Standbesuch: roam | seek | chat */
  visitMode: 'roam' | 'seek' | 'chat';
  targetBooth: number;
  /** Verbleibende Chat-Zeit am Stand */
  boothChatT: number;
  boothChatLine: string | null;
  npc?: Npc;
  pest?: { cd: number; follow: boolean };
  hidden?: boolean;
  /** Cooldown bis zum nächsten Standbesuch */
  seekCd: number;
  /** Deterministischer RNG-Zustand */
  rng: number;
  anim: AnimState;
  /** Spieler-ID, der diesen Kunden/VIP schleppt; null = frei */
  towedBy: number | null;
  /** Reihenfolge in der Schlange */
  towSeq: number;
  /** Nach Freischuss kurz nicht erneut einfangbar */
  towImmuneT: number;
};
type Projectile = {
  x: number;
  y: number;
  vx: number;
  vy: number;
  kind: AmmoKind;
  dmg: number;
  hitR: number;
  life: number;
  ownerId: number;
};

/** Mensch und Rivalen — dieselbe Entität, Input nur über Controller. */
type GamePlayer = {
  id: number;
  name: string;
  color: string;
  x: number;
  y: number;
  facing: CVec;
  r: number;
  pensAmmo: number;
  flyersAmmo: number;
  activeAmmo: AmmoKind;
  score: number;
  stunT: number;
  immuneT: number;
  talk: string | null;
  talkT: number;
  isHuman: boolean;
  controller: Controller;
  standIndex: number;
  penRefillAcc: number;
  flyerRefillAcc: number;
  fireCd: number;
  /** Höchstens 1 Becher */
  hasCoffee: boolean;
  /** Kaffee-Ausdauer 0–100 */
  stamina: number;
  yawnCd: number;
  busyT: number;
  /** Letzter Controller-Input (Debug) */
  lastMoveX: number;
  lastMoveY: number;
  /** 0 = Mensch, 1..4 = Rivalenblatt */
  rivalIndex: number;
  anim: AnimState;
};
type Rect = { x: number; y: number; w: number; h: number };

const COMPANY_NAMES = [
  "Dielenknarz Defence",
  "Hahnenschrei Heavy Industries",
  "Nordtrott Grummelmann",
  "Steelstorm Heavy Industries",
  "Großgrasien",
  "Rüdiger Türöffner",
  "Brukoskop Analytik",
  "Luftnase Sensorik",
  "ProKeim Diagnostics",
  "Thalassa Électronique",
  "Hensoptik Systems",
  "Vinciana Difesa",
  "Fjordwacht Marinetechnik",
  "Schwarmwerk Dronetics",
  "Saabotage Nordic",
  "Rheinknall AG",
  "Kettenkuddelmuddel Defence Systems (KKDS)",
  "Preisschock Aeronautics",
  "Bäh Systems Imperial",
  "Wolkenkomitee Defence & Delays",
  "Krawalltheon (KTX)",
  "Generalverdacht Dynamix",
  "Raketenkonsens MBV",
  "Bosporus Elektronik (Bosporsan)",
  "Babbelcock Services",
  "Bayflieger Drones",
  "Hüpfburg Aerospace",
  "Dassoleil Aviation",
  "Kaprobust Systems",
  "Goldkante Group",
  "Sambaero Aeronáutica",
  "Pastaflotta Navale",
  "Curryvarta Aeronautics",
  "Huntingdock Shipyards",
  "Wolkensprung Aerospace",
  "Ell-Drei Hairiness",
  "Countdown Raketenwerke",
  "Mitsuhimmel Schwerindustrie",
  "Nomnom Munition",
  "Nautilus Groupe",
  "Siestaflotte Astilleros",
  "Oh-Gosh Trucks",
  "Saunapanzer Oy",
  "Kinetikette Labs",
  "Renkenrad Getriebewerke",
  "Rolling-Royal Propulsion",
  "Gewürzturbine Motoren",
  "Stadtstaat Engineering",
  "Textwahn Industries"
];

const HIT_LINES = [
  'Aua!',
  'Ich nehm zwei!',
  'Haben Sie den auch in Blau?',
  'Das war Absicht, oder?',
  'Schreib ich in den Newsletter!',
  'Mein Badge ist kaputt.',
  'Gibt es Rabatt auf Treffer?',
  'Oooh, Premium-Kuli!',
  'Moment, ich tweet das.',
  'Das zählt als Lead!',
];

const NOBODY_LINES = [
  'Kennen Sie schon unseren Newsletter?',
  'Ich bin eigentlich nur wegen der Gummibärchen hier.',
  'Wussten Sie, dass Schrauben auch Gefühle haben?',
  'Darf ich Ihnen unser Flyer-Paket verkaufen?',
  'Mein Onkel hat auch mal einen Stand gehabt.',
  'Die Kaffeeschlange ist die wahre Innovation.',
  'Haben Sie zwei Minuten? Es dauert nur eine Stunde.',
  'Ich sammle Visitenkarten. Alle.',
  'Das WLAN hier hat Charakter.',
  'Synergie! Haben Sie Synergie?',
  'Mein Elevator Pitch braucht einen Aufzug.',
  'Die Lanyards dieses Jahr sind revolutionär.',
  'Zwischen Door Opener und Follow-up liegt der Sinn.',
  'Ich rechne Leads in Gummibärchen um.',
  'Das ist kein Smalltalk, das ist Micro-Networking.',
];

const VIP_PEN_LINES = [
  'Für Kulis bin ich nicht zuständig.',
  'Mein Büro meldet sich.',
  'Bitte über die Assistentin.',
  'Das landet in der Ablage „nie“.',
  'Nur Flyer, bitte – Papier mit Stil.',
  'Mein Terminkalender hat keine Kuli-Spalte.',
];

const VIP_WIN_LINES = [
  'Interessant. Schicken Sie Unterlagen.',
  'Wir sprechen nächste Woche.',
  'Notiert – Ihr Stand hat Potenzial.',
  'Die Entourage ist beeindruckt. Fast.',
];

const STEAL_LINES = [
  'Hey, das sind unsere Werbegeschenke!',
  'Die Flyer sind gezählt!',
  'Zurück mit dem Kuli!',
  'Das war Absicht, was?',
  'Mein Praktikant hat die inventarisiert!',
];

const VIP_SPEED = PERSON_SPEED * 0.42;
const VIP_HP = 20; // 2 Flyer à 10
const VIP_POINTS = 500;
const VIP_R = 18;
const STUN_TIME = 1.0;
const STUN_IMMUNE = 1.2;
const STEAL_PENS = 3;
const STEAL_FLYERS = 1;
const STEAL_COOLDOWN = 8;
const STAND_STOCK_PENS = 40;
const STAND_STOCK_FLYERS = 10;
const RIVAL_COLORS = ['#e060a0', '#60c070', '#d0a040', '#70a0e0'];

function makeAnim(row: DirRow = 0): AnimState {
  return { frame: 0, accMs: 0, row };
}
const BOOTH_CHAT_MIN = 3;
const BOOTH_CHAT_MAX = 6;
const MAX_BOOTH_CHATS = 16;
const TOW_GAP = 38;
const TOW_FREE_IMMUNE_MIN = 3;
const TOW_FREE_IMMUNE_MAX = 5;
const TOW_SPEED = PERSON_SPEED * 1.15;
let nextTowSeq = 1;
const SEEK_CD_MIN = 6;
const SEEK_CD_MAX = 18;
const MESSE_LINES: string[] = Array.isArray(messeSprueche)
  ? (messeSprueche as string[])
  : [];



const densityLabel = (v: number): string => {
  if (v < 25) return 'offen';
  if (v < 50) return 'wenig';
  if (v < 75) return 'mittel';
  return 'verstellt';
};

optBooths.min = '20';
optBooths.max = '95';
optBooths.value = '55';
valBooths.textContent = optBooths.value + ' %';
valDensity.textContent = densityLabel(Number(optDensity.value));

for (const n of COMPANY_NAMES) {
  const o = document.createElement('option');
  o.value = n;
  o.textContent = n;
  optCompany.appendChild(o);
}
optCompany.value = COMPANY_NAMES[0]!;
optBooths.addEventListener('input', () => {
  valBooths.textContent = optBooths.value + ' %';
});
optDensity.addEventListener('input', () => {
  valDensity.textContent = densityLabel(Number(optDensity.value));
});
valCustomers.textContent = optCustomers.value;
valNobodys.textContent = optNobodys.value;
valVips.textContent = optVips.value;
optCustomers.addEventListener('input', () => {
  valCustomers.textContent = optCustomers.value;
});
optNobodys.addEventListener('input', () => {
  valNobodys.textContent = optNobodys.value;
});
optVips.addEventListener('input', () => {
  valVips.textContent = optVips.value;
});
valRivals.textContent = optRivals.value;
optRivals.addEventListener('input', () => {
  valRivals.textContent = optRivals.value;
});
function zoomLabel(z: number): string {
  return `${z.toFixed(1).replace('.', ',')}×`;
}
function readZoomSlider(): number {
  const raw = Number(optZoom.value) / 10;
  return Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, raw));
}
function syncZoomFromUi(): void {
  camZoom = readZoomSlider();
  valZoom.textContent = zoomLabel(camZoom);
  refreshCamSize();
}
function refreshCamSize(): void {
  const cssW = view?.cssW ?? 0;
  const cssH = view?.cssH ?? 0;
  const portrait =
    cssW > 0 && cssH > 0
      ? cssW < cssH
      : typeof window !== 'undefined' && window.innerWidth < window.innerHeight;
  const touch =
    typeof isTouchDevice !== 'undefined'
      ? isTouchDevice
      : 'ontouchstart' in window || (navigator.maxTouchPoints ?? 0) > 0;
  const bonus = portrait && touch ? ZOOM_PORTRAIT_BONUS : 1;
  const z = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, camZoom * bonus));
  CAM_W = CAM_BASE_W / z;
  CAM_H = CAM_BASE_H / z;
  clampCam();
}
optZoom.value = String(Math.round(ZOOM_DEFAULT * 10));
valZoom.textContent = zoomLabel(ZOOM_DEFAULT);
optZoom.addEventListener('input', () => {
  syncZoomFromUi();
  if (playing) resize();
});

let playing = false;
let scoreboardOpen = !(typeof window !== 'undefined' && ('ontouchstart' in window || (navigator.maxTouchPoints ?? 0) > 0));
let scoreboardRect: { x: number; y: number; w: number; h: number } | null = null;
function toggleScoreboardAt(cx: number, cy: number): boolean {
  const r = scoreboardRect;
  if (!r || cx < r.x || cx > r.x + r.w || cy < r.y || cy > r.y + r.h) return false;
  scoreboardOpen = !scoreboardOpen;
  return true;
}
let booths: Booth[] = [];
let props: Prop[] = [];
let floorMarks: FloorMark[] = [];
let colliders: Rect[] = [];
let ownBooth: Booth | null = null;
let persons: Person[] = [];
const projectiles: Projectile[] = [];
type FloatFx = {
  x: number;
  y: number;
  text: string;
  life: number;
  maxLife: number;
  vy: number;
  color: string;
};
let floatFx: FloatFx[] = [];
let rivalStockBarsDrawn = 0;
let nearRivalSteal = false;
let hashBuckets: Rect[][] = [];
let hashCols = 1;
let hashRows = 1;

let players: GamePlayer[] = [];
let localRaw: LocalRaw = createLocalRaw();
let pathGrid: PathGrid | null = null;
let nextPlayerId = 1;

let emptyMsgT = 0;
let emptyMsg = '';
let spawnAcc = 0;
let entranceSpawnCd = 0;
type SpawnKind = 'customer' | 'nobody' | 'vip';
let spawnQueue: SpawnKind[] = [];
let visitorSeed = 0xC0FFEE;
let cachedPlaceOverlaps: {
  ok: boolean;
  placePlace: number;
  placeBooth: number;
  places: number;
  booths: number;
} | null = null;
let cachedBoothLayout: ReturnType<typeof boothLayoutReport> | null = null;
let cachedAisleReach: { ok: boolean; boothGoals: number; reached: number } | null = null;
let cachedVisitorSpread: {
  count: number;
  minDistPlayers: number | null;
  anyBlocked: boolean;
} | null = null;
let targetCustomers = 40;
let targetNobodys = 30;
let targetVips = 1;
let targetRivals = 2;
let nextPersonId = 1;
let entrancePoints: Vec[] = [];
/** Cached static minimap (hall + booths) */
let miniStatic: HTMLCanvasElement | null = null;
/** Vordefinierte VIP-Routen (Hauptgang-Wegpunkte) */
let vipRouteTemplates: Vec[][] = [];
let hallPlaces: HallPlace[] = [];

function human(): GamePlayer | null {
  if (!players.length) return null;
  return players.find((p) => p.isHuman) ?? players[0] ?? null;
}

function nearStand(px: number, py: number, b: Booth): boolean {
  return (
    px >= b.x - REFILL_PAD &&
    px <= b.x + b.w + REFILL_PAD &&
    py >= b.y - REFILL_PAD &&
    py <= b.y + b.h + REFILL_PAD + 50
  );
}

function isRivalBooth(b: Booth): boolean {
  return !b.own && b.ownerPlayerId != null;
}

function spawnFloat(x: number, y: number, text: string, color = '#ffe08a'): void {
  floatFx.push({
    x,
    y,
    text,
    life: 1.15,
    maxLife: 1.15,
    vy: -38,
    color,
  });
}

function ensureBoothDisp(b: Booth): void {
  if (b.dispPens == null) b.dispPens = b.stockPens ?? 0;
  if (b.dispFlyers == null) b.dispFlyers = b.stockFlyers ?? 0;
}

function tickBoothStockDisplay(dt: number): void {
  for (const b of booths) {
    if (!isRivalBooth(b) && !b.own) continue;
    ensureBoothDisp(b);
    const tp = b.stockPens ?? 0;
    const tf = b.stockFlyers ?? 0;
    // schnelle, aber glatte Annäherung
    const k = 1 - Math.pow(0.001, dt);
    b.dispPens = (b.dispPens ?? tp) + (tp - (b.dispPens ?? tp)) * Math.min(1, k * 14);
    b.dispFlyers = (b.dispFlyers ?? tf) + (tf - (b.dispFlyers ?? tf)) * Math.min(1, k * 14);
    if (Math.abs((b.dispPens ?? 0) - tp) < 0.05) b.dispPens = tp;
    if (Math.abs((b.dispFlyers ?? 0) - tf) < 0.05) b.dispFlyers = tf;
  }
}

function tickFloatFx(dt: number): void {
  for (let i = floatFx.length - 1; i >= 0; i--) {
    const f = floatFx[i]!;
    f.life -= dt;
    f.y += f.vy * dt;
    if (f.life <= 0) floatFx.splice(i, 1);
  }
}
let mouseWorld: Vec | null = null;
let camX = 0;
let camY = 0;
let view = { offsetX: 0, offsetY: 0, scale: 1, cssW: 1, cssH: 1 };

const isTouchDevice =
  'ontouchstart' in window || (navigator.maxTouchPoints ?? 0) > 0;

let joyPointerId: number | null = null;
let joyVec: Vec = { x: 0, y: 0 };
let firePointerId: number | null = null;
let switchPointerId: number | null = null;

function shuffle<T>(arr: T[]): T[] {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = (Math.random() * (i + 1)) | 0;
    const t = arr[i]!;
    arr[i] = arr[j]!;
    arr[j] = t;
  }
  return arr;
}

function rebuildHash(): void {
  hashCols = Math.ceil(WORLD_W / HASH) + 1;
  hashRows = Math.ceil(WORLD_H / HASH) + 1;
  hashBuckets = Array.from({ length: hashCols * hashRows }, () => []);
  for (const c of colliders) {
    const x0 = Math.max(0, (c.x / HASH) | 0);
    const y0 = Math.max(0, (c.y / HASH) | 0);
    const x1 = Math.min(hashCols - 1, ((c.x + c.w) / HASH) | 0);
    const y1 = Math.min(hashRows - 1, ((c.y + c.h) / HASH) | 0);
    for (let y = y0; y <= y1; y++) {
      for (let x = x0; x <= x1; x++) {
        hashBuckets[y * hashCols + x]!.push(c);
      }
    }
  }
}

function circleHitsRect(cx: number, cy: number, r: number, b: Rect): boolean {
  const nearestX = Math.max(b.x, Math.min(cx, b.x + b.w));
  const nearestY = Math.max(b.y, Math.min(cy, b.y + b.h));
  const dx = cx - nearestX;
  const dy = cy - nearestY;
  return dx * dx + dy * dy < r * r;
}

function pointInCollider(px: number, py: number): boolean {
  const gx = Math.min(hashCols - 1, Math.max(0, (px / HASH) | 0));
  const gy = Math.min(hashRows - 1, Math.max(0, (py / HASH) | 0));
  for (const c of hashBuckets[gy * hashCols + gx]!) {
    if (px >= c.x && px <= c.x + c.w && py >= c.y && py <= c.y + c.h) return true;
  }
  return false;
}

function blocked(cx: number, cy: number, r: number): boolean {
  if (cx < r || cy < r || cx > WORLD_W - r || cy > WORLD_H - r) return true;
  const x0 = Math.max(0, ((cx - r) / HASH) | 0);
  const y0 = Math.max(0, ((cy - r) / HASH) | 0);
  const x1 = Math.min(hashCols - 1, ((cx + r) / HASH) | 0);
  const y1 = Math.min(hashRows - 1, ((cy + r) / HASH) | 0);
  for (let y = y0; y <= y1; y++) {
    for (let x = x0; x <= x1; x++) {
      for (const c of hashBuckets[y * hashCols + x]!) {
        if (circleHitsRect(cx, cy, r, c)) return true;
      }
    }
  }
  return false;
}

function findFreeNear(x: number, y: number, r: number, maxR = 220): Vec | null {
  if (!blocked(x, y, r)) return { x, y };
  for (let rad = 16; rad <= maxR; rad += 16) {
    const steps = 12 + ((rad / 16) | 0) * 4;
    for (let i = 0; i < steps; i++) {
      const a = (i / steps) * Math.PI * 2;
      const px = x + Math.cos(a) * rad;
      const py = y + Math.sin(a) * rad;
      if (px < r || py < r || px > WORLD_W - r || py > WORLD_H - r) continue;
      if (!blocked(px, py, r)) return { x: px, y: py };
    }
  }
  return null;
}

function tryMoveCircle(
  ent: { x: number; y: number },
  nx: number,
  ny: number,
  r: number,
): boolean {
  // Wenn bereits in Collider: zuerst Zielzelle, sonst Achsen, sonst freischieben
  if (blocked(ent.x, ent.y, r)) {
    if (!blocked(nx, ny, r)) {
      ent.x = nx;
      ent.y = ny;
      return false;
    }
    if (!blocked(nx, ent.y, r)) {
      ent.x = nx;
      return true;
    }
    if (!blocked(ent.x, ny, r)) {
      ent.y = ny;
      return true;
    }
    const free = findFreeNear(ent.x, ent.y, r);
    if (free) {
      ent.x = free.x;
      ent.y = free.y;
    }
    return true;
  }
  const okX = !blocked(nx, ent.y, r);
  const okY = !blocked(ent.x, ny, r);
  if (okX) ent.x = nx;
  if (okY) ent.y = ny;
  return !okX || !okY;
}

function clampCam(): void {
  const h = human();
  if (!h) return;
  camX = Math.max(0, Math.min(Math.max(0, WORLD_W - CAM_W), h.x - CAM_W / 2));
  camY = Math.max(0, Math.min(Math.max(0, WORLD_H - CAM_H), h.y - CAM_H / 2));
}


function inView(x: number, y: number, w: number, h: number, m = 80): boolean {
  return x + w > camX - m && x < camX + CAM_W + m && y + h > camY - m && y < camY + CAM_H + m;
}

function aisleLetter(i: number): string {
  return String.fromCharCode(65 + (i % 26));
}


type AusstellerExhibit = {
  key?: string;
  label: string;
  fx: number;
  fy: number;
  w: number;
  h: number;
  furniture?: boolean;
};
type Aussteller = {
  id: string;
  name: string;
  slogan: string;
  gag?: string;
  color: string;
  imgTexts?: { imgW: number; imgH: number; items: { text: string; x0: number; x1: number; y0: number; y1: number; color: string; bg?: string }[] };
  booth: { type: string; tilesW: number; tilesH: number; open: string[] };
  placement: { rowFromEntrance: number; colOffset: number };
  exhibits: AusstellerExhibit[];
  talk?: string;
  personal_sprueche?: string[];
};
const AUSSTELLER: Aussteller[] = (ausstellerData as { aussteller: Aussteller[] }).aussteller;

/** Keine umlaufenden Wände mehr: die Objekte selbst bilden die Standgrenze (Lücken zum Durchlaufen). */
function computeBoothWalls(_b: Booth): Rect[] {
  return [];
}
/** Exponate auseinanderschieben: Mindestabstand = Durchgangsbreite, im Stand halten. */
const EXH_GAP = PLAYER_R * 2 + 16;
function spreadExhibits(b: Booth, list: Exhibit[]): Exhibit[] {
  for (let it = 0; it < 40; it++) {
    let moved = false;
    for (let i = 0; i < list.length; i++) {
      for (let j = i + 1; j < list.length; j++) {
        const a = list[i]!;
        const c = list[j]!;
        const ox = Math.min(a.x + a.w, c.x + c.w) - Math.max(a.x, c.x) + EXH_GAP;
        const oy = Math.min(a.y + a.h, c.y + c.h) - Math.max(a.y, c.y) + EXH_GAP;
        if (ox <= 0 || oy <= 0) continue;
        moved = true;
        if (ox < oy) {
          const d = (a.x + a.w / 2 < c.x + c.w / 2 ? -1 : 1) * (ox / 2 + 1);
          a.x += d;
          c.x -= d;
        } else {
          const d = (a.y + a.h / 2 < c.y + c.h / 2 ? -1 : 1) * (oy / 2 + 1);
          a.y += d;
          c.y -= d;
        }
      }
    }
    for (const e of list) {
      e.x = Math.max(b.x + 8, Math.min(b.x + b.w - e.w - 8, e.x));
      e.y = Math.max(b.y + 8, Math.min(b.y + b.h - e.h - 8, e.y));
    }
    if (!moved) break;
  }
  return list;
}
/** Kollisionsboxen je Objekt aus der Stand-Art (Anteile des Bildes). */
const STAND_COLL = standKollision as unknown as Record<string, [number, number, number, number][]>;
function standImageColliders(b: Booth): Exhibit[] | null {
  const boxes = b.featured ? STAND_COLL[b.featured] : undefined;
  if (!boxes?.length) return null;
  const ex: Exhibit[] = boxes.map(([fx, fy, fw, fh]) => ({ key: '', label: '', x: Math.round(b.x + fx * b.w), y: Math.round(b.y + fy * b.h), w: Math.round(fw * b.w), h: Math.round(fh * b.h), furniture: true }));
  // Unpassierbare Schlitze (< 1,2x Spielerbreite) zwischen Exponaten schließen, damit niemand darin hängen bleibt
  const need = PLAYER_R * 2 * 1.2;
  for (let pass = 0, n = ex.length, start = 0; pass < 3 && start < n; pass++, start = n, n = ex.length)
  for (let i = 0; i < n; i++) for (let j = 0; j < n; j++) {
    if (i < start && j < start) continue;
    if (i === j) continue;
    const a = ex[i]!, c = ex[j]!;
    const gx = c.x - (a.x + a.w);
    const y0 = Math.max(a.y, c.y), y1 = Math.min(a.y + a.h, c.y + c.h);
    if (gx > 0 && gx < need && y1 > y0) ex.push({ key: '', label: '', x: a.x + a.w, y: y0, w: gx, h: y1 - y0, furniture: true });
    const gy = c.y - (a.y + a.h);
    const x0 = Math.max(a.x, c.x), x1 = Math.min(a.x + a.w, c.x + c.w);
    if (gy > 0 && gy < need && x1 > x0) ex.push({ key: '', label: '', x: x0, y: a.y + a.h, w: x1 - x0, h: gy, furniture: true });
  }
  return ex;
}

/** Collider-Rechtecke eines Stands (begehbar → Wände + Exponate, sonst voll). */
function boothColliderRects(b: Booth): Rect[] {
  if (!b.walls) return [{ x: b.x, y: b.y, w: b.w, h: b.h }];
  const out: Rect[] = [...b.walls];
  for (const e of b.exhibits ?? []) out.push({ x: e.x, y: e.y, w: e.w, h: e.h });
  return out;
}

function makeExhibit(b: { x: number; y: number; w: number; h: number }, d: AusstellerExhibit): Exhibit {
  const base = exhibitSize(d.key) ?? { w: d.w, h: d.h };
  const k = EXH_SCALE * boothScale;
  const sz = { w: Math.round(base.w * k), h: Math.round(base.h * k) };
  return {
    key: d.key,
    label: d.label,
    x: b.x + d.fx * b.w - sz.w / 2,
    y: b.y + d.fy * b.h - sz.h / 2,
    w: sz.w,
    h: sz.h,
    furniture: d.furniture,
  };
}

/**
 * Hallenplan: breite Hauptgänge, schmale Seitengänge, Standblöcke.
 * occupancyPct = Belegung der Standplätze, density = Absperrungen/Sackgassen.
 */
function generateHallPlan(
  occupancyPct: number,
  density: number,
  ownName: string,
): {
  booths: Booth[];
  props: Prop[];
  marks: FloorMark[];
  start: Vec;
  vipRoutes: Vec[][];
  places: HallPlace[];
} {
  const boothList: Booth[] = [];
  const propList: Prop[] = [];
  const marks: FloorMark[] = [];
  const blockers: Rect[] = [];

  const margin = 80;
  const hallTop = margin + 60 + SVC_STRIP_H;
  const hallBottom = WORLD_H - ENTRANCE_H;
  const hallLeft = margin;
  const hallRight = WORLD_W - margin;

  // Outer walls
  blockers.push({ x: 0, y: 0, w: WORLD_W, h: 40 });
  blockers.push({ x: 0, y: WORLD_H - 40, w: WORLD_W, h: 40 });
  blockers.push({ x: 0, y: 0, w: 40, h: WORLD_H });
  blockers.push({ x: WORLD_W - 40, y: 0, w: 40, h: WORLD_H });

  // Entrance plaza (south) — open, with gates on sides
  const gateW = 220;
  const entryCx = WORLD_W / 2;
  blockers.push({ x: 40, y: hallBottom, w: entryCx - gateW / 2 - 40, h: 36 });
  blockers.push({
    x: entryCx + gateW / 2,
    y: hallBottom,
    w: WORLD_W - 40 - (entryCx + gateW / 2),
    h: 36,
  });
  marks.push({ x: entryCx, y: hallBottom + ENTRANCE_H * 0.45, text: 'EINGANG', big: true });
  marks.push({ x: entryCx, y: hallBottom + ENTRANCE_H * 0.62, text: 'Halle 4–6', big: false });

  // Vertical main aisles (Gang 1..n) and horizontal main aisles
  const { bw: blockW, bh: blockH } = blockDims();
  const S = boothScale;
  const smallHall = hallSizeIdx === 0;
  const vAisleXs: number[] = [hallLeft];
  let x = hallLeft;
  const blockXs: number[] = [];
  while (x + MAIN_AISLE + blockW <= hallRight) {
    vAisleXs.push(x);
    blockXs.push(x + MAIN_AISLE);
    x += MAIN_AISLE + blockW;
  }
  vAisleXs.push(x);

  const blockYs: number[] = [];
  const hAisleYs: number[] = [hallTop];
  let y = hallTop;
  let row = 0;
  while (y + MAIN_AISLE + blockH <= hallBottom - 20) {
    hAisleYs.push(y);
    blockYs.push(y + MAIN_AISLE);
    y += MAIN_AISLE + blockH;
    row++;
  }
  hAisleYs.push(y);

  // Floor marks for main aisles
  for (let i = 0; i < vAisleXs.length - 1; i++) {
    const ax = vAisleXs[i]!;
    const aw = (blockXs[i] ?? hallRight) - ax;
    if (aw < MAIN_AISLE * 0.8) continue;
    const cx = ax + MAIN_AISLE / 2;
    for (let j = 0; j < blockYs.length; j++) {
      const hallNo = 4 + (j % 3);
      marks.push({
        x: cx,
        y: blockYs[j]! + blockH / 2,
        text: `Gang ${i + 1}`,
      });
      if (j === 0) {
        marks.push({ x: cx, y: hAisleYs[0]! + 40, text: `Halle ${hallNo}` });
      }
    }
  }
  for (let j = 0; j < hAisleYs.length - 1; j++) {
    const ay = hAisleYs[j]!;
    const letter = aisleLetter(j);
    for (let i = 0; i < blockXs.length; i++) {
      marks.push({
        x: blockXs[i]! + blockW / 2,
        y: ay + MAIN_AISLE / 2,
        text: `Gang ${letter}`,
      });
    }
  }

  // Hall banners
  for (let j = 0; j < blockYs.length; j++) {
    const hallNo = 4 + (j % 3);
    marks.push({
      x: WORLD_W / 2,
      y: blockYs[j]! - MAIN_AISLE / 2,
      text: `Halle ${hallNo}`,
      big: true,
    });
  }

  type Slot = {
    x: number;
    y: number;
    w: number;
    h: number;
    kind: BoothKind;
    openN: boolean;
    openS: boolean;
    openE: boolean;
    openW: boolean;
    letter: string;
    num: number;
    island?: boolean;
  };
  const slots: Slot[] = [];
  let standCounter = 1;
  const featuredByBlock = new Map<string, Aussteller>();
  {
    const midC = (blockXs.length / 2) | 0;
    for (const a of AUSSTELLER) {
      const r = blockYs.length - 1 - a.placement.rowFromEntrance;
      const c = midC + a.placement.colOffset;
      if (r < 0 || c < 0 || c >= blockXs.length) continue;
      const k = `${c},${r}`;
      if (!featuredByBlock.has(k)) featuredByBlock.set(k, a);
    }
  }

  for (let by = 0; by < blockYs.length; by++) {
    for (let bx = 0; bx < blockXs.length; bx++) {
      const bx0 = blockXs[bx]!;
      const by0 = blockYs[by]!;
      const letter = aisleLetter(bx);
      const isIslandBlock = (bx + by) % 5 === 2;
      if (featuredByBlock.has(`${bx},${by}`)) continue;
      if (smallHall) continue;
      // Ausrichtung variieren: Reihen zeigen zum Hauptgang oder zum Seitengang
      const flipN = (bx * 7 + by * 3) % 3 === 1;
      const flipS = (bx * 5 + by * 11) % 3 === 2;
      const peninsulaBlock = (bx + 2 * by) % 4 === 1;

      if (isIslandBlock) {
        // Large island in center, walkable ring around
        const iw = Math.round(360 * S);
        const ih = Math.round(280 * S);
        const ix = bx0 + (blockW - iw) / 2;
        const iy = by0 + (blockH - ih) / 2;
        slots.push({
          x: ix,
          y: iy,
          w: iw,
          h: ih,
          kind: 'island',
          openN: true,
          openS: true,
          openE: true,
          openW: true,
          letter,
          num: standCounter++,
          island: true,
        });
        continue;
      }

      // Double-loaded rows: north-facing and south-facing along horizontal edges,
      // plus side booths along vertical edges; side aisle through middle
      const rowD = Math.round(130 * S);
      const boothW = Math.round(150 * S);
      const rowCount = Math.max(1, Math.floor((blockW - 16 + 8) / (boothW + 8)));
      const midY = by0 + blockH / 2;
      // North row (open south toward internal? open north toward main aisle above)
      // Booths along north edge of block, open to north main aisle
      let n = 1;
      for (let ri = 0; ri < rowCount; ri++) {
        const sx = bx0 + 8 + ri * (boothW + 8);
        const corner = ri === 0 || ri === rowCount - 1;
        const eastEnd = ri === rowCount - 1;
        const westEnd = ri === 0;
        slots.push({
          x: sx,
          y: by0 + 6,
          w: boothW,
          h: rowD,
          kind: corner ? (peninsulaBlock ? 'peninsula' : 'corner') : 'row',
          openN: !flipN || (corner && peninsulaBlock),
          openS: flipN || (corner && peninsulaBlock),
          openE: eastEnd,
          openW: westEnd,
          letter,
          num: n++,
        });
      }
      // South row
      for (let ri = 0; ri < rowCount; ri++) {
        const sx = bx0 + 8 + ri * (boothW + 8);
        const corner = ri === 0 || ri === rowCount - 1;
        const eastEnd = ri === rowCount - 1;
        const westEnd = ri === 0;
        slots.push({
          x: sx,
          y: by0 + blockH - rowD - 6,
          w: boothW,
          h: rowD,
          kind: corner ? (peninsulaBlock ? 'peninsula' : 'corner') : 'row',
          openN: flipS || (corner && peninsulaBlock),
          openS: !flipS || (corner && peninsulaBlock),
          openE: eastEnd,
          openW: westEnd,
          letter,
          num: n++,
        });
      }
      // Side aisle marker through block center
      marks.push({
        x: bx0 + blockW / 2,
        y: midY,
        text: `${letter}-${by + 1}`,
      });
      void standCounter;
      standCounter += n;
    }
  }

  // Occupancy: keep a fraction of slots
  shuffle(slots);
  const keepN = Math.max(
    8,
    Math.round((slots.length * Math.min(95, Math.max(15, occupancyPct))) / 100),
  );
  const kept = slots.slice(0, keepN);
  // Always keep some islands for visual
  for (const s of slots) {
    if (s.island && !kept.includes(s) && kept.length < slots.length) {
      kept.push(s);
    }
  }
  // Große Aussteller-Stände ragen bei schmalen Gassen in Nachbarblöcke: dort keine normalen Stände
  const featRects = [...featuredByBlock].map(([k, a]) => {
    const [c, r] = k.split(',').map(Number) as [number, number];
    const w = Math.round(a.booth.tilesW * 64 * S);
    const h = Math.round(a.booth.tilesH * 64 * S);
    return { x: blockXs[c]! + (blockW - w) / 2, y: blockYs[r]! + (blockH - h) / 2, w, h };
  });
  for (let i = kept.length - 1; i >= 0; i--) {
    if (featRects.some((f) => rectsOverlapM(kept[i]!, f, 16))) kept.splice(i, 1);
  }

  const names = shuffle(COMPANY_NAMES.filter((n) => n !== ownName));
  let ni = 0;
  for (const s of kept) {
    const company = s.island
      ? names[ni++ % names.length]!
      : names[ni++ % names.length]!;
    const standNo = `${s.letter}-${String(s.num).padStart(2, '0')}`;
    const nb: Booth = {
      x: s.x,
      y: s.y,
      w: s.w,
      h: s.h,
      label: company,
      standNo,
      kind: s.kind,
      openN: s.openN,
      openS: s.openS,
      openE: s.openE,
      openW: s.openW,
    };
    nb.walls = computeBoothWalls(nb);
    const thW = Math.min(128, nb.w - 2 * EXH_GAP - 16);
    nb.exhibits = s.island || (thW >= 56 && nb.h >= 64 + 2 * EXH_GAP + 16)
      ? [makeExhibit(nb, { key: 'theke', label: '', fx: 0.5, fy: s.island ? 0.5 : nb.openS ? 0.3 : 0.7, w: s.island ? 128 : thW, h: 64, furniture: true })]
      : [];
    boothList.push(nb);
    for (const r of boothColliderRects(nb)) blockers.push(r);
  }

  // Prominente Aussteller (datengetrieben)
  for (const [k, a] of featuredByBlock) {
    const [c, r] = k.split(',').map(Number) as [number, number];
    const bx0 = blockXs[c]!;
    const by0 = blockYs[r]!;
    const open = new Set(a.booth.open);
    const fb: Booth = {
      x: bx0 + (blockW - Math.round(a.booth.tilesW * 64 * S)) / 2,
      y: by0 + (blockH - Math.round(a.booth.tilesH * 64 * S)) / 2,
      w: Math.round(a.booth.tilesW * 64 * S),
      h: Math.round(a.booth.tilesH * 64 * S),
      label: a.name,
      standNo: `${aisleLetter(c)}-${String(90 + r).padStart(2, '0')}`,
      kind: (a.booth.type as BoothKind) ?? 'island',
      openN: open.has('N'),
      openS: open.has('S'),
      openE: open.has('E'),
      openW: open.has('W'),
      slogan: a.slogan,
      gag: a.gag,
      featured: a.id,
      talkKey: a.talk ?? a.id,
      color: a.color,
    };
    fb.walls = computeBoothWalls(fb);
    fb.exhibits = standImageColliders(fb) ?? spreadExhibits(fb, a.exhibits.map((d) => makeExhibit(fb, d)));
    boothList.push(fb);
    for (const rr of boothColliderRects(fb)) blockers.push(rr);
  }

  for (const p of propList) {
    blockers.push({ x: p.x, y: p.y, w: p.w, h: p.h });
  }

  // Density: barriers blocking side aisles / creating dead ends (never main aisles)
  const barrierChance = density / 100;
  const sideBarriers: Rect[] = [];
  for (let by = 0; by < blockYs.length; by++) {
    for (let bx = 0; bx < blockXs.length; bx++) {
      if (smallHall || featuredByBlock.has(`${bx},${by}`)) continue;
      if (Math.random() > barrierChance * 0.85) continue;
      const bx0 = blockXs[bx]!;
      const by0 = blockYs[by]!;
      // Block internal side passage (horizontal barrier in block mid)
      if (Math.random() < 0.55) {
        const bw = 40 + Math.random() * 80;
        const bx1 = bx0 + 40 + Math.random() * (blockW - bw - 80);
        sideBarriers.push({
          x: bx1,
          y: by0 + blockH / 2 - 16,
          w: bw,
          h: 32,
        });
      } else {
        // Vertical stub from block edge into side zone
        sideBarriers.push({
          x: bx0 + blockW / 2 - 16,
          y: by0 + 80,
          w: 32,
          h: 50 + Math.random() * 60,
        });
      }
    }
  }
  // Extra dead-end fences near block corners at high density
  if (density > 60) {
    for (let i = 0; i < Math.floor((density - 60) / 8); i++) {
      const bx = blockXs[(Math.random() * blockXs.length) | 0]!;
      const by = blockYs[(Math.random() * blockYs.length) | 0]!;
      sideBarriers.push({
        x: bx + 20,
        y: by + blockH - SIDE_AISLE - 20,
        w: 100,
        h: 28,
      });
    }
  }

  for (const b of sideBarriers) {
    // Keine Absperrungen in der Testhalle oder in/an Aussteller-Ständen
    if (smallHall) continue;
    if (boothList.some((ob) => ob.featured && rectsOverlapM(b, ob, 40))) continue;
    propList.push({ ...b, shape: 'barrier' });
    blockers.push(b);
  }

  // Own stand near entrance, facing into hall
  const ownW = 140;
  const ownH = 100;
  const ownX = entryCx - ownW / 2;
  const ownY = hallBottom - ownH - 50;
  const own: Booth = {
    x: ownX,
    y: ownY,
    w: ownW,
    h: ownH,
    label: `${ownName}`,
    standNo: 'DEIN STAND',
    kind: 'own',
    own: true,
    openS: true,
    openN: true,
  };
  boothList.push(own);
  blockers.push({ x: own.x, y: own.y, w: own.w, h: own.h });

  const start: Vec = { x: entryCx, y: hallBottom + ENTRANCE_H * 0.55 };

  // Flood-fill connectivity on coarse grid; remove barriers that disconnect
  const CELL = 48;
  const cols = Math.ceil(WORLD_W / CELL);
  const rows = Math.ceil(WORLD_H / CELL);
  const solid = (cx: number, cy: number): boolean => {
    const px = cx * CELL + CELL / 2;
    const py = cy * CELL + CELL / 2;
    if (px < PLAYER_R || py < PLAYER_R || px > WORLD_W - PLAYER_R || py > WORLD_H - PLAYER_R)
      return true;
    for (const c of blockers) {
      if (circleHitsRect(px, py, PLAYER_R + 2, c)) return true;
    }
    return false;
  };

  const startCell = {
    x: Math.min(cols - 1, Math.max(0, (start.x / CELL) | 0)),
    y: Math.min(rows - 1, Math.max(0, (start.y / CELL) | 0)),
  };

  const flood = (): Set<number> => {
    const seen = new Set<number>();
    if (solid(startCell.x, startCell.y)) {
      // nudge
      for (const [dx, dy] of [
        [0, -1],
        [0, 1],
        [1, 0],
        [-1, 0],
        [0, -2],
      ]) {
        const nx = startCell.x + dx;
        const ny = startCell.y + dy;
        if (nx < 0 || ny < 0 || nx >= cols || ny >= rows) continue;
        if (!solid(nx, ny)) {
          startCell.x = nx;
          startCell.y = ny;
          break;
        }
      }
    }
    const q: number[] = [startCell.y * cols + startCell.x];
    seen.add(q[0]!);
    while (q.length) {
      const k = q.pop()!;
      const cx = k % cols;
      const cy = (k / cols) | 0;
      for (const [dx, dy] of [
        [1, 0],
        [-1, 0],
        [0, 1],
        [0, -1],
      ]) {
        const nx = cx + dx;
        const ny = cy + dy;
        if (nx < 0 || ny < 0 || nx >= cols || ny >= rows) continue;
        const nk = ny * cols + nx;
        if (seen.has(nk) || solid(nx, ny)) continue;
        seen.add(nk);
        q.push(nk);
      }
    }
    return seen;
  };

  let reached = flood();
  // Sample open cells; if many unreachable near booths, remove barriers
  const removeBlocker = (b: Rect): void => {
    const i = blockers.indexOf(b);
    if (i >= 0) blockers.splice(i, 1);
    const pi = propList.findIndex(
      (p) => p.x === b.x && p.y === b.y && p.w === b.w && p.h === b.h && p.shape === 'barrier',
    );
    if (pi >= 0) propList.splice(pi, 1);
  };

  for (let guard = 0; guard < 80; guard++) {
    // find unreachable floor cell near a barrier
    let fixed = false;
    for (const b of [...sideBarriers]) {
      if (!blockers.includes(b)) continue;
      const cx = ((b.x + b.w / 2) / CELL) | 0;
      const cy = ((b.y + b.h / 2) / CELL) | 0;
      // check neighbors
      for (const [dx, dy] of [
        [0, 2],
        [0, -2],
        [2, 0],
        [-2, 0],
        [1, 1],
        [-1, -1],
      ]) {
        const nx = cx + dx;
        const ny = cy + dy;
        if (nx < 0 || ny < 0 || nx >= cols || ny >= rows) continue;
        if (solid(nx, ny)) continue;
        if (!reached.has(ny * cols + nx)) {
          removeBlocker(b);
          const si = sideBarriers.indexOf(b);
          if (si >= 0) sideBarriers.splice(si, 1);
          fixed = true;
          break;
        }
      }
      if (fixed) break;
    }
    reached = flood();
    if (!fixed) break;
  }

  // Final connectivity: punch through any remaining trapped pockets by clearing nearest barrier
  for (let guard = 0; guard < 40; guard++) {
    let trapped: number | null = null;
    for (let cy = 1; cy < rows - 1; cy += 2) {
      for (let cx = 1; cx < cols - 1; cx += 2) {
        if (solid(cx, cy)) continue;
        const k = cy * cols + cx;
        // Tote Ecken in Aussteller-Ständen (hinter Exponaten) sind ok
        const wx = cx * CELL + CELL / 2;
        const wy = cy * CELL + CELL / 2;
        if (
          boothList.some(
            (bb) => bb.featured && wx >= bb.x && wx <= bb.x + bb.w && wy >= bb.y && wy <= bb.y + bb.h,
          )
        )
          continue;
        if (!reached.has(k)) {
          trapped = k;
          break;
        }
      }
      if (trapped !== null) break;
    }
    if (trapped === null) break;
    const tx = trapped % cols;
    const ty = (trapped / cols) | 0;
    // remove closest barrier to trapped cell
    let best: Rect | null = null;
    let bestD = Infinity;
    for (const b of blockers) {
      // don't remove outer walls or own booth
      if (b.h <= 40 && (b.y < 50 || b.y > WORLD_H - 50)) continue;
      if (b.w <= 40 && (b.x < 50 || b.x > WORLD_W - 50)) continue;
      if (own && b.x === own.x && b.y === own.y) continue;
      if (boothList.some((bb) => bb.featured && rectsOverlapM(b, bb, 0))) continue;
      const dx = b.x + b.w / 2 - tx * CELL;
      const dy = b.y + b.h / 2 - ty * CELL;
      const d = dx * dx + dy * dy;
      if (d < bestD) {
        bestD = d;
        best = b;
      }
    }
    if (!best) break;
    // Prefer removing barriers/props over booths
    const asProp = propList.find(
      (p) => p.x === best!.x && p.y === best!.y && p.shape === 'barrier',
    );
    if (asProp) {
      removeBlocker(best);
    } else {
      // carve a 40px gap beside the blocker instead of deleting a booth
      const gap = {
        x: best.x - 44,
        y: best.y + best.h / 2 - 20,
        w: 40,
        h: 40,
      };
      // mark gap by not adding — instead shrink blocker if it's a barrier-sized
      removeBlocker(best);
      const bi = boothList.findIndex((bb) => bb.x === best!.x && bb.y === best!.y && !bb.own);
      if (bi >= 0) {
        boothList.splice(bi, 1);
      }
      void gap;
    }
    reached = flood();
  }

  // Assign colliders globally via return — caller sets colliders from booths+props+walls
  // Store wall blockers into props as barriers for drawing thin walls
  const boothRects = new Set<Rect>();
  for (const bb of boothList) {
    for (const r of bb.walls ?? []) boothRects.add(r);
  }
  const isBoothPart = (b: Rect): boolean =>
    boothRects.has(b) ||
    boothList.some((bb) =>
      (bb.exhibits ?? []).some((e) => e.x === b.x && e.y === b.y && e.w === b.w && e.h === b.h),
    );
  for (const b of blockers) {
    // Stand-Wände/Exponate sind keine Absperrungen (sonst gestreifte Blöcke über Exponaten)
    if (isBoothPart(b)) continue;
    const isBooth = boothList.some((bb) => bb.x === b.x && bb.y === b.y && bb.w === b.w);
    const isProp = propList.some((p) => p.x === b.x && p.y === b.y && p.w === b.w && p.h === b.h);
    if (!isBooth && !isProp) {
      propList.push({ ...b, shape: 'barrier' });
    }
  }

  // VIP-Routen: Kreuzungen der Hauptgänge, mehrere Serpentinen
  const cross: Vec[] = [];
  for (const ay of hAisleYs) {
    for (const ax of vAisleXs) {
      const cx = ax + MAIN_AISLE / 2;
      const cy = ay + MAIN_AISLE / 2;
      if (cx > hallLeft && cx < hallRight && cy > hallTop && cy < hallBottom) {
        cross.push({ x: cx, y: cy });
      }
    }
  }
  // Eingang als Start/Ende
  const entryPt = { x: entryCx, y: hallBottom + ENTRANCE_H * 0.35 };
  const vipRoutes: Vec[][] = [];
  void cross;
  // Aufbau aus Raster
  const gridPts: Vec[][] = [];
  for (let j = 0; j < hAisleYs.length; j++) {
    const row: Vec[] = [];
    for (let i = 0; i < vAisleXs.length; i++) {
      row.push({
        x: vAisleXs[i]! + MAIN_AISLE / 2,
        y: hAisleYs[j]! + MAIN_AISLE / 2,
      });
    }
    gridPts.push(row);
  }
  // Route 1: Serpentine Nord↔Süd entlang vertikaler Gänge
  {
    const r: Vec[] = [{ ...entryPt }];
    for (let i = 0; i < gridPts[0]!.length; i++) {
      const colIdx = i;
      if (i % 2 === 0) {
        for (let j = gridPts.length - 1; j >= 0; j--) r.push(gridPts[j]![colIdx]!);
      } else {
        for (let j = 0; j < gridPts.length; j++) r.push(gridPts[j]![colIdx]!);
      }
    }
    r.push({ ...entryPt });
    if (r.length >= 4) vipRoutes.push(r);
  }
  // Route 2: Serpentine Ost↔West
  {
    const r: Vec[] = [{ ...entryPt }];
    for (let j = gridPts.length - 1; j >= 0; j--) {
      if ((gridPts.length - 1 - j) % 2 === 0) {
        for (let i = 0; i < gridPts[j]!.length; i++) r.push(gridPts[j]![i]!);
      } else {
        for (let i = gridPts[j]!.length - 1; i >= 0; i--) r.push(gridPts[j]![i]!);
      }
    }
    r.push({ ...entryPt });
    if (r.length >= 4) vipRoutes.push(r);
  }
  // Route 3: Diagonale / Randtour (weniger Punkte)
  {
    const r: Vec[] = [{ ...entryPt }];
    if (gridPts.length && gridPts[0]!.length) {
      const lastR = gridPts.length - 1;
      const lastC = gridPts[0]!.length - 1;
      r.push(gridPts[lastR]![0]!);
      r.push(gridPts[0]![0]!);
      r.push(gridPts[0]![lastC]!);
      r.push(gridPts[lastR]![lastC]!);
      r.push(gridPts[lastR]![Math.floor(lastC / 2)]!);
    }
    r.push({ ...entryPt });
    if (r.length >= 4) vipRoutes.push(r);
  }

  // Orte: Raucher / Kaffee / Restaurant — nur Zonen (keine Collider), Flood-Fill bleibt intakt
  const places: HallPlace[] = [];
  const placeCandidates: { kind: HallPlaceKind; x: number; y: number; w: number; h: number; label: string }[] = [];
  // Raucher: Eingangsplaza links/rechts + ein Gang-Knoten
  placeCandidates.push({
    kind: 'smoke',
    x: entryCx - gateW / 2 - 220,
    y: hallBottom + 60,
    w: 160,
    h: 140,
    label: 'Raucherbereich',
  });
  placeCandidates.push({
    kind: 'smoke',
    x: entryCx + gateW / 2 + 60,
    y: hallBottom + 60,
    w: 160,
    h: 140,
    label: 'Raucherbereich',
  });
  if (gridPts.length >= 2 && gridPts[0]!.length >= 2) {
    const gp = gridPts[gridPts.length - 1]![1]!;
    placeCandidates.push({
      kind: 'smoke',
      x: gp.x - 70,
      y: gp.y - 60,
      w: 140,
      h: 120,
      label: 'Raucherbereich',
    });
  }
  // Kaffeestände an Hauptgang-Kreuzungen
  const coffeeSpots: Vec[] = [];
  if (gridPts.length && gridPts[0]!.length) {
    const midR = (gridPts.length / 2) | 0;
    const midC = (gridPts[0]!.length / 2) | 0;
    coffeeSpots.push(gridPts[midR]![0]!);
    coffeeSpots.push(gridPts[midR]![gridPts[0]!.length - 1]!);
    coffeeSpots.push(gridPts[0]![midC]!);
    if (gridPts.length > 2) coffeeSpots.push(gridPts[gridPts.length - 1]![midC]!);
  }
  let ci = 1;
  for (const c of coffeeSpots.slice(0, 4)) {
    placeCandidates.push({
      kind: 'coffee',
      x: c.x - 110,
      y: c.y - 82,
      w: 220,
      h: 165,
      label: `Kaffee ${ci++}`,
    });
  }
  // Zwei Restaurants: groß, links und rechts am oberen Hallenrand (Servicestreifen)
  {
    const rw = Math.round(Math.min(RESTO.worldW * 1.6, (hallRight - hallLeft) * 0.27));
    const rh = Math.round((rw * RESTO.imgH) / RESTO.imgW);
    const ry = Math.max(70, hallTop - rh - 40);
    const names = ['Kantine Kesselblick', 'Zur Bratpfanne'];
    [hallLeft + 20, hallRight - 20 - rw].forEach((rx, k) => {
      places.push({ kind: 'restaurant' as const, x: rx, y: ry, w: rw, h: rh, label: names[k]!, cooldown: 0 });
    });
  }
  // Toiletten: groß, am oberen Hallenrand (Servicestreifen)
  {
    const tw2 = Math.round(Math.min(WC.worldW * 1.55, (hallRight - hallLeft) * 0.17));
    const th2 = Math.round((tw2 * WC.imgH) / WC.imgW);
    const ty = Math.max(70, hallTop - th2 - 40);
    const tw3 = Math.round(tw2 * (1 + WC_INNER_FR));
    const mk = (fx: number) => ({ kind: 'toilet' as const, x: Math.round(WORLD_W * fx - tw3 / 2), y: ty, w: tw3, h: th2, label: 'WC', cooldown: 0 });
    const clash = (t: HallPlace) => places.some((q) => t.x - 60 < q.x + q.w && t.x + t.w + 60 > q.x && t.y - 60 < q.y + q.h && t.y + t.h + 60 > q.y);
    let nWc = 0;
    for (const fx of [0.33, 0.67]) {
      const t = mk(fx);
      if (clash(t)) continue;
      places.push(t);
      nWc++;
    }
    if (!nWc) {
      // Kleine Halle: ein WC in der Mitte, notfalls rechtes Restaurant weichen lassen
      const t = mk(0.5);
      if (clash(t)) {
        const i = places.findIndex((q) => q.kind === 'restaurant' && q.x > WORLD_W / 2);
        if (i >= 0) places.splice(i, 1);
      }
      places.push(t);
    }
  }
  // Orte ohne Überlappung untereinander und mit Ständen (Margin ≈ Seitengang)
  const PLACE_MARGIN = Math.max(40, (SIDE_AISLE / 2) | 0);
  const PLACE_ATTEMPTS = 24;
  const rectsOverlap = (
    a: { x: number; y: number; w: number; h: number },
    b: { x: number; y: number; w: number; h: number },
    m: number,
  ): boolean =>
    a.x - m < b.x + b.w &&
    a.x + a.w + m > b.x &&
    a.y - m < b.y + b.h &&
    a.y + a.h + m > b.y;

  const placeHitsSomething = (r: { x: number; y: number; w: number; h: number }): boolean => {
    for (const b of boothList) {
      if (rectsOverlap(r, b, PLACE_MARGIN)) return true;
    }
    for (const pl of places) {
      if (rectsOverlap(r, pl, PLACE_MARGIN)) return true;
    }
    return false;
  };

  for (let pi = 0; pi < placeCandidates.length; pi++) {
    const pc = placeCandidates[pi]!;
    let placed = false;
    const attempts = pc.kind === 'coffee' ? PLACE_ATTEMPTS * 4 : PLACE_ATTEMPTS;
    const step = 28;
    for (let attempt = 0; attempt < attempts; attempt++) {
      // deterministische Offsets aus Index
      const ox = ((attempt * 47 + pi * 13) % 11) - 5;
      const oy = ((attempt * 31 + pi * 17) % 11) - 5;
      const x = Math.max(
        hallLeft + 10,
        Math.min(hallRight - pc.w - 10, pc.x + ox * step * (attempt >= PLACE_ATTEMPTS ? 2 : 1)),
      );
      const y = Math.max(
        hallTop + 10,
        Math.min(WORLD_H - 80 - pc.h, pc.y + oy * step * (attempt >= PLACE_ATTEMPTS ? 2 : 1)),
      );
      const cand = { x, y, w: pc.w, h: pc.h };
      if (placeHitsSomething(cand)) continue;
      places.push({ ...pc, x, y, cooldown: 0 });
      const sign =
        pc.kind === 'smoke' ? 'RAUCHER' : pc.kind === 'coffee' ? 'KAFFEE' : 'RESTAURANT';
      marks.push({ x: x + pc.w / 2, y: y - (pc.kind === 'coffee' ? 44 : 18), text: sign, big: pc.kind === 'restaurant' });
      placed = true;
      break;
    }
    // nach N Versuchen verwerfen
    void placed;
  }

  return { booths: boothList, props: propList, marks, start, vipRoutes, places };
}

function randomDir(): Vec {
  const a = Math.random() * Math.PI * 2;
  return { x: Math.cos(a) * PERSON_SPEED, y: Math.sin(a) * PERSON_SPEED };
}

function personRand(p: Person): number {
  let x = p.rng >>> 0;
  x ^= (x << 13) >>> 0;
  x ^= x >>> 17;
  x ^= (x << 5) >>> 0;
  p.rng = x >>> 0;
  return (p.rng >>> 0) / 4294967296;
}

function personRandRange(p: Person, lo: number, hi: number): number {
  return lo + personRand(p) * (hi - lo);
}

function countBoothChats(): number {
  let n = 0;
  for (const q of persons) {
    if (q.boothChatT > 0) n++;
  }
  return n;
}

function pickBoothForVisit(p: Person): number {
  if (!booths.length) return -1;
  // deterministisch: Offset aus RNG, dann ersten freien Stand wählen
  const start = (personRand(p) * booths.length) | 0;
  for (let k = 0; k < booths.length; k++) {
    const i = (start + k) % booths.length;
    const b = booths[i]!;
    if (b.w < 40 || b.h < 40) continue;
    return i;
  }
  return start % booths.length;
}

function boothApproachPoint(b: Booth): Vec {
  return { x: b.x + b.w / 2, y: b.y + b.h + 34 };
}

function startBoothChat(p: Person): void {
  if (countBoothChats() >= MAX_BOOTH_CHATS) {
    p.visitMode = 'roam';
    p.targetBooth = -1;
    p.seekCd = personRandRange(p, 2, 5);
    return;
  }
  const line =
    MESSE_LINES.length > 0
      ? MESSE_LINES[(personRand(p) * MESSE_LINES.length) | 0]!
      : 'Interessant …';
  p.visitMode = 'chat';
  p.boothChatT = bubbleDur(line, personRandRange(p, BOOTH_CHAT_MIN, BOOTH_CHAT_MAX));
  p.boothChatLine = line;
  p.vx = 0;
  p.vy = 0;
}

// ---- Standpersonal + Standgespräche (STEP 22) ----
type TalkLine = { s: string; t: string };
const STANDTALK = standtalkData as unknown as Record<string, TalkLine[][]>;
const STAFF_LINE_T = 2.5;
const STAFF_TALK_CD = 24;
/** Sprechblasen-Dauer: max(4 s, 2× alter Wert, 70 ms/Zeichen). */
function bubbleDur(text: string | null | undefined, old: number): number {
  return Math.max(4, old * 2, (text?.length ?? 0) * 0.07);
}
const MAX_STAFF_TALKS = 3;
const STAFF_TALK_R = 110;
type Staff = {
  booth: Booth;
  homeX: number;
  homeY: number;
  x: number;
  y: number;
  tx: number;
  ty: number;
  waitT: number;
  anim: AnimState;
  sprite: string;
  r: number;
  noTalk?: boolean;
  soloT?: number;
  soloCd?: number;
  soloIdx?: number;
};
type StaffTalk = { staff: Staff; person: Person; lines: TalkLine[]; idx: number; t: number };
let staffList: Staff[] = [];
let staffTalks: StaffTalk[] = [];
const boothTalkCd = new Map<Booth, number>();

function initStaff(): void {
  staffList = [];
  staffTalks = [];
  boothTalkCd.clear();
  loadStandImages(booths.filter((b) => b.featured).map((b) => b.featured!));
  for (const b of booths) {
    if (!b.featured) continue;
    const n = b.w * b.h > 600 * 500 ? 2 : 1;
    for (let i = 0; i < n; i++) {
      const fx = n === 1 ? 0.5 : i === 0 ? 0.3 : 0.7;
      const f =
        findFreeNear(b.x + b.w * fx, b.y + b.h * 0.65, PLAYER_R, Math.min(b.w, b.h) * 0.4) ??
        { x: b.x + b.w * fx, y: b.y + b.h * 0.65 };
      staffList.push({
        booth: b,
        homeX: f.x,
        homeY: f.y,
        x: f.x,
        y: f.y,
        tx: f.x,
        ty: f.y,
        waitT: Math.random() * 2,
        anim: makeAnim(),
        sprite: i === 0 ? 'nobody' : 'grosskunde',
        r: PLAYER_R,
      });
    }
    boothTalkCd.set(b, 3 + Math.random() * 6);
  }
}

function startStaffTalk(st: Staff, p: Person): boolean {
  const pool = STANDTALK[st.booth.talkKey ?? st.booth.featured ?? ''];
  if (!pool || pool.length === 0) return false;
  const lines = pool[(Math.random() * pool.length) | 0]!;
  if (!lines.length) return false;
  p.visitMode = 'chat';
  p.boothChatLine = null;
  p.boothChatT = lines.reduce((a, l) => a + bubbleDur(l.t, STAFF_LINE_T), 0) + 0.2;
  p.vx = 0;
  p.vy = 0;
  staffTalks.push({ staff: st, person: p, lines, idx: 0, t: bubbleDur(lines[0]!.t, STAFF_LINE_T) });
  boothTalkCd.set(st.booth, STAFF_TALK_CD + Math.random() * 8);
  return true;
}

/** Einzelsprüche des Standpersonals (aussteller.json: personal_sprueche), wenn der Spieler in der Nähe ist. */
function staffSoloLines(st: Staff): string[] {
  return (AUSSTELLER.find((a) => a.id === st.booth.featured)?.personal_sprueche ?? []).filter((t) => typeof t === 'string' && t.length > 0);
}
const staffSoloStats = { said: 0 };
function updateStaffSolo(dt: number): void {
  const hum = human();
  for (const st of staffList) {
    const L = staffSoloLines(st);
    if (!L.length) continue;
    if ((st.soloT ?? 0) > 0) {
      st.soloT = st.soloT! - dt;
      continue;
    }
    st.soloCd = (st.soloCd ?? 1.5) - dt;
    if (st.soloCd > 0 || !hum || Math.hypot(hum.x - st.x, hum.y - st.y) > 190) continue;
    if (staffTalks.some((k) => k.staff === st)) continue;
    const t = L[(st.soloIdx ?? 0) % L.length]!;
    st.soloIdx = (st.soloIdx ?? 0) + 1;
    st.soloT = bubbleDur(t, STAFF_LINE_T);
    st.soloCd = 6 + Math.random() * 5;
    staffSoloStats.said++;
  }
}
function updateStaff(dt: number): void {
  updateStaffSolo(dt);
  for (const [b, t] of boothTalkCd) boothTalkCd.set(b, t - dt);
  // laufende Gespräche
  for (let i = staffTalks.length - 1; i >= 0; i--) {
    const tk = staffTalks[i]!;
    const alive = persons.includes(tk.person) && canVisitorBrowse(tk.person);
    tk.t -= dt;
    if (tk.t <= 0) {
      tk.idx++;
      tk.t = bubbleDur(tk.lines[tk.idx]?.t, STAFF_LINE_T);
    }
    if (!alive || tk.idx >= tk.lines.length) {
      if (alive) tk.person.boothChatT = Math.min(tk.person.boothChatT, 0.01);
      staffTalks.splice(i, 1);
      continue;
    }
    tk.person.boothChatT = Math.max(tk.person.boothChatT, 0.3);
  }
  for (const st of staffList) {
    const talk = staffTalks.find((k) => k.staff === st);
    let mx = 0;
    let my = 0;
    if (talk) {
      mx = talk.person.x - st.x;
      my = talk.person.y - st.y;
      tickAnim(st.anim, false, mx, my, dt, 4);
    } else {
      st.waitT -= dt;
      if (st.waitT <= 0) {
        const a = Math.random() * Math.PI * 2;
        const d = Math.random() * 28;
        const nx = st.homeX + Math.cos(a) * d;
        const ny = st.homeY + Math.sin(a) * d;
        if (!blocked(nx, ny, st.r)) {
          st.tx = nx;
          st.ty = ny;
        }
        st.waitT = 1.5 + Math.random() * 3;
      }
      const dx = st.tx - st.x;
      const dy = st.ty - st.y;
      const dl = Math.hypot(dx, dy);
      const moving = dl > 2;
      if (moving) {
        const sp = Math.min(dl, 35 * dt);
        st.x += (dx / dl) * sp;
        st.y += (dy / dl) * sp;
      }
      tickAnim(st.anim, moving, dx, dy, dt, 4);
      // Ansprechen
      if (
        !st.noTalk &&
        staffTalks.length < MAX_STAFF_TALKS &&
        (boothTalkCd.get(st.booth) ?? 0) <= 0 &&
        !staffTalks.some((k) => k.staff.booth === st.booth)
      ) {
        let best: Person | null = null;
        let bd = STAFF_TALK_R;
        for (const p of persons) {
          if (!canVisitorBrowse(p) || p.boothChatT > 0 || p.visitMode === 'chat') continue;
          const sv = svcOf.get(p);
          if (sv && sv.stage !== 'eat') continue;
          const d = Math.hypot(p.x - st.x, p.y - st.y);
          if (d > STAFF_TALK_R) continue;
          const sc = staffPickScore(st, p, d);
          if (sc < bd) {
            bd = sc;
            best = p;
          }
        }
        if (best && Math.random() < 0.6) {
          startStaffTalk(st, best);
          staffAddressed(st, best);
        }
        else boothTalkCd.set(st.booth, 1 + Math.random() * 2);
      }
    }
  }
}

function drawStaff(): void {
  for (const st of staffList) {
    if (!inView(st.x - 40, st.y - 40, 80, 80)) continue;
    const size = Math.max(40, st.r * 2.8);
    if (!drawSprite(ctx, st.sprite, st.x, st.y, size, st.anim.row, st.anim.frame)) {
      ctx.beginPath();
      ctx.arc(st.x, st.y, st.r, 0, Math.PI * 2);
      ctx.fillStyle = '#ddd';
      ctx.fill();
    }
    // Namensband in Standfarbe
    ctx.fillStyle = st.booth.color ?? '#c33';
    ctx.fillRect(st.x - 9, st.y + st.r * 0.2, 18, 5);
    ctx.strokeStyle = '#fff';
    ctx.lineWidth = 1;
    ctx.strokeRect(st.x - 9, st.y + st.r * 0.2, 18, 5);
  }
}

function drawStaffTalks(): void {
  for (const pl of hallPlaces) if (pl.kind === 'toilet') drawWcInnerBubbles(pl);
  for (const tk of staffTalks) {
    const ln = tk.lines[tk.idx];
    if (!ln) continue;
    const who = ln.s === 'personal' ? tk.staff : tk.person;
    drawBubble(who.x, who.y, ln.t, who.r);
  }
  for (const st of staffList) {
    if ((st.soloT ?? 0) <= 0) continue;
    const L = staffSoloLines(st);
    if (L.length) drawBubble(st.x, st.y, L[((st.soloIdx ?? 1) - 1) % L.length]!, st.r);
  }
}

// ---- Restaurant + WC + Pipialarm (STEP 23) ----
type ImgRect = { x0: number; x1: number; y0: number; y1: number };
type RestoData = {
  image: string; imgW: number; imgH: number; worldW: number;
  chalk: ImgRect & { pad: number; lines: string[] };
  till: ImgRect; cashier: number[]; counter: ImgRect; counterStaff: number[]; pickup: number[];
  queue: number[][]; seats: number[][];
  tables: [number, number, string][];
  tableSprites: Record<string, { image: string; w: number; h: number; top: number[] }>;
  waiter: number[]; cleanerHome: number[][]; exit: number[];
};
type WcData = {
  image: string; imgW: number; imgH: number; worldW: number;
  signs: (ImgRect & { text: string })[];
  door: number[]; queue: number[][]; alarmZone: ImgRect; exit: number[];
};
const RESTO = restaurantData as unknown as RestoData;
const WC = toiletteData as WcData;
type SvcStage = 'toQueue' | 'pay' | 'toCounter' | 'tray' | 'toSeat' | 'eat' | 'leave' | 'inside' | 'out';
type Svc = { place: HallPlace; stage: SvcStage; t: number; seat: number; litterT: number };
type Litter = { x: number; y: number; kind: 'cup' | 'napkin' | 'puddle'; age: number; life: number; rot: number };
type Cleaner = { mop?: Vec | null; x: number; y: number; homeX: number; homeY: number; target: Litter | null; workT: number; anim: AnimState; r: number; mover?: Person };
type QTalk = { a: Person; b: Person; lines: TalkLine[]; idx: number; t: number; place: HallPlace };
const svcOf = new Map<Person, Svc>();
const queues = new Map<HallPlace, Person[]>();
const seatsTaken = new Map<HallPlace, (Person | null)[]>();
const qTalkCd = new Map<HallPlace, number>();
let litter: Litter[] = [];
let cleaners: Cleaner[] = [];
let qTalks: QTalk[] = [];
const svcStats = { restoDone: 0, wcDone: 0, cleared: 0, stagesSeen: new Set<string>() };
const PUDDLE_LIFE = 75;
const TOILET_AFTER_FOOD = 0.6;

/** WC = Vorraum (Bild) + Innenraum rechts daneben; Breitenanteil des Innenraums relativ zum Vorraum. */
const WC_INNER_FR = 0.85;
function placeW(pl: HallPlace): number {
  return pl.kind === 'toilet' ? pl.w / (1 + WC_INNER_FR) : pl.w;
}
function wcInner(pl: HallPlace): Rect {
  const fw = placeW(pl);
  const x0 = pl.x + fw * 0.955;
  return { x: x0, y: pl.y, w: pl.x + pl.w - x0, h: pl.h };
}
function imgPt(pl: HallPlace, pt: number[]): Vec {
  const d = pl.kind === 'toilet' ? WC : RESTO;
  return { x: pl.x + (pt[0]! * placeW(pl)) / d.imgW, y: pl.y + (pt[1]! * pl.h) / d.imgH };
}
/** Tischplatten eines Restaurants in Weltkoordinaten (sichtbar = Kollision). */
function restoTableRects(pl: HallPlace): Rect[] {
  const sx = pl.w / RESTO.imgW;
  const sy = pl.h / RESTO.imgH;
  return RESTO.tables.map(([cx, cy, k]) => {
    const t = RESTO.tableSprites[k]!.top;
    return { x: pl.x + (cx + t[0]!) * sx, y: pl.y + (cy + t[1]!) * sy, w: (t[2]! - t[0]!) * sx, h: (t[3]! - t[1]!) * sy };
  });
}
function imgRect(pl: HallPlace, r: ImgRect): Rect {
  const d = pl.kind === 'toilet' ? WC : RESTO;
  const sx = placeW(pl) / d.imgW;
  const sy = pl.h / d.imgH;
  return { x: pl.x + r.x0 * sx, y: pl.y + r.y0 * sy, w: (r.x1 - r.x0) * sx, h: (r.y1 - r.y0) * sy };
}
function wcPlace(x?: number, y?: number): HallPlace | null {
  if (x === undefined || y === undefined) return hallPlaces.find((p) => p.kind === 'toilet') ?? null;
  let best: HallPlace | null = null;
  let bd = Infinity;
  for (const q of hallPlaces) {
    if (q.kind !== 'toilet') continue;
    const d = Math.hypot(q.x + q.w / 2 - x, q.y + q.h / 2 - y);
    if (d < bd) {
      bd = d;
      best = q;
    }
  }
  return best;
}

function initService(): void {
  svcOf.clear();
  queues.clear();
  seatsTaken.clear();
  qTalkCd.clear();
  litter = [];
  cleaners = [];
  qTalks = [];
  svcStats.restoDone = 0;
  svcStats.wcDone = 0;
  svcStats.cleared = 0;
  svcStats.stagesSeen.clear();
  loadPlaceImage(RESTO.image);
  for (const k in RESTO.tableSprites) loadPlaceImage(RESTO.tableSprites[k]!.image);
  loadPlaceImage(WC.image);
  for (const a of Object.values(KAFFEE_ART)) loadPlaceImage(a.img);
  for (const pl of hallPlaces) {
    if (pl.kind !== 'restaurant' && pl.kind !== 'toilet') continue;
    queues.set(pl, []);
    qTalkCd.set(pl, 6);
    if (pl.kind !== 'restaurant') continue;
    seatsTaken.set(pl, RESTO.seats.map(() => null));
    const pseudo: Booth = { x: pl.x, y: pl.y, w: pl.w, h: pl.h, label: 'Restaurant', standNo: '', kind: 'own', talkKey: 'restaurant', color: '#f4f4f4' };
    const mk = (pt: number[], sprite: string, noTalk: boolean): void => {
      const v = imgPt(pl, pt);
      staffList.push({ booth: pseudo, homeX: v.x, homeY: v.y, x: v.x, y: v.y, tx: v.x, ty: v.y, waitT: 1, anim: makeAnim(), sprite, r: PLAYER_R, noTalk });
    };
    mk(RESTO.waiter, 'nobody', false);
    mk(RESTO.cashier, 'grosskunde', true);
    mk(RESTO.counterStaff, 'nobody', true);
    mk([RESTO.counterStaff[0]! + 70, RESTO.counterStaff[1]!], 'kunde_normal', true);
    boothTalkCd.set(pseudo, 4);
    for (const h of RESTO.cleanerHome) {
      const v = imgPt(pl, h);
      cleaners.push({ x: v.x, y: v.y, homeX: v.x, homeY: v.y, target: null, workT: 0, anim: makeAnim(), r: PLAYER_R });
    }
  }
}

function startSvc(p: Person, pl: HallPlace): boolean {
  if (svcOf.has(p) || p.pest) return false;
  const q = queues.get(pl);
  if (!q) return false;
  const maxQ = (pl.kind === 'toilet' ? WC.queue : RESTO.queue).length;
  if (q.length >= maxQ) return false;
  q.push(p);
  svcOf.set(p, { place: pl, stage: 'toQueue', t: 0, seat: -1, litterT: 0 });
  p.visitMode = 'roam';
  p.boothChatT = 0;
  return true;
}
function endSvc(p: Person): void {
  const s = svcOf.get(p);
  if (!s) return;
  const q = queues.get(s.place);
  if (q) {
    const i = q.indexOf(p);
    if (i >= 0) q.splice(i, 1);
  }
  const seats = seatsTaken.get(s.place);
  if (seats && s.seat >= 0 && seats[s.seat] === p) seats[s.seat] = null;
  qTalks = qTalks.filter((k) => k.a !== p && k.b !== p);
  npcSvcDone(p, s.place.kind, s.stage);
  svcOf.delete(p);
  p.hidden = false;
}
function svcMove(p: Person, t: Vec, dt: number, spd = PERSON_SPEED * npcSpeedMul(p)): boolean {
  let tx = t.x;
  let ty = t.y;
  const dist = Math.hypot(tx - p.x, ty - p.y);
  if (dist < 5) {
    p.vx = 0;
    p.vy = 0;
    return true;
  }
  if (dist > 60 && pathGrid) {
    const st = npcPathStep(p, t);
    if (st) {
      tx = st.x;
      ty = st.y;
    }
  }
  const dx = tx - p.x;
  const dy = ty - p.y;
  const l = Math.hypot(dx, dy) || 1;
  const step = Math.min(dist, spd * dt);
  const blockedNow = tryMoveCircle(p, p.x + (dx / l) * step, p.y + (dy / l) * step, p.r);
  if (blockedNow && dist < 26) {
    // Ziel praktisch erreicht, nur von Kollision/Nachbarn verdeckt
    p.vx = 0;
    p.vy = 0;
    return true;
  }
  if (blockedNow && dist <= 60 && pathGrid) {
    // Nahbereich blockiert (z. B. Exponat): doch über das Wegraster ausweichen
    const st = gridPathStep(pathGrid, p, t);
    if (st) {
      const ex = st.x - p.x;
      const ey = st.y - p.y;
      const el = Math.hypot(ex, ey) || 1;
      tryMoveCircle(p, p.x + (ex / el) * step, p.y + (ey / el) * step, p.r);
    }
  }
  p.vx = (dx / l) * spd;
  p.vy = (dy / l) * spd;
  return false;
}
function dropLitter(x: number, y: number): void {
  if (litter.filter((l) => l.kind !== 'puddle').length >= 14) return;
  litter.push({ x: x + (Math.random() - 0.5) * 20, y: y + (Math.random() - 0.5) * 16, kind: Math.random() < 0.5 ? 'cup' : 'napkin', age: 0, life: 9999, rot: Math.random() * 6 });
}

/** true = Person wird vom Service-Skript gesteuert */
function updateSvc(p: Person, dt: number): boolean {
  const s = svcOf.get(p);
  if (!s) return false;
  if (!canVisitorBrowse(p)) {
    endSvc(p);
    return false;
  }
  svcStats.stagesSeen.add(s.place.kind + ':' + s.stage);
  const pl = s.place;
  const q = queues.get(pl)!;
  const isWc = pl.kind === 'toilet';
  const talking = qTalks.some((k) => k.a === p || k.b === p);
  s.t -= dt;
  switch (s.stage) {
    case 'toQueue': {
      const i = q.indexOf(p);
      const slots = isWc ? WC.queue : RESTO.queue;
      const slotPt = imgPt(pl, slots[Math.max(0, Math.min(i, slots.length - 1))]!);
      let at = svcMove(p, slotPt, dt, i === 0 ? PERSON_SPEED : PERSON_SPEED * 0.8);
      if (i === 0 && !at) {
        // Vorne festgehangen (Gedränge/Kollision): nach einer Weile in der Nähe als angekommen werten
        const dq = Math.hypot(slotPt.x - p.x, slotPt.y - p.y);
        if ((s.t < -6 && dq < 140) || s.t < -25) at = true;
      } else if (i !== 0 && s.t < -6) s.t = 0;
      if (at && i === 0) {
        // Vorne angekommen: laufendes Warteschlangen-Gespräch beenden statt zu blockieren
        if (talking) {
          for (let k = qTalks.length - 1; k >= 0; k--) if (qTalks[k]!.a === p || qTalks[k]!.b === p) qTalks.splice(k, 1);
        }
        if (isWc) {
          s.stage = 'inside';
          s.t = 2.5 + Math.random() * 2;
          p.hidden = true;
          const d = imgPt(pl, WC.door);
          p.x = d.x;
          p.y = d.y;
        } else {
          s.stage = 'pay';
          s.t = 2 + Math.random();
        }
      }
      break;
    }
    case 'pay':
      p.vx = p.vy = 0;
      if (s.t <= 0) {
        q.shift();
        s.stage = 'toCounter';
      }
      break;
    case 'toCounter':
      if (svcMove(p, imgPt(pl, RESTO.pickup), dt)) {
        s.stage = 'tray';
        s.t = 1.5 + Math.random();
      }
      break;
    case 'tray':
      p.vx = p.vy = 0;
      if (s.t <= 0) {
        const seats = seatsTaken.get(pl)!;
        const free = seats.map((v, i) => (v ? -1 : i)).filter((i) => i >= 0);
        if (free.length === 0) {
          s.stage = 'leave';
          break;
        }
        s.seat = free[(Math.random() * free.length) | 0]!;
        seats[s.seat] = p;
        s.stage = 'toSeat';
      }
      break;
    case 'toSeat':
      if (svcMove(p, imgPt(pl, RESTO.seats[s.seat]!), dt)) {
        s.stage = 'eat';
        s.t = 8 + Math.random() * 8;
        s.litterT = 2 + Math.random() * 5;
      }
      break;
    case 'eat':
      p.vx = p.vy = 0;
      s.litterT -= dt;
      if (s.litterT <= 0) {
        if (Math.random() < 0.6) dropLitter(p.x, p.y + 6);
        s.litterT = 99;
      }
      if (s.t <= 0) {
        const seats = seatsTaken.get(pl)!;
        if (seats[s.seat] === p) seats[s.seat] = null;
        s.seat = -1;
        if (Math.random() < 0.35) dropLitter(p.x, p.y);
        s.stage = 'leave';
      }
      break;
    case 'leave':
      if (svcMove(p, imgPt(pl, RESTO.exit), dt)) {
        svcStats.restoDone++;
        endSvc(p);
        const wc = wcPlace(p.x, p.y);
        if (wc && Math.random() < TOILET_AFTER_FOOD) startSvc(p, wc);
      }
      break;
    case 'inside':
      p.vx = p.vy = 0;
      if (s.t <= 0) {
        q.shift();
        p.hidden = false;
        s.stage = 'out';
      }
      break;
    case 'out':
      if (svcMove(p, imgPt(pl, WC.exit), dt)) {
        svcStats.wcDone++;
        endSvc(p);
      }
      break;
  }
  const moving = Math.hypot(p.vx, p.vy) > 12;
  tickAnim(p.anim, moving, p.vx, p.vy, dt, getSpriteMeta()?.frames ?? 4);
  return true;
}

function tickService(dt: number): void {
  // Besucher entscheiden sich gelegentlich
  for (const p of persons) {
    if (p.npc || svcOf.has(p) || !canVisitorBrowse(p) || p.boothChatT > 0 || p.visitMode === 'chat') continue;
    for (const pl of hallPlaces) {
      if (pl.kind !== 'restaurant' && pl.kind !== 'toilet') continue;
      const d = Math.hypot(p.x - (pl.x + pl.w / 2), p.y - (pl.y + pl.h / 2));
      if (d > 320) continue;
      if (Math.random() < dt * (pl.kind === 'restaurant' ? 0.06 : 0.03)) startSvc(p, pl);
    }
  }
  // Warteschlangen-Gespräche
  for (const [pl, q] of queues) {
    qTalkCd.set(pl, (qTalkCd.get(pl) ?? 0) - dt);
    if ((qTalkCd.get(pl) ?? 0) > 0 || q.length < 3 || qTalks.some((k) => k.place === pl)) continue;
    const a = q[1]!;
    const b = q[2]!;
    if (svcOf.get(a)?.stage !== 'toQueue' || svcOf.get(b)?.stage !== 'toQueue') continue;
    startQTalk(pl, a, b);
  }
  for (let i = qTalks.length - 1; i >= 0; i--) {
    const k = qTalks[i]!;
    k.t -= dt;
    if (k.t <= 0) {
      k.idx++;
      k.t = bubbleDur(k.lines[k.idx]?.t, STAFF_LINE_T);
    }
    if (k.idx >= k.lines.length || !persons.includes(k.a) || !persons.includes(k.b)) qTalks.splice(i, 1);
  }
  // Müll / Pfützen
  for (const l of litter) l.age += dt;
  litter = litter.filter((l) => l.age < l.life);
  for (const c of cleaners) {
    if (c.target && !litter.includes(c.target)) c.target = null;
    if (!c.target) {
      let best: Litter | null = null;
      let bd = Infinity;
      for (const l of litter) {
        if (cleaners.some((o) => o !== c && o.target === l)) continue;
        const d = Math.hypot(l.x - c.x, l.y - c.y) * (l.kind === 'puddle' ? 0.35 : 1);
        if (d < bd) {
          bd = d;
          best = l;
        }
      }
      if (best) {
        c.target = best;
        c.workT = 0;
      }
    }
    const t = c.target ?? c.mop ?? { x: c.homeX, y: c.homeY };
    const fake = (c.mover ??= { x: c.x, y: c.y, r: c.r, vx: 0, vy: 0 } as unknown as Person);
    fake.x = c.x;
    fake.y = c.y;
    const arrived = svcMove(fake, t, dt, 80);
    c.x = fake.x;
    c.y = fake.y;
    tickAnim(c.anim, !arrived, fake.vx, fake.vy, dt, getSpriteMeta()?.frames ?? 4);
    if (arrived && c.target) {
      c.workT += dt;
      if (c.workT > (c.target.kind === 'puddle' ? 2.5 : 0.9)) {
        spawnWet(c.target.x, c.target.y, false, 6 + Math.random() * 4);
        wetStats.after++;
        litter = litter.filter((l) => l !== c.target);
        c.target = null;
        svcStats.cleared++;
      }
    } else if (arrived && c.mop) {
      c.workT += dt;
      if (c.workT > 2) {
        c.workT = 0;
        cleanerMopDone(c);
      }
    }
  }
}
function startQTalk(pl: HallPlace, a: Person, b: Person): boolean {
  const pool = STANDTALK[pl.kind === 'toilet' ? 'toilette' : 'restaurant_queue'];
  if (!pool?.length) return false;
  qTalks.push({ a, b, lines: pool[(Math.random() * pool.length) | 0]!, idx: 0, t: STAFF_LINE_T * 2, place: pl });
  qTalkCd.set(pl, 12 + Math.random() * 10);
  return true;
}

// Pipialarm (Mensch)
const PIPI_MIN_GAP = 180;
const PIPI_MAX_GAP = 360;
const PIPI_PENALTY = 60;
const PIPI_SLOW_T = 5;
const PIPI_SLOW = 0.6;
const PIPI_FAIL_LINES = [
  'Zu spät! Die Messe-Reinigung ist informiert.',
  'Mist! Das Messe-Parkett hat das nicht verdient.',
  'Zu spät! Ab jetzt heißt der Gang „Feuchtgebiet B“.',
];
const PIPI_OK_LINES = ['Puh … gerade noch!', 'Erleichtert weiter netzwerken!', 'Geschafft. Würde ich weiterempfehlen.'];
let pipiT = 0;
let pipiMax = 0;
let pipiNext = 0;
let pipiSlowT = 0;
let pipiMsg = '';
let pipiMsgT = 0;
let pipiSeed = 1;
function pipiRand(): number {
  pipiSeed = (Math.imul(pipiSeed, 1664525) + 1013904223) >>> 0;
  return pipiSeed / 4294967296;
}
function initPipi(seed: number): void {
  pipiSeed = (seed ^ 0x9e3779b9) >>> 0;
  pipiT = 0;
  pipiSlowT = 0;
  pipiMsgT = 0;
  pipiNext = PIPI_MIN_GAP * 0.6 + pipiRand() * (PIPI_MAX_GAP - PIPI_MIN_GAP);
}
function inWcZone(x: number, y: number): boolean {
  return hallPlaces.some((wc) => {
    if (wc.kind !== 'toilet') return false;
    const r = imgRect(wc, WC.alarmZone);
    const n = wcInner(wc);
    return (x >= r.x && x <= r.x + r.w && y >= r.y && y <= r.y + r.h) || (x >= n.x && x <= n.x + n.w && y >= n.y && y <= n.y + n.h);
  });
}

// ---- WC-Innenraum: Kabinen, Pissoirs, Waschbecken, Nutzlose ----
type WcSpot = { kind: 'kabine' | 'pissoir'; u: number; v: number; occT: number };
type WcNutz = { role: 'telefon' | 'trockner' | 'quatscht' | 'kabine'; u: number; v: number; line: number; talkT: number; pauseT: number; moveT: number; cab: number };
type WcInnerState = { spots: WcSpot[]; nutz: WcNutz[] };
const WC_NUTZ_LINES: string[] = ((wcNutzloseData as { sprueche?: string[] }).sprueche ?? []).filter((t) => !!t);
const WC_NUTZ_FALLBACK = ['…', '…', '…'];
const WC_CAB_U = [0.34, 0.58, 0.82];
const WC_PIS_U = [0.28, 0.43, 0.58];
const WC_SPOT_R = 30;
const wcInnerStates = new Map<HallPlace, WcInnerState>();
let wcCollDirty = false;
let wcHintT = 0;
const wcStats = { used: 0, freed: 0, occupied: 0 };
function wcState(pl: HallPlace): WcInnerState {
  let st = wcInnerStates.get(pl);
  if (!st) {
    const spots: WcSpot[] = [
      ...WC_CAB_U.map((u, i) => ({ kind: 'kabine' as const, u, v: 0.25, occT: i === 1 ? 8 + Math.random() * 8 : 0 })),
      ...WC_PIS_U.map((u, i) => ({ kind: 'pissoir' as const, u, v: 0.77, occT: i === 0 ? 6 + Math.random() * 8 : 0 })),
    ];
    const cab = (Math.random() * 3) | 0;
    const mkN = (role: WcNutz['role'], u: number, v: number, k: number): WcNutz => ({ role, u, v, line: k, talkT: 0, pauseT: 1 + k * 1.7, moveT: 18 + Math.random() * 15, cab });
    st = {
      spots,
      nutz: [mkN('telefon', 0.83, 0.47, 0), mkN('trockner', 0.86, 0.66, 1), mkN('quatscht', 0.4, 0.55, 2), mkN('kabine', WC_CAB_U[cab]!, 0.46, 3)],
    };
    wcInnerStates.set(pl, st);
  }
  return st;
}
function wcUV(pl: HallPlace, u: number, v: number): Vec {
  const n = wcInner(pl);
  return { x: n.x + u * n.w, y: n.y + v * n.h };
}
function wcURect(pl: HallPlace, u0: number, v0: number, u1: number, v1: number): Rect {
  const n = wcInner(pl);
  return { x: n.x + u0 * n.w, y: n.y + v0 * n.h, w: (u1 - u0) * n.w, h: (v1 - v0) * n.h };
}
function wcSpotBlocked(st: WcInnerState, i: number): boolean {
  const sp = st.spots[i]!;
  if (sp.occT > 0) return true;
  return sp.kind === 'kabine' && st.nutz.some((z) => z.role === 'kabine' && WC_CAB_U[z.cab] === sp.u);
}
/** Feste Einbauten: Wände (Eingangslücke links), Trennwände, Schüsseln, Pissoirs, Waschbecken, Trockner. */
function wcFixedRects(pl: HallPlace): Rect[] {
  const R = (a: number, b: number, c: number, d: number) => wcURect(pl, a, b, c, d);
  const out = [R(0, 0, 1, 0.07), R(0, 0.93, 1, 1), R(0.965, 0, 1, 1), R(0, 0, 0.035, 0.38), R(0, 0.68, 0.035, 1)];
  for (const x of [0.22, 0.46, 0.7, 0.94]) out.push(R(x - 0.008, 0.07, x + 0.008, 0.36));
  for (const u of WC_CAB_U) out.push(R(u - 0.045, 0.07, u + 0.045, 0.15));
  for (const x of [0.205, 0.355, 0.505, 0.655]) out.push(R(x - 0.007, 0.8, x + 0.007, 0.93));
  for (const u of WC_PIS_U) out.push(R(u - 0.035, 0.87, u + 0.035, 0.93));
  out.push(R(0.885, 0.36, 0.965, 0.58));
  out.push(R(0.93, 0.62, 0.965, 0.7));
  return out;
}
/** Bewegliche Hindernisse: geschlossene Kabinentüren, Leute am Pissoir, Nutzlose. */
function wcDynRects(pl: HallPlace): Rect[] {
  const st = wcState(pl);
  const out: Rect[] = [];
  st.spots.forEach((sp) => {
    if (sp.occT <= 0) return;
    if (sp.kind === 'kabine') out.push(wcURect(pl, sp.u - 0.115, 0.35, sp.u + 0.115, 0.37));
    else {
      const c = wcUV(pl, sp.u, 0.82);
      out.push({ x: c.x - PERSON_R, y: c.y - PERSON_R, w: PERSON_R * 2, h: PERSON_R * 2 });
    }
  });
  for (const z of st.nutz) {
    const c = wcUV(pl, z.u, z.v);
    out.push({ x: c.x - PERSON_R, y: c.y - PERSON_R, w: PERSON_R * 2, h: PERSON_R * 2 });
  }
  return out;
}
function wcInnerRects(pl: HallPlace): Rect[] {
  return [...wcFixedRects(pl), ...wcDynRects(pl)];
}
/** Freies Pissoir / freie Kabine in Reichweite des Punkts. */
function wcFreeSpotAt(x: number, y: number): { pl: HallPlace; i: number } | null {
  for (const pl of hallPlaces) {
    if (pl.kind !== 'toilet') continue;
    const st = wcState(pl);
    for (let i = 0; i < st.spots.length; i++) {
      if (wcSpotBlocked(st, i)) continue;
      const sp = st.spots[i]!;
      const c = wcUV(pl, sp.u, sp.v);
      if (Math.hypot(c.x - x, c.y - y) < WC_SPOT_R) return { pl, i };
    }
  }
  return null;
}
/** Rolle eines Spruchs anhand des Inhalts. */
function wcLineRole(t: string): WcNutz['role'] {
  if (/trockner|föhn|hände/i.test(t)) return 'trockner';
  if (/call|telefon|handy|hört man|anruf|leitung|empfang/i.test(t)) return 'telefon';
  if (/besetzt|kabine|deep work|tür/i.test(t)) return 'kabine';
  return 'quatscht';
}
const WC_NUTZ_BY_ROLE = new Map<WcNutz['role'], string[]>();
for (const t of WC_NUTZ_LINES) {
  const r = wcLineRole(t);
  WC_NUTZ_BY_ROLE.set(r, [...(WC_NUTZ_BY_ROLE.get(r) ?? []), t]);
}
function wcLine(z: WcNutz): string {
  const R = WC_NUTZ_BY_ROLE.get(z.role);
  const L = R?.length ? R : WC_NUTZ_LINES.length ? WC_NUTZ_LINES : WC_NUTZ_FALLBACK;
  return L[z.line % L.length]!;
}
function tickWcInner(dt: number): void {
  const hum = human();
  wcHintT = Math.max(0, wcHintT - dt);
  for (const pl of hallPlaces) {
    if (pl.kind !== 'toilet') continue;
    const st = wcState(pl);
    let free = 0;
    st.spots.forEach((_, i) => { if (!wcSpotBlocked(st, i)) free++; });
    st.spots.forEach((sp, i) => {
      if (sp.occT > 0) {
        sp.occT -= dt;
        if (sp.occT <= 0) {
          sp.occT = 0;
          wcStats.freed++;
          wcCollDirty = true;
        }
      } else if (free > 2 && !wcSpotBlocked(st, i) && Math.random() < dt / 14) {
        const c = wcUV(pl, sp.u, sp.v);
        if (hum && Math.hypot(c.x - hum.x, c.y - hum.y) < 70) return;
        sp.occT = 6 + Math.random() * 12;
        free--;
        wcStats.occupied++;
        wcCollDirty = true;
      }
    });
    for (const z of st.nutz) {
      const near = !!hum && Math.hypot(wcUV(pl, z.u, z.v).x - hum.x, wcUV(pl, z.u, z.v).y - hum.y) < 170;
      if (z.talkT > 0) {
        z.talkT -= dt;
        if (z.talkT <= 0) {
          z.pauseT = 2.5 + Math.random() * 3;
          z.line++;
        }
      } else if (near) {
        z.pauseT -= dt;
        if (z.pauseT <= 0) z.talkT = bubbleDur(wcLine(z), 3.5);
      }
      if (z.role === 'kabine') {
        z.moveT -= dt;
        const c = wcUV(pl, z.u, z.v);
        if (z.moveT <= 0 && !(hum && Math.hypot(c.x - hum.x, c.y - hum.y) < 60)) {
          z.cab = (z.cab + 1 + ((Math.random() * 2) | 0)) % WC_CAB_U.length;
          z.u = WC_CAB_U[z.cab]!;
          z.moveT = 18 + Math.random() * 15;
          wcCollDirty = true;
        }
      }
    }
  }
  if (wcCollDirty) {
    wcCollDirty = false;
    applyColliders();
    const h2 = human();
    if (h2 && blocked(h2.x, h2.y, PLAYER_R)) {
      const f = findFreeNear(h2.x, h2.y, PLAYER_R + 1, 80);
      if (f) {
        h2.x = f.x;
        h2.y = f.y;
      }
    }
  }
}
function drawWcInner(pl: HallPlace): void {
  const n = wcInner(pl);
  const st = wcState(pl);
  const R = (a: number, b: number, c: number, d: number) => wcURect(pl, a, b, c, d);
  ctx.save();
  // Boden
  ctx.fillStyle = '#e9f1f4';
  ctx.fillRect(n.x, n.y, n.w, n.h);
  ctx.strokeStyle = 'rgba(90,130,150,0.25)';
  ctx.lineWidth = 1;
  const tile = Math.max(10, n.w / 22);
  ctx.beginPath();
  for (let tx = n.x; tx <= n.x + n.w; tx += tile) { ctx.moveTo(tx, n.y); ctx.lineTo(tx, n.y + n.h); }
  for (let ty = n.y; ty <= n.y + n.h; ty += tile) { ctx.moveTo(n.x, ty); ctx.lineTo(n.x + n.w, ty); }
  ctx.stroke();
  const box = (r: Rect, fill: string, stroke = '#4a5866', rad = 3) => {
    ctx.fillStyle = fill;
    ctx.beginPath();
    ctx.roundRect(r.x, r.y, r.w, r.h, rad);
    ctx.fill();
    ctx.strokeStyle = stroke;
    ctx.lineWidth = 2;
    ctx.stroke();
  };
  // Wände
  for (const r of [R(0, 0, 1, 0.07), R(0, 0.93, 1, 1), R(0.965, 0, 1, 1), R(0, 0, 0.035, 0.38), R(0, 0.68, 0.035, 1)]) box(r, '#9aa7b2', '#56626d', 2);
  // Kabinen
  for (const x of [0.22, 0.46, 0.7, 0.94]) box(R(x - 0.008, 0.07, x + 0.008, 0.36), '#5d87a8', '#33536b', 1);
  st.spots.forEach((sp, i) => {
    if (sp.kind === 'kabine') {
      box(R(sp.u - 0.045, 0.07, sp.u + 0.045, 0.15), '#ffffff', '#7b8794', 6);
      const busy = wcSpotBlocked(st, i);
      if (sp.occT > 0) box(R(sp.u - 0.115, 0.35, sp.u + 0.115, 0.37), '#5d87a8', '#33536b', 1);
      const c = wcUV(pl, sp.u, 0.3);
      ctx.fillStyle = busy ? '#e04848' : '#3fbf5f';
      ctx.beginPath();
      ctx.arc(c.x, c.y + n.h * 0.03, Math.max(4, n.w * 0.012), 0, Math.PI * 2);
      ctx.fill();
    } else {
      box(R(sp.u - 0.035, 0.87, sp.u + 0.035, 0.93), '#ffffff', '#7b8794', 6);
      if (sp.occT > 0) {
        const c = wcUV(pl, sp.u, 0.82);
        drawSprite(ctx, 'kunde_normal', c.x, c.y, Math.max(30, n.w * 0.08), 3, 0);
      }
    }
  });
  for (const x of [0.205, 0.355, 0.505, 0.655]) box(R(x - 0.007, 0.8, x + 0.007, 0.93), '#5d87a8', '#33536b', 1);
  // Waschbecken + Trockner
  box(R(0.885, 0.36, 0.965, 0.58), '#cfd8de');
  for (const v of [0.41, 0.53]) {
    const c = wcUV(pl, 0.925, v);
    ctx.fillStyle = '#ffffff';
    ctx.beginPath();
    ctx.ellipse(c.x, c.y, n.w * 0.025, n.h * 0.035, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = '#7b8794';
    ctx.stroke();
  }
  box(R(0.93, 0.62, 0.965, 0.7), '#6f7a84', '#3c444b', 2);
  // Nutzlose
  const gs = Math.max(30, n.w * 0.08);
  for (const z of st.nutz) {
    const c = wcUV(pl, z.u, z.v);
    const dir = z.role === 'telefon' || z.role === 'trockner' ? 1 : z.role === 'kabine' ? 3 : 0;
    drawSprite(ctx, 'nobody', c.x, c.y, gs, dir, 0);
    if (z.role === 'telefon') {
      ctx.font = `${Math.round(gs * 0.45)}px system-ui, sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('📱', c.x + gs * 0.35, c.y - gs * 0.35);
    }
  }
  ctx.restore();
}
function drawWcInnerBubbles(pl: HallPlace): void {
  const st = wcState(pl);
  for (const z of st.nutz) {
    if (z.talkT <= 0) continue;
    const c = wcUV(pl, z.u, z.v);
    drawBubble(c.x, c.y, wcLine(z), PERSON_R);
  }
}
function startPipi(): boolean {
  const hum = human();
  const wc = hum ? wcPlace(hum.x, hum.y) : null;
  if (!hum || !wc || hungerT > 0) return false;
  modeStreakReset();
  const zc = wcUV(wc, 0.5, 0.55);
  const d = Math.hypot(zc.x - hum.x, zc.y - hum.y);
  // Pfad ist länger als Luftlinie; halbe Geschwindigkeit bei leerem Kaffee einkalkuliert
  pipiMax = Math.max(30, Math.min(45, 12 + (d * 1.6) / (PLAYER_SPEED * 0.55)));
  pipiT = pipiMax;
  sfx('sirene');
  return true;
}
function tickPipi(dt: number): void {
  pipiSlowT = Math.max(0, pipiSlowT - dt);
  pipiMsgT = Math.max(0, pipiMsgT - dt);
  const hum = human();
  if (!hum) return;
  if (inWcZone(hum.x, hum.y)) loseTowToWc(hum);
  if (giftOn('pipi_pause')) return; // Geschenk: PIPIALARM pausiert
  if (pipiT > 0) {
    const spot = wcFreeSpotAt(hum.x, hum.y);
    if (!spot && wcHintT <= 0 && inWcZone(hum.x, hum.y)) {
      wcHintT = 4;
      spawnFloat(hum.x, hum.y - 30, 'Freies Pissoir oder Kabine suchen!', '#ffe08a');
    }
    if (spot) {
      wcStats.used++;
      pipiT = 0;
      pipiMsg = PIPI_OK_LINES[(pipiRand() * PIPI_OK_LINES.length) | 0]!;
      pipiMsgT = bubbleDur(pipiMsg, 3);
      spawnFloat(hum.x, hum.y - 30, pipiMsg, '#9ff0a0');
      pipiNext = PIPI_MIN_GAP + pipiRand() * (PIPI_MAX_GAP - PIPI_MIN_GAP);
      return;
    }
    pipiT -= dt;
    if (pipiT <= 0) {
      pipiT = 0;
      hum.score = Math.max(0, hum.score - PIPI_PENALTY);
      pipiSlowT = PIPI_SLOW_T;
      pipiMsg = PIPI_FAIL_LINES[(pipiRand() * PIPI_FAIL_LINES.length) | 0]!;
      pipiMsgT = bubbleDur(pipiMsg, 4);
      spawnFloat(hum.x, hum.y - 30, `−${PIPI_PENALTY}`, '#ff8080');
      pipiFailFx(hum);
      loseTowToWc(hum);
      litter.push({ x: hum.x, y: hum.y + 8, kind: 'puddle', age: 0, life: PUDDLE_LIFE - 15 + pipiRand() * 30, rot: pipiRand() * 6 });
      pipiNext = PIPI_MIN_GAP + pipiRand() * (PIPI_MAX_GAP - PIPI_MIN_GAP);
    }
    return;
  }
  pipiNext -= dt;
  if (pipiNext <= 0) {
    if (!startPipi()) pipiNext = 30;
  }
}
// PIPIALARM-Fail: 1,5 s Stillstand, Spritz-Partikel, Zittern, Blip, Sprechblase
const PIPI_FAIL_FREEZE = 1.5;
const PIPI_FAIL_BUBBLES: string[] = (pipiFailData as string[]).filter((x) => typeof x === 'string' && x.length > 0);
type PipiPart = { x: number; y: number; vx: number; vy: number; t: number; life: number };
const pipiParts: PipiPart[] = [];
let pipiShakeT = 0;
let pipiFailCount = 0;
const KUNDE_WEG_LINES: string[] = (kundeWegData as string[]).filter((x) => typeof x === 'string' && x.length > 0);
let kundeWegCount = 0;
let kundeWegLast: string | null = null;
/** WC betreten oder eingemacht: geschleppte Kunden/VIPs gehen beleidigt, Serie weg. */
function loseTowToWc(hum: GamePlayer): void {
  const list = towList(hum.id);
  if (!list.length) return;
  for (const p of list) {
    p.towedBy = null;
    p.towSeq = 0;
    p.bubble = KUNDE_WEG_LINES[(Math.random() * KUNDE_WEG_LINES.length) | 0] ?? null;
    p.bubbleT = bubbleDur(p.bubble ?? '', BUBBLE_TIME + 1.5);
    kundeWegLast = p.bubble;
    p.lifeT = -1;
    startLeavingGroup(p);
    kundeWegCount++;
  }
  modeStreakReset();
  spawnFloat(hum.x, hum.y - 44, 'Kunde weg!', '#ff8080');
}
function pipiFailFx(hum: GamePlayer): void {
  pipiFailCount++;
  hum.stunT = Math.max(hum.stunT, PIPI_FAIL_FREEZE);
  pipiShakeT = PIPI_FAIL_FREEZE;
  if (PIPI_FAIL_BUBBLES.length) {
    hum.talk = PIPI_FAIL_BUBBLES[(Math.random() * PIPI_FAIL_BUBBLES.length) | 0]!;
    hum.talkT = bubbleDur(hum.talk, 3.5);
  }
  for (let k = 0; k < 26; k++) {
    const a = -Math.PI / 2 + (Math.random() - 0.5) * 2.2;
    const v = 60 + Math.random() * 110;
    pipiParts.push({ x: hum.x, y: hum.y + 4, vx: Math.cos(a) * v, vy: Math.sin(a) * v, t: 0, life: 0.6 + Math.random() * 0.8 });
  }
  sfx('plaetscher');
}
function tickPipiFx(dt: number): void {
  pipiShakeT = Math.max(0, pipiShakeT - dt);
  for (let i = pipiParts.length - 1; i >= 0; i--) {
    const p = pipiParts[i]!;
    p.t += dt;
    p.x += p.vx * dt;
    p.y += p.vy * dt;
    p.vy += 260 * dt;
    if (p.t >= p.life) pipiParts.splice(i, 1);
  }
}
function drawPipiFx(): void {
  for (const p of pipiParts) {
    const k = 1 - p.t / p.life;
    ctx.fillStyle = p.t < p.life * 0.5 ? `rgba(240,215,40,${0.85 * k})` : `rgba(250,245,200,${0.6 * k})`;
    ctx.beginPath();
    ctx.arc(p.x, p.y, 2 + (1 - k) * 4, 0, Math.PI * 2);
    ctx.fill();
  }
}
function pipiShakeX(pl: GamePlayer): number {
  return pipiShakeT > 0 && pl === human() ? Math.sin(simTime * 70) * 3 * (pipiShakeT / PIPI_FAIL_FREEZE) : 0;
}
function pipiSpeedFactor(): number {
  return (pipiSlowT > 0 ? PIPI_SLOW : 1) * (hungerSlowT > 0 ? HUNGER_SLOW : 1);
}

// Zeichnen
function chalkFont(px: number): string {
  return `${px}px "Segoe Print", "Comic Sans MS", "Chalkboard SE", cursive`;
}
function drawStandImgTexts(b: Booth): void {
  const it = AUSSTELLER.find((a) => a.id === b.featured)?.imgTexts;
  if (!it) return;
  const sx = b.w / it.imgW;
  const sy = b.h / it.imgH;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  for (const t of it.items) {
    const r = { x: b.x + t.x0 * sx, y: b.y + t.y0 * sy, w: (t.x1 - t.x0) * sx, h: (t.y1 - t.y0) * sy };
    const pad = Math.min(r.w, r.h) * 0.1;
    const f = fitLines(t.text, r.w - pad * 2, r.h - pad * 2, 14, (px) => `bold ${px}px system-ui, sans-serif`);
    const lh = f.px * 1.15;
    if (t.bg) {
      ctx.fillStyle = t.bg;
      const bh = f.lines.length * lh + pad;
      ctx.fillRect(r.x + pad / 2, r.y + r.h / 2 - bh / 2, r.w - pad, bh);
    }
    ctx.font = `bold ${f.px}px system-ui, sans-serif`;
    ctx.fillStyle = t.color;
    const y0 = r.y + r.h / 2 - ((f.lines.length - 1) * lh) / 2;
    f.lines.forEach((l, i) => ctx.fillText(l, r.x + r.w / 2, y0 + i * lh));
  }
}
function fitLines(text: string, maxW: number, maxH: number, maxPx: number, font: (px: number) => string): { lines: string[]; px: number } {
  for (let px = maxPx; px >= 3; px -= 0.5) {
    ctx.font = font(px);
    const words = text.split(' ');
    const lines: string[] = [];
    let line = '';
    let ok = true;
    for (const w of words) {
      const t = line ? `${line} ${w}` : w;
      if (ctx.measureText(t).width > maxW) {
        if (!line) {
          ok = false;
          break;
        }
        lines.push(line);
        line = w;
      } else line = t;
    }
    if (line) lines.push(line);
    if (ok && lines.length * px * 1.15 <= maxH) return { lines, px };
  }
  return { lines: [text], px: 3 };
}
function drawRestaurant(pl: HallPlace): void {
  const img = placeImage(RESTO.image);
  if (img) ctx.drawImage(img, pl.x, pl.y, pl.w, pl.h);
  else {
    ctx.fillStyle = 'rgba(50, 80, 60, 0.6)';
    ctx.fillRect(pl.x, pl.y, pl.w, pl.h);
  }
  // Namensschild
  {
    const sw = Math.min(pl.w * 0.5, 360);
    const sh = Math.max(30, pl.h * 0.09);
    const sx = pl.x + pl.w / 2 - sw / 2;
    const sy = pl.y - sh - 8;
    ctx.fillStyle = '#7a2e12';
    ctx.beginPath();
    ctx.roundRect(sx, sy, sw, sh, 10);
    ctx.fill();
    ctx.strokeStyle = '#ffd27a';
    ctx.lineWidth = 3;
    ctx.stroke();
    ctx.fillStyle = '#ffe9b8';
    ctx.font = `900 ${sh * 0.56}px system-ui, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(`🍽 ${pl.label}`, sx + sw / 2, sy + sh / 2, sw - 12);
  }
  // Tische (Kollision = Tischplatte, siehe restoTableRects)
  {
    const sx = pl.w / RESTO.imgW;
    const sy = pl.h / RESTO.imgH;
    for (const [cx, cy, k] of RESTO.tables) {
      const sp = RESTO.tableSprites[k]!;
      const ti = placeImage(sp.image);
      const w = sp.w * sx;
      const h = sp.h * sy;
      const x = pl.x + cx * sx - w / 2;
      const y = pl.y + cy * sy - h / 2;
      if (ti) ctx.drawImage(ti, x, y, w, h);
      else {
        ctx.fillStyle = '#a5753f';
        ctx.fillRect(x + w * 0.25, y + h * 0.2, w * 0.5, h * 0.5);
      }
    }
  }
  // Stammgäste an den Tischen
  {
    const taken = seatsTaken.get(pl);
    const keys = ['kunde_normal', 'nobody', 'grosskunde', 'kunde_hart'];
    const gs = Math.max(34, pl.w * 0.06);
    RESTO.seats.forEach((st, i) => {
      if (i % 3 !== 1 || taken?.[i]) return;
      const v = imgPt(pl, st);
      const bob = Math.sin(performance.now() / 400 + i) * 1.2;
      drawSprite(ctx, keys[i % keys.length]!, v.x, v.y + bob, gs, i % 2 ? 1 : 2, 0);
    });
  }
  // Kreidetafel
  const cr = imgRect(pl, RESTO.chalk);
  const pad = (RESTO.chalk.pad * pl.w) / RESTO.imgW;
  const n = RESTO.chalk.lines.length;
  const lh = (cr.h - pad * 2) / n;
  let px = lh * 0.82;
  ctx.font = chalkFont(px);
  const widest = Math.max(...RESTO.chalk.lines.map((l) => ctx.measureText(l).width));
  if (widest > cr.w - pad * 2) px *= (cr.w - pad * 2) / widest;
  ctx.font = chalkFont(px);
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  RESTO.chalk.lines.forEach((l, i) => {
    const y = cr.y + pad + lh * (i + 0.5);
    ctx.fillStyle = 'rgba(255,255,255,0.35)';
    ctx.fillText(l, cr.x + pad + 0.4, y + 0.4);
    ctx.fillStyle = 'rgba(250,250,245,0.92)';
    ctx.fillText(l, cr.x + pad, y);
  });
  // Kasse
  const tr = imgRect(pl, RESTO.till);
  ctx.fillStyle = '#6b4a2a';
  ctx.fillRect(tr.x, tr.y, tr.w, tr.h);
  ctx.fillStyle = '#2a2a30';
  ctx.fillRect(tr.x + tr.w * 0.55, tr.y + tr.h * 0.15, tr.w * 0.35, tr.h * 0.55);
  ctx.fillStyle = '#7fe0a0';
  ctx.fillRect(tr.x + tr.w * 0.6, tr.y + tr.h * 0.22, tr.w * 0.25, tr.h * 0.2);
  ctx.fillStyle = '#fff';
  ctx.font = `bold ${Math.max(6, tr.h * 0.42)}px system-ui, sans-serif`;
  ctx.textAlign = 'center';
  ctx.fillText('Kasse', tr.x + tr.w * 0.28, tr.y + tr.h * 0.5);
  // Essensausgabe mit Wärmelampe
  const fr = imgRect(pl, RESTO.counter);
  const cx = fr.x + fr.w / 2;
  const glow = ctx.createRadialGradient(cx, fr.y + fr.h * 0.4, 2, cx, fr.y + fr.h * 0.4, fr.w * 0.6);
  const flick = 0.55 + 0.1 * Math.sin(performance.now() / 300);
  glow.addColorStop(0, `rgba(255,170,60,${flick})`);
  glow.addColorStop(1, 'rgba(255,120,30,0)');
  ctx.fillStyle = glow;
  ctx.fillRect(fr.x - fr.w * 0.3, fr.y - fr.h, fr.w * 1.6, fr.h * 3);
  ctx.fillStyle = '#b8bcc4';
  ctx.fillRect(fr.x, fr.y, fr.w, fr.h);
  ctx.strokeStyle = '#555a66';
  ctx.lineWidth = 1.5;
  ctx.strokeRect(fr.x, fr.y, fr.w, fr.h);
  for (let i = 0; i < 3; i++) {
    const bx = fr.x + fr.w * (0.12 + i * 0.27);
    ctx.fillStyle = '#d9822b';
    ctx.fillRect(bx, fr.y + fr.h * 0.25, fr.w * 0.2, fr.h * 0.45);
  }
  // Lampenarm
  ctx.fillStyle = '#333';
  ctx.fillRect(fr.x + fr.w * 0.05, fr.y - 4, fr.w * 0.9, 4);
  ctx.fillStyle = '#ffcf70';
  for (let i = 0; i < 3; i++) ctx.fillRect(fr.x + fr.w * (0.15 + i * 0.3), fr.y - 3, fr.w * 0.1, 3);
  // Schild
  const sw = fr.w * 0.5;
  const sh = Math.max(10, fr.h * 0.5);
  ctx.fillStyle = '#c0392b';
  ctx.fillRect(cx - sw / 2, fr.y + fr.h + 2, sw, sh);
  ctx.fillStyle = '#fff';
  ctx.font = `bold ${sh * 0.72}px system-ui, sans-serif`;
  ctx.textBaseline = 'middle';
  ctx.fillText('Ausgabe', cx, fr.y + fr.h + 2 + sh / 2, sw - 4);
}
function drawToilet(pl: HallPlace): void {
  // Fliesen-Vorplatz + Dachschild
  const ap = pl.w * 0.05;
  ctx.fillStyle = '#d8eef8';
  ctx.fillRect(pl.x - ap, pl.y - ap, pl.w + ap * 2, pl.h + ap * 2);
  ctx.strokeStyle = 'rgba(80,140,180,0.35)';
  ctx.lineWidth = 1;
  const tile = Math.max(10, pl.w / 24);
  ctx.beginPath();
  for (let tx = pl.x - ap; tx <= pl.x + pl.w + ap; tx += tile) {
    ctx.moveTo(tx, pl.y - ap);
    ctx.lineTo(tx, pl.y + pl.h + ap);
  }
  for (let ty = pl.y - ap; ty <= pl.y + pl.h + ap; ty += tile) {
    ctx.moveTo(pl.x - ap, ty);
    ctx.lineTo(pl.x + pl.w + ap, ty);
  }
  ctx.stroke();
  ctx.strokeStyle = '#2f7fb8';
  ctx.lineWidth = 4;
  ctx.strokeRect(pl.x - ap, pl.y - ap, pl.w + ap * 2, pl.h + ap * 2);
  {
    const sw = pl.w * 0.42;
    const sh = Math.max(26, pl.h * 0.12);
    const sx = pl.x + pl.w / 2 - sw / 2;
    const sy = pl.y - ap - sh - 6;
    ctx.fillStyle = '#1f5f93';
    ctx.beginPath();
    ctx.roundRect(sx, sy, sw, sh, 8);
    ctx.fill();
    ctx.strokeStyle = '#fff';
    ctx.lineWidth = 3;
    ctx.stroke();
    ctx.fillStyle = '#fff';
    ctx.font = `900 ${sh * 0.62}px system-ui, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('🚾 WC', sx + sw / 2 - sh * 0.3, sy + sh / 2, sw - sh);
    const busy = (queues.get(pl)?.length ?? 0) > 2;
    ctx.fillStyle = busy ? '#ff4d4d' : '#4dff7a';
    ctx.beginPath();
    ctx.arc(sx + sw - sh * 0.45, sy + sh / 2, sh * 0.22, 0, Math.PI * 2);
    ctx.fill();
  }
  const img = placeImage(WC.image);
  if (img) ctx.drawImage(img, pl.x, pl.y, placeW(pl), pl.h);
  else {
    ctx.fillStyle = 'rgba(220,220,210,0.7)';
    ctx.fillRect(pl.x, pl.y, pl.w, pl.h);
  }
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  for (const sg of WC.signs) {
    const r = imgRect(pl, sg);
    const pad = r.w * 0.08;
    const f = fitLines(sg.text, r.w - pad * 2, r.h - pad * 2, 12, (px) => `bold ${px}px system-ui, sans-serif`);
    ctx.font = `bold ${f.px}px system-ui, sans-serif`;
    ctx.fillStyle = '#1d2a44';
    const lh = f.px * 1.15;
    const y0 = r.y + r.h / 2 - ((f.lines.length - 1) * lh) / 2;
    f.lines.forEach((l, i) => ctx.fillText(l, r.x + r.w / 2, y0 + i * lh));
  }
  drawWcInner(pl);
}
let collDebug = false;
function drawCollDebug(): void {
  if (!collDebug) return;
  ctx.save();
  ctx.lineWidth = 2;
  for (const c of colliders) {
    if (!inView(c.x, c.y, c.w, c.h)) continue;
    ctx.fillStyle = 'rgba(255,40,40,0.18)';
    ctx.strokeStyle = '#ff3030';
    ctx.fillRect(c.x, c.y, c.w, c.h);
    ctx.strokeRect(c.x, c.y, c.w, c.h);
  }
  const h = human();
  if (h) {
    ctx.strokeStyle = '#30ff60';
    ctx.beginPath();
    ctx.arc(h.x, h.y, PLAYER_R, 0, Math.PI * 2);
    ctx.stroke();
  }
  ctx.restore();
}
function drawLitter(): void {
  for (const l of litter) {
    if (!inView(l.x - 30, l.y - 30, 60, 60)) continue;
    ctx.save();
    ctx.translate(l.x, l.y);
    if (l.kind === 'puddle') {
      const fade = Math.min(1, (l.life - l.age) / 8);
      ctx.globalAlpha = 0.85 * fade;
      ctx.fillStyle = '#ffd83a';
      ctx.strokeStyle = '#c99a10';
      ctx.lineWidth = 2;
      ctx.beginPath();
      for (let i = 0; i <= 10; i++) {
        const a = (i / 10) * Math.PI * 2;
        const rr = 18 + 5 * Math.sin(a * 3 + l.rot);
        const x = Math.cos(a) * rr * 1.3;
        const y = Math.sin(a) * rr * 0.8;
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
      ctx.fillStyle = 'rgba(255,255,255,0.7)';
      ctx.beginPath();
      ctx.ellipse(-8, -4, 6, 2.5, -0.3, 0, Math.PI * 2);
      ctx.fill();
    } else {
      ctx.rotate(l.rot);
      if (l.kind === 'cup') {
        ctx.fillStyle = '#f2f2f2';
        ctx.fillRect(-3, -5, 6, 10);
        ctx.fillStyle = '#d33';
        ctx.fillRect(-3, -5, 6, 2);
      } else {
        ctx.fillStyle = '#fafaf0';
        ctx.fillRect(-4, -4, 8, 8);
        ctx.strokeStyle = '#bbb';
        ctx.lineWidth = 0.8;
        ctx.strokeRect(-4, -4, 8, 8);
      }
    }
    ctx.restore();
  }
}
function drawCleanersAndQTalks(): void {
  for (const c of cleaners) {
    if (!inView(c.x - 40, c.y - 40, 80, 80)) continue;
    const size = Math.max(40, c.r * 2.8);
    if (!drawSprite(ctx, 'kunde_hart', c.x, c.y, size, c.anim.row, c.anim.frame)) {
      ctx.fillStyle = '#4ad';
      ctx.beginPath();
      ctx.arc(c.x, c.y, c.r, 0, Math.PI * 2);
      ctx.fill();
    }
    // Mopp
    ctx.strokeStyle = '#8a6a3a';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(c.x + 8, c.y - 6);
    ctx.lineTo(c.x + 16, c.y + 14);
    ctx.stroke();
    ctx.fillStyle = '#e8e0c8';
    ctx.fillRect(c.x + 11, c.y + 13, 10, 4);
  }
  for (const k of qTalks) {
    const ln = k.lines[k.idx];
    if (!ln) continue;
    const who = k.idx % 2 === 0 ? k.a : k.b;
    drawBubble(who.x, who.y, ln.t, who.r);
  }
}
function drawPipiOverlay(): void {
  if (pipiT <= 0 && pipiMsgT <= 0) return;
  const W = view.cssW;
  const H = view.cssH;
  ctx.save();
  if (pipiT > 0) {
    const pulse = 0.35 + 0.35 * Math.abs(Math.sin(performance.now() / (pipiT < 10 ? 140 : 260)));
    const edge = Math.max(14, Math.min(W, H) * 0.05);
    const g = (x0: number, y0: number, x1: number, y1: number): CanvasGradient => {
      const gr = ctx.createLinearGradient(x0, y0, x1, y1);
      gr.addColorStop(0, `rgba(255,0,0,${pulse})`);
      gr.addColorStop(1, 'rgba(255,0,0,0)');
      return gr;
    };
    ctx.fillStyle = g(0, 0, edge, 0);
    ctx.fillRect(0, 0, edge, H);
    ctx.fillStyle = g(W, 0, W - edge, 0);
    ctx.fillRect(W - edge, 0, edge, H);
    ctx.fillStyle = g(0, H, 0, H - edge);
    ctx.fillRect(0, H - edge, W, edge);
    ctx.fillStyle = g(0, 0, 0, edge);
    ctx.fillRect(0, 0, W, edge);
  }
  const y = H * 0.27;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.lineJoin = 'round';
  if (pipiT > 0) {
    const big = Math.min(46, W * 0.11);
    ctx.font = `900 ${big}px system-ui, sans-serif`;
    ctx.lineWidth = 6;
    ctx.strokeStyle = '#3a0000';
    ctx.strokeText('PIPIALARM!', W / 2, y);
    ctx.fillStyle = '#ffde3a';
    ctx.fillText('PIPIALARM!', W / 2, y);
    ctx.font = `800 ${big * 0.6}px system-ui, sans-serif`;
    const txt = `🚾 WC in ${Math.ceil(pipiT)} s`;
    ctx.lineWidth = 5;
    ctx.strokeText(txt, W / 2, y + big * 0.85);
    ctx.fillStyle = pipiT < 10 ? '#ff6060' : '#ffffff';
    ctx.fillText(txt, W / 2, y + big * 0.85);
  } else if (pipiMsgT > 0) {
    ctx.font = `800 ${Math.min(22, W * 0.055)}px system-ui, sans-serif`;
    ctx.lineWidth = 5;
    ctx.strokeStyle = '#000';
    ctx.strokeText(pipiMsg, W / 2, y, W * 0.9);
    ctx.fillStyle = pipiSlowT > 0 ? '#ffb0b0' : '#b0ffb8';
    ctx.fillText(pipiMsg, W / 2, y, W * 0.9);
  }
  ctx.restore();
}

// ---- HUNGERALARM (Spieler, sehr selten) ----
const HUNGER_MIN_GAP = 480;
const HUNGER_MAX_GAP = 840;
const HUNGER_PENALTY = 80;
const HUNGER_SLOW = 0.3;
const HUNGER_FAIL_BUBBLES: string[] = hungerFailData as string[];
const HUNGER_GROWL_GAP = 7;
const HUNGER_EAT_T = 3;
const HUNGER_FAIL_LINES = ['Unterzuckert … alles dreht sich.', 'Magenknurren übertönt das Verkaufsgespräch.', 'Zu spät. Der Kreislauf streikt.'];
const HUNGER_OK_LINES = ['Satt! Weiter geht’s.', 'Das hat gutgetan.', 'Gestärkt zurück in die Halle.'];
let hungerT = 0;
let hungerMax = 0;
let hungerNext = 0;
let hungerEat = 0;
let hungerSlowT = 0;
let hungerMsg = '';
let hungerMsgT = 0;
let hungerFail = false;
let hungerGrowlT = 0;
let hungerIconT = 0;
let hungerBubble = '';
let hungerBubbleT = 0;
function hungerSay(): void {
  if (!HUNGER_FAIL_BUBBLES.length) return;
  hungerBubble = HUNGER_FAIL_BUBBLES[(pipiRand() * HUNGER_FAIL_BUBBLES.length) | 0]!;
  hungerBubbleT = 3.2;
}
function initHunger(): void {
  hungerT = 0;
  hungerEat = 0;
  hungerSlowT = 0;
  hungerMsgT = 0;
  hungerFail = false;
  hungerBubbleT = 0;
  hungerIconT = 0;
  hungerNext = HUNGER_MIN_GAP * 0.7 + pipiRand() * (HUNGER_MAX_GAP - HUNGER_MIN_GAP);
}
function restoPlace(): HallPlace | null {
  return hallPlaces.find((q) => q.kind === 'restaurant') ?? null;
}
function nearestPlace(kind: string, x: number, y: number): HallPlace | null {
  let best: HallPlace | null = null;
  let bd = Infinity;
  for (const q of hallPlaces) {
    if (q.kind !== kind) continue;
    const d = Math.hypot(q.x + q.w / 2 - x, q.y + q.h / 2 - y);
    if (d < bd) {
      bd = d;
      best = q;
    }
  }
  return best;
}
function inResto(x: number, y: number): boolean {
  return hallPlaces.some((r) => r.kind === 'restaurant' && x >= r.x && x <= r.x + r.w && y >= r.y && y <= r.y + r.h);
}
function startHunger(): boolean {
  const hum = human();
  const r = hum ? nearestPlace('restaurant', hum.x, hum.y) : null;
  if (!hum || !r || pipiT > 0 || hungerFail) return false;
  modeStreakReset();
  const d = Math.hypot(r.x + r.w / 2 - hum.x, r.y + r.h / 2 - hum.y);
  hungerMax = Math.max(40, Math.min(60, 18 + (d * 1.6) / (PLAYER_SPEED * 0.55)));
  hungerT = hungerMax;
  hungerEat = 0;
  sfx('magen');
  return true;
}
function tickHunger(dt: number): void {
  hungerSlowT = Math.max(0, hungerSlowT - dt);
  hungerMsgT = Math.max(0, hungerMsgT - dt);
  hungerBubbleT = Math.max(0, hungerBubbleT - dt);
  hungerIconT = Math.max(0, hungerIconT - dt);
  const hum = human();
  if (!hum) return;
  if (hungerFail) {
    // Schlurfen bis zum Essen im Restaurant
    hungerSlowT = 1;
    hungerGrowlT -= dt;
    if (hungerGrowlT <= 0) {
      hungerGrowlT = HUNGER_GROWL_GAP;
      hungerIconT = 1.6;
      sfx('magen');
      if (hungerBubbleT <= 0 && pipiRand() < 0.5) hungerSay();
    }
    if (inResto(hum.x, hum.y)) {
      hungerEat += dt;
      if (hungerEat >= HUNGER_EAT_T) {
        hungerFail = false;
        hungerSlowT = 0;
        hungerEat = 0;
        hungerMsg = HUNGER_OK_LINES[(pipiRand() * HUNGER_OK_LINES.length) | 0]!;
        hungerMsgT = 2.5;
        spawnFloat(hum.x, hum.y - 30, hungerMsg, '#9ff0a0');
        hungerNext = HUNGER_MIN_GAP + pipiRand() * (HUNGER_MAX_GAP - HUNGER_MIN_GAP);
      }
    } else hungerEat = Math.max(0, hungerEat - dt * 2);
    return;
  }
  if (hungerT > 0) {
    if (inResto(hum.x, hum.y)) {
      hungerEat += dt;
      if (hungerEat >= HUNGER_EAT_T) {
        hungerT = 0;
        hungerMsg = HUNGER_OK_LINES[(pipiRand() * HUNGER_OK_LINES.length) | 0]!;
        hungerMsgT = 2.5;
        spawnFloat(hum.x, hum.y - 30, hungerMsg, '#9ff0a0');
        hungerNext = HUNGER_MIN_GAP + pipiRand() * (HUNGER_MAX_GAP - HUNGER_MIN_GAP);
        // Nach dem Essen garantiert PIPIALARM
        pipiNext = Math.max(pipiNext, 60);
        startPipi();
        return;
      }
    } else hungerEat = Math.max(0, hungerEat - dt * 2);
    hungerT -= dt;
    if (hungerT <= 0) {
      hungerT = 0;
      hum.score = Math.max(0, hum.score - HUNGER_PENALTY);
      hungerSlowT = 1;
      hungerFail = true;
      hungerEat = 0;
      hungerGrowlT = HUNGER_GROWL_GAP;
      hungerIconT = 1.6;
      hungerSay();
      hungerMsg = HUNGER_FAIL_LINES[(pipiRand() * HUNGER_FAIL_LINES.length) | 0]!;
      hungerMsgT = 4;
      spawnFloat(hum.x, hum.y - 30, `−${HUNGER_PENALTY}`, '#ff8080');
      hungerNext = HUNGER_MIN_GAP + pipiRand() * (HUNGER_MAX_GAP - HUNGER_MIN_GAP);
    }
    return;
  }
  if (pipiT > 0) return;
  hungerNext -= dt;
  if (hungerNext <= 0 && !startHunger()) hungerNext = 45;
}
function drawEdgeArrow(q: HallPlace | null, color: string, icon: string): void {
  const hum = human();
  if (!hum || !q) return;
  const W = view.cssW;
  const H = view.cssH;
  const tx = view.offsetX + (q.x + q.w / 2 - camX) * view.scale;
  const ty = view.offsetY + (q.y + q.h / 2 - camY) * view.scale;
  const px = view.offsetX + (hum.x - camX) * view.scale;
  const py = view.offsetY + (hum.y - camY) * view.scale;
  const m = 34;
  const onScreen = tx > m && tx < W - m && ty > m && ty < H - m;
  const a = Math.atan2(ty - py, tx - px);
  let ax = tx;
  let ay = ty - 46;
  let rot = Math.PI / 2;
  if (!onScreen) {
    const c = Math.cos(a);
    const sn = Math.sin(a);
    const kx = c > 0 ? (W - m - px) / c : c < 0 ? (m - px) / c : Infinity;
    const ky = sn > 0 ? (H - m - py) / sn : sn < 0 ? (m - py) / sn : Infinity;
    const k = Math.max(0, Math.min(kx, ky));
    ax = px + c * k;
    ay = py + sn * k;
    rot = a;
  } else ay += Math.sin(performance.now() / 180) * 5;
  ctx.save();
  ctx.translate(ax, ay);
  ctx.save();
  ctx.rotate(rot);
  ctx.beginPath();
  ctx.moveTo(22, 0);
  ctx.lineTo(-10, -16);
  ctx.lineTo(-4, 0);
  ctx.lineTo(-10, 16);
  ctx.closePath();
  ctx.fillStyle = color;
  ctx.strokeStyle = '#000';
  ctx.lineWidth = 3;
  ctx.stroke();
  ctx.fill();
  ctx.restore();
  ctx.font = '20px system-ui, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  const ox = onScreen ? 0 : -Math.cos(a) * 30;
  const oy = onScreen ? -28 : -Math.sin(a) * 30;
  ctx.fillText(icon, ox, oy);
  ctx.restore();
}
function drawHungerOverlay(): void {
  if (pipiT > 0) drawEdgeArrow(nearestPlace('toilet', human()?.x ?? 0, human()?.y ?? 0), '#5ec8ff', '🚾');
  if (hungerT > 0 || hungerFail) drawEdgeArrow(nearestPlace('restaurant', human()?.x ?? 0, human()?.y ?? 0), '#ffb347', '🍽');
  if (hungerFail) {
    const W0 = view.cssW;
    ctx.save();
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.lineJoin = 'round';
    const f = Math.min(30, W0 * 0.07);
    ctx.font = `900 ${f}px system-ui, sans-serif`;
    ctx.lineWidth = 6;
    ctx.strokeStyle = '#3a1a00';
    const t = hungerEat > 0 ? `🍽 Essen … ${Math.min(100, Math.round((hungerEat / HUNGER_EAT_T) * 100))} %` : 'Hunger! Ab ins Restaurant';
    ctx.strokeText(t, W0 / 2, view.cssH * 0.2, W0 * 0.94);
    ctx.fillStyle = '#ffb347';
    ctx.fillText(t, W0 / 2, view.cssH * 0.2, W0 * 0.94);
    ctx.restore();
  }
  if (hungerT <= 0 && hungerMsgT <= 0) return;
  const W = view.cssW;
  const H = view.cssH;
  ctx.save();
  if (hungerT > 0) {
    const pulse = 0.3 + 0.35 * Math.abs(Math.sin(performance.now() / (hungerT < 10 ? 140 : 300)));
    const edge = Math.max(14, Math.min(W, H) * 0.05);
    const g = (x0: number, y0: number, x1: number, y1: number): CanvasGradient => {
      const gr = ctx.createLinearGradient(x0, y0, x1, y1);
      gr.addColorStop(0, `rgba(255,140,0,${pulse})`);
      gr.addColorStop(1, 'rgba(255,140,0,0)');
      return gr;
    };
    ctx.fillStyle = g(0, 0, edge, 0);
    ctx.fillRect(0, 0, edge, H);
    ctx.fillStyle = g(W, 0, W - edge, 0);
    ctx.fillRect(W - edge, 0, edge, H);
    ctx.fillStyle = g(0, H, 0, H - edge);
    ctx.fillRect(0, H - edge, W, edge);
    ctx.fillStyle = g(0, 0, 0, edge);
    ctx.fillRect(0, 0, W, edge);
  }
  const y = H * 0.27;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.lineJoin = 'round';
  if (hungerT > 0) {
    const big = Math.min(46, W * 0.11);
    ctx.font = `900 ${big}px system-ui, sans-serif`;
    ctx.lineWidth = 6;
    ctx.strokeStyle = '#3a1a00';
    ctx.strokeText('Hunger! Ab ins Restaurant', W / 2, y, W * 0.94);
    ctx.fillStyle = '#ffb347';
    ctx.fillText('Hunger! Ab ins Restaurant', W / 2, y, W * 0.94);
    ctx.font = `800 ${big * 0.6}px system-ui, sans-serif`;
    const txt = hungerEat > 0 ? `🍽 Essen … ${Math.min(100, Math.round((hungerEat / HUNGER_EAT_T) * 100))} %` : `🍽 Restaurant in ${Math.ceil(hungerT)} s`;
    ctx.lineWidth = 5;
    ctx.strokeText(txt, W / 2, y + big * 0.85);
    ctx.fillStyle = hungerT < 10 ? '#ff6060' : '#ffffff';
    ctx.fillText(txt, W / 2, y + big * 0.85);
  } else if (hungerMsgT > 0 && !hungerFail) {
    ctx.font = `800 ${Math.min(22, W * 0.055)}px system-ui, sans-serif`;
    ctx.lineWidth = 5;
    ctx.strokeStyle = '#000';
    ctx.strokeText(hungerMsg, W / 2, y, W * 0.9);
    ctx.fillStyle = hungerSlowT > 0 ? '#ffb0b0' : '#b0ffb8';
    ctx.fillText(hungerMsg, W / 2, y, W * 0.9);
  }
  ctx.restore();
}

// ---- Nervensäge ----
const PEST_LINES: string[] = nervensaegeData as string[];
const PEST_SEE_R = 240;
const PEST_LOSE_R = 420;
const PEST_TALK_TIME = 7;
const PEST_COOLDOWN = 14;
function pestCount(): number {
  return modePestsOverride(2 + hallSizeIdx);
}
function spawnPests(n = pestCount()): void {
  const players0 = players;
  for (let k = 0, tries = 0; k < n && tries < 60; tries++) {
    const x = 120 + Math.random() * (WORLD_W - 240);
    const y = 120 + Math.random() * (WORLD_H - 240);
    if (players0.some((pl) => Math.hypot(pl.x - x, pl.y - y) < 500)) continue;
    const f = findFreeNear(x, y, PERSON_R + 2, 160);
    if (!f) continue;
    const p = makePersonBase('nobody', false);
    p.x = f.x;
    p.y = f.y;
    p.lifeT = 1e9;
    p.pest = { cd: 3 + Math.random() * 5, follow: false };
    persons.push(p);
    k++;
  }
}
function updatePest(p: Person, dt: number): boolean {
  const st = p.pest!;
  st.cd = Math.max(0, st.cd - dt);
  if (p.bubbleT > 0) {
    p.bubbleT -= dt;
    p.vx = 0;
    p.vy = 0;
    return true;
  }
  const hum = human();
  const d = hum ? Math.hypot(hum.x - p.x, hum.y - p.y) : Infinity;
  if (hum && st.cd <= 0 && !giftOn('tarnweste') && (d < PEST_SEE_R || (st.follow && d < PEST_LOSE_R))) {
    if (!st.follow) {
      st.follow = true;
      p.bubble = 'Hallo! Moment mal!';
      sfx('murmel');
      p.bubbleT = bubbleDur(p.bubble, 0.6);
    }
    npcMoveTo(p, hum, PERSON_SPEED * 1.15, dt);
    return true;
  }
  st.follow = false;
  const hit = tryMoveCircle(p, p.x + p.vx * 0.6 * dt, p.y + p.vy * 0.6 * dt, p.r);
  if (hit || Math.random() < 0.3 * dt) {
    const a = Math.random() * Math.PI * 2;
    p.vx = Math.cos(a) * PERSON_SPEED;
    p.vy = Math.sin(a) * PERSON_SPEED;
  }
  return true;
}
function drawPestMarks(): void {
  for (const p of persons) {
    if (!p.pest || p.hidden || !inView(p.x - 30, p.y - 60, 60, 80)) continue;
    ctx.save();
    ctx.strokeStyle = p.pest.follow ? '#ff5fd2' : 'rgba(255,95,210,0.55)';
    ctx.lineWidth = 2.5;
    ctx.beginPath();
    ctx.ellipse(p.x, p.y + p.r * 0.7, p.r * 1.1, p.r * 0.45, 0, 0, Math.PI * 2);
    ctx.stroke();
    ctx.restore();
  }
}

const HUD_TOP_FREE = 100; // unter Munition/Minimap
// ---- Hallendurchsagen (Daten: src/data/durchsagen.json), dezent oben als Laufband ----
const DS_FILE = durchsagenData as { durchsagen?: Record<string, string[]>; ticker?: string[] };
const DS_MIN = 70, DS_MAX = 110, DS_SHOW = 6.5;
let dsNext = 40 + Math.random() * 30;
let dsCur: { text: string; t: number } | null = null;
let dsKontext = '';
function durchsageKontext(k: string): void { dsKontext = k; }
function durchsageJetzt(k?: string): void {
  const lists = DS_FILE.durchsagen ?? {};
  const key = k ?? (dsKontext && lists[dsKontext]?.length && Math.random() < 0.6 ? dsKontext : Math.random() < 0.2 && DS_FILE.ticker?.length ? '_ticker' : 'allgemein');
  const arr = key === '_ticker' ? DS_FILE.ticker ?? [] : lists[key] ?? lists.allgemein ?? [];
  if (!arr.length) return;
  dsCur = { text: arr[(Math.random() * arr.length) | 0]!, t: DS_SHOW };
  dsNext = DS_MIN + Math.random() * (DS_MAX - DS_MIN);
}
function tickDurchsage(dt: number): void {
  if (dsCur) { dsCur.t -= dt; if (dsCur.t <= 0) dsCur = null; }
  dsNext -= dt;
  if (dsNext <= 0) durchsageJetzt();
}
function drawDurchsage(top: number): void {
  if (!dsCur) return;
  const W = view.cssW;
  const a = Math.min(1, dsCur.t, DS_SHOW - dsCur.t) ;
  ctx.save();
  ctx.globalAlpha = Math.max(0, Math.min(1, a * 2)) * 0.85;
  ctx.font = '12px system-ui, sans-serif';
  const txt = '📢 ' + dsCur.text;
  const w = Math.min(W - 16, ctx.measureText(txt).width + 16);
  ctx.fillStyle = 'rgba(20,24,34,0.75)';
  ctx.fillRect((W - w) / 2, top, w, 20);
  ctx.fillStyle = '#dde6f5';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(txt, W / 2, top + 10, w - 10);
  ctx.restore();
}

// ---- Infostand + Geschenke (Daten: src/data/geschenke.json) ----
type GiftDef = { id: string; name: string; beschreibung: string; effekt: string; dauer_s: number; funktional: boolean };
const GIFT_FILE = geschenkeData as { geschenke?: GiftDef[]; uebergabe_sprueche?: string[] };
const GIFTS: GiftDef[] = (GIFT_FILE.geschenke ?? []).filter((g) => g && typeof g.id === 'string' && g.dauer_s > 0);
const GIFT_LINES: string[] = GIFT_FILE.uebergabe_sprueche?.length ? GIFT_FILE.uebergabe_sprueche : ['Bitte sehr.'];
const INFO_R = 60;
// Balancing pro id (Mechanik im Code)
const GIFT_STAMINA_MUL = 0.5; // panzerschokolade: Kaffee/Essen hält doppelt
const GIFT_TURBO = 1.4; // turbosohlen
const GIFT_KART = 1.8; // gokart
const GIFT_KART_STEER = 3; // träges Lenken (1/s)
const GIFT_TOW = 3; // lanyard_xxl
const GIFT_MAGNET_TOW = 2; // kunden_magnet
const GIFT_REFILL = 2; // powerbank
const GIFT_VIP_R = 180; // vip_ticket
const GIFT_MEGA_R = 280; // megafon
let infoDesk: { x: number; y: number } | null = null;
let gift: { def: GiftDef; t: number } | null = null;
let giftInZone = false;
let giftReveal: { name: string; t: number } | null = null;
const GIFT_REVEAL_T = 2;
let giftBubble = '';
let giftBubbleT = 0;
let giftShield = false;
let giftsGiven = 0;
let kartV = { x: 0, y: 0 };
function giftOn(id: string): boolean {
  return !!gift && gift.def.id === id;
}
function initInfoDesk(): void {
  gift = null;
  giftInZone = false;
  giftReveal = null;
  giftBubbleT = 0;
  giftShield = false;
  kartV = { x: 0, y: 0 };
  const f = findFreeNear(WORLD_W / 2, WORLD_H / 2, 40, 600);
  infoDesk = f ? { x: f.x, y: f.y } : null;
}
function giveGift(_hum: GamePlayer, def?: GiftDef): boolean {
  if (!infoDesk || !GIFTS.length) return false;
  // Überraschungspaket: zufällig, möglichst nicht dasselbe wie das aktive
  let d = def ?? GIFTS[(Math.random() * GIFTS.length) | 0]!;
  if (!def && gift && GIFTS.length > 1 && d.id === gift.def.id) d = GIFTS[(GIFTS.indexOf(d) + 1 + ((Math.random() * (GIFTS.length - 1)) | 0)) % GIFTS.length]!;
  gift = { def: d, t: d.dauer_s };
  giftReveal = { name: d.name, t: GIFT_REVEAL_T };
  kartV = { x: 0, y: 0 };
  giftShield = d.id === 'formular_b7';
  giftsGiven++;
  giftBubble = GIFT_LINES[(Math.random() * GIFT_LINES.length) | 0]!;
  giftBubbleT = bubbleDur(giftBubble, 3.5);
  sfx('tadaa');
  return true;
}
function tickGift(dt: number): void {
  tickDurchsage(dt);
  if (giftReveal) {
    giftReveal.t -= dt;
    if (giftReveal.t <= 0) giftReveal = null;
  }
  giftBubbleT = Math.max(0, giftBubbleT - dt);
  const hum = human();
  if (!hum) return;
  if (gift) {
    gift.t -= dt;
    if (gift.t <= 0) {
      gift = null;
      giftShield = false;
    }
  }
  // Abholen beim Betreten der Zone (jederzeit, ersetzt das aktive)
  const inZ = !!infoDesk && Math.hypot(hum.x - infoDesk.x, hum.y - infoDesk.y) < INFO_R;
  if (inZ && !giftInZone) giveGift(hum);
  giftInZone = inZ;
}
function giftSpeedMul(): number {
  return giftOn('gokart') ? GIFT_KART : giftOn('turbosohlen') ? GIFT_TURBO : 1;
}
function drawInfoDesk(): void {
  if (!infoDesk || !inView(infoDesk.x - 60, infoDesk.y - 70, 120, 120)) return;
  const { x, y } = infoDesk;
  ctx.save();
  ctx.fillStyle = 'rgba(80,160,255,0.18)';
  ctx.beginPath();
  ctx.arc(x, y, INFO_R, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = '#2c5fa8';
  ctx.fillRect(x - 34, y - 12, 68, 22);
  ctx.fillStyle = '#e8f0ff';
  ctx.fillRect(x - 34, y - 14, 68, 4);
  ctx.fillStyle = '#f3c9a0';
  ctx.beginPath();
  ctx.arc(x, y - 26, 8, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = '#2c5fa8';
  ctx.fillRect(x - 8, y - 18, 16, 8);
  ctx.fillStyle = '#fff';
  ctx.font = 'bold 12px system-ui, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText('i  Info', x, y);
  // Pakete auf dem Tresen (Inhalt unsichtbar)
  for (const dx of [-24, 18]) {
    ctx.fillStyle = '#e04a5a';
    ctx.fillRect(x + dx, y - 22, 9, 9);
    ctx.fillStyle = '#ffe08a';
    ctx.fillRect(x + dx + 3.5, y - 22, 2, 9);
  }
  ctx.restore();
  if (giftBubbleT > 0) drawBubble(x, y - 30, giftBubble, 10);
}
function drawGiftCosmetics(pl: GamePlayer, sk: string, drawSize: number): void {
  if (!pl.isHuman || !gift) return;
  const id = gift.def.id;
  if (id === 'gokart') {
    ctx.fillStyle = '#d33';
    ctx.fillRect(pl.x - 20, pl.y + 4, 40, 14);
    ctx.fillStyle = '#222';
    for (const [dx, dy] of [[-18, 4], [14, 4], [-18, 16], [14, 16]] as const) ctx.fillRect(pl.x + dx, pl.y + dy - 2, 6, 5);
  } else if (id === 'luftballon') {
    ctx.strokeStyle = '#ddd';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(pl.x + 8, pl.y - 6);
    ctx.lineTo(pl.x + 14, pl.y - 52);
    ctx.stroke();
    ctx.fillStyle = '#ff4d6d';
    ctx.beginPath();
    ctx.ellipse(pl.x + 14, pl.y - 64, 10, 13, 0, 0, Math.PI * 2);
    ctx.fill();
  } else if (id === 'grosser_kopf') {
    const big = drawSize * 1.6;
    ctx.save();
    ctx.beginPath();
    ctx.rect(pl.x - big, pl.y - big, big * 2, big * 0.5 + big * 0.06);
    ctx.clip();
    drawSprite(ctx, sk, pl.x, pl.y - drawSize * 0.12, big, pl.anim.row, pl.anim.frame);
    ctx.restore();
  } else if (id === 'blumenhut') {
    const hy = pl.y - drawSize * 0.62;
    ctx.fillStyle = '#c8a060';
    ctx.beginPath();
    ctx.ellipse(pl.x, hy, 17, 5, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillRect(pl.x - 9, hy - 9, 18, 9);
    const cols = ['#ff6fa8', '#ffd84d', '#8fd0ff', '#ff9f40', '#c08fff'];
    for (let i = 0; i < 5; i++) {
      ctx.fillStyle = cols[i]!;
      ctx.beginPath();
      ctx.arc(pl.x - 12 + i * 6, hy - 2 - (i % 2) * 5, 3.2, 0, Math.PI * 2);
      ctx.fill();
    }
  } else if (id === 'namensschild') {
    ctx.fillStyle = '#fff';
    ctx.fillRect(pl.x - 30, pl.y - drawSize * 0.75 - 14, 60, 14);
    ctx.fillStyle = '#222';
    ctx.font = 'bold 10px system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(pl.name, pl.x, pl.y - drawSize * 0.75 - 7, 56);
  }
}
function drawGiftReveal(): void {
  if (!giftReveal) return;
  const W = view.cssW;
  const H = view.cssH;
  const p = 1 - giftReveal.t / GIFT_REVEAL_T; // 0..1
  const cx = W / 2;
  const cy = H * 0.45;
  const s = Math.min(W, H) * 0.12;
  ctx.save();
  ctx.globalAlpha = Math.min(1, giftReveal.t * 3);
  ctx.fillStyle = 'rgba(0,0,0,0.55)';
  ctx.fillRect(cx - s * 2.4, cy - s * 1.6, s * 4.8, s * 3);
  if (p < 0.45) {
    // Paket wackelt
    const wob = Math.sin(p * 60) * 0.15;
    ctx.translate(cx, cy);
    ctx.rotate(wob);
    ctx.fillStyle = '#e04a5a';
    ctx.fillRect(-s / 2, -s / 2, s, s);
    ctx.fillStyle = '#ffe08a';
    ctx.fillRect(-s * 0.08, -s / 2, s * 0.16, s);
    ctx.fillRect(-s / 2, -s * 0.08, s, s * 0.16);
  } else {
    // Deckel fliegt, Name erscheint
    const q = (p - 0.45) / 0.55;
    ctx.fillStyle = '#e04a5a';
    ctx.fillRect(cx - s / 2, cy, s, s / 2);
    ctx.fillRect(cx - s * 0.55, cy - s * 0.2 - q * s, s * 1.1, s * 0.18);
    ctx.font = `900 ${Math.round(s * 0.42)}px system-ui, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.lineWidth = 5;
    ctx.strokeStyle = '#000';
    ctx.strokeText(giftReveal.name, cx, cy - s * 0.7, W * 0.9);
    ctx.fillStyle = '#ffe08a';
    ctx.fillText(giftReveal.name, cx, cy - s * 0.7, W * 0.9);
  }
  ctx.restore();
}
function drawGiftHud(top: number): void {
  drawGiftReveal();
  if (!gift) return;
  const W = view.cssW;
  const txt = `🎁 ${gift.def.name} · ${Math.ceil(gift.t)} s`;
  ctx.save();
  ctx.font = 'bold 13px system-ui, sans-serif';
  const w = Math.min(W - 20, ctx.measureText(txt).width + 18);
  const x = W / 2 - w / 2;
  ctx.fillStyle = 'rgba(0,0,0,0.6)';
  ctx.fillRect(x, top, w, 22);
  ctx.fillStyle = '#ffe08a';
  ctx.fillRect(x, top + 19, w * (gift.t / gift.def.dauer_s), 3);
  ctx.fillStyle = '#fff';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(txt, W / 2, top + 10, w - 8);
  ctx.restore();
}

// ---- Security-Streife (rein dekorativ, keine Spielwirkung) ----
const SECURITY_LINES: string[] = (securityData as string[]).filter((x) => typeof x === 'string' && x.length > 0);
type Guard = { x: number; y: number; r: number; route: Vec[]; i: number; phase: 'patrol' | 'scan' | 'out'; t: number; face: number; bubble: string; bubbleT: number; talkCd: number };
const guards: Guard[] = [];
let guardCells: Vec[] = [];
function securityCount(): number {
  return 2 + (hallSizeIdx > 0 ? 1 : 0);
}
function guardEntrance(): Vec {
  const e = entrancePoints[(Math.random() * entrancePoints.length) | 0];
  return e ? { x: e.x, y: e.y } : { x: WORLD_W / 2, y: WORLD_H - 120 };
}
function guardNewRoute(g: Guard): void {
  const n = 4 + ((Math.random() * 3) | 0);
  g.route = [];
  for (let k = 0; k < n && guardCells.length; k++) g.route.push(guardCells[(Math.random() * guardCells.length) | 0]!);
  g.route.push(guardEntrance());
  g.i = 0;
}
function initSecurity(): void {
  guards.length = 0;
  guardCells = collectReachableAisleCells(160);
  for (let k = 0; k < securityCount(); k++) {
    const e = guardEntrance();
    const g: Guard = { x: e.x, y: e.y, r: PERSON_R, route: [], i: 0, phase: 'out', t: 5 + k * 25 + Math.random() * 10, face: 0, bubble: '', bubbleT: 0, talkCd: 8 + Math.random() * 10 };
    guardNewRoute(g);
    guards.push(g);
  }
}
function tickSecurity(dt: number): void {
  for (const g of guards) {
    g.bubbleT = Math.max(0, g.bubbleT - dt);
    g.talkCd -= dt;
    if (g.phase === 'out') {
      g.t -= dt;
      if (g.t <= 0) {
        const e = guardEntrance();
        g.x = e.x;
        g.y = e.y;
        guardNewRoute(g);
        g.phase = 'patrol';
      }
      continue;
    }
    if (g.phase === 'scan') {
      g.t -= dt;
      g.face += dt * 1.6 * Math.sin(simTime * 0.9 + g.x);
      if (g.t <= 0) g.phase = 'patrol';
      continue;
    }
    const goal = g.route[g.i];
    if (!goal) {
      g.phase = 'out';
      g.t = 30 + Math.random() * 50;
      continue;
    }
    const d = Math.hypot(goal.x - g.x, goal.y - g.y);
    if (d < 30) {
      g.i++;
      if (g.i < g.route.length) {
        g.phase = 'scan';
        g.t = 1.8 + Math.random() * 1.6;
      }
    } else {
      const st = npcPathStep(g, goal) ?? goal;
      const dx = st.x - g.x;
      const dy = st.y - g.y;
      const l = Math.hypot(dx, dy) || 1;
      const sp = PERSON_SPEED * 0.75 * dt;
      g.face = Math.atan2(dy, dx);
      // dekorativ: kein Kollisionseinfluss auf andere, aber nicht durch Wände
      if (tryMoveCircle(g, g.x + (dx / l) * sp, g.y + (dy / l) * sp, g.r)) {
        g.t += dt;
        if (g.t > 2.5) {
          g.t = 0;
          g.i++;
        }
      } else g.t = 0;
    }
    if (g.talkCd <= 0 && SECURITY_LINES.length) {
      g.talkCd = 14 + Math.random() * 18;
      g.bubble = SECURITY_LINES[(Math.random() * SECURITY_LINES.length) | 0]!;
      g.bubbleT = bubbleDur(g.bubble, 3.2);
      const hh = human();
      if (hh && Math.hypot(hh.x - g.x, hh.y - g.y) < 320) sfx('funk');
    }
  }
}
function drawSecurity(): void {
  for (const g of guards) {
    if (g.phase === 'out' || !inView(g.x - 30, g.y - 40, 60, 70)) continue;
    const { x, y } = g;
    const bob = g.phase === 'patrol' ? Math.sin(simTime * 9 + x) * 1.2 : 0;
    ctx.save();
    ctx.fillStyle = 'rgba(0,0,0,0.25)';
    ctx.beginPath();
    ctx.ellipse(x, y + 12, 12, 5, 0, 0, Math.PI * 2);
    ctx.fill();
    // Körper (dunkle Uniform)
    ctx.fillStyle = '#1d2433';
    ctx.beginPath();
    ctx.ellipse(x, y + 2 + bob, 12, 13, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#c9a227';
    ctx.fillRect(x - 3, y - 4 + bob, 6, 3);
    // Kopf
    ctx.fillStyle = '#e2b892';
    ctx.beginPath();
    ctx.arc(x, y - 12 + bob, 8, 0, Math.PI * 2);
    ctx.fill();
    // Sonnenbrille
    ctx.fillStyle = '#05070a';
    ctx.fillRect(x - 7, y - 14 + bob, 14, 3.5);
    // Funkgerät (beim Scannen/Reden an den Kopf)
    const radio = g.phase === 'scan' || g.bubbleT > 0;
    const rx = radio ? x + 9 : x + 11;
    const ry = radio ? y - 14 + bob : y + 4 + bob;
    ctx.fillStyle = '#111';
    ctx.fillRect(rx - 2, ry - 4, 5, 8);
    ctx.strokeStyle = '#111';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(rx + 1, ry - 4);
    ctx.lineTo(rx + 1, ry - 10);
    ctx.stroke();
    if (g.phase === 'scan') {
      ctx.strokeStyle = 'rgba(160,220,255,0.35)';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(x, y - 12, 26, g.face - 0.4, g.face + 0.4);
      ctx.stroke();
    }
    ctx.restore();
    if (g.bubbleT > 0) drawBubble(x, y - 8, g.bubble, g.r);
  }
}

// ---- Sprachausgabe: nur Spieler oder nahe Blasen, je Figur nur bei neuem Text
const voiceLast = new Map<number, string>();
const VOICE_NEAR = 260;
function voiceObserve(fid: number, text: string, x: number, y: number): void {
  if (voiceLast.get(fid) === text) return;
  voiceLast.set(fid, text);
  if (voiceLast.size > 400) voiceLast.clear();
  const h = human();
  if (!h) return;
  if (fid !== h.id && Math.hypot(x - h.x, y - h.y) > VOICE_NEAR) return;
  speak(text, fid);
}
function setupAudioUi(): void {
  installAudioUnlock();
  const box = document.getElementById('audio-ui');
  if (!box) return;
  const btns: [string, 'music' | 'sfx' | 'voice', string][] = [
    ['♪', 'music', 'Musik'],
    ['🔊', 'sfx', 'Effekte'],
    ['💬', 'voice', 'Stimme'],
  ];
  for (const [ico, k, label] of btns) {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'aud-btn';
    b.dataset.k = k;
    b.title = label;
    b.setAttribute('aria-label', label);
    b.textContent = ico;
    const sync = (): void => {
      b.classList.toggle('off', !audioPrefs()[k]);
    };
    sync();
    b.addEventListener('pointerdown', (e) => e.stopPropagation());
    b.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      toggleAudio(k);
      sync();
    });
    box.appendChild(b);
  }
}
setupAudioUi();

// ---- STEP 26: Spielmodi (Messetag / Dauermesse / Duell) ----
type GameMode = 'frei' | 'messetag' | 'dauermesse' | 'duell';
const MODE_KEY = 'messe_mode_v1';
const HS_KEY = 'messe_hs_v1';
const MODE_DURS = [120, 180, 300, 600, 900, 1200, 0]; // 0 = unendlich
const MODE_LABEL: Record<GameMode, string> = { frei: 'Freies Spiel', messetag: 'Messetag', dauermesse: 'Dauermesse', duell: 'Duell' };
// Messetag: [Tag][Halle], Basis je 90 s
const MT_BASE_S = 90;
const MT_QUOTA = [[4, 5, 7], [4, 6, 7], [5, 6, 8]];
const MT_RIVALS = [[1, 2, 3], [2, 3, 4], [2, 3, 4]];
const MT_PESTS = [[1, 3, 4], [2, 3, 5], [2, 4, 5]];
const MT_VIP = [[0.1, 0.15, 0.2], [0.12, 0.17, 0.22], [0.14, 0.19, 0.25]];
const MT_STREAK_STEP = 0.2;
const MT_STREAK_MAX = 2;
const MT_ESC_EVERY = 300; // lange Modi: alle 5 min +1 Rivale, +1 Nervensäge
const MT_MAX_RIVALS = 6;
const MT_MAX_PESTS = 8;
// Dauermesse
const DM_START = 100;
const DM_LOSS = 1.2;
const DM_CUSTOMER = 12;
const DM_VIP = 40;
const DM_LOSS_F = [1.0, 1.1, 1.2, 1.3, 1.35, 1.4];
// Duell
const DU_DUR = 120;
const DU_SPEED = [0.8, 0.9, 1.0, 1.05, 1.1];
const DU_POACH = [0.03, 0.04, 0.05, 0.06, 0.08];
const DU_STARS = [[1, 3, 6], [1, 4, 7], [2, 4, 8], [2, 5, 9], [3, 6, 10]];
const DU_HALL = [0, 1, 1, 2, 2];

function mtFactor(dur: number): number {
  return dur <= 300 ? 1 : dur < 900 ? 0.9 : 0.85;
}
function stageAt(tbl: number[][], stage: number): number {
  const s = Math.min(8, stage);
  return tbl[(s / 3) | 0]![s % 3]!;
}
function mtQuota(stage: number, dur: number, loop = 0): number {
  const basis = stageAt(MT_QUOTA, stage);
  if (dur <= 0) return basis * 3 + loop;
  return Math.ceil(((basis * dur) / MT_BASE_S) * mtFactor(dur));
}
function stageName(stage: number): string {
  const s = Math.min(8, stage);
  return `Tag ${((s / 3) | 0) + 1} · Halle ${(s % 3) + 1}`;
}
type ModeSave = { mode: GameMode; dur: Record<string, number>; mtStage: Record<string, number>; duHall: number };
function loadModeSave(): ModeSave {
  const def: ModeSave = { mode: 'frei', dur: { messetag: 180, dauermesse: 0 }, mtStage: {}, duHall: 0 };
  try {
    const raw = localStorage.getItem(MODE_KEY);
    if (raw) return { ...def, ...(JSON.parse(raw) as Partial<ModeSave>) };
  } catch {
    /* ignorieren */
  }
  return def;
}
const modeSave = loadModeSave();
function storeModeSave(): void {
  try {
    localStorage.setItem(MODE_KEY, JSON.stringify(modeSave));
  } catch {
    /* ignorieren */
  }
}
function hsKey(m: GameMode, dur: number, hall = 0): string {
  return m === 'duell' ? `duell_h${hall + 1}` : `${m}_${dur > 0 ? dur : 'endlos'}`;
}
function hsAll(): Record<string, number> {
  try {
    return JSON.parse(localStorage.getItem(HS_KEY) ?? '{}') as Record<string, number>;
  } catch {
    return {};
  }
}
function hsGet(m: GameMode, dur: number, hall = 0): number {
  return hsAll()[hsKey(m, dur, hall)] ?? 0;
}
function hsSet(m: GameMode, dur: number, v: number, hall = 0): boolean {
  const all = hsAll();
  const k = hsKey(m, dur, hall);
  if (v <= (all[k] ?? 0)) return false;
  all[k] = v;
  try {
    localStorage.setItem(HS_KEY, JSON.stringify(all));
  } catch {
    /* ignorieren */
  }
  return true;
}
function modeDurOf(m: GameMode): number {
  if (m === 'duell') return DU_DUR;
  return modeSave.dur[m] ?? (m === 'messetag' ? 180 : 0);
}

const ms = {
  mode: 'frei' as GameMode,
  dur: 0,
  t: 0,
  stage: 0,
  loop: 0,
  delivered: 0,
  quota: 0,
  streak: 1,
  budget: DM_START,
  hall: 0,
  over: false,
  escN: 0,
  bannerT: 0,
  banner: '',
  ended: [] as string[],
};

/** Vor dem Hallenbau: Hallengröße je Modus */
function modePrepareHall(): void {
  ms.mode = modeSave.mode;
  if (ms.mode === 'duell') {
    ms.hall = Math.max(0, Math.min(4, modeSave.duHall));
    hallSizeIdx = DU_HALL[ms.hall]!;
  } else if (ms.mode === 'messetag') {
    ms.stage = modeSave.mtStage[String(modeDurOf('messetag'))] ?? 0;
    hallSizeIdx = Math.min(2, ms.stage % 3);
  }
}
function modeRivalsOverride(n: number): number {
  if (ms.mode === 'messetag') return stageAt(MT_RIVALS, ms.stage);
  if (ms.mode === 'duell') return 1;
  return n;
}
function modePestsOverride(n: number): number {
  if (ms.mode === 'messetag') return stageAt(MT_PESTS, ms.stage);
  if (ms.mode === 'duell') return 1 + Math.min(2, ms.hall >> 1);
  return n;
}
function modeVipsOverride(n: number): number {
  if (ms.mode === 'messetag') return Math.max(1, Math.round(stageAt(MT_VIP, ms.stage) * 20));
  return n;
}
function modeStart(): void {
  ms.dur = ms.mode === 'frei' ? 0 : modeDurOf(ms.mode);
  ms.t = 0;
  ms.loop = 0;
  ms.delivered = 0;
  ms.streak = 1;
  ms.budget = DM_START;
  ms.over = false;
  ms.escN = 0;
  ms.quota = ms.mode === 'messetag' ? mtQuota(ms.stage, ms.dur) : 0;
  ms.banner = ms.mode === 'frei' ? '' : ms.mode === 'messetag' ? `${stageName(ms.stage)} · Quote ${ms.quota}` : ms.mode === 'duell' ? `Duell · Halle ${ms.hall + 1}` : 'Dauermesse · Budget halten!';
  ms.bannerT = ms.mode === 'frei' ? 0 : 3;
  setSong(ms.mode === 'dauermesse' || ms.mode === 'duell' ? 'hektik' : 'fahrstuhl');
}
function modeStreakReset(): void {
  ms.streak = 1;
}
function modeOnDeliver(pl: GamePlayer, p: Person): void {
  if (ms.mode === 'frei' || ms.over || !pl.isHuman) return;
  if (ms.mode === 'messetag') {
    const bonus = Math.round(p.points * (ms.streak - 1));
    if (bonus > 0) {
      pl.score += bonus;
      spawnFloat(pl.x, pl.y - 44, `Serie ×${ms.streak.toFixed(1)} +${bonus}`, '#ffd27a');
    }
    ms.streak = Math.min(MT_STREAK_MAX, +(ms.streak + MT_STREAK_STEP).toFixed(2));
    ms.delivered++;
    if (ms.dur <= 0 && ms.delivered >= ms.quota) modeUnlockNext();
  } else if (ms.mode === 'dauermesse') {
    ms.budget = Math.min(200, ms.budget + (p.kind === 'vip' ? DM_VIP : DM_CUSTOMER));
    ms.delivered++;
  } else {
    ms.delivered++;
  }
}
function dmTempo(): number {
  return Math.min(2, 1 + Math.floor(ms.t / 60) * 0.2);
}
function modeRivalSpeedMul(): number {
  if (ms.mode === 'duell') return DU_SPEED[ms.hall]!;
  if (ms.mode === 'dauermesse') return 1 + (dmTempo() - 1) * 0.3;
  return 1;
}
function modeSpawnTempo(): number {
  return ms.mode === 'dauermesse' ? dmTempo() : 1;
}
function modeAddRivals(n: number): void {
  let added = 0;
  for (let i = 0; i < n && players.length - 1 < MT_MAX_RIVALS; i++) if (spawnRivalRuntime()) added++;
  if (added) {
    pathGrid = buildPathGrid(WORLD_W, WORLD_H, 40, (wx, wy) => blocked(wx, wy, PLAYER_R + 8));
    rebuildMinimapStatic();
  }
}
function modeAddPests(n: number): void {
  const have = persons.filter((p) => p.pest).length;
  const want = Math.min(MT_MAX_PESTS, have + n);
  if (want > have) spawnPests(want - have);
}
function modeUnlockNext(): void {
  const prev = ms.stage;
  if (ms.stage < 8) ms.stage++;
  else ms.loop++;
  ms.delivered = 0;
  ms.quota = mtQuota(ms.stage, 0, ms.loop);
  modeSave.mtStage['0'] = Math.max(modeSave.mtStage['0'] ?? 0, ms.stage);
  storeModeSave();
  // Hallengröße bleibt; Gegner und Nervensägen auf die neue Stufe anheben
  const r = stageAt(MT_RIVALS, ms.stage) - stageAt(MT_RIVALS, prev);
  const pe = stageAt(MT_PESTS, ms.stage) - stageAt(MT_PESTS, prev);
  if (r > 0) modeAddRivals(r);
  if (pe > 0) modeAddPests(pe);
  targetVips = Math.max(targetVips, modeVipsOverride(targetVips));
  ms.banner = ms.loop > 0 ? `Runde ${ms.loop + 1} · Quote ${ms.quota}` : `${stageName(ms.stage)} freigeschaltet!`;
  ms.bannerT = 3;
  sfx('tadaa');
}
function modeEnd(win: boolean, text: string): void {
  if (ms.over) return;
  ms.over = true;
  const hum = human();
  let score = hum?.score ?? 0;
  if (ms.mode === 'dauermesse' && win) score += Math.floor(ms.budget / 10);
  if (hum) hum.score = score;
  ms.ended.push(`${ms.mode}:${win ? 'win' : 'lose'}:${score}`);
  sfx('glocke');
  durchsageKontext('feierabend');
  durchsageJetzt('feierabend');
  const rec = hsSet(ms.mode, ms.dur, score, ms.hall);
  if (win && ms.mode === 'messetag' && ms.dur > 0) {
    const k = String(ms.dur);
    modeSave.mtStage[k] = Math.min(8, Math.max(modeSave.mtStage[k] ?? 0, ms.stage + 1));
  }
  if (win && ms.mode === 'duell') modeSave.duHall = Math.min(4, Math.max(modeSave.duHall, ms.hall + 1));
  storeModeSave();
  const full = `${text} · ${score} Punkte${rec ? ' · Neuer Rekord!' : ''}`;
  ms.banner = full;
  ms.bannerT = 99;
  const res = document.getElementById('mode-result');
  if (res) res.textContent = full;
  btnStart.textContent = win ? 'Weiter' : 'Nochmal';
  setTimeout(() => {
    playing = false;
    menuEl.classList.remove('hidden');
    touchUi.classList.remove('visible');
    touchUi.setAttribute('aria-hidden', 'true');
    renderModeUi();
  }, 1600);
}
function modePoach(dt: number): void {
  const hum = human();
  const rival = players.find((pl) => !pl.isHuman);
  if (!hum || !rival) return;
  const chance = DU_POACH[ms.hall]! * dt;
  for (const p of towList(hum.id)) {
    if (Math.random() < chance) {
      freeFromTow(p);
      captureIntoTow(p, rival.id);
      modeStreakReset();
      spawnFloat(p.x, p.y - 24, 'Abgeworben!', '#ff8a8a');
      break;
    }
  }
}
function tickMode(dt: number): void {
  if (ms.bannerT > 0 && ms.bannerT < 90) ms.bannerT -= dt;
  if (ms.mode === 'frei' || ms.over || !playing) return;
  ms.t += dt;
  const hum = human();
  const long = ms.dur <= 0 || ms.dur >= 600;
  if (ms.mode !== 'duell' && long && Math.floor(ms.t / MT_ESC_EVERY) > ms.escN) {
    ms.escN++;
    modeAddRivals(1);
    modeAddPests(1);
    ms.banner = 'Mehr Betrieb in der Halle!';
    ms.bannerT = 2.5;
  }
  if (ms.mode === 'messetag') {
    if (ms.dur > 0 && ms.t >= ms.dur) {
      const ok = ms.delivered >= ms.quota;
      modeEnd(ok, ok ? `Feierabend! Quote ${ms.delivered}/${ms.quota} geschafft` : `Feierabend. Quote ${ms.delivered}/${ms.quota} verfehlt`);
    }
  } else if (ms.mode === 'dauermesse') {
    const f = DM_LOSS_F[Math.min(5, Math.floor(ms.t / 60))]!;
    ms.budget = Math.max(0, ms.budget - DM_LOSS * f * dt);
    if (ms.budget <= 0) modeEnd(false, `Budget aufgebraucht nach ${fmtClock(ms.t)}`);
    else if (ms.dur > 0 && ms.t >= ms.dur) modeEnd(true, `Überlebt! Restbudget ${Math.round(ms.budget)}`);
  } else if (ms.mode === 'duell') {
    modePoach(dt);
    if (ms.t >= DU_DUR) {
      const rival = players.find((pl) => !pl.isHuman);
      const diff = (hum?.score ?? 0) - (rival?.score ?? 0);
      const st = DU_STARS[ms.hall]!;
      const stars = st.filter((s) => diff >= s).length;
      modeEnd(diff >= 1, diff >= 1 ? `Duell gewonnen (+${diff}) ${'★'.repeat(stars)}${'☆'.repeat(3 - stars)}` : `Duell verloren (${diff})`);
    }
  }
}
function fmtClock(sec: number): string {
  const s = Math.max(0, Math.floor(sec));
  return `${(s / 60) | 0}:${String(s % 60).padStart(2, '0')}`;
}
function modeHudText(): string {
  if (ms.mode === 'frei') return '';
  if (ms.mode === 'messetag') {
    const clock = ms.dur > 0 ? fmtClock(ms.dur - ms.t) : fmtClock(ms.t);
    return `${clock}  Quote ${ms.delivered}/${ms.quota}  Serie ×${ms.streak.toFixed(1)}`;
  }
  if (ms.mode === 'dauermesse') {
    const clock = ms.dur > 0 ? fmtClock(ms.dur - ms.t) : fmtClock(ms.t);
    return `${clock}  Budget ${Math.round(ms.budget)}  Tempo ×${dmTempo().toFixed(1)}`;
  }
  const rival = players.find((pl) => !pl.isHuman);
  return `${fmtClock(DU_DUR - ms.t)}  Du ${human()?.score ?? 0} : ${rival?.score ?? 0}`;
}
/** Zeichnet Modus-Leiste; liefert belegte Höhe */
function drawModeHud(top: number): number {
  const txt = modeHudText();
  if (ms.bannerT > 0 && ms.banner) {
    ctx.save();
    ctx.font = 'bold 18px system-ui, sans-serif';
    ctx.textAlign = 'center';
    const w = Math.min(view.cssW - 20, ctx.measureText(ms.banner).width + 30);
    const y = view.cssH * 0.3;
    ctx.fillStyle = 'rgba(10,12,20,0.82)';
    ctx.fillRect(view.cssW / 2 - w / 2, y - 22, w, 36);
    ctx.fillStyle = '#ffe08a';
    ctx.fillText(ms.banner, view.cssW / 2, y + 3, w - 16);
    ctx.restore();
  }
  if (!txt) return 0;
  ctx.save();
  ctx.font = 'bold 13px system-ui, sans-serif';
  ctx.textAlign = 'center';
  const w = Math.min(view.cssW - 16, ctx.measureText(txt).width + 22);
  ctx.fillStyle = 'rgba(10,12,20,0.7)';
  ctx.fillRect(view.cssW / 2 - w / 2, top, w, 22);
  if (ms.mode === 'dauermesse') {
    ctx.fillStyle = ms.budget < 25 ? '#c04040' : '#3a8a5a';
    ctx.fillRect(view.cssW / 2 - w / 2, top + 19, (w * Math.min(100, ms.budget)) / 100, 3);
  }
  ctx.fillStyle = '#fff';
  ctx.fillText(txt, view.cssW / 2, top + 15, w - 10);
  ctx.restore();
  return 26;
}
function modeInfoText(m: GameMode): string {
  const d = modeDurOf(m);
  if (m === 'messetag') {
    const st = modeSave.mtStage[String(d)] ?? 0;
    return `${stageName(st)}: Bring bis Feierabend ${mtQuota(st, d)} Kunden an deinen Stand. Serien geben Bonus.${d <= 0 ? ' Endlos: Quote erfüllt = nächste Halle.' : ''}`;
  }
  if (m === 'dauermesse') return 'Dein Budget schmilzt. Jeder Kunde +12, VIP +40. Das Tempo steigt jede Minute.';
  if (m === 'duell') return `Halle ${modeSave.duHall + 1} von 5: Zwei Minuten gegen einen Rivalen. Er wirbt deine Kunden im Schlepptau ab.`;
  return 'Ohne Uhr und Quote. Halle frei einstellen.';
}
function renderModeUi(): void {
  const m = modeSave.mode;
  document.querySelectorAll<HTMLButtonElement>('.mode-btn').forEach((b) => b.classList.toggle('sel', b.dataset.mode === m));
  const durs = document.getElementById('durs');
  if (durs) {
    durs.style.display = m === 'messetag' || m === 'dauermesse' ? '' : 'none';
    const cur = modeDurOf(m);
    durs.querySelectorAll<HTMLButtonElement>('.dur-btn').forEach((b) => b.classList.toggle('sel', Number(b.dataset.dur) === cur));
  }
  const info = document.getElementById('mode-info');
  if (info) info.textContent = modeInfoText(m);
  const hs = document.getElementById('mode-hs');
  if (hs) {
    const v = m === 'frei' ? 0 : hsGet(m, modeDurOf(m), modeSave.duHall);
    hs.textContent = m === 'frei' ? '' : `Rekord: ${v} Punkte`;
  }
}
function setupModeUi(): void {
  const durs = document.getElementById('durs');
  if (durs && !durs.childElementCount) {
    for (const d of MODE_DURS) {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'dur-btn';
      b.dataset.dur = String(d);
      b.textContent = d > 0 ? `${d / 60} min` : '∞';
      b.addEventListener('click', () => {
        modeSave.dur[modeSave.mode] = d;
        storeModeSave();
        renderModeUi();
      });
      durs.appendChild(b);
    }
  }
  document.querySelectorAll<HTMLButtonElement>('.mode-btn').forEach((b) =>
    b.addEventListener('click', () => {
      modeSave.mode = (b.dataset.mode as GameMode) ?? 'frei';
      storeModeSave();
      const res = document.getElementById('mode-result');
      if (res) res.textContent = '';
      btnStart.textContent = "Los geht's";
      renderModeUi();
    }),
  );
  renderModeUi();
}
function modeSelect(m: GameMode, dur?: number): void {
  modeSave.mode = m;
  if (dur != null) modeSave.dur[m] = dur;
  renderModeUi();
}
function modeDebugInfo(): Record<string, unknown> {
  return { mode: ms.mode, dur: ms.dur, t: Math.round(ms.t), stage: ms.stage, loop: ms.loop, delivered: ms.delivered, quota: ms.quota, streak: ms.streak, budget: Math.round(ms.budget), over: ms.over, rivals: players.length - 1, pests: persons.filter((p) => p.pest).length, hud: modeHudText(), ended: ms.ended.slice(), mlabel: MODE_LABEL[ms.mode] };
}

// ---- Hippie-Demo (Daten: src/data/demo.json) ----
const DEMO_DATA = demoData as { schilder?: string[]; rufe?: string[]; security?: string[]; abgang?: string[] };
const demoList = (k: keyof typeof DEMO_DATA): string[] => (DEMO_DATA[k] ?? []).filter((x) => typeof x === 'string' && x.length > 0);
const DEMO_SCHILDER = demoList('schilder');
const DEMO_RUFE = demoList('rufe');
const DEMO_SECURITY = demoList('security');
const DEMO_ABGANG = demoList('abgang');
const DEMO_SIT_S = 40;
const DEMO_LURE_R = 150;
const DEMO_COLORS = ['#e8a33d', '#c45ab3', '#5ab36a', '#4f8fd8', '#e0d24a', '#e0663f'];
const DEMO_HP = 3;
const DEMO_R = 22;
type Hippie = { stuckT: number; lx: number; ly: number; hp: number; wobT: number; gear: 'banner' | 'trommel' | 'wagen'; x: number; y: number; r: number; off: Vec; sign: string; color: string; bubble: string; bubbleT: number; talkCd: number; leaving: boolean; gone: boolean; exit: Vec };
let demoNext = 75 + Math.random() * 45;
let demoPhase: 'aus' | 'marsch' | 'sitzen' | 'abzug' = 'aus';
let demoT = 0;
let demoGoal: Vec = { x: 0, y: 0 };
let demoGuard: { x: number; y: number; bubble: string; bubbleT: number } | null = null;
const hippies: Hippie[] = [];
const demoStats = { ended: 0, started: 0, poofed: 0, cleared: 0, lured: 0, hits: 0 };
let demoGuardCd = 0;
function demoPick(a: string[]): string {
  return a.length ? a[(Math.random() * a.length) | 0]! : '';
}
function resetDemo(): void {
  hippies.length = 0;
  demoPhase = 'aus';
  demoGuard = null;
  demoGuardCd = 0;
  demoNext = 75 + Math.random() * 45;
}
function startDemo(): boolean {
  if (demoPhase !== 'aus') return false;
  const cells = collectReachableAisleCells(160);
  const hh = human();
  const e = guardEntrance();
  const near = cells.filter((c) => (!hh || Math.hypot(c.x - hh.x, c.y - hh.y) > 250) && Math.hypot(c.x - e.x, c.y - e.y) > 300 && Math.hypot(c.x - e.x, c.y - e.y) < 1100);
  const goal = (near.length ? near : cells)[(Math.random() * (near.length || cells.length)) | 0];
  if (!goal) return false;
  demoGoal = { x: goal.x, y: goal.y };
  hippies.length = 0;
  const n = 5 + ((Math.random() * 3) | 0);
  const signs = [...DEMO_SCHILDER].sort(() => Math.random() - 0.5);
  for (let k = 0; k < n; k++) {
    const a = (k / n) * Math.PI * 2;
    hippies.push({
      hp: DEMO_HP, stuckT: 0, lx: 0, ly: 0,
      wobT: 0,
      gear: k % 3 === 0 ? 'banner' : k % 3 === 1 ? 'trommel' : 'wagen',
      x: e.x + (Math.random() - 0.5) * 40,
      y: e.y + (Math.random() - 0.5) * 20,
      r: DEMO_R,
      off: { x: Math.cos(a) * 62, y: Math.sin(a) * 40 },
      sign: k % 3 !== 1 ? signs[k % Math.max(1, signs.length)] ?? '' : '',
      color: DEMO_COLORS[k % DEMO_COLORS.length]!,
      bubble: '',
      bubbleT: 0,
      talkCd: 1 + k * 1.7 + Math.random() * 2,
      leaving: false,
      gone: false,
      exit: { x: e.x, y: e.y },
    });
  }
  demoPhase = 'marsch';
  demoT = 0;
  demoStats.started++;
  setSong('demo');
  durchsageKontext('demo');
  durchsageJetzt('demo');
  return true;
}
/** Hängender Abzug: sofort verschwinden */
function demoVanish(h: Hippie): void {
  spawnFloat(h.x, h.y - 24, 'Poof!', '#f3c6ff');
  h.gone = true;
}
function demoPoof(h: Hippie): void {
  if (h.leaving) return;
  h.leaving = true;
  h.bubble = demoPick(DEMO_ABGANG);
  h.bubbleT = bubbleDur(h.bubble, 2.4);
  spawnFloat(h.x, h.y - 24, 'Poof!', '#f3c6ff');
  sfx('poof');
  demoStats.poofed++;
}
/** Ein Arcade-Treffer: wackeln + Sterne, nach DEMO_HP Treffern Poof */
function demoHit(h: Hippie): void {
  if (h.leaving || h.gone) return;
  h.hp--;
  h.wobT = 0.5;
  demoStats.hits++;
  sfx('boing');
  spawnFloat(h.x, h.y - 30, h.hp > 0 ? '★'.repeat(DEMO_HP - h.hp) : '★★★', '#ffe14d');
  if (h.hp <= 0) demoPoof(h);
}
function endDemo(): void {
  demoStats.ended++;
  demoPhase = 'aus';
  hippies.length = 0;
  demoGuard = null;
  demoNext = 150 + Math.random() * 90;
  durchsageKontext('');
  setSong(ms.mode === 'dauermesse' || ms.mode === 'duell' ? 'hektik' : 'fahrstuhl');
}
function tickDemo(dt: number): void {
  if (demoPhase === 'aus') {
    demoNext -= dt;
    if (demoNext <= 0) {
      if (!startDemo()) demoNext = 30;
    }
    return;
  }
  demoT += dt;
  if (demoPhase === 'sitzen' && demoT > DEMO_SIT_S && !demoGuard) {
    // nächster Eingang zur Demo, damit die Security rechtzeitig ankommt
    const e = entrancePoints.length ? entrancePoints.reduce((a, b) => (Math.hypot(b.x - demoGoal.x, b.y - demoGoal.y) < Math.hypot(a.x - demoGoal.x, a.y - demoGoal.y) ? b : a)) : guardEntrance();
    demoGuard = { x: e.x, y: e.y, bubble: '', bubbleT: 0 };
  }
  if (demoGuard) {
    const g = demoGuard;
    g.bubbleT = Math.max(0, g.bubbleT - dt);
    const tgt = hippies.find((h) => !h.leaving && !h.gone);
    const aim: Vec = tgt ?? demoGoal;
    const d = Math.hypot(aim.x - g.x, aim.y - g.y);
    demoGuardCd = Math.max(0, demoGuardCd - dt);
    if (tgt && demoPhase !== 'abzug' && d < tgt.r + PERSON_R + 40) {
      if (!g.bubbleT && demoGuardCd === 0 && tgt.hp === DEMO_HP) {
        g.bubble = demoPick(DEMO_SECURITY);
        g.bubbleT = bubbleDur(g.bubble, 2.2);
      }
      if (demoGuardCd === 0) {
        demoHit(tgt);
        demoGuardCd = 0.45;
      }
    }
    if (d > (tgt ? tgt.r + PERSON_R + 6 : 70) && demoPhase !== 'abzug') {
      const st = d < 120 ? aim : npcPathStep(g, aim) ?? aim;
      const l = Math.hypot(st.x - g.x, st.y - g.y) || 1;
      const sp = PERSON_SPEED * (d > 400 ? 2.2 : 1.5) * dt;
      tryMoveCircle(g, g.x + ((st.x - g.x) / l) * sp, g.y + ((st.y - g.y) / l) * sp, PERSON_R);
    }
    if ((!tgt || demoT > DEMO_SIT_S + 60) && demoPhase !== 'abzug') {
      g.bubble = demoPick(DEMO_SECURITY);
      g.bubbleT = bubbleDur(g.bubble, 3);
      sfx('funk');
      demoPhase = 'abzug';
      demoStats.cleared++;
      for (const h of hippies) {
        if (h.leaving) continue;
        h.leaving = true;
        h.bubble = Math.random() < 0.5 ? demoPick(DEMO_ABGANG) : '';
        h.bubbleT = h.bubble ? bubbleDur(h.bubble, 2.4) : 0;
      }
    }
  }
  // Notbremse: Security kommt nicht an -> Demo zieht trotzdem ab; Abzug hängt -> Poof
  if (demoPhase !== 'abzug' && demoT > (demoPhase === 'marsch' ? 60 : DEMO_SIT_S + 75)) {
    demoPhase = 'abzug';
    demoT = 0;
    demoStats.cleared++;
    for (const h of hippies) h.leaving = true;
  }
  if (demoPhase === 'abzug' && demoT > 40) {
    for (const h of hippies) if (!h.gone) demoVanish(h);
  }
  let alive = 0;
  for (const h of hippies) {
    if (h.gone) continue;
    h.bubbleT = Math.max(0, h.bubbleT - dt);
    h.wobT = Math.max(0, h.wobT - dt);
    if (!h.leaving) {
      for (const q of persons) {
        const dx = q.x - h.x, dy = q.y - h.y, dd = Math.hypot(dx, dy), min = h.r + PERSON_R;
        if (dd > 0.01 && dd < min) tryMoveCircle(q, q.x + (dx / dd) * (min - dd), q.y + (dy / dd) * (min - dd), PERSON_R);
      }
    }
    const target = h.leaving ? h.exit : { x: demoGoal.x + h.off.x, y: demoGoal.y + h.off.y };
    const d = Math.hypot(target.x - h.x, target.y - h.y);
    if (h.leaving && d < 30) {
      h.gone = true;
      continue;
    }
    alive++;
    if (d > 8) {
      const st = d > 40 ? npcPathStep(h, target) ?? target : target;
      const l = Math.hypot(st.x - h.x, st.y - h.y) || 1;
      const sp = PERSON_SPEED * (h.leaving ? 1.0 : 0.8) * dt;
      // breite Figur: bei Engstellen mit Personenradius durchquetschen statt hängen
      const mr = h.stuckT > 1 ? PERSON_R : h.r;
      tryMoveCircle(h, h.x + ((st.x - h.x) / l) * sp, h.y + ((st.y - h.y) / l) * sp, mr);
      const moved = Math.hypot(h.x - h.lx, h.y - h.ly);
      h.stuckT = moved < sp * 0.25 ? h.stuckT + dt : Math.max(0, h.stuckT - dt * 2);
      if (!h.leaving && h.stuckT > 3 && demoPhase !== 'marsch') {
        // Sitzplatz unerreichbar: hier hinsetzen
        h.off = { x: h.x - demoGoal.x, y: h.y - demoGoal.y };
        h.stuckT = 0;
      }
      if (h.leaving && h.stuckT > 6) {
        demoVanish(h);
        continue;
      }
    } else h.stuckT = 0;
    h.lx = h.x;
    h.ly = h.y;
    if (!h.leaving) {
      h.talkCd -= dt;
      if (h.talkCd <= 0 && !hippies.some((o) => o !== h && o.bubbleT > 0)) {
        h.talkCd = 6 + Math.random() * 8;
        h.bubble = demoPick(DEMO_RUFE);
        h.bubbleT = bubbleDur(h.bubble, 2.6);
      }
    }
  }
  if (demoPhase === 'marsch' && (Math.hypot(hippies.reduce((s, h) => s + h.x, 0) / Math.max(1, hippies.length) - demoGoal.x, hippies.reduce((s, h) => s + h.y, 0) / Math.max(1, hippies.length) - demoGoal.y) < 120 || demoT > 45)) {
    demoPhase = 'sitzen';
    demoT = 0;
  }
  // Sitzblockade lockt freie Kunden an: sie bleiben stehen und schauen zu
  if (demoPhase === 'sitzen') {
    for (const p of persons) {
      if (p.kind !== 'customer' || p.leaving || p.towedBy != null) continue;
      if (Math.hypot(p.x - demoGoal.x, p.y - demoGoal.y) > DEMO_LURE_R) continue;
      if (p.restT <= 0) demoStats.lured++;
      p.restT = Math.max(p.restT, 0.5);
    }
  }
  if (alive === 0) endDemo();
}
/** Projektil gegen Demo: true, wenn getroffen */
function demoHitTest(x: number, y: number, r: number): boolean {
  if (demoPhase === 'aus') return false;
  for (const h of hippies) {
    if (h.leaving || h.gone) continue;
    if (Math.hypot(x - h.x, y - h.y) < h.r + r) {
      demoHit(h);
      return true;
    }
  }
  return false;
}
function drawDemo(): void {
  if (demoPhase === 'aus') return;
  for (const h of hippies) {
    if (h.gone || !inView(h.x - 60, h.y - 70, 120, 100)) continue;
    const bob = Math.sin(simTime * 7 + h.off.x) * 1.5;
    const wob = h.wobT > 0 ? Math.sin(h.wobT * 40) * 4 : 0;
    const bw = h.r * 1.25, bh = h.r * 0.95;
    ctx.save();
    ctx.translate(wob, 0);
    ctx.globalAlpha = h.leaving ? 0.75 : 1;
    if (h.gear === 'wagen') {
      ctx.fillStyle = '#8a5a2b';
      ctx.fillRect(h.x - 16, h.y + 10, 32, 16);
      ctx.fillStyle = '#e0d24a';
      ctx.fillRect(h.x - 12, h.y + 6, 10, 8);
      ctx.fillStyle = '#5ab36a';
      ctx.fillRect(h.x + 1, h.y + 5, 10, 9);
      ctx.fillStyle = '#2a2a2a';
      ctx.beginPath(); ctx.arc(h.x - 11, h.y + 27, 4, 0, Math.PI * 2); ctx.arc(h.x + 11, h.y + 27, 4, 0, Math.PI * 2); ctx.fill();
      ctx.strokeStyle = '#5a3a1b'; ctx.lineWidth = 2;
      ctx.beginPath(); ctx.moveTo(h.x + 16, h.y + 14); ctx.lineTo(h.x + bw, h.y + 2); ctx.stroke();
    }
    ctx.fillStyle = 'rgba(0,0,0,0.25)';
    ctx.beginPath();
    ctx.ellipse(h.x, h.y + bh * 0.8, bw, bh * 0.4, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = h.color;
    ctx.beginPath();
    ctx.ellipse(h.x, h.y + bob, bw, bh, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = 'rgba(0,0,0,0.35)'; ctx.lineWidth = 1.5; ctx.stroke();
    // Arme
    ctx.fillStyle = '#f1c27d';
    ctx.beginPath(); ctx.arc(h.x - bw * 0.95, h.y + bob + 2, 4.5, 0, Math.PI * 2); ctx.arc(h.x + bw * 0.95, h.y + bob + 2, 4.5, 0, Math.PI * 2); ctx.fill();
    if (h.gear === 'trommel') {
      ctx.fillStyle = '#c0392b';
      ctx.fillRect(h.x - 9, h.y + bob + 2, 18, 12);
      ctx.fillStyle = '#f4efe2';
      ctx.beginPath(); ctx.ellipse(h.x, h.y + bob + 2, 9, 3, 0, 0, Math.PI * 2); ctx.fill();
    }
    // Kopf mit freundlichem Gesicht
    ctx.fillStyle = '#f1c27d';
    ctx.beginPath();
    ctx.arc(h.x, h.y - bh * 0.95 + bob, h.r * 0.5, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#3a2a1a';
    ctx.beginPath(); ctx.arc(h.x - 3.5, h.y - bh * 0.95 + bob - 1, 1.3, 0, Math.PI * 2); ctx.arc(h.x + 3.5, h.y - bh * 0.95 + bob - 1, 1.3, 0, Math.PI * 2); ctx.fill();
    ctx.strokeStyle = '#3a2a1a'; ctx.lineWidth = 1.2;
    ctx.beginPath(); ctx.arc(h.x, h.y - bh * 0.95 + bob + 2, 3.5, 0.2, Math.PI - 0.2); ctx.stroke();
    if (h.wobT > 0) {
      ctx.fillStyle = '#ffe14d';
      ctx.font = 'bold 11px system-ui, sans-serif';
      ctx.textAlign = 'center';
      for (let k = 0; k < 3; k++) { const a2 = simTime * 8 + (k * Math.PI * 2) / 3; ctx.fillText('★', h.x + Math.cos(a2) * 14, h.y - bh * 1.6 + Math.sin(a2) * 4); }
    }
    // Blumenkranz
    for (let k = 0; k < 5; k++) {
      const a = Math.PI + (k / 4) * Math.PI;
      ctx.fillStyle = DEMO_COLORS[(k + 2) % DEMO_COLORS.length]!;
      ctx.beginPath();
      ctx.arc(h.x + Math.cos(a) * h.r * 0.5, h.y - bh * 0.95 + bob + Math.sin(a) * h.r * 0.45, 2.2, 0, Math.PI * 2);
      ctx.fill();
    }
    if (h.sign && !h.leaving) {
      ctx.font = 'bold 9px system-ui, sans-serif';
      const tw = Math.min(h.gear === 'banner' ? 130 : 110, ctx.measureText(h.sign).width + 8);
      const sy = h.y - h.r * 2.3 + bob * 0.5;
      ctx.strokeStyle = '#7a5a33';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(h.x, h.y - h.r * 0.4);
      ctx.lineTo(h.x, sy);
      ctx.stroke();
      ctx.fillStyle = '#fbf6e6';
      ctx.fillRect(h.x - tw / 2, sy - 14, tw, 15);
      ctx.strokeRect(h.x - tw / 2, sy - 14, tw, 15);
      ctx.fillStyle = '#3a2a1a';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(h.sign, h.x, sy - 6.5, tw - 4);
    }
    ctx.restore();
    if (h.bubbleT > 0 && h.bubble) drawBubble(h.x, h.y - h.r * (h.sign && !h.leaving ? 3.1 : 1.7), h.bubble, h.r);
  }
  const g = demoGuard;
  if (g && inView(g.x - 40, g.y - 60, 80, 90)) {
    ctx.save();
    ctx.fillStyle = '#1f2a3a';
    ctx.beginPath();
    ctx.arc(g.x, g.y, PERSON_R, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#ffd34d';
    ctx.font = 'bold 7px system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('SECURITY', g.x, g.y + 2);
    ctx.restore();
    if (g.bubbleT > 0 && g.bubble) drawBubble(g.x, g.y - PERSON_R * 1.6, g.bubble, PERSON_R);
  }
}
function demoDebugInfo() {
  return { phase: demoPhase, n: hippies.filter((h) => !h.gone && !h.leaving).length, next: Math.round(demoNext), guard: !!demoGuard, ...demoStats };
}

// ---- Kaffeestände mit Bild (Daten: src/data/kaffee.json; Bildmaße 1024x768) ----
type KaffeeStand = { id: string; name: string; slogan?: string; personal_sprueche?: string[] };
const KAFFEE_STAENDE: KaffeeStand[] = ((kaffeeStandData as { kaffeestaende?: KaffeeStand[] }).kaffeestaende ?? []).filter((k) => k && typeof k.id === 'string');
const KAFFEE_ART: Record<string, { img: string; schild: string; sign: { x0: number; y0: number; x1: number; y1: number }; farbe: string }> = {
  bohnenbunker: { img: 'stands/kaffee_bohnenbunker.webp', schild: 'Bohnenbunker', sign: { x0: 440, y0: 52, x1: 548, y1: 195 }, farbe: '#f2f2e8' },
  automat: { img: 'stands/kaffee_automat.webp', schild: 'K-3000', sign: { x0: 228, y0: 58, x1: 370, y1: 118 }, farbe: '#14402a' },
  thermostisch: { img: 'stands/kaffee_thermostisch.webp', schild: 'Selbstbedienung', sign: { x0: 313, y0: 22, x1: 516, y1: 84 }, farbe: '#3a2a1a' },
  feldkueche: { img: 'stands/kaffee_feldkueche.webp', schild: 'Feldkantine', sign: { x0: 446, y0: 450, x1: 588, y1: 513 }, farbe: '#1d2a44' },
};
function kaffeeStandOf(pl: HallPlace): KaffeeStand | null {
  if (pl.kind !== 'coffee' || !KAFFEE_STAENDE.length) return null;
  const n = Number(pl.label.replace(/\D/g, '')) || 1;
  return KAFFEE_STAENDE[(n - 1) % KAFFEE_STAENDE.length] ?? null;
}
/** zeichnet Bild + Schild; false, wenn (noch) kein Bild da ist */
function drawKaffeeArt(pl: HallPlace): boolean {
  const ks = kaffeeStandOf(pl);
  const art = ks ? KAFFEE_ART[ks.id] : undefined;
  const img = art ? placeImage(art.img) : null;
  if (!art || !img) return false;
  ctx.save();
  ctx.beginPath();
  ctx.roundRect(pl.x, pl.y, pl.w, pl.h, 10);
  ctx.clip();
  ctx.drawImage(img, pl.x, pl.y, pl.w, pl.h);
  ctx.restore();
  const sx = pl.w / 1024;
  const sy = pl.h / 768;
  const r = { x: pl.x + art.sign.x0 * sx, y: pl.y + art.sign.y0 * sy, w: (art.sign.x1 - art.sign.x0) * sx, h: (art.sign.y1 - art.sign.y0) * sy };
  const f = fitLines(art.schild, r.w * 0.9, r.h * 0.9, 9, (px) => `bold ${px}px system-ui, sans-serif`);
  ctx.font = `bold ${f.px}px system-ui, sans-serif`;
  ctx.fillStyle = art.farbe;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  const lh = f.px * 1.1;
  const y0 = r.y + r.h / 2 - ((f.lines.length - 1) * lh) / 2;
  f.lines.forEach((l, i) => ctx.fillText(l, r.x + r.w / 2, y0 + i * lh));
  // Namensschild (handylesbar)
  ctx.font = 'bold 15px system-ui, sans-serif';
  const nt = '☕ ' + art.schild;
  const nw = Math.min(pl.w - 8, ctx.measureText(nt).width + 16);
  const nx = pl.x + pl.w / 2 - nw / 2;
  const ny = pl.y - 27;
  ctx.fillStyle = 'rgba(40, 24, 12, 0.88)';
  ctx.beginPath();
  ctx.roundRect(nx, ny, nw, 22, 6);
  ctx.fill();
  ctx.strokeStyle = '#d4894a';
  ctx.lineWidth = 1.5;
  ctx.stroke();
  ctx.fillStyle = '#fff3e0';
  ctx.fillText(nt, pl.x + pl.w / 2, ny + 11.5, nw - 8);
  return true;
}
function kaffeeSpruch(pl: HallPlace): void {
  const ks = kaffeeStandOf(pl);
  const arr = (ks?.personal_sprueche ?? []).filter((x) => typeof x === 'string' && x.length > 0);
  if (!arr.length) return;
  spawnFloat(pl.x + pl.w / 2, pl.y - 6, arr[(Math.random() * arr.length) | 0]!, '#ffe0b0');
}


// ---- Nasse Zonen: Wischen + 'Vorsicht rutschig'-Schild (Sprüche: src/data/reinigung.json) ----
const REINIGUNG_SPRUECHE: string[] = (Array.isArray(reinigungData) ? (reinigungData as unknown[]) : []).filter((x): x is string => typeof x === 'string' && x.length > 0);
const WET_R = 96; // ≈ 1,5 Tiles
type WetZone = { x: number; y: number; r: number; t: number; life: number; sign: boolean };
let wetZones: WetZone[] = [];
let wetNext = 45 + Math.random() * 30;
const slipV: Vec = { x: 0, y: 0 };
let slipFallT = 0;
const wetStats = { mopped: 0, after: 0, slips: 0 };
function resetWet(): void {
  wetZones = [];
  wetNext = 45 + Math.random() * 30;
  slipV.x = 0;
  slipV.y = 0;
  slipFallT = 0;
}
function spawnWet(x: number, y: number, sign: boolean, life: number): void {
  wetZones.push({ x, y, r: sign ? WET_R : WET_R * 0.6, t: 0, life, sign });
}
function wetAt(x: number, y: number): boolean {
  return wetZones.some((z) => Math.hypot(x - z.x, y - z.y) < z.r);
}
function tickWet(dt: number): void {
  for (const z of wetZones) z.t += dt;
  wetZones = wetZones.filter((z) => z.t < z.life);
  slipFallT = Math.max(0, slipFallT - dt);
  wetNext -= dt;
  if (wetNext <= 0) {
    wetNext = 50 + Math.random() * 40;
    const c = cleaners.find((q) => !q.target && !q.mop);
    const cells = collectReachableAisleCells(160);
    const g = cells[(Math.random() * cells.length) | 0];
    if (c && g) c.mop = { x: g.x, y: g.y };
  }
}
/** Reinigung am Wisch-Ziel angekommen: Schild aufstellen */
function cleanerMopDone(c: Cleaner): void {
  if (!c.mop) return;
  spawnWet(c.mop.x, c.mop.y, true, 20 + Math.random() * 10);
  wetStats.mopped++;
  if (REINIGUNG_SPRUECHE.length) spawnFloat(c.x, c.y - 34, REINIGUNG_SPRUECHE[(Math.random() * REINIGUNG_SPRUECHE.length) | 0]!, '#fff3a0');
  c.mop = null;
}
/** Spieler auf nasser Zone: Trägheit, weniger Kontrolle, gelegentlich Ausrutscher. true = Bewegung erledigt */
function wetMove(pl: GamePlayer, wantX: number, wantY: number, dt: number): boolean {
  if (!pl.isHuman || !wetAt(pl.x, pl.y)) {
    slipV.x = 0;
    slipV.y = 0;
    return false;
  }
  const k = Math.min(1, 1.6 * dt);
  slipV.x += (wantX - slipV.x) * k;
  slipV.y += (wantY - slipV.y) * k;
  const fr = Math.max(0, 1 - 0.25 * dt);
  slipV.x *= fr;
  slipV.y *= fr;
  tryMoveCircle(pl, pl.x + slipV.x * dt, pl.y + slipV.y * dt, pl.r);
  const sp = Math.hypot(slipV.x, slipV.y);
  if (sp > 60 && slipFallT <= 0 && Math.random() < dt * 0.35) {
    slipFallT = 2.5;
    pl.stunT = Math.max(pl.stunT, 0.7);
    wetStats.slips++;
    sfx('boing');
    spawnFloat(pl.x, pl.y - 30, 'Hoppla!', '#bfe6ff');
  }
  return true;
}
function drawWetSign(x: number, y: number): void {
  ctx.fillStyle = 'rgba(0,0,0,0.25)';
  ctx.beginPath();
  ctx.ellipse(x, y + 16, 14, 4, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = '#ffd21f';
  ctx.strokeStyle = '#4a3a00';
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.moveTo(x - 13, y + 16);
  ctx.lineTo(x - 9, y - 22);
  ctx.lineTo(x + 9, y - 22);
  ctx.lineTo(x + 13, y + 16);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();
  ctx.fillStyle = '#1a1a1a';
  ctx.beginPath();
  ctx.moveTo(x, y - 18);
  ctx.lineTo(x + 7, y - 6);
  ctx.lineTo(x - 7, y - 6);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = '#ffd21f';
  ctx.font = 'bold 9px system-ui, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText('!', x, y - 10);
  ctx.fillStyle = '#1a1a1a';
  ctx.font = 'bold 6px system-ui, sans-serif';
  ctx.fillText('VORSICHT', x, y + 1);
  ctx.fillText('RUTSCHIG', x, y + 9);
}
function drawWet(): void {
  for (const z of wetZones) {
    if (!inView(z.x - z.r, z.y - z.r, z.r * 2, z.r * 2)) continue;
    const fade = Math.min(1, (z.life - z.t) / 3, z.t / 0.5);
    ctx.save();
    ctx.globalAlpha = 0.45 * fade;
    ctx.fillStyle = '#7cc4ff';
    ctx.beginPath();
    ctx.ellipse(z.x, z.y, z.r, z.r * 0.8, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.globalAlpha = 0.7 * fade;
    ctx.strokeStyle = '#e8f6ff';
    ctx.lineWidth = 2;
    for (let k = 0; k < 3; k++) {
      const a = k * 2.1 + z.x * 0.01;
      ctx.beginPath();
      ctx.arc(z.x + Math.cos(a) * z.r * 0.45, z.y + Math.sin(a) * z.r * 0.35, 8, 3.6, 5.2);
      ctx.stroke();
    }
    ctx.globalAlpha = fade;
    if (z.sign) drawWetSign(z.x, z.y);
    ctx.restore();
  }
}

// ---- NPC-KI: Personas, Bedürfnisse, Gruppen, Ausweichen, LOD (STEP 24) ----
type Persona = {
  id: string;
  name: string;
  share: number;
  speed: number;
  goals: [number, number];
  prefer: 'featured' | 'stock' | 'any' | 'near' | 'big';
  chatMul: number;
  patience: number;
  hunger: number;
  bladder: number;
  tired: number;
  group: number;
  talky: number;
};
type PersonaData = {
  personas: Persona[];
  lines: Record<string, string[]>;
  queue: string[];
  giveUp: string[];
  tired: string[];
  smalltalk: [string, string][];
};
const PERSONA = personasData as unknown as PersonaData;
type Npc = {
  persona: Persona;
  hunger: number;
  bladder: number;
  tired: number;
  goals: number[];
  lead: number | null;
  slot: number;
  decideCd: number;
  waitBooth: number;
  waitT: number;
  qSlot: number;
  restGoal: Vec | null;
  slow: number;
  lodAcc: number;
};
const NPC_NEED_HUNGER = 0.8;
const NPC_NEED_BLADDER = 0.85;
const NPC_NEED_TIRED = 0.85;
const NPC_CELL = 64;
const NPC_LOD_STEP = 0.12;
const NPC_VIEW_MARGIN = 160;
let npcLod = true;
let simTime = 0;
let pathCalls = 0;
const npcStats = {
  boothVisits: 0,
  queued: 0,
  giveUps: 0,
  needResto: 0,
  needWc: 0,
  needRest: 0,
  smalltalks: 0,
  staffAddressed: 0,
  staffGoal: 0,
  sidesteps: 0,
  pushes: 0,
  groupsFormed: 0,
  lodSkipped: 0,
  lodUpdated: 0,
  pestTalks: 0,
  pestHumanTalks: 0,
  pestLastStun: 0,
};
let npcById = new Map<number, Person>();
let boothChatCount = new Int16Array(0);
let boothWaitCount = new Int16Array(0);
let smalltalkCd = 0;
const pathCache = new WeakMap<object, { gx: number; gy: number; wx: number; wy: number; t: number }>();

function npcPickPersona(p: Person): Persona {
  const r = personRand(p);
  let acc = 0;
  for (const ps of PERSONA.personas) {
    acc += ps.share;
    if (r < acc) return ps;
  }
  return PERSONA.personas[PERSONA.personas.length - 1]!;
}
function npcPickGoals(p: Person, ps: Persona): number[] {
  const k = Math.round(personRandRange(p, ps.goals[0], ps.goals[1] + 0.49));
  const cand: { i: number; w: number }[] = [];
  booths.forEach((b, i) => {
    if (b.w < 40 || b.h < 40) return;
    let w = 1;
    if (ps.prefer === 'featured') w = b.featured ? 5 : 1;
    else if (ps.prefer === 'stock') w = b.own || isRivalBooth(b) ? 5 : 1;
    else if (ps.prefer === 'big') w = (b.w * b.h) / 40000 + 0.3;
    else if (ps.prefer === 'near') w = 1 / (1 + Math.hypot(b.x + b.w / 2 - p.x, b.y + b.h / 2 - p.y) / 400);
    cand.push({ i, w });
  });
  const out: number[] = [];
  for (let n = 0; n < k && cand.length; n++) {
    const sum = cand.reduce((s, c) => s + c.w, 0);
    let r = personRand(p) * sum;
    let j = 0;
    for (; j < cand.length - 1; j++) {
      r -= cand[j]!.w;
      if (r <= 0) break;
    }
    out.push(cand[j]!.i);
    cand.splice(j, 1);
  }
  // Rundgang: jeweils nächster Stand zuerst
  const tour: number[] = [];
  let cx = p.x;
  let cy = p.y;
  while (out.length) {
    let bi = 0;
    let bd = Infinity;
    out.forEach((i, j) => {
      const b = booths[i]!;
      const d = Math.hypot(b.x + b.w / 2 - cx, b.y + b.h / 2 - cy);
      if (d < bd) {
        bd = d;
        bi = j;
      }
    });
    const i = out.splice(bi, 1)[0]!;
    tour.push(i);
    cx = booths[i]!.x + booths[i]!.w / 2;
    cy = booths[i]!.y + booths[i]!.h / 2;
  }
  return tour;
}
function initNpc(p: Person): void {
  if (p.kind !== 'customer' && p.kind !== 'nobody') return;
  const ps = npcPickPersona(p);
  p.npc = {
    persona: ps,
    hunger: personRand(p) * 0.55,
    bladder: personRand(p) * 0.45,
    tired: personRand(p) * 0.3,
    goals: npcPickGoals(p, ps),
    lead: null,
    slot: 0,
    decideCd: personRandRange(p, 0.5, 3),
    waitBooth: -1,
    waitT: 0,
    qSlot: 0,
    restGoal: null,
    slow: 1,
    lodAcc: personRand(p) * NPC_LOD_STEP,
  };
  // Mit Zielliste länger in der Halle
  p.lifeT += 40 + personRand(p) * 50;
}
function npcSpeedMul(p: Person): number {
  const n = p.npc;
  if (!n) return 1;
  return n.persona.speed * n.slow * (n.tired > NPC_NEED_TIRED ? 0.8 : 1);
}
function npcOnScreen(x: number, y: number): boolean {
  return (
    x > camX - NPC_VIEW_MARGIN &&
    y > camY - NPC_VIEW_MARGIN &&
    x < camX + CAM_W + NPC_VIEW_MARGIN &&
    y < camY + CAM_H + NPC_VIEW_MARGIN
  );
}
/** Wegpunkt mit Cache: BFS nur alle 0,3 s (sichtbar) bzw. 0,9 s (außerhalb) */
function npcPathStep(p: { x: number; y: number }, goal: Vec): Vec | null {
  if (!pathGrid) return null;
  const c = pathCache.get(p);
  const ttl = npcOnScreen(p.x, p.y) ? 0.3 : 0.9;
  if (
    c &&
    simTime - c.t < ttl &&
    Math.hypot(c.gx - goal.x, c.gy - goal.y) < 40 &&
    Math.hypot(c.wx - p.x, c.wy - p.y) > 8
  ) {
    return { x: c.wx, y: c.wy };
  }
  pathCalls++;
  const st = gridPathStep(pathGrid, p, goal);
  if (st) pathCache.set(p, { gx: goal.x, gy: goal.y, wx: st.x, wy: st.y, t: simTime });
  else pathCache.delete(p);
  return st;
}
function npcMoveTo(p: Person, goal: Vec, spd: number, dt: number): number {
  const dist = Math.hypot(goal.x - p.x, goal.y - p.y);
  let tx = goal.x;
  let ty = goal.y;
  if (dist > 50) {
    const st = npcPathStep(p, goal);
    if (st) {
      tx = st.x;
      ty = st.y;
    }
  }
  const dx = tx - p.x;
  const dy = ty - p.y;
  const len = Math.hypot(dx, dy) || 1;
  const step = Math.min(dist, spd * dt);
  p.vx = (dx / len) * spd;
  p.vy = (dy / len) * spd;
  tryMoveCircle(p, p.x + (dx / len) * step, p.y + (dy / len) * step, p.r);
  return dist;
}
function npcApproach(p: Person, b: Booth): Vec {
  const f = 0.25 + ((p.id * 0.618) % 1) * 0.5;
  return { x: b.x + b.w * f, y: b.y + b.h + 34 };
}
function boothCap(b: Booth): number {
  return b.featured ? 3 : 2;
}
function npcBoothFull(bi: number): boolean {
  const b = booths[bi];
  return !!b && (boothChatCount[bi] ?? 0) >= boothCap(b);
}
function npcSay(p: Person, pool: string[] | undefined, t = 1.4): void {
  if (!pool || !pool.length || p.bubbleT > 0) return;
  p.bubble = pool[(personRand(p) * pool.length) | 0]!;
  p.bubbleT = bubbleDur(p.bubble, t);
}
function npcFollowers(lead: Person): Person[] {
  const out: Person[] = [];
  for (const q of persons) if (q.npc?.lead === lead.id) out.push(q);
  return out;
}
function npcStartSvc(p: Person, pl: HallPlace): boolean {
  if (!startSvc(p, pl)) return false;
  for (const f of npcFollowers(p)) if (canVisitorBrowse(f) && !svcOf.has(f)) startSvc(f, pl);
  return true;
}
function npcSvcDone(p: Person, kind: HallPlaceKind, stage: string): void {
  const n = p.npc;
  if (!n) return;
  if (kind === 'restaurant' && stage === 'leave') {
    n.hunger = 0;
    n.bladder = Math.min(1, n.bladder + 0.35);
    n.tired = Math.max(0, n.tired - 0.4);
  } else if (kind === 'toilet' && stage === 'out') {
    n.bladder = 0;
  }
}
function npcDecide(p: Person): void {
  const n = p.npc!;
  const ps = n.persona;
  n.decideCd = personRandRange(p, 1.5, 4);
  if (n.bladder > NPC_NEED_BLADDER) {
    const wc = wcPlace(p.x, p.y);
    if (wc && npcStartSvc(p, wc)) {
      npcStats.needWc++;
      return;
    }
  }
  if (n.hunger > NPC_NEED_HUNGER) {
    const r = nearestPlace('restaurant', p.x, p.y);
    if (r && npcStartSvc(p, r)) {
      npcStats.needResto++;
      return;
    }
  }
  if (n.tired > NPC_NEED_TIRED && !n.restGoal) {
    let best: HallPlace | null = null;
    let bd = Infinity;
    for (const pl of hallPlaces) {
      if (pl.kind !== 'coffee' && pl.kind !== 'smoke') continue;
      const d = Math.hypot(pl.x + pl.w / 2 - p.x, pl.y + pl.h / 2 - p.y);
      if (d < bd) {
        bd = d;
        best = pl;
      }
    }
    if (best) {
      const f = findFreeNear(best.x + best.w * personRandRange(p, 0.25, 0.75), best.y + best.h * personRandRange(p, 0.3, 0.7), p.r + 1, 120);
      if (f) {
        n.restGoal = f;
        npcStats.needRest++;
        return;
      }
    }
    n.tired = 0.5;
  }
  if (n.goals.length) {
    p.visitMode = 'seek';
    p.targetBooth = n.goals[0]!;
    n.waitBooth = -1;
    n.waitT = 0;
    return;
  }
  // Ziele abgearbeitet → Ausgang
  if (ps && p.lifeT > 0.3) p.lifeT = 0.3;
}
/** Persona-Logik im Bummel-Zweig. true = Frame verbraucht. */
function npcUpdate(p: Person, dt: number): boolean {
  const n = p.npc;
  if (!n) return false;
  // Gruppe: Mitläufer folgen dem Anführer
  if (n.lead != null) {
    const lead = npcById.get(n.lead);
    if (!lead || lead.towedBy != null || lead.becomeNobody || !persons.includes(lead)) {
      n.lead = null;
    } else if (lead.leaving) {
      n.lead = null;
      if (p.lifeT > 0.2) p.lifeT = 0.2;
      return false;
    } else {
      const ls = svcOf.get(lead);
      if (ls && !svcOf.has(p) && startSvc(p, ls.place)) return true;
      const stopped = lead.boothChatT > 0 || lead.bubbleT > 0 || lead.restT > 0 || lead.hidden || !!ls;
      const vl = Math.hypot(lead.vx, lead.vy) || 1;
      const side = (n.slot % 2 === 0 ? 1 : -1) * (12 + 10 * ((n.slot / 2) | 0));
      const tx = lead.x - (lead.vx / vl) * 26 + (-lead.vy / vl) * side;
      const ty = lead.y - (lead.vy / vl) * 26 + (lead.vx / vl) * side;
      const d = Math.hypot(tx - p.x, ty - p.y);
      if (stopped && d < 70) {
        p.vx = 0;
        p.vy = 0;
        return true;
      }
      if (d > 6) npcMoveTo(p, { x: tx, y: ty }, PERSON_SPEED * Math.min(1.35, lead.npc ? npcSpeedMul(lead) * 1.15 + d / 400 : 1.1), dt);
      else {
        p.vx = lead.vx * 0.5;
        p.vy = lead.vy * 0.5;
      }
      return true;
    }
  }
  // Pause bei Müdigkeit
  if (n.restGoal) {
    const d = npcMoveTo(p, n.restGoal, PERSON_SPEED * npcSpeedMul(p) * 0.9, dt);
    if (d < 20) {
      n.restGoal = null;
      n.tired = 0;
      p.restT = personRandRange(p, 5, 9);
      p.vx = 0;
      p.vy = 0;
      npcSay(p, PERSONA.tired);
    }
    return true;
  }
  // Ziel-Stand ansteuern, ggf. anstehen
  if (p.visitMode === 'seek' && p.targetBooth >= 0) {
    const bi = p.targetBooth;
    const b = booths[bi];
    if (!b) {
      p.visitMode = 'roam';
      p.targetBooth = -1;
      n.goals = n.goals.filter((g) => g !== bi);
      return false;
    }
    const goal = npcApproach(p, b);
    const d = Math.hypot(goal.x - p.x, goal.y - p.y);
    if (d > 70 && n.waitBooth !== bi) {
      npcMoveTo(p, goal, PERSON_SPEED * npcSpeedMul(p) * 0.9, dt);
      return true;
    }
    if (npcBoothFull(bi) || (n.waitBooth === bi && n.qSlot > 0 && (boothWaitCount[bi] ?? 0) > 0 && npcFirstInLine(p, bi) !== p)) {
      if (n.waitBooth !== bi) {
        n.waitBooth = bi;
        n.waitT = 0;
        n.qSlot = (boothWaitCount[bi] ?? 0) + 1;
        npcStats.queued++;
        npcSay(p, PERSONA.queue, 1.2);
      }
      n.waitT += dt;
      const qx = b.x + b.w / 2 + ((n.qSlot % 2) * 2 - 1) * 14;
      const qy = b.y + b.h + 40 + n.qSlot * 22;
      if (Math.hypot(qx - p.x, qy - p.y) > 6) npcMoveTo(p, { x: qx, y: qy }, PERSON_SPEED * 0.6, dt);
      else {
        p.vx = 0;
        p.vy = 0;
      }
      if (n.waitT > n.persona.patience) {
        npcStats.giveUps++;
        npcSay(p, PERSONA.giveUp, 1.2);
        n.goals = n.goals.filter((g) => g !== bi);
        if (n.goals.length && personRand(p) < 0.5) n.goals.push(bi);
        n.waitBooth = -1;
        p.visitMode = 'roam';
        p.targetBooth = -1;
        n.decideCd = personRandRange(p, 0.5, 2);
        const a = personRand(p) * Math.PI * 2;
        p.vx = Math.cos(a) * PERSON_SPEED;
        p.vy = Math.sin(a) * PERSON_SPEED;
      }
      return true;
    }
    if (d > 30) {
      npcMoveTo(p, goal, PERSON_SPEED * npcSpeedMul(p) * 0.9, dt);
      return true;
    }
    n.waitBooth = -1;
    startBoothChat(p);
    if (p.boothChatT > 0) {
      p.boothChatT *= n.persona.chatMul;
      if (personRand(p) < 0.45) {
        const pool = PERSONA.lines[n.persona.id];
        if (pool?.length) p.boothChatLine = pool[(personRand(p) * pool.length) | 0]!;
      }
      boothChatCount[bi] = (boothChatCount[bi] ?? 0) + 1;
      n.goals = n.goals.filter((g) => g !== bi);
      n.tired = Math.min(1, n.tired + 0.04);
      npcStats.boothVisits++;
    }
    return true;
  }
  n.decideCd -= dt;
  if (n.decideCd <= 0) npcDecide(p);
  return false;
}
function npcFirstInLine(p: Person, bi: number): Person {
  let best = p;
  for (const q of persons) {
    if (q.npc?.waitBooth === bi && q.npc.qSlot < best.npc!.qSlot) best = q;
  }
  return best;
}
/** Vorlauf je Frame: Index, Belegung der Stände, Wartende */
function npcPrepass(dt: number): void {
  simTime += dt;
  smalltalkCd = Math.max(0, smalltalkCd - dt);
  npcById = new Map();
  if (boothChatCount.length !== booths.length) {
    boothChatCount = new Int16Array(booths.length);
    boothWaitCount = new Int16Array(booths.length);
  } else {
    boothChatCount.fill(0);
    boothWaitCount.fill(0);
  }
  for (const p of persons) {
    npcById.set(p.id, p);
    if (p.boothChatT > 0 && p.targetBooth >= 0 && p.targetBooth < booths.length) boothChatCount[p.targetBooth]!++;
    const n = p.npc;
    if (!n) continue;
    if (n.waitBooth >= 0 && n.waitBooth < booths.length) boothWaitCount[n.waitBooth]!++;
    if (n.lead == null) {
      n.hunger = Math.min(1, n.hunger + n.persona.hunger * dt);
      n.bladder = Math.min(1, n.bladder + n.persona.bladder * dt);
      n.tired = Math.min(1, n.tired + n.persona.tired * dt);
    }
  }
}
/** Personen-Update mit LOD: außerhalb der Kamera nur ~8×/s */
function npcUpdateAll(dt: number): void {
  npcPrepass(dt);
  for (let i = persons.length - 1; i >= 0; i--) {
    const p = persons[i]!;
    const n = p.npc;
    let step = dt;
    if (npcLod && n && p.towedBy == null && !npcOnScreen(p.x, p.y)) {
      n.lodAcc += dt;
      if (n.lodAcc < NPC_LOD_STEP) {
        npcStats.lodSkipped++;
        continue;
      }
      step = Math.min(0.25, n.lodAcc);
      n.lodAcc = 0;
    }
    npcStats.lodUpdated++;
    if (!updatePerson(p, step)) persons.splice(i, 1);
  }
  npcSteer(dt);
}
// Raster für Nachbarschaftssuche
let gridHead = new Int32Array(0);
let gridNext = new Int32Array(0);
let gridCols = 1;
let gridRows = 1;
function npcBuildGrid(): void {
  gridCols = Math.max(1, Math.ceil(WORLD_W / NPC_CELL));
  gridRows = Math.max(1, Math.ceil(WORLD_H / NPC_CELL));
  if (gridHead.length !== gridCols * gridRows) gridHead = new Int32Array(gridCols * gridRows);
  gridHead.fill(-1);
  if (gridNext.length < persons.length) gridNext = new Int32Array(persons.length * 2);
  for (let i = 0; i < persons.length; i++) {
    const p = persons[i]!;
    if (p.hidden) continue;
    const cx = Math.max(0, Math.min(gridCols - 1, (p.x / NPC_CELL) | 0));
    const cy = Math.max(0, Math.min(gridRows - 1, (p.y / NPC_CELL) | 0));
    const k = cy * gridCols + cx;
    gridNext[i] = gridHead[k]!;
    gridHead[k] = i;
  }
}
function npcIsStill(q: Person): boolean {
  return q.boothChatT > 0 || q.bubbleT > 0 || q.restT > 0 || Math.hypot(q.vx, q.vy) < 10 || svcOf.get(q)?.stage === 'eat';
}
/** Ausweichen, Stau, kein Durchlaufen; Smalltalk im Vorbeigehen */
function npcSteer(dt: number): void {
  npcBuildGrid();
  const puddles = litter.filter((l) => l.kind === 'puddle');
  for (let i = 0; i < persons.length; i++) {
    const p = persons[i]!;
    if (p.hidden || p.towedBy != null) continue;
    const pStill = npcIsStill(p);
    const sv = svcOf.get(p);
    if (sv && (sv.stage === 'eat' || sv.stage === 'inside')) continue;
    const onScr = npcOnScreen(p.x, p.y);
    if (!onScr && npcLod && (p.id + ((simTime * 60) | 0)) % 4 !== 0) continue;
    const sp = Math.hypot(p.vx, p.vy);
    const hx = sp > 1 ? p.vx / sp : 0;
    const hy = sp > 1 ? p.vy / sp : 0;
    const cx = (p.x / NPC_CELL) | 0;
    const cy = (p.y / NPC_CELL) | 0;
    let pushX = 0;
    let pushY = 0;
    let ahead = 0;
    let sideX = 0;
    let sideY = 0;
    for (let gy = cy - 1; gy <= cy + 1; gy++) {
      if (gy < 0 || gy >= gridRows) continue;
      for (let gx = cx - 1; gx <= cx + 1; gx++) {
        if (gx < 0 || gx >= gridCols) continue;
        for (let j = gridHead[gy * gridCols + gx]!; j >= 0; j = gridNext[j]!) {
          if (j === i) continue;
          const q = persons[j]!;
          const dx = p.x - q.x;
          const dy = p.y - q.y;
          const d = Math.hypot(dx, dy);
          if (d < 0.01 || d > 56) continue;
          const minD = p.r + q.r + 2;
          if (!pStill && d < minD) {
            const share = npcIsStill(q) ? 1 : 0.5;
            pushX += (dx / d) * (minD - d) * share;
            pushY += (dy / d) * (minD - d) * share;
          }
          if (!pStill && sp > 10) {
            const dot = (-dx * hx - dy * hy) / d;
            if (dot > 0.7 && d < 48) {
              ahead++;
              const crs = hx * -dy - hy * -dx;
              const s = crs > 0 ? -1 : 1;
              sideX += -hy * s;
              sideY += hx * s;
            }
          }
          // Smalltalk unter Bummelnden
          if (
            p.npc &&
            q.npc &&
            j > i &&
            d < 34 &&
            smalltalkCd <= 0 &&
            onScr &&
            p.visitMode === 'roam' &&
            q.visitMode === 'roam' &&
            canVisitorBrowse(p) &&
            canVisitorBrowse(q) &&
            !svcOf.has(p) &&
            !svcOf.has(q) &&
            p.bubbleT <= 0 &&
            q.bubbleT <= 0 &&
            personRand(p) < 0.25 * p.npc.persona.talky
          ) {
            const pair = PERSONA.smalltalk[(personRand(p) * PERSONA.smalltalk.length) | 0]!;
            p.bubble = pair[0];
            p.bubbleT = bubbleDur(p.bubble, 2.4);
            q.bubble = pair[1];
            q.bubbleT = bubbleDur(q.bubble, 2.4);
            p.vx = p.vy = q.vx = q.vy = 0;
            smalltalkCd = 4 + personRand(p) * 5;
            npcStats.smalltalks++;
          }
        }
      }
    }
    for (const l of puddles) {
      const dx = p.x - l.x;
      const dy = p.y - l.y;
      const d = Math.hypot(dx, dy);
      if (d < 30 && d > 0.01 && !pStill) {
        pushX += (dx / d) * (30 - d) * 0.3;
        pushY += (dy / d) * (30 - d) * 0.3;
      }
    }
    if (p.npc) {
      const target = ahead >= 3 ? 0.5 : ahead >= 1 ? 0.8 : 1;
      p.npc.slow += (target - p.npc.slow) * Math.min(1, dt * 4);
    }
    if (ahead > 0) {
      const sl = Math.hypot(sideX, sideY) || 1;
      pushX += (sideX / sl) * 45 * dt;
      pushY += (sideY / sl) * 45 * dt;
      npcStats.sidesteps++;
    }
    const pl = Math.hypot(pushX, pushY);
    if (pl > 0.05) {
      const m = Math.min(pl, 6) / pl;
      tryMoveCircle(p, p.x + pushX * m, p.y + pushY * m, p.r);
      npcStats.pushes++;
    }
  }
}
/** Gruppen-Mitläufer neben dem Anführer erzeugen */
function npcAddFollowers(lead: Person, k: number, kinds: Kind[]): void {
  if (!lead.npc) return;
  let made = 0;
  for (let s = 0; s < k && s < kinds.length; s++) {
    const f = findFreeNear(lead.x + (s % 2 ? 22 : -22), lead.y + 18 + s * 6, PERSON_R + 2, 120);
    if (!f) break;
    const q = makePerson(kinds[s]!, false);
    q.x = f.x;
    q.y = f.y;
    q.vx = lead.vx;
    q.vy = lead.vy;
    if (q.npc) {
      q.npc.persona = lead.npc.persona;
      q.npc.lead = lead.id;
      q.npc.slot = s;
      q.npc.goals = [];
    }
    persons.push(q);
    made++;
  }
  if (made) npcStats.groupsFormed++;
}
/** Bei Spawn ggf. Gruppe bilden; verbraucht Einträge aus der Spawn-Warteschlange */
function npcMaybeGroup(lead: Person): void {
  const n = lead.npc;
  if (!n || personRand(lead) >= n.persona.group) return;
  const want = 1 + ((personRand(lead) * 3) | 0);
  const kinds: Kind[] = [];
  for (let i = spawnQueue.length - 1; i >= 0 && kinds.length < want; i--) {
    const k = spawnQueue[i]!;
    if (k === 'vip') continue;
    kinds.push(k);
    spawnQueue.splice(i, 1);
  }
  if (!kinds.length) {
    if (countKind('nobody') < targetNobodys) kinds.push('nobody');
    else return;
  }
  npcAddFollowers(lead, kinds.length, kinds);
}
/** Standpersonal: Bewertung eines Vorbeigehenden (kleiner = besser) */
function staffPickScore(st: Staff, p: Person, d: number): number {
  let s = d;
  const n = p.npc;
  if (!n) return s;
  const bi = booths.indexOf(st.booth);
  if (bi >= 0 && n.goals.includes(bi)) s *= 0.4;
  if (n.persona.prefer === 'featured' || n.persona.prefer === 'big') s *= 0.7;
  if (p.visitMode === 'seek' && p.targetBooth !== bi) s *= 1.6;
  if (n.lead != null) s *= 1.3;
  return s;
}
function staffAddressed(st: Staff, p: Person): void {
  npcStats.staffAddressed++;
  const n = p.npc;
  if (!n) return;
  const bi = booths.indexOf(st.booth);
  if (bi >= 0 && n.goals.includes(bi)) {
    n.goals = n.goals.filter((g) => g !== bi);
    npcStats.staffGoal++;
  }
  n.waitBooth = -1;
}
// Leistungsmessung
const perfUpd: number[] = [];
const perfDraw: number[] = [];
const perfIv: number[] = [];
function perfPush(arr: number[], v: number): void {
  arr.push(v);
  if (arr.length > 240) arr.shift();
}
function perfSummary(): Record<string, number> {
  const avg = (a: number[]) => (a.length ? a.reduce((s, v) => s + v, 0) / a.length : 0);
  const p95 = (a: number[]) => {
    if (!a.length) return 0;
    const s = [...a].sort((x, y) => x - y);
    return s[Math.min(s.length - 1, Math.floor(s.length * 0.95))]!;
  };
  const iv = avg(perfIv);
  let onScreen = 0;
  for (const p of persons) if (npcOnScreen(p.x, p.y)) onScreen++;
  return {
    frames: perfIv.length,
    fps: iv > 0 ? 1000 / iv : 0,
    frameMs: iv,
    frameP95: p95(perfIv),
    updMs: avg(perfUpd),
    updP95: p95(perfUpd),
    drawMs: avg(perfDraw),
    drawP95: p95(perfDraw),
    persons: persons.length,
    onScreen,
    pathCalls,
  };
}

function canVisitorBrowse(p: Person): boolean {
  return (
    !p.leaving &&
    p.followVipId === null &&
    !p.pest &&
    (p.kind === 'customer' || p.kind === 'nobody') &&
    !p.becomeNobody &&
    p.towedBy == null
  );
}

function nextVisitorRand(): number {
  visitorSeed = (Math.imul(visitorSeed, 1664525) + 1013904223) >>> 0;
  return visitorSeed / 4294967296;
}

function prunePlacesOverlappingBooths(): void {
  const m = Math.max(40, (SIDE_AISLE / 2) | 0);
  hallPlaces = hallPlaces.filter((pl) => {
    for (const b of booths) {
      if (rectsOverlapM(pl, b, m)) return false;
    }
    return true;
  });
}

/** Orte untereinander und gegen Stände (Margin), für Smoke. */
function placeOverlapReport(): {
  ok: boolean;
  placePlace: number;
  placeBooth: number;
  places: number;
  booths: number;
} {
  const m = Math.max(40, (SIDE_AISLE / 2) | 0);
  let placePlace = 0;
  let placeBooth = 0;
  for (let i = 0; i < hallPlaces.length; i++) {
    const a = hallPlaces[i]!;
    for (let j = i + 1; j < hallPlaces.length; j++) {
      if (rectsOverlapM(a, hallPlaces[j]!, m)) placePlace++;
    }
    for (const b of booths) {
      if (rectsOverlapM(a, b, m)) placeBooth++;
    }
  }
  return {
    ok: placePlace === 0 && placeBooth === 0,
    placePlace,
    placeBooth,
    places: hallPlaces.length,
    booths: booths.length,
  };
}

/** Stände: Überlappung, Innenraum über offene Seiten erreichbar, Eingänge erreichbar. */
function boothLayoutReport(): {
  ok: boolean;
  boothOverlaps: number;
  walled: number;
  interiorUnreached: string[];
  entrancesUnreached: number;
  featured: string[];
  kinds: Record<string, number>;
  openDirs: Record<string, number>;
} {
  let boothOverlaps = 0;
  for (let i = 0; i < booths.length; i++) {
    for (let j = i + 1; j < booths.length; j++) {
      if (rectsOverlapM(booths[i]!, booths[j]!, 0)) boothOverlaps++;
    }
  }
  const step = 24;
  const cells = collectReachableAisleCells(step);
  const set = new Set(cells.map((c) => `${(c.x / step) | 0},${(c.y / step) | 0}`));
  const interiorUnreached: string[] = [];
  let walled = 0;
  const kinds: Record<string, number> = {};
  const openDirs: Record<string, number> = { N: 0, S: 0, E: 0, W: 0 };
  for (const b of booths) {
    kinds[b.kind] = (kinds[b.kind] ?? 0) + 1;
    if (!b.walls) continue;
    walled++;
    if (b.openN) openDirs.N!++;
    if (b.openS) openDirs.S!++;
    if (b.openE) openDirs.E!++;
    if (b.openW) openDirs.W!++;
    let hit = false;
    for (let cy = Math.ceil(b.y / step); !hit && cy * step < b.y + b.h; cy++) {
      for (let cx = Math.ceil(b.x / step); cx * step < b.x + b.w; cx++) {
        if (set.has(`${cx},${cy}`)) {
          hit = true;
          break;
        }
      }
    }
    if (!hit) interiorUnreached.push(`${b.standNo} ${b.label}`);
  }
  let entrancesUnreached = 0;
  for (const e of entrancePoints) {
    const near = cells.some((c) => Math.hypot(c.x - e.x, c.y - e.y) < 90);
    if (!near) entrancesUnreached++;
  }
  const featured = booths.filter((b) => b.featured).map((b) => b.label);
  return {
    ok: boothOverlaps === 0 && interiorUnreached.length === 0 && entrancesUnreached === 0,
    boothOverlaps,
    walled,
    interiorUnreached,
    entrancesUnreached,
    featured,
    kinds,
    openDirs,
  };
}

/** Flood: Anteil erreichbarer Stand-Anlaufpunkte. */
function aisleReachability(): { ok: boolean; boothGoals: number; reached: number } {
  const cells = collectReachableAisleCells(96);
  const cellSet = new Set(cells.map((c) => `${(c.x / 96) | 0},${(c.y / 96) | 0}`));
  let reached = 0;
  let boothGoals = 0;
  for (const b of booths) {
    if (b.w < 40) continue;
    boothGoals++;
    const g = { x: b.x + b.w / 2, y: b.y + b.h + 36 };
    const free = findFreeNear(g.x, g.y, PLAYER_R + 2, 160) ?? g;
    if (!blocked(free.x, free.y, PLAYER_R + 2)) {
      // grob: nahe einer flood-Zelle?
      const key = `${(free.x / 96) | 0},${(free.y / 96) | 0}`;
      let near = cellSet.has(key);
      if (!near) {
        for (const c of cells) {
          if (Math.hypot(c.x - free.x, c.y - free.y) < 140) {
            near = true;
            break;
          }
        }
      }
      if (near) reached++;
    }
  }
  const ok = boothGoals === 0 || reached >= Math.max(1, boothGoals - 1);
  return { ok, boothGoals, reached };
}

/** Erreichbare Gang-Zellen (Flood vom Eingang), ohne Collider. */
function collectReachableAisleCells(step = 64): Vec[] {
  const cols = Math.ceil(WORLD_W / step);
  const rows = Math.ceil(WORLD_H / step);
  const solid = (cx: number, cy: number): boolean => {
    const px = cx * step + step / 2;
    const py = cy * step + step / 2;
    return blocked(px, py, PERSON_R + 2);
  };
  const sx = Math.min(cols - 1, Math.max(0, ((WORLD_W / 2) / step) | 0));
  const sy = Math.min(rows - 1, Math.max(0, ((WORLD_H - ENTRANCE_H * 0.35) / step) | 0));
  const start = { x: sx, y: sy };
  if (solid(start.x, start.y)) {
    for (const [dx, dy] of [
      [0, -1],
      [0, 1],
      [1, 0],
      [-1, 0],
      [0, -2],
      [0, -3],
    ]) {
      const nx = start.x + dx;
      const ny = start.y + dy;
      if (nx < 0 || ny < 0 || nx >= cols || ny >= rows) continue;
      if (!solid(nx, ny)) {
        start.x = nx;
        start.y = ny;
        break;
      }
    }
  }
  const seen = new Set<number>();
  const q: number[] = [start.y * cols + start.x];
  if (!solid(start.x, start.y)) seen.add(q[0]!);
  while (q.length) {
    const k = q.pop()!;
    const cx = k % cols;
    const cy = (k / cols) | 0;
    for (const [dx, dy] of [
      [1, 0],
      [-1, 0],
      [0, 1],
      [0, -1],
    ]) {
      const nx = cx + dx;
      const ny = cy + dy;
      if (nx < 0 || ny < 0 || nx >= cols || ny >= rows) continue;
      const nk = ny * cols + nx;
      if (seen.has(nk) || solid(nx, ny)) continue;
      seen.add(nk);
      q.push(nk);
    }
  }
  const out: Vec[] = [];
  for (const k of seen) {
    const cx = k % cols;
    const cy = (k / cols) | 0;
    out.push({ x: cx * step + step / 2, y: cy * step + step / 2 });
  }
  return out;
}

function seedVisitorsSpread(nCust: number, nNob: number): void {
  const cells = collectReachableAisleCells(72);
  if (!cells.length) {
    for (let i = 0; i < nCust; i++) persons.push(makePerson('customer', true));
    for (let i = 0; i < nNob; i++) persons.push(makePerson('nobody', true));
    return;
  }
  // shuffle deterministisch
  for (let i = cells.length - 1; i > 0; i--) {
    const j = (nextVisitorRand() * (i + 1)) | 0;
    const tmp = cells[i]!;
    cells[i] = cells[j]!;
    cells[j] = tmp;
  }
  const placed: Vec[] = [];
  const tryPlace = (kind: Kind): void => {
    let chosen: Vec | null = null;
    for (const c of cells) {
      if (blocked(c.x, c.y, PERSON_R + 2)) continue;
      let ok = true;
      for (const pl of players) {
        if (Math.hypot(c.x - pl.x, c.y - pl.y) < VISITOR_PLAYER_SEP) {
          ok = false;
          break;
        }
      }
      if (!ok) continue;
      for (const b of booths) {
        const cx = b.x + b.w / 2;
        const cy = b.y + b.h / 2;
        if (Math.hypot(c.x - cx, c.y - cy) < VISITOR_PLAYER_SEP * 0.7) {
          ok = false;
          break;
        }
      }
      if (!ok) continue;
      for (const p of placed) {
        if (Math.hypot(c.x - p.x, c.y - p.y) < VISITOR_MIN_SEP) {
          ok = false;
          break;
        }
      }
      if (!ok) continue;
      chosen = c;
      break;
    }
    const p = makePerson(kind, false);
    if (chosen) {
      p.x = chosen.x;
      p.y = chosen.y;
      placed.push(chosen);
    } else {
      // Fallback: irgendwo freier erreichbarer Punkt
      for (const c of cells) {
        if (!blocked(c.x, c.y, PERSON_R + 2)) {
          p.x = c.x;
          p.y = c.y;
          placed.push(c);
          break;
        }
      }
    }
    persons.push(p);
  };
  for (let i = 0; i < nCust; i++) tryPlace('customer');
  for (let i = 0; i < nNob; i++) tryPlace('nobody');
  // Sicherheitsnetz: niemand startet in einem Collider
  for (const p of persons) {
    if (!blocked(p.x, p.y, p.r)) continue;
    const f = findFreeNear(p.x, p.y, p.r + 2, 400);
    if (f) {
      p.x = f.x;
      p.y = f.y;
    }
  }
}

function pickEntrance(): Vec {
  if (entrancePoints.length) {
    return entrancePoints[(Math.random() * entrancePoints.length) | 0]!;
  }
  return { x: WORLD_W / 2, y: WORLD_H - ENTRANCE_H * 0.4 };
}

function setupEntrances(): void {
  entrancePoints = [
    { x: WORLD_W / 2, y: WORLD_H - ENTRANCE_H * 0.35 },
    { x: WORLD_W * 0.28, y: WORLD_H - ENTRANCE_H * 0.35 },
    { x: WORLD_W * 0.72, y: WORLD_H - ENTRANCE_H * 0.35 },
    { x: 90, y: WORLD_H * 0.45 },
    { x: WORLD_W - 90, y: WORLD_H * 0.45 },
  ];
}

function tierStats(tier: CustTier): { hp: number; r: number; points: number } {
  if (tier === 'gross') return { hp: 10, r: 22, points: 100 };
  if (tier === 'hart') return { hp: 3, r: 16, points: 30 };
  return { hp: 1, r: PERSON_R, points: 10 };
}

function rollTier(): CustTier {
  const r = Math.random();
  if (r < 0.12) return 'gross';
  if (r < 0.4) return 'hart';
  return 'normal';
}

function allocId(): number {
  return nextPersonId++;
}

function makePerson(kind: Kind, atEntrance: boolean): Person {
  const p = makePersonBase(kind, atEntrance);
  initNpc(p);
  return p;
}
function makePersonBase(kind: Kind, _atEntrance: boolean): Person {
  const pos = pickEntrance();
  const d = randomDir();
  const id = allocId();
  if (kind === 'nobody') {
    return {
      id,
      x: pos.x,
      y: pos.y,
      vx: d.x,
      vy: d.y,
      kind: 'nobody',
      tier: 'normal',
      hp: 0,
      maxHp: 0,
      r: PERSON_R,
      points: 0,
      bubble: null,
      bubbleT: 0,
      becomeNobody: false,
      lifeT: VISIT_MIN + Math.random() * (VISIT_MAX - VISIT_MIN),
      leaving: false,
      followVipId: null,
      entourageN: 0,
      route: [],
      routeIdx: 0,
      restT: 0,
      visitMode: 'roam',
      targetBooth: -1,
      boothChatT: 0,
      boothChatLine: null,
      seekCd: 4 + (id % 7),
      rng: (id * 1103515245 + 12345) >>> 0,
      anim: makeAnim(),
      towedBy: null,
      towSeq: 0,
      towImmuneT: 0,
    };
  }
  if (kind === 'vip') {
    const inward = { x: WORLD_W / 2 - pos.x, y: WORLD_H * 0.4 - pos.y };
    const il = Math.hypot(inward.x, inward.y) || 1;
    return {
      id,
      x: pos.x,
      y: pos.y,
      vx: (inward.x / il) * VIP_SPEED,
      vy: (inward.y / il) * VIP_SPEED,
      kind: 'vip',
      tier: 'normal',
      hp: VIP_HP,
      maxHp: VIP_HP,
      r: VIP_R,
      points: VIP_POINTS,
      bubble: null,
      bubbleT: 0,
      becomeNobody: false,
      lifeT: VISIT_MIN + 20 + Math.random() * 25,
      leaving: false,
      followVipId: null,
      entourageN: 4 + ((Math.random() * 3) | 0), // 4–6
      route: [],
      routeIdx: 0,
      restT: 0,
      visitMode: 'roam',
      targetBooth: -1,
      boothChatT: 0,
      boothChatLine: null,
      seekCd: 4 + (id % 7),
      rng: (id * 1103515245 + 12345) >>> 0,
      anim: makeAnim(),
      towedBy: null,
      towSeq: 0,
      towImmuneT: 0,
    };
  }
  const tier = rollTier();
  const st = tierStats(tier);
  const inward = { x: WORLD_W / 2 - pos.x, y: WORLD_H * 0.4 - pos.y };
  const il = Math.hypot(inward.x, inward.y) || 1;
  return {
    id,
    x: pos.x,
    y: pos.y,
    vx: (inward.x / il) * PERSON_SPEED,
    vy: (inward.y / il) * PERSON_SPEED,
    kind: 'customer',
    tier,
    hp: st.hp,
    maxHp: st.hp,
    r: st.r,
    points: st.points,
    bubble: null,
    bubbleT: 0,
    becomeNobody: false,
    lifeT: VISIT_MIN + Math.random() * (VISIT_MAX - VISIT_MIN),
    leaving: false,
    followVipId: null,
    entourageN: 0,
    route: [],
    routeIdx: 0,
      restT: 0,
      visitMode: 'roam',
      targetBooth: -1,
      boothChatT: 0,
      boothChatLine: null,
      seekCd: 4 + (id % 7),
      rng: (id * 1103515245 + 12345) >>> 0,
      anim: makeAnim(),
      towedBy: null,
      towSeq: 0,
      towImmuneT: 0,
  };
}

function makeEntourageMember(vip: Person, slot: number, total: number): Person {
  const ang = (slot / total) * Math.PI * 2 + Math.random() * 0.4;
  const dist = 38 + Math.random() * 28;
  const d = randomDir();
  const id = allocId();
  return {
    id,
    x: vip.x + Math.cos(ang) * dist,
    y: vip.y + Math.sin(ang) * dist,
    vx: d.x * 0.5,
    vy: d.y * 0.5,
    kind: 'nobody',
    tier: 'normal',
    hp: 0,
    maxHp: 0,
    r: PERSON_R,
    points: 0,
    bubble: null,
    bubbleT: 0,
    becomeNobody: false,
    lifeT: vip.lifeT + 5,
    leaving: false,
    followVipId: vip.id,
    entourageN: 0,
    route: [],
    routeIdx: 0,
    restT: 0,
    visitMode: 'roam',
    targetBooth: -1,
    boothChatT: 0,
    boothChatLine: null,
    seekCd: 4 + (id % 7),
    rng: (id * 1103515245 + 12345) >>> 0,
      anim: makeAnim(),
      towedBy: null,
      towSeq: 0,
      towImmuneT: 0,
  };
}

function assignVipRoute(vip: Person): void {
  if (!vipRouteTemplates.length) {
    vip.route = [];
    vip.routeIdx = 0;
    return;
  }
  const tmpl = vipRouteTemplates[(Math.random() * vipRouteTemplates.length) | 0]!;
  // Kopie; Start nahe Eingang — Index 0 oder 1
  vip.route = tmpl.map((p) => ({ x: p.x, y: p.y }));
  vip.routeIdx = 1 < vip.route.length ? 1 : 0;
  const first = vip.route[vip.routeIdx];
  if (first) {
    const dx = first.x - vip.x;
    const dy = first.y - vip.y;
    const len = Math.hypot(dx, dy) || 1;
    vip.vx = (dx / len) * VIP_SPEED;
    vip.vy = (dy / len) * VIP_SPEED;
  }
}

function spawnVipWithEntourage(): void {
  const vip = makePerson('vip', true);
  assignVipRoute(vip);
  persons.push(vip);
  const n = vip.entourageN;
  for (let i = 0; i < n; i++) persons.push(makeEntourageMember(vip, i, n));
}

function countKind(kind: Kind): number {
  let n = 0;
  for (const p of persons) {
    if (p.kind !== kind || p.becomeNobody) continue;
    if (kind === 'nobody' && p.followVipId !== null) continue; // Entourage zählt nicht zur Nobody-Zielzahl
    n++;
  }
  return n;
}

function findVip(id: number): Person | null {
  for (const p of persons) {
    if (p.id === id && p.kind === 'vip') return p;
  }
  return null;
}

function startLeavingGroup(vip: Person): void {
  vip.leaving = true;
  const exit = pickEntrance();
  const dx = exit.x - vip.x;
  const dy = exit.y - vip.y;
  const len = Math.hypot(dx, dy) || 1;
  vip.vx = (dx / len) * VIP_SPEED * 1.2;
  vip.vy = (dy / len) * VIP_SPEED * 1.2;
  for (const p of persons) {
    if (p.followVipId !== vip.id) continue;
    p.leaving = true;
    p.lifeT = 0;
    const ox = exit.x + (Math.random() - 0.5) * 40 - p.x;
    const oy = exit.y + (Math.random() - 0.5) * 40 - p.y;
    const ol = Math.hypot(ox, oy) || 1;
    p.vx = (ox / ol) * PERSON_SPEED * 1.2;
    p.vy = (oy / ol) * PERSON_SPEED * 1.2;
  }
}

/** Spawner: Start-Besucher sind verteilt; Nachschub gestaffelt über Eingänge (1–3 s). */
function tickSpawner(dt: number): void {
  const cap = targetCustomers + targetNobodys + targetVips * 8 + 30;
  if (persons.length >= cap) return;

  // Warteschlange aus Start (Restziele) zuerst, 1–3 s Abstand
  if (spawnQueue.length) {
    entranceSpawnCd -= dt * modeSpawnTempo();
    if (entranceSpawnCd > 0) return;
    const kind = spawnQueue.shift()!;
    if (kind === 'vip') spawnVipWithEntourage();
    else {
      const np = makePerson(kind, true);
      persons.push(np);
      npcMaybeGroup(np);
    }
    entranceSpawnCd =
      (ENTRANCE_SPAWN_MIN + nextVisitorRand() * (ENTRANCE_SPAWN_MAX - ENTRANCE_SPAWN_MIN)) /
      (1 + spawnQueue.length / 40);
    return;
  }

  spawnAcc += dt * modeSpawnTempo();
  if (spawnAcc < SPAWN_INTERVAL) return;
  spawnAcc = 0;
  const cust = countKind('customer');
  const nob = countKind('nobody');
  const vips = countKind('vip');
  if (vips < targetVips) {
    spawnVipWithEntourage();
  } else if (cust < targetCustomers) {
    persons.push(makePerson('customer', true));
  } else if (nob < targetNobodys) {
    persons.push(makePerson('nobody', true));
  }
}


function setActiveAmmo(pl: GamePlayer, kind: AmmoKind): void {
  pl.activeAmmo = kind;
  if (pl.isHuman) {
    fireBtn.innerHTML = kind === 'pen' ? 'Kuli<br />werfen' : 'Flyer<br />werfen';
  }
}

function toggleAmmo(pl: GamePlayer): void {
  setActiveAmmo(pl, pl.activeAmmo === 'pen' ? 'flyer' : 'pen');
}

function shootPlayer(pl: GamePlayer, dir: CVec): void {
  if (!playing || pl.stunT > 0) return;
  if (pl.fireCd > 0) return;
  const len = Math.hypot(dir.x, dir.y);
  if (len < 1e-6) return;
  const nx = dir.x / len;
  const ny = dir.y / len;
  if (pl.activeAmmo === 'pen') {
    if (pl.pensAmmo <= 0) {
      if (pl.isHuman) {
        emptyMsg = 'Keine Kulis mehr!';
        emptyMsgT = EMPTY_MSG_TIME;
      }
      return;
    }
    projectiles.push({
      x: pl.x + nx * (PLAYER_R + 4),
      y: pl.y + ny * (PLAYER_R + 4),
      vx: nx * PEN_SPEED,
      vy: ny * PEN_SPEED,
      kind: 'pen',
      dmg: PEN_DMG,
      hitR: 6,
      life: 2.5,
      ownerId: pl.id,
    });
    if (pl.isHuman && giftOn('dreifachkuli')) {
      for (const a of [-0.2, 0.2]) {
        const c = Math.cos(a);
        const sn = Math.sin(a);
        const rx = nx * c - ny * sn;
        const ry = nx * sn + ny * c;
        projectiles.push({ x: pl.x + rx * (PLAYER_R + 4), y: pl.y + ry * (PLAYER_R + 4), vx: rx * PEN_SPEED, vy: ry * PEN_SPEED, kind: 'pen', dmg: PEN_DMG, hitR: 6, life: 2.5, ownerId: pl.id });
      }
    }
    if (pl.isHuman && giftOn('konfetti')) spawnFloat(pl.x, pl.y - 30, '✦ ✧ ✦', '#ff9ff3');
    pl.pensAmmo -= 1;
    if (pl.isHuman) sfx('pew');
  } else {
    if (pl.flyersAmmo <= 0) {
      if (pl.isHuman) {
        emptyMsg = 'Keine Flyer mehr!';
        emptyMsgT = EMPTY_MSG_TIME;
      }
      return;
    }
    projectiles.push({
      x: pl.x + nx * (PLAYER_R + 4),
      y: pl.y + ny * (PLAYER_R + 4),
      vx: nx * FLYER_SPEED,
      vy: ny * FLYER_SPEED,
      kind: 'flyer',
      dmg: FLYER_DMG,
      hitR: FLYER_HIT_R,
      life: FLYER_RANGE / FLYER_SPEED,
      ownerId: pl.id,
    });
    pl.flyersAmmo -= 1;
    if (pl.isHuman) sfx('flyer');
  }
  pl.facing = { x: nx, y: ny };
  pl.fireCd = FIRE_COOLDOWN;
}


function towList(ownerId: number): Person[] {
  return persons
    .filter(
      (p) =>
        p.towedBy === ownerId &&
        (p.kind === 'customer' || p.kind === 'vip') &&
        !p.becomeNobody,
    )
    .sort((a, b) => a.towSeq - b.towSeq);
}

function towStats(ownerId: number): { count: number; value: number } {
  const list = towList(ownerId);
  let value = 0;
  for (const p of list) value += p.points;
  return { count: list.length, value };
}

const TOW_MAX = 1;
function captureIntoTow(p: Person, ownerId: number): void {
  if (p.towImmuneT > 0) return;
  const own = players.find((q) => q.id === ownerId);
  if (towList(ownerId).length >= (own?.isHuman && giftOn('lanyard_xxl') ? GIFT_TOW : own?.isHuman && giftOn('kunden_magnet') ? GIFT_MAGNET_TOW : TOW_MAX)) {
    p.hp = Math.max(p.hp, 1);
    if (p.bubbleT <= 0) {
      p.bubble = 'Sie haben ja schon Begleitung.';
      p.bubbleT = bubbleDur(p.bubble, 0.8);
    }
    return;
  }
  p.towedBy = ownerId;
  if (human()?.id === ownerId) sfx(p.kind === 'vip' ? 'vip' : 'ding');
  p.towSeq = nextTowSeq++;
  p.hp = 0;
  p.boothChatT = 0;
  p.boothChatLine = null;
  p.visitMode = 'roam';
  p.targetBooth = -1;
  p.restT = 0;
  p.leaving = false;
  p.becomeNobody = false;
  p.vx = 0;
  p.vy = 0;
  p.bubble = 'Mitkommen …';
  p.bubbleT = bubbleDur(p.bubble, 0.7);
}

function freeFromTow(p: Person): void {
  p.towedBy = null;
  p.towSeq = 0;
  p.towImmuneT = TOW_FREE_IMMUNE_MIN + Math.random() * (TOW_FREE_IMMUNE_MAX - TOW_FREE_IMMUNE_MIN);
  if (p.kind === 'vip') {
    p.hp = VIP_HP;
    p.maxHp = VIP_HP;
  } else if (p.kind === 'customer') {
    const st = tierStats(p.tier);
    p.hp = st.hp;
    p.maxHp = st.hp;
    p.r = st.r;
  }
  p.bubble = 'Frei! Lauf!';
  p.bubbleT = bubbleDur(p.bubble, 0.9);
  const a = Math.random() * Math.PI * 2;
  p.vx = Math.cos(a) * PERSON_SPEED * 1.2;
  p.vy = Math.sin(a) * PERSON_SPEED * 1.2;
  p.visitMode = 'roam';
  p.seekCd = 5 + Math.random() * 6;
}

function deliverTowAtBooth(pl: GamePlayer): void {
  const stand = booths[pl.standIndex];
  if (!stand || !nearStand(pl.x, pl.y, stand)) return;
  const list = towList(pl.id);
  for (const p of list) {
    if (!nearStand(p.x, p.y, stand) && Math.hypot(p.x - pl.x, p.y - pl.y) > 90) continue;
    pl.score += p.points;
    modeOnDeliver(pl, p);
    if (pl.isHuman) sfx('kaching');
    spawnFloat(p.x, p.y - 20, `+${p.points}`, '#7dffc8');
    spawnFloat(stand.x + stand.w / 2, stand.y - 8, 'Abgeliefert!', '#c8ffe8');
    p.towedBy = null;
    p.towSeq = 0;
    if (p.kind === 'vip') {
      p.bubble = VIP_WIN_LINES[(Math.random() * VIP_WIN_LINES.length) | 0]!;
      p.bubbleT = bubbleDur(p.bubble, BUBBLE_TIME + 0.3);
      p.lifeT = -1;
      startLeavingGroup(p);
    } else {
      p.bubble = HIT_LINES[(Math.random() * HIT_LINES.length) | 0]!;
      p.bubbleT = bubbleDur(p.bubble, BUBBLE_TIME);
      p.becomeNobody = true;
      p.vx = 0;
      p.vy = 0;
    }
  }
}

function damageCustomer(p: Person, dmg: number, scorerId: number): void {
  if (p.kind !== 'customer' || p.becomeNobody) return;
  // Fremdes Schlepptau: Treffer befreit
  if (p.towedBy != null) {
    if (p.towedBy === scorerId) return;
    freeFromTow(p);
    return;
  }
  if (p.towImmuneT > 0) return;
  if (p.bubbleT > 0 && p.boothChatT <= 0) return;
  if (human()?.id === scorerId) sfx('boing');
  p.hp -= dmg;
  if (p.hp > 0) return;
  captureIntoTow(p, scorerId);
}

function damageVip(p: Person, dmg: number, scorerId: number): void {
  if (p.kind !== 'vip' || p.leaving) return;
  if (p.towedBy != null) {
    if (p.towedBy === scorerId) return;
    freeFromTow(p);
    return;
  }
  if (p.towImmuneT > 0) return;
  if (human()?.id === scorerId) sfx('boing');
  p.hp -= dmg;
  if (p.hp > 0) {
    p.bubble = 'Hmm… weiter so.';
    p.bubbleT = bubbleDur(p.bubble, 0.7);
    return;
  }
  captureIntoTow(p, scorerId);
}

function stunPlayer(target: GamePlayer, fromId: number): void {
  if (target.id === fromId) return;
  if (target.immuneT > 0 || target.stunT > 0) return;
  target.stunT = STUN_TIME;
  target.immuneT = STUN_TIME + STUN_IMMUNE;
  target.talk = 'Autsch – Kuli-Treffer!';
  target.talkT = bubbleDur(target.talk, 0.9);
}

function buildWorldSense(self: GamePlayer): WorldSense {
  return {
    dt: 0,
    worldW: WORLD_W,
    worldH: WORLD_H,
    blockedAt: (wx, wy) => blocked(wx, wy, PLAYER_R),
    pathStep: (from, to) => (pathGrid ? gridPathStep(pathGrid, from, to) : null),
    customers: persons
      .filter(
        (p) =>
          p.kind === 'customer' &&
          !p.becomeNobody &&
          p.hp > 0 &&
          p.towedBy == null &&
          p.towImmuneT <= 0,
      )
      .map((p) => ({
        id: p.id,
        x: p.x,
        y: p.y,
        r: p.r,
        tier: p.tier,
        hp: p.hp,
        points: p.points,
      })),
    vips: persons
      .filter(
        (p) =>
          p.kind === 'vip' &&
          !p.leaving &&
          p.hp > 0 &&
          p.towedBy == null &&
          p.towImmuneT <= 0,
      )
      .map((p) => ({
        id: p.id,
        x: p.x,
        y: p.y,
        r: p.r,
        hp: p.hp,
        points: p.points,
      })),
    nobodys: persons
      .filter((p) => p.kind === 'nobody' && p.towedBy == null)
      .map((p) => ({ x: p.x, y: p.y, r: p.r })),
    rivals: players
      .filter((p) => p.id !== self.id)
      .map((p) => ({ id: p.id, x: p.x, y: p.y, r: p.r })),
    enemyTows: persons
      .filter(
        (p) =>
          p.towedBy != null &&
          p.towedBy !== self.id &&
          (p.kind === 'customer' || p.kind === 'vip') &&
          !p.becomeNobody,
      )
      .map((p) => ({
        id: p.id,
        x: p.x,
        y: p.y,
        r: p.r,
        ownerId: p.towedBy!,
        points: p.points,
      })),
    ...(() => {
      const ts = towStats(self.id);
      return { towCount: ts.count, towValue: ts.value };
    })(),
    ownStand: (() => {
      const b = booths[self.standIndex] ?? booths.find((x) => x.ownerPlayerId === self.id) ?? {
        x: self.x,
        y: self.y,
        w: 40,
        h: 40,
      };
      return { x: b.x, y: b.y, w: b.w, h: b.h };
    })(),
    foreignStands: booths
      .map((b, i) => ({ b, i }))
      .filter(({ b }) => b.ownerPlayerId != null && b.ownerPlayerId !== self.id)
      .map(({ b, i }) => ({
        id: i,
        x: b.x,
        y: b.y,
        w: b.w,
        h: b.h,
        ownerId: b.ownerPlayerId!,
      })),
    coffeeStands: hallPlaces
      .filter((pl) => pl.kind === 'coffee')
      .map((pl) => ({
        x: pl.x,
        y: pl.y,
        w: pl.w,
        h: pl.h,
        ready: pl.cooldown <= 0,
        drinkReady: (pl.drinkCd?.[self.id] ?? 0) <= 0,
      })),
  };
}

function toControlSelf(pl: GamePlayer): ControlSelf {
  const ts = towStats(pl.id);
  return {
    id: pl.id,
    x: pl.x,
    y: pl.y,
    facing: pl.facing,
    pensAmmo: pl.pensAmmo,
    flyersAmmo: pl.flyersAmmo,
    activeAmmo: pl.activeAmmo,
    stunT: pl.stunT,
    r: pl.r,
    hasCoffee: pl.hasCoffee,
    stamina: pl.stamina,
    towCount: ts.count,
    towValue: ts.value,
  };
}

function tryDrinkCoffee(pl: GamePlayer): boolean {
  if (pl.stamina >= STAMINA_MAX * 0.95) return false;
  for (const place of hallPlaces) {
    if (place.kind !== 'coffee') continue;
    if (!nearCoffeeStand(pl.x, pl.y, place)) continue;
    place.drinkCd ??= {};
    if ((place.drinkCd[pl.id] ?? 0) > 0) continue;
    pl.stamina = STAMINA_MAX;
    if (pl.isHuman) sfx('slurp');
    place.drinkCd[pl.id] = DRINK_COOLDOWN;
    spawnFloat(pl.x, pl.y - 34, KAFFEE_GETRUNKEN[(Math.random() * KAFFEE_GETRUNKEN.length) | 0]!, '#ffd59a');
    return true;
  }
  return false;
}

function tickStamina(pl: GamePlayer, moving: boolean, dt: number): void {
  pl.busyT = Math.max(0, pl.busyT - dt);
  const mult = moving || pl.busyT > 0 ? STAMINA_BUSY_MULT : 1;
  const gm = pl.isHuman && giftOn('panzerschokolade') ? GIFT_STAMINA_MUL : 1;
  pl.stamina = Math.max(0, pl.stamina - STAMINA_DRAIN * mult * gm * dt);
  pl.yawnCd = Math.max(0, pl.yawnCd - dt);
  if (pl.stamina <= 0 && pl.yawnCd <= 0) {
    pl.yawnCd = YAWN_EVERY;
    spawnFloat(pl.x, pl.y - 34, KAFFEE_LEER[(Math.random() * KAFFEE_LEER.length) | 0]!, '#ffc0a0');
  }
}

function applyInput(pl: GamePlayer, input: PlayerInput, dt: number): void {
  if (input.ammoSwitch) toggleAmmo(pl);
  const mlen = Math.hypot(input.moveX, input.moveY);
  let mlenSkip = false;
  tickStamina(pl, mlen > 0.05 && pl.stunT <= 0, dt);
  if (input.fire) pl.busyT = 0.6;
  // Trinken: Bereich betreten (Touch & Desktop gleich)
  tryDrinkCoffee(pl);
  if (pl.stunT > 0) return;

  {
    const sp0 = mlen > 0.05 ? Math.min(1, mlen) * PLAYER_SPEED * staminaSpeedFactor(pl.stamina) * (pl.isHuman ? pipiSpeedFactor() * giftSpeedMul() : 1) : 0;
    if (wetMove(pl, mlen > 0.05 ? (input.moveX / mlen) * sp0 : 0, mlen > 0.05 ? (input.moveY / mlen) * sp0 : 0, dt)) {
      if (mlen > 0.05) pl.facing = { x: input.moveX / mlen, y: input.moveY / mlen };
      mlenSkip = true;
    }
  }
  if (mlen > 0.05 && !mlenSkip) {
    const speed = Math.min(1, mlen) * PLAYER_SPEED * staminaSpeedFactor(pl.stamina) * (pl.isHuman ? pipiSpeedFactor() * giftSpeedMul() : modeRivalSpeedMul());
    let mx = (input.moveX / mlen) * speed * dt;
    let my = (input.moveY / mlen) * speed * dt;
    if (pl.isHuman && giftOn('gokart')) {
      const k = Math.min(1, GIFT_KART_STEER * dt);
      kartV.x += ((input.moveX / mlen) * speed - kartV.x) * k;
      kartV.y += ((input.moveY / mlen) * speed - kartV.y) * k;
      mx = kartV.x * dt;
      my = kartV.y * dt;
    }
    if (pl.isHuman && giftOn('quietschschuhe') && Math.random() < dt * 2.2) spawnFloat(pl.x + (Math.random() - 0.5) * 16, pl.y + 10, 'quietsch', '#ffd0f0');
    if (pl.isHuman && giftOn('quietschente') && Math.random() < dt * 0.8) spawnFloat(pl.x, pl.y - 30, 'quietsch', '#fff3a0');
    pl.facing = { x: input.moveX / mlen, y: input.moveY / mlen };
    tryMoveCircle(pl, pl.x + mx, pl.y + my, pl.r);
  }

  // Kaffee: zuerst abholen / übergeben (Berührung oder Werf-Taste)
  if (!pl.hasCoffee) tryPickupCoffee(pl);
  else tryDeliverCoffee(pl);

  if (input.fire) {
    if (pl.hasCoffee && tryDeliverCoffee(pl)) {
      // Becher übergeben statt schießen
    } else if (input.aimX != null && input.aimY != null) {
      shootPlayer(pl, { x: input.aimX - pl.x, y: input.aimY - pl.y });
    } else {
      shootPlayer(pl, pl.facing);
    }
  }
}

const STAND_REFILL_CD = 20;
function refillAtOwnStand(pl: GamePlayer, dt: number): void {
  const stand = booths[pl.standIndex];
  if (!stand) return;
  stand.refillCd = Math.max(0, (stand.refillCd ?? 0) - dt * (pl.isHuman && giftOn('powerbank') ? GIFT_REFILL : 1));
  pl.penRefillAcc = 0;
  pl.flyerRefillAcc = 0;
  if (!nearStand(pl.x, pl.y, stand) || stand.refillCd > 0) return;
  const needP = Math.min(MAX_PENS - pl.pensAmmo, stand.stockPens ?? 0);
  const needF = Math.min(MAX_FLYERS - pl.flyersAmmo, stand.stockFlyers ?? 0);
  if (needP <= 0 && needF <= 0) return;
  if (needP > 0) {
    pl.pensAmmo += needP;
    stand.stockPens = (stand.stockPens ?? 0) - needP;
  }
  if (needF > 0) {
    pl.flyersAmmo += needF;
    stand.stockFlyers = (stand.stockFlyers ?? 0) - needF;
  }
  stand.refillCd = STAND_REFILL_CD;
  standRefills++;
  if (pl.isHuman) sfx('aufgeladen');
  spawnFloat(pl.x, pl.y - 34, `Aufgefüllt${needP > 0 ? ` +${needP} Kulis` : ''}${needF > 0 ? ` +${needF} Flyer` : ''}`, '#9ff0a0');
}
let standRefills = 0;

function trySteal(pl: GamePlayer): boolean {
  for (let i = 0; i < booths.length; i++) {
    const b = booths[i]!;
    if (b.ownerPlayerId == null || b.ownerPlayerId === pl.id) continue;
    if (!nearStand(pl.x, pl.y, b)) continue;
    if (!b.stealCd) b.stealCd = {};
    if ((b.stealCd[pl.id] ?? 0) > 0) continue;
    let stole = false;
    let fxText = '';
    let fxColor = '#ffe08a';
    if ((b.stockPens ?? 0) >= STEAL_PENS) {
      b.stockPens = (b.stockPens ?? 0) - STEAL_PENS;
      pl.pensAmmo = Math.min(MAX_PENS, pl.pensAmmo + STEAL_PENS);
      stole = true;
      fxText = `−${STEAL_PENS} Kuli`;
      fxColor = '#7dffc8';
    } else if ((b.stockFlyers ?? 0) >= STEAL_FLYERS) {
      b.stockFlyers = (b.stockFlyers ?? 0) - STEAL_FLYERS;
      pl.flyersAmmo = Math.min(MAX_FLYERS, pl.flyersAmmo + STEAL_FLYERS);
      stole = true;
      fxText = `−${STEAL_FLYERS} Flyer`;
      fxColor = '#ffe08a';
    }
    if (!stole) continue;
    b.stealCd[pl.id] = STEAL_COOLDOWN;
    ensureBoothDisp(b);
    spawnFloat(b.x + b.w / 2, b.y - 10, fxText, fxColor);
    spawnFloat(b.x + b.w / 2 + 12, b.y + 8, '👜', '#ffd0e8');
    const owner = players.find((x) => x.id === b.ownerPlayerId);
    if (owner) {
      owner.talk = STEAL_LINES[(Math.random() * STEAL_LINES.length) | 0]!;
      owner.talkT = bubbleDur(owner.talk, 1.4);
    }
    return true;
  }
  return false;
}


function pointInPlace(px: number, py: number, pl: HallPlace): boolean {
  return px >= pl.x && px <= pl.x + pl.w && py >= pl.y && py <= pl.y + pl.h;
}

function placeAt(px: number, py: number, kind?: HallPlaceKind): HallPlace | null {
  for (const pl of hallPlaces) {
    if (kind && pl.kind !== kind) continue;
    if (pointInPlace(px, py, pl)) return pl;
  }
  return null;
}

function inRestaurant(px: number, py: number): boolean {
  return placeAt(px, py, 'restaurant') !== null;
}

function inSmoke(px: number, py: number): boolean {
  return placeAt(px, py, 'smoke') !== null;
}

function coffeePointsFor(tier: CustTier): number {
  if (tier === 'gross') return COFFEE_POINTS_GROSS;
  if (tier === 'hart') return COFFEE_POINTS_HART;
  return COFFEE_POINTS_NORMAL;
}

function nearCoffeeStand(px: number, py: number, stand: HallPlace): boolean {
  const cx = stand.x + stand.w / 2;
  const cy = stand.y + stand.h / 2;
  return Math.hypot(px - cx, py - cy) < Math.max(stand.w, stand.h) * 0.65 + COFFEE_DELIVER_R;
}

function tryPickupCoffee(pl: GamePlayer): boolean {
  if (pl.hasCoffee) return false;
  for (const place of hallPlaces) {
    if (place.kind !== 'coffee') continue;
    if (place.cooldown > 0) continue;
    if (!nearCoffeeStand(pl.x, pl.y, place)) continue;
    pl.hasCoffee = true;
    place.cooldown = COFFEE_COOLDOWN;
    if (pl.isHuman) {
      emptyMsg = 'Kaffee geholt';
      emptyMsgT = EMPTY_MSG_TIME;
      kaffeeSpruch(place);
    }
    return true;
  }
  return false;
}

function tryDeliverCoffee(pl: GamePlayer): boolean {
  if (!pl.hasCoffee) return false;
  // Kunde muss im Radius eines Kaffeestands sein; Spieler ebenfalls nahe
  let best: Person | null = null;
  let bestD = Infinity;
  let usedStand: HallPlace | null = null;
  for (const place of hallPlaces) {
    if (place.kind !== 'coffee') continue;
    if (!nearCoffeeStand(pl.x, pl.y, place)) continue;
    for (const p of persons) {
      if (p.kind !== 'customer' || p.becomeNobody || p.leaving || p.bubbleT > 0) continue;
      if (!nearCoffeeStand(p.x, p.y, place)) continue;
      const d = Math.hypot(p.x - pl.x, p.y - pl.y);
      if (d < pl.r + p.r + 28 && d < bestD) {
        bestD = d;
        best = p;
        usedStand = place;
      }
    }
  }
  if (!best || !usedStand) return false;
  pl.hasCoffee = false;
  pl.score += coffeePointsFor(best.tier);
  best.bubble = COFFEE_THANKS[(Math.random() * COFFEE_THANKS.length) | 0]!;
  best.bubbleT = bubbleDur(best.bubble, BUBBLE_TIME + 0.3);
  best.becomeNobody = true;
  best.hp = 0;
  best.vx = 0;
  best.vy = 0;
  return true;
}

function tickHallPlaces(dt: number): void {
  for (const pl of hallPlaces) {
    if (pl.cooldown > 0) pl.cooldown = Math.max(0, pl.cooldown - dt);
    if (pl.drinkCd) {
      for (const k of Object.keys(pl.drinkCd)) {
        const id = Number(k);
        pl.drinkCd[id] = Math.max(0, (pl.drinkCd[id] ?? 0) - dt);
      }
    }
  }
}

let HALL_DIAG = Math.hypot(WORLD_W, WORLD_H);
/** Mindestabstand zwischen Spieler-Spawns (~40 % der Hallendiagonale). */
let MIN_SPAWN_DIST = HALL_DIAG * 0.4;

/** Deterministische Anker an Ecken/Rändern der begehbaren Halle. */
function spawnAnchorSeeds(): Vec[] {
  const m = 280;
  const top = 220 + SVC_STRIP_H;
  const bot = WORLD_H - ENTRANCE_H - 180;
  const left = m;
  const right = WORLD_W - m;
  const cx = WORLD_W / 2;
  const cy = (top + bot) / 2;
  return [
    { x: left, y: top },
    { x: right, y: top },
    { x: left, y: bot },
    { x: right, y: bot },
    { x: cx, y: top },
    { x: cx, y: bot },
    { x: left, y: cy },
    { x: right, y: cy },
    { x: left + (right - left) * 0.28, y: top + (bot - top) * 0.32 },
    { x: right - (right - left) * 0.28, y: top + (bot - top) * 0.32 },
    { x: left + (right - left) * 0.28, y: bot - (bot - top) * 0.28 },
    { x: right - (right - left) * 0.28, y: bot - (bot - top) * 0.28 },
  ];
}

/** Wählt `count` freie Spawn-Punkte mit max. Mindestabstand (deterministisch). */
function pickSpreadSpawns(count: number): Vec[] {
  const seeds = spawnAnchorSeeds();
  const cands: Vec[] = [];
  for (const s of seeds) {
    const free = findFreeNear(s.x, s.y, PLAYER_R + 2, 320);
    if (free) cands.push(free);
  }
  // Fallback-Raster falls zu wenig freie Anker
  if (cands.length < count) {
    for (let gy = 0; gy < 6; gy++) {
      for (let gx = 0; gx < 8; gx++) {
        const x = 300 + (gx / 7) * (WORLD_W - 600);
        const y = 250 + (gy / 5) * (WORLD_H - ENTRANCE_H - 400);
        const free = findFreeNear(x, y, PLAYER_R + 2, 200);
        if (free) cands.push(free);
      }
    }
  }
  const chosen: Vec[] = [];
  for (let n = 0; n < count; n++) {
    let best: Vec | null = null;
    let bestScore = -1;
    for (let i = 0; i < cands.length; i++) {
      const c = cands[i]!;
      let minD = HALL_DIAG;
      for (const ch of chosen) {
        minD = Math.min(minD, Math.hypot(ch.x - c.x, ch.y - c.y));
      }
      if (chosen.length === 0) minD = HALL_DIAG;
      // Schon gewählte Nachbarn aussortieren
      if (minD < 120 && chosen.length) continue;
      const meets = chosen.length === 0 || minD >= MIN_SPAWN_DIST;
      const score = (meets ? HALL_DIAG * 20 : 0) + minD - i * 0.01;
      if (score > bestScore) {
        bestScore = score;
        best = c;
      }
    }
    if (!best) break;
    chosen.push(best);
  }
  return chosen;
}

function rectsOverlapM(
  a: { x: number; y: number; w: number; h: number },
  b: { x: number; y: number; w: number; h: number },
  m: number,
): boolean {
  return (
    a.x - m < b.x + b.w &&
    a.x + a.w + m > b.x &&
    a.y - m < b.y + b.h &&
    a.y + a.h + m > b.y
  );
}

function placeBoothAt(
  x: number,
  y: number,
  w: number,
  h: number,
  label: string,
  standNo: string,
  ownerId: number | null,
  isOwn: boolean,
): number {
  // Stand nördlich des Spawn-Punkts, Spawn bleibt im Gang
  let bx = x - w / 2;
  let by = y - h - 40;
  bx = Math.max(60, Math.min(WORLD_W - w - 60, bx));
  by = Math.max(SVC_STRIP_H + 140, Math.min(WORLD_H - ENTRANCE_H - h - 40, by));
  const BOOTH_PLACE_MARGIN = Math.max(40, (SIDE_AISLE / 2) | 0);
  // Collider- + Orte-Konflikt: Stand leicht verschieben
  for (let k = 0; k < 24; k++) {
    const test = { x: bx, y: by, w, h };
    let hit = false;
    for (const c of colliders) {
      if (
        test.x < c.x + c.w &&
        test.x + test.w > c.x &&
        test.y < c.y + c.h &&
        test.y + test.h > c.y
      ) {
        hit = true;
        break;
      }
    }
    if (!hit) {
      for (const ob of booths) {
        if (ob.featured && rectsOverlapM(test, ob, 30)) {
          hit = true;
          break;
        }
        if (rectsOverlapM(test, ob, 8)) {
          hit = true;
          break;
        }
      }
    }
    if (!hit) {
      for (const pl of hallPlaces) {
        if (rectsOverlapM(test, pl, BOOTH_PLACE_MARGIN)) {
          hit = true;
          break;
        }
      }
    }
    if (!hit) break;
    const ox = ((k * 37) % 7) - 3;
    const oy = ((k * 19) % 5) - 2;
    bx = Math.max(60, Math.min(WORLD_W - w - 60, x - w / 2 + ox * 36));
    by = Math.max(SVC_STRIP_H + 140, Math.min(WORLD_H - ENTRANCE_H - h - 40, y - h - 40 + oy * 36 - k * 8));
    if (by < 60) {
      by = Math.min(WORLD_H - ENTRANCE_H - h - 40, y + 40);
    }
  }
  // Liegt der Stand noch in einem Aussteller-Stand: zur nächsten freien Seite hinausschieben
  for (let k = 0; k < 4; k++) {
    const f = booths.find((ob) => (ob.featured != null || ob.own) && rectsOverlapM({ x: bx, y: by, w, h }, ob, 0));
    if (!f) break;
    const opts = [
      { x: f.x - w - 30, y: by },
      { x: f.x + f.w + 30, y: by },
      { x: bx, y: f.y - h - 30 },
      { x: bx, y: f.y + f.h + 30 },
    ]
      .map((o) => ({ x: Math.max(60, Math.min(WORLD_W - w - 60, o.x)), y: Math.max(60, Math.min(WORLD_H - ENTRANCE_H - h - 40, o.y)) }))
      .sort((a, b) => Math.hypot(a.x - bx, a.y - by) - Math.hypot(b.x - bx, b.y - by));
    const free = opts.find((o) => !booths.some((ob) => (ob.featured != null || ob.own || ob.ownerPlayerId != null) && rectsOverlapM({ x: o.x, y: o.y, w, h }, ob, 8))) ?? opts[0]!;
    bx = free.x;
    by = free.y;
  }
  // Restkonflikte: generische Stände weichen (Featured/Spielerstände bleiben)
  {
    const test = { x: bx, y: by, w, h };
    booths = booths.filter(
      (ob) =>
        ob.featured != null ||
        ob.ownerPlayerId != null ||
        ob.own ||
        !rectsOverlapM(test, ob, 8),
    );
    // Standindizes der Spieler nach Filter neu zuordnen
    for (const pl of players) {
      const idx = booths.findIndex((ob) => ob.ownerPlayerId === pl.id);
      if (idx >= 0) pl.standIndex = idx;
    }
  }
  const stand: Booth = {
    x: bx,
    y: by,
    w,
    h,
    label,
    standNo,
    kind: isOwn ? 'own' : 'row',
    own: isOwn,
    ownerPlayerId: ownerId,
    stockPens: STAND_STOCK_PENS,
    stockFlyers: STAND_STOCK_FLYERS,
    dispPens: STAND_STOCK_PENS,
    dispFlyers: STAND_STOCK_FLYERS,
    stealCd: {},
    openS: true,
    openN: true,
  };
  booths.push(stand);
  return booths.length - 1;
}

function placeRivalStand(ownerId: number, name: string, at: Vec): number {
  return placeBoothAt(at.x, at.y, Math.round(190 * boothScale), Math.round(135 * boothScale), name, 'RIVALE', ownerId, false);
}

/** Max. Canvas-Pixel pro Seite / Fläche (mobile GPU-Limits). */
const MAX_CANVAS_SIDE = 4096;
const MAX_CANVAS_AREA = 12_000_000;

function stageCssSize(): { cssW: number; cssH: number } {
  const rect = stage.getBoundingClientRect();
  let cssW = rect.width;
  let cssH = rect.height;
  // Vor dem Layout oft 0 — Fallback auf Viewport abzüglich Kopfzeile
  if (cssW < 32 || cssH < 32) {
    const vv = window.visualViewport;
    const vw = vv?.width ?? window.innerWidth;
    const vh = vv?.height ?? window.innerHeight;
    const bar = document.getElementById('bar');
    const barH = bar?.getBoundingClientRect().height ?? 40;
    cssW = Math.max(cssW, vw);
    cssH = Math.max(cssH, vh - barH);
  }
  cssW = Math.max(64, cssW || 64);
  cssH = Math.max(64, cssH || 64);
  return { cssW, cssH };
}

function resize(): void {
  const rect = stage.getBoundingClientRect();
  const { cssW, cssH } = stageCssSize();
  view.cssW = cssW;
  view.cssH = cssH;
  refreshCamSize();
  // Wenn Stage noch 0 hoch/breit ist: feste CSS-Größe setzen, sonst bleibt
  // das Bitmap unsichtbar (Brave Android vor dem Flex-Layout).
  if (rect.width < 32 || rect.height < 32) {
    canvas.style.width = `${cssW}px`;
    canvas.style.height = `${cssH}px`;
  } else {
    canvas.style.width = '100%';
    canvas.style.height = '100%';
  }
  let dpr = Math.min(window.devicePixelRatio || 1, 3);
  if (!Number.isFinite(dpr) || dpr <= 0) dpr = 1;
  let bw = Math.max(1, Math.round(cssW * dpr));
  let bh = Math.max(1, Math.round(cssH * dpr));
  // Fläche / Seitenlimit einhalten (Brave/Android GPU)
  if (bw > MAX_CANVAS_SIDE || bh > MAX_CANVAS_SIDE || bw * bh > MAX_CANVAS_AREA) {
    const sideScale = Math.min(MAX_CANVAS_SIDE / bw, MAX_CANVAS_SIDE / bh, 1);
    const areaScale = Math.sqrt(MAX_CANVAS_AREA / Math.max(1, bw * bh));
    const s = Math.min(sideScale, areaScale);
    dpr *= s;
    bw = Math.max(1, Math.round(cssW * dpr));
    bh = Math.max(1, Math.round(cssH * dpr));
  }
  if (canvas.width !== bw || canvas.height !== bh) {
    canvas.width = bw;
    canvas.height = bh;
  }
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  const scaleRaw = Math.min(cssW / CAM_W, cssH / CAM_H);
  const scale = Number.isFinite(scaleRaw) && scaleRaw > 0 ? scaleRaw : 1;
  view = {
    offsetX: (cssW - CAM_W * scale) / 2,
    offsetY: (cssH - CAM_H * scale) / 2,
    scale,
    cssW,
    cssH,
  };
  clampCam();
  if (playing) {
    miniStatic = null;
    rebuildMinimapStatic();
  }
}

function clientToWorld(clientX: number, clientY: number): Vec | null {
  const rect = canvas.getBoundingClientRect();
  const cssX = clientX - rect.left;
  const cssY = clientY - rect.top;
  const lx = (cssX - view.offsetX) / view.scale;
  const ly = (cssY - view.offsetY) / view.scale;
  if (lx < 0 || ly < 0 || lx > CAM_W || ly > CAM_H) return null;
  return { x: lx + camX, y: ly + camY };
}

function setJoyKnob(dx: number, dy: number): void {
  joyKnob.style.transform = `translate(${dx}px, ${dy}px)`;
}

function updateJoyFromClient(clientX: number, clientY: number): void {
  const rect = joystickEl.getBoundingClientRect();
  let dx = clientX - (rect.left + rect.width / 2);
  let dy = clientY - (rect.top + rect.height / 2);
  const len = Math.hypot(dx, dy);
  if (len > JOY_RADIUS && len > 0) {
    dx = (dx / len) * JOY_RADIUS;
    dy = (dy / len) * JOY_RADIUS;
  }
  setJoyKnob(dx, dy);
  joyVec = { x: dx / JOY_RADIUS, y: dy / JOY_RADIUS };
  localRaw.joyX = joyVec.x;
  localRaw.joyY = joyVec.y;
}

function clearJoy(): void {
  joyPointerId = null;
  joyVec = { x: 0, y: 0 };
  localRaw.joyX = 0;
  localRaw.joyY = 0;
  setJoyKnob(0, 0);
}

function ensureRoundRect(): void {
  const proto = CanvasRenderingContext2D.prototype as unknown as {
    roundRect?: (
      this: CanvasRenderingContext2D,
      x: number,
      y: number,
      w: number,
      h: number,
      radii?: number | DOMPointInit | (number | DOMPointInit)[],
    ) => void;
  };
  if (typeof proto.roundRect === 'function') return;
  proto.roundRect = function (x, y, w, h, radii = 0) {
    let r = 0;
    if (typeof radii === 'number') r = radii;
    else if (Array.isArray(radii) && typeof radii[0] === 'number') r = radii[0];
    else if (radii && typeof radii === 'object' && 'x' in radii && typeof radii.x === 'number')
      r = radii.x;
    const rr = Math.min(Math.max(0, r), Math.abs(w) / 2, Math.abs(h) / 2);
    this.moveTo(x + rr, y);
    this.arcTo(x + w, y, x + w, y + h, rr);
    this.arcTo(x + w, y + h, x, y + h, rr);
    this.arcTo(x, y + h, x, y, rr);
    this.arcTo(x, y, x + w, y, rr);
    this.closePath();
  };
}
ensureRoundRect();

function showFatalError(err: unknown): void {
  const msg = err instanceof Error ? `${err.message}\n${err.stack ?? ''}` : String(err);
  console.error(err);
  let el = document.getElementById('fatal-error');
  if (!el) {
    el = document.createElement('pre');
    el.id = 'fatal-error';
    el.style.cssText =
      'position:fixed;inset:8px;z-index:9999;overflow:auto;background:rgba(40,0,0,0.92);' +
      'color:#ffb0b0;padding:12px;font:12px/1.35 ui-monospace,monospace;white-space:pre-wrap;' +
      'border:2px solid #c44;border-radius:8px;pointer-events:auto;';
    document.body.appendChild(el);
  }
  el.textContent = 'Fehler:\n' + msg;
}

function applyColliders(): void {
  colliders = [];
  for (const b of booths) for (const r of boothColliderRects(b)) colliders.push(r);
  for (const p of props) colliders.push({ x: p.x, y: p.y, w: p.w, h: p.h });
  for (const pl of hallPlaces) if (pl.kind === 'restaurant') colliders.push(...restoTableRects(pl));
  for (const pl of hallPlaces) if (pl.kind === 'toilet') colliders.push(...wcInnerRects(pl));
  rebuildHash();
}

function addRivalPlayer(i: number, at: Vec): void {
  const ownName = human()?.name ?? '';
  const rivalNames = COMPANY_NAMES.filter((n) => n !== ownName);
    const id = nextPlayerId++;
    const nm = rivalNames[i % rivalNames.length]!;
    const standIndex = placeRivalStand(id, nm, at);
    applyColliders();
    const b = booths[standIndex]!;
    const spawn =
      findFreeNear(at.x, at.y, PLAYER_R + 2, 280) ??
      findFreeNear(b.x + b.w / 2, b.y + b.h + 40, PLAYER_R + 2, 280) ??
      at;
    players.push({
      id,
      name: nm,
      color: RIVAL_COLORS[i % RIVAL_COLORS.length]!,
      x: spawn.x,
      y: spawn.y,
      facing: { x: 0, y: -1 },
      r: PLAYER_R,
      pensAmmo: MAX_PENS,
      flyersAmmo: MAX_FLYERS,
      activeAmmo: 'pen',
      score: 0,
      stunT: 0,
      immuneT: 0,
      talk: null,
      talkT: 0,
      isHuman: false,
      controller: new BotController(),
      standIndex,
      penRefillAcc: 0,
      flyerRefillAcc: 0,
      fireCd: 0,
      hasCoffee: false,
    stamina: STAMINA_MAX,
    yawnCd: 0,
    busyT: 0,
      lastMoveX: 0,
      lastMoveY: 0,
      rivalIndex: i + 1,
      anim: makeAnim(3),
    });
}
/** Laufzeit: weiteren Rivalen samt Stand einsetzen (Modus-Eskalation) */
function spawnRivalRuntime(): boolean {
  for (let tries = 0; tries < 50; tries++) {
    const x = 200 + Math.random() * (WORLD_W - 400);
    const y = 200 + Math.random() * (WORLD_H - 400 - ENTRANCE_H);
    if (players.some((pl) => Math.hypot(pl.x - x, pl.y - y) < 500)) continue;
    const f = findFreeNear(x, y, PLAYER_R + 2, 200);
    if (!f) continue;
    addRivalPlayer(players.length - 1, f);
    applyColliders();
    targetRivals = players.length - 1;
    return true;
  }
  return false;
}
function startGame(): void {
  try {
  syncZoomFromUi();
  syncHallUi();
  modePrepareHall();
  configureHall();
  const ownName = optCompany.value || COMPANY_NAMES[0]!;
  const occupancy = Number(optBooths.value);
  const density = Number(optDensity.value);
  const gen = generateHallPlan(occupancy, density, ownName);
  // Eingangs-Eigenstand aus Generator entfernen — neu verteilt platzieren
  booths = gen.booths.filter((b) => !b.own);
  props = gen.props;
  floorMarks = gen.marks;
  vipRouteTemplates = gen.vipRoutes;
  hallPlaces = gen.places;
  ownBooth = null;
  applyColliders();

  targetRivals = modeRivalsOverride(Number(optRivals.value));
  const spawnPts = pickSpreadSpawns(1 + targetRivals);
  const fallbackSpawn = (): Vec => {
    const f = findFreeNear(WORLD_W / 2, WORLD_H - ENTRANCE_H * 0.45, PLAYER_R + 2, 400);
    return f ?? { x: WORLD_W / 2, y: WORLD_H - ENTRANCE_H * 0.45 };
  };

  localRaw = createLocalRaw();
  nextPlayerId = 1;
  players = [];
  const humanId = nextPlayerId++;
  const humanAt = spawnPts[0] ?? fallbackSpawn();
  const humanStand = placeBoothAt(
    humanAt.x,
    humanAt.y,
    Math.round(200 * boothScale),
    Math.round(140 * boothScale),
    ownName,
    'DEIN STAND',
    humanId,
    true,
  );
  ownBooth = booths[humanStand] ?? null;
  applyColliders();
  const humanFree = findFreeNear(humanAt.x, humanAt.y, PLAYER_R + 2, 280) ?? humanAt;
  players.push({
    id: humanId,
    name: ownName,
    color: '#4a9fd8',
    x: humanFree.x,
    y: humanFree.y,
    facing: { x: 0, y: -1 },
    r: PLAYER_R,
    pensAmmo: MAX_PENS,
    flyersAmmo: MAX_FLYERS,
    activeAmmo: 'pen',
    score: 0,
    stunT: 0,
    immuneT: 0,
    talk: null,
    talkT: 0,
    isHuman: true,
    controller: new LocalController(localRaw),
    standIndex: humanStand,
    penRefillAcc: 0,
    flyerRefillAcc: 0,
    fireCd: 0,
    hasCoffee: false,
    stamina: STAMINA_MAX,
    yawnCd: 0,
    busyT: 0,
    lastMoveX: 0,
    lastMoveY: 0,
    rivalIndex: 0,
    anim: makeAnim(3),
  });
  setActiveAmmo(players[0]!, 'pen');

  for (let i = 0; i < targetRivals; i++) addRivalPlayer(i, spawnPts[i + 1] ?? fallbackSpawn());
  applyColliders();
  prunePlacesOverlappingBooths();

  // Zelle 40, Radius 22: dünne Standwände (6 px) liegen nie zwischen den Stichproben
  pathGrid = buildPathGrid(WORLD_W, WORLD_H, 40, (wx, wy) => blocked(wx, wy, PLAYER_R + 8));

  emptyMsgT = 0;
  emptyMsg = '';
  spawnAcc = 0;
  entranceSpawnCd = 0;
  spawnQueue = [];
  visitorSeed = (0xC0FFEE ^ (targetRivals * 9973) ^ ((occupancy * 131) | 0)) >>> 0;
  projectiles.length = 0;
  targetCustomers = Number(optCustomers.value);
  targetNobodys = Number(optNobodys.value);
  targetVips = modeVipsOverride(Number(optVips.value));
  nextPersonId = 1;
  setupEntrances();
  persons = [];
  initStaff();
  initService();
  initPipi(visitorSeed ^ 0x51ed);
  initHunger();
  initInfoDesk();
  initSecurity();
  resetDemo();
  resetWet();
  const seedC = Math.min(10, targetCustomers);
  const seedN = Math.min(8, targetNobodys);
  seedVisitorsSpread(seedC, seedN);
  spawnPests();
  // Restliche Zielzahlen später gestaffelt über Eingänge
  for (let i = seedC; i < targetCustomers; i++) spawnQueue.push('customer');
  for (let i = seedN; i < targetNobodys; i++) spawnQueue.push('nobody');
  for (const p of persons.slice(0, 6)) npcMaybeGroup(p);
  for (let i = 0; i < Math.min(1, targetVips); i++) spawnVipWithEntourage();
  for (let i = Math.min(1, targetVips); i < targetVips; i++) spawnQueue.push('vip');
  entranceSpawnCd = ENTRANCE_SPAWN_MIN + ((visitorSeed % 1000) / 1000) * (ENTRANCE_SPAWN_MAX - ENTRANCE_SPAWN_MIN);
  cachedPlaceOverlaps = placeOverlapReport();
  cachedAisleReach = aisleReachability();
  cachedBoothLayout = boothLayoutReport();
  {
    let minD = Infinity;
    let anyBlocked = false;
    let count = 0;
    for (const p of persons) {
      if (p.kind !== 'customer' && p.kind !== 'nobody') continue;
      if (p.followVipId != null) continue;
      count++;
      if (blocked(p.x, p.y, p.r)) anyBlocked = true;
      for (const pl of players) {
        const d = Math.hypot(p.x - pl.x, p.y - pl.y);
        if (d < minD) minD = d;
      }
    }
    cachedVisitorSpread = {
      count,
      minDistPlayers: minD === Infinity ? null : minD,
      anyBlocked,
    };
  }
  clampCam();
  rebuildMinimapStatic();
  playing = true;
  modeStart();
  menuEl.classList.add('hidden');
  if (isTouchDevice) {
    touchUi.classList.add('visible');
    touchUi.setAttribute('aria-hidden', 'false');
  }
  canvas.focus();
  // Layout auf Mobile oft erst nach einem Frame fertig
  requestAnimationFrame(() => {
    try {
      resize();
      requestAnimationFrame(() => {
        try {
          resize();
        } catch (err) {
          showFatalError(err);
        }
      });
    } catch (err) {
      showFatalError(err);
    }
  });
  } catch (err) {
    playing = false;
    showFatalError(err);
  }
}

setupModeUi();
btnStart.addEventListener('click', (e) => {
  e.preventDefault();
  // Exponat-Größen/Sprites vor der Hallengenerierung bereit (max. 3 s warten)
  void Promise.race([loadExhibits(), new Promise((r) => setTimeout(r, 3000))]).then(() => startGame());
});

window.addEventListener('keydown', (e) => {
  if (!playing) return;
  localRaw.keys.add(e.code);
  if (e.code === 'Space') {
    e.preventDefault();
    localRaw.fireQueued = true;
  } else if (e.code === 'KeyE') {
    e.preventDefault();
    const h = human();
    if (h) trySteal(h);
  } else if (e.code === 'KeyK') {
    collDebug = !collDebug;
  } else if (e.code === 'KeyQ') {
    e.preventDefault();
    localRaw.switchQueued = true;
  } else if (e.code === 'KeyM') {
    e.preventDefault();
    playAllSfx();
  } else if (e.code === 'KeyR') {
    e.preventDefault();
    scoreboardOpen = !scoreboardOpen;
  } else if (e.code === 'Digit1' || e.code === 'Numpad1') {
    e.preventDefault();
    const h = human();
    if (h) setActiveAmmo(h, 'pen');
  } else if (e.code === 'Digit2' || e.code === 'Numpad2') {
    e.preventDefault();
    const h = human();
    if (h) setActiveAmmo(h, 'flyer');
  }
});
window.addEventListener('keyup', (e) => localRaw.keys.delete(e.code));

canvas.addEventListener('pointermove', (e) => {
  if (!playing || e.pointerType === 'touch') return;
  mouseWorld = clientToWorld(e.clientX, e.clientY);
  localRaw.aimWorld = mouseWorld;
});
canvas.addEventListener('pointerleave', () => {
  mouseWorld = null;
  localRaw.aimWorld = null;
});
canvas.addEventListener('pointerdown', (e) => {
  if (!playing) return;
  {
    const r = canvas.getBoundingClientRect();
    if (toggleScoreboardAt(e.clientX - r.left, e.clientY - r.top)) {
      e.preventDefault();
      return;
    }
  }
  if (e.pointerType === 'touch') {
    e.preventDefault();
    canvas.focus();
    const w = clientToWorld(e.clientX, e.clientY);
    if (w) {
      localRaw.aimWorld = w;
      localRaw.fireQueued = true;
    }
    return;
  }
  if (e.button !== 0) return;
  e.preventDefault();
  canvas.focus();
  const w = clientToWorld(e.clientX, e.clientY);
  if (w) {
    localRaw.aimWorld = w;
    localRaw.fireQueued = true;
  }
});

joystickEl.addEventListener('pointerdown', (e) => {
  if (!playing || joyPointerId !== null) return;
  e.preventDefault();
  e.stopPropagation();
  joyPointerId = e.pointerId;
  joystickEl.setPointerCapture(e.pointerId);
  updateJoyFromClient(e.clientX, e.clientY);
});
joystickEl.addEventListener('pointermove', (e) => {
  if (e.pointerId !== joyPointerId) return;
  e.preventDefault();
  updateJoyFromClient(e.clientX, e.clientY);
});
function endJoy(e: PointerEvent): void {
  if (e.pointerId !== joyPointerId) return;
  clearJoy();
}
joystickEl.addEventListener('pointerup', endJoy);
joystickEl.addEventListener('pointercancel', endJoy);

fireBtn.addEventListener('pointerdown', (e) => {
  if (!playing || firePointerId !== null) return;
  e.preventDefault();
  e.stopPropagation();
  firePointerId = e.pointerId;
  fireBtn.classList.add('pressed');
  fireBtn.setPointerCapture(e.pointerId);
  localRaw.fireQueued = true;
});
function endFire(e: PointerEvent): void {
  if (e.pointerId !== firePointerId) return;
  firePointerId = null;
  fireBtn.classList.remove('pressed');
}
fireBtn.addEventListener('pointerup', endFire);
fireBtn.addEventListener('pointercancel', endFire);

switchBtn.addEventListener('pointerdown', (e) => {
  if (!playing || switchPointerId !== null) return;
  e.preventDefault();
  e.stopPropagation();
  switchPointerId = e.pointerId;
  switchBtn.classList.add('pressed');
  switchBtn.setPointerCapture(e.pointerId);
  localRaw.switchQueued = true;
});
function endSwitch(e: PointerEvent): void {
  if (e.pointerId !== switchPointerId) return;
  switchPointerId = null;
  switchBtn.classList.remove('pressed');
}
switchBtn.addEventListener('pointerup', endSwitch);
switchBtn.addEventListener('pointercancel', endSwitch);

document.addEventListener('contextmenu', (e) => e.preventDefault());
document.addEventListener('selectstart', (e) => {
  if (!(e.target instanceof HTMLSelectElement) && !(e.target instanceof HTMLInputElement)) {
    e.preventDefault();
  }
});
document.addEventListener(
  'touchmove',
  (e) => {
    if (menuEl.classList.contains('hidden')) e.preventDefault();
  },
  { passive: false },
);

window.addEventListener('resize', resize);
window.addEventListener('orientationchange', () => setTimeout(resize, 50));

function updatePerson(p: Person, dt: number): boolean {
  if (p.pest) return updatePest(p, dt);
  if (gift && p.towedBy == null && !p.leaving && (p.kind === 'vip' || p.kind === 'customer')) {
    const hum = human();
    if (hum) {
      const d = Math.hypot(hum.x - p.x, hum.y - p.y);
      if (p.kind === 'vip' && giftOn('vip_ticket') && d < GIFT_VIP_R) {
        p.vx = 0;
        p.vy = 0;
        return true;
      }
      if (p.kind === 'customer' && giftOn('megafon') && d < GIFT_MEGA_R && d > 50 && canVisitorBrowse(p)) {
        npcMoveTo(p, hum, PERSON_SPEED, dt);
        return true;
      }
    }
  }
  // Schlepptau hat Vorrang vor Bubble-Freeze
  if (p.towedBy != null && (p.kind === 'customer' || p.kind === 'vip')) {
    // fall through to tow block below after immune tick — handled there
  } else if (p.bubbleT > 0) {
    p.bubbleT -= dt;
    if (p.bubbleT <= 0) {
      p.bubble = null;
      if (p.becomeNobody) {
        p.kind = 'nobody';
        p.becomeNobody = false;
        p.hp = 0;
        p.maxHp = 0;
        p.r = PERSON_R;
        p.lifeT = VISIT_MIN * 0.6 + Math.random() * 12;
        p.followVipId = null;
        const d = randomDir();
        p.vx = d.x;
        p.vy = d.y;
      } else if (p.kind === 'vip' && p.lifeT < 0 && !p.leaving) {
        startLeavingGroup(p);
      }
    }
    return true;
  }

  if (p.towImmuneT > 0) p.towImmuneT = Math.max(0, p.towImmuneT - dt);

  // Im Schlepptau: Schlange hinter dem Spieler
  if (p.towedBy != null && (p.kind === 'customer' || p.kind === 'vip')) {
    const owner = players.find((x) => x.id === p.towedBy);
    if (!owner) {
      freeFromTow(p);
      return true;
    }
    const chain = towList(owner.id);
    const idx = chain.findIndex((x) => x.id === p.id);
    let leadX = owner.x;
    let leadY = owner.y;
    let lx = owner.facing.x;
    let ly = owner.facing.y;
    if (idx > 0) {
      const prev = chain[idx - 1]!;
      leadX = prev.x;
      leadY = prev.y;
      lx = prev.vx;
      ly = prev.vy;
    }
    const llen = Math.hypot(lx, ly) || 1;
    const bx = leadX - (lx / llen) * TOW_GAP;
    const by = leadY - (ly / llen) * TOW_GAP;
    let tx = bx;
    let ty = by;
    if (pathGrid) {
      const step = gridPathStep(pathGrid, p, { x: bx, y: by });
      if (step) {
        tx = step.x;
        ty = step.y;
      }
    }
    const dx = tx - p.x;
    const dy = ty - p.y;
    const dist = Math.hypot(dx, dy);
    if (dist > 4) {
      const len = dist || 1;
      const spd = Math.min(TOW_SPEED, 40 + dist * 2.2);
      tryMoveCircle(p, p.x + (dx / len) * spd * dt, p.y + (dy / len) * spd * dt, p.r);
      p.vx = (dx / len) * spd;
      p.vy = (dy / len) * spd;
    } else {
      p.vx = 0;
      p.vy = 0;
    }
    if (p.bubbleT > 0) {
      p.bubbleT -= dt;
      if (p.bubbleT <= 0) p.bubble = null;
    }
    return true;
  }

  // Entourage follows VIP
  if (p.followVipId !== null && !p.leaving) {
    const vip = findVip(p.followVipId);
    if (!vip || vip.leaving) {
      p.leaving = true;
      p.lifeT = 0;
    } else {
      const ang = (p.id % 7) * 0.9 + performance.now() * 0.0003;
      const dist = 40 + (p.id % 5) * 6;
      const tx = vip.x + Math.cos(ang) * dist;
      const ty = vip.y + Math.sin(ang) * dist;
      const dx = tx - p.x;
      const dy = ty - p.y;
      const len = Math.hypot(dx, dy) || 1;
      const spd = PERSON_SPEED * 0.95;
      tryMoveCircle(p, p.x + (dx / len) * spd * dt, p.y + (dy / len) * spd * dt, p.r);
      return true;
    }
  }

  // Raucherbereich: Nobodys/Kunden halten kurz, Kunden regenerieren & immun
  if (
    !p.leaving &&
    p.followVipId === null &&
    (p.kind === 'customer' || p.kind === 'nobody') &&
    p.restT <= 0 &&
    inSmoke(p.x, p.y) &&
    Math.random() < 0.35 * dt
  ) {
    p.restT = SMOKE_REST_MIN + Math.random() * (SMOKE_REST_MAX - SMOKE_REST_MIN);
    p.vx = 0;
    p.vy = 0;
    if (p.kind === 'customer') {
      p.bubble = 'Kurz Pause …';
      p.bubbleT = bubbleDur(p.bubble, 0.8);
    }
  }
  if (p.restT > 0) {
    p.restT -= dt;
    p.vx *= 0.1;
    p.vy *= 0.1;
    if (p.kind === 'customer' && p.maxHp > 0 && !p.becomeNobody) {
      p.hp = Math.min(p.maxHp, p.hp + SMOKE_HP_PER_SEC * dt);
    }
    tryMoveCircle(p, p.x + p.vx * dt, p.y + p.vy * dt, p.r);
    if (p.restT <= 0) {
      const a = personRand(p) * Math.PI * 2;
      p.vx = Math.cos(a) * PERSON_SPEED;
      p.vy = Math.sin(a) * PERSON_SPEED;
    }
    return true;
  }

  if (updateSvc(p, dt)) return true;

  // Standbesuch / Chat (Kunden + Nobodys, keine VIP-Route/Entourage)
  if (canVisitorBrowse(p)) {
    if (p.boothChatT > 0 || p.visitMode === 'chat') {
      p.boothChatT = Math.max(0, p.boothChatT - dt);
      p.lifeT -= dt;
      p.vx = 0;
      p.vy = 0;
      if (p.boothChatT <= 0) {
        p.boothChatLine = null;
        p.visitMode = 'roam';
        p.targetBooth = -1;
        p.seekCd = personRandRange(p, SEEK_CD_MIN, SEEK_CD_MAX);
        const a = personRand(p) * Math.PI * 2;
        p.vx = Math.cos(a) * PERSON_SPEED;
        p.vy = Math.sin(a) * PERSON_SPEED;
      }
      return true;
    }

    if (p.npc && npcUpdate(p, dt)) return true;

    if (!p.npc && p.visitMode === 'seek' && p.targetBooth >= 0) {
      const b = booths[p.targetBooth];
      if (!b) {
        p.visitMode = 'roam';
        p.targetBooth = -1;
      } else {
        const goal = boothApproachPoint(b);
        let tx = goal.x;
        let ty = goal.y;
        if (pathGrid) {
          const step = gridPathStep(pathGrid, p, goal);
          if (step) {
            tx = step.x;
            ty = step.y;
          }
        }
        const dx = tx - p.x;
        const dy = ty - p.y;
        const len = Math.hypot(dx, dy) || 1;
        const spd = PERSON_SPEED * 0.9;
        p.vx = (dx / len) * spd;
        p.vy = (dy / len) * spd;
        tryMoveCircle(p, p.x + p.vx * dt, p.y + p.vy * dt, p.r);
        if (Math.hypot(goal.x - p.x, goal.y - p.y) < 42) {
          startBoothChat(p);
        }
        return true;
      }
    }

    p.seekCd -= dt;
    if (p.npc) {
      // Persona-Logik entscheidet
    } else if (p.seekCd <= 0 && countBoothChats() < MAX_BOOTH_CHATS && personRand(p) < 0.55) {
      const bi = pickBoothForVisit(p);
      if (bi >= 0) {
        p.visitMode = 'seek';
        p.targetBooth = bi;
        p.seekCd = personRandRange(p, SEEK_CD_MIN, SEEK_CD_MAX);
      } else {
        p.seekCd = personRandRange(p, 3, 8);
      }
    } else if (p.seekCd <= 0) {
      p.seekCd = personRandRange(p, 2, 6);
    }
  }

  if (p.kind === 'vip' && p.lifeT < 0 && !p.leaving) {
    // convinced, waiting for bubble already handled; safety
    startLeavingGroup(p);
  }

  if (p.kind !== 'vip' || p.lifeT >= 0) {
    p.lifeT -= dt;
  }
  if (!p.leaving && p.lifeT <= 0 && p.lifeT > -0.5) {
    if (p.kind === 'vip') {
      startLeavingGroup(p);
    } else {
      p.leaving = true;
      const exit = pickEntrance();
      const dx = exit.x - p.x;
      const dy = exit.y - p.y;
      const len = Math.hypot(dx, dy) || 1;
      p.vx = (dx / len) * PERSON_SPEED * 1.15;
      p.vy = (dy / len) * PERSON_SPEED * 1.15;
    }
  }

  if (p.leaving) {
    const lm = npcSpeedMul(p);
    tryMoveCircle(p, p.x + p.vx * lm * dt, p.y + p.vy * lm * dt, p.r);
    for (const e of entrancePoints) {
      if (Math.hypot(p.x - e.x, p.y - e.y) < 70) return false;
    }
    if (p.x < 50 || p.y < 50 || p.x > WORLD_W - 50 || p.y > WORLD_H - 50) return false;
    return true;
  }

  if (p.kind === 'vip') {
    // Entlang der VIP-Route (Hauptgang-Wegpunkte), sonst leichtes Irren
    if (p.route.length && p.routeIdx < p.route.length && !p.leaving) {
      const wp = p.route[p.routeIdx]!;
      let tx = wp.x;
      let ty = wp.y;
      if (pathGrid) {
        const step = npcPathStep(p, wp);
        if (step) {
          tx = step.x;
          ty = step.y;
        }
      }
      const dx = tx - p.x;
      const dy = ty - p.y;
      const dist = Math.hypot(wp.x - p.x, wp.y - p.y);
      const len = Math.hypot(dx, dy) || 1;
      p.vx = (dx / len) * VIP_SPEED;
      p.vy = (dy / len) * VIP_SPEED;
      const hit = tryMoveCircle(p, p.x + p.vx * dt, p.y + p.vy * dt, p.r);
      if (dist < 55) {
        p.routeIdx++;
        if (p.routeIdx >= p.route.length) {
          // Route zu Ende → von vorn (Rundgang) bis lifeT abläuft
          p.routeIdx = 1 < p.route.length ? 1 : 0;
        }
      } else if (hit || Math.random() < 0.08 * dt) {
        // leichte Korrektur
        const a = Math.atan2(wp.y - p.y, wp.x - p.x) + (Math.random() - 0.5) * 0.6;
        p.vx = Math.cos(a) * VIP_SPEED;
        p.vy = Math.sin(a) * VIP_SPEED;
      }
    } else {
      const hit = tryMoveCircle(p, p.x + p.vx * dt, p.y + p.vy * dt, p.r);
      if (hit || Math.random() < 0.25 * dt) {
        const a = Math.random() * Math.PI * 2;
        p.vx = Math.cos(a) * VIP_SPEED;
        p.vy = Math.sin(a) * VIP_SPEED;
      }
    }
    return true;
  }

  const rm = npcSpeedMul(p);
  const hit = tryMoveCircle(p, p.x + p.vx * rm * dt, p.y + p.vy * rm * dt, p.r);
  if (hit || personRand(p) < 0.35 * dt) {
    const a = personRand(p) * Math.PI * 2;
    p.vx = Math.cos(a) * PERSON_SPEED;
    p.vy = Math.sin(a) * PERSON_SPEED;
  }
  return true;
}


function update(dt: number): void {
  if (!playing) return;
  emptyMsgT = Math.max(0, emptyMsgT - dt);

  // steal cooldowns
  for (const b of booths) {
    if (!b.stealCd) continue;
    for (const k of Object.keys(b.stealCd)) {
      const id = Number(k);
      b.stealCd[id] = (b.stealCd[id] ?? 0) - dt;
      if (b.stealCd[id]! <= 0) delete b.stealCd[id];
    }
  }

  for (const pl of players) {
    pl.fireCd = Math.max(0, pl.fireCd - dt);
    pl.stunT = Math.max(0, pl.stunT - dt);
    pl.immuneT = Math.max(0, pl.immuneT - dt);
    if (pl.talkT > 0) {
      pl.talkT -= dt;
      if (pl.talkT <= 0) pl.talk = null;
    } else if (pl.stunT <= 0 && pl.talk && pl.talk.startsWith('Autsch')) {
      pl.talk = null;
    }

    const sense = buildWorldSense(pl);
    sense.dt = dt;
    const input = pl.controller.getInput(sense, toControlSelf(pl), dt);
    pl.lastMoveX = input.moveX;
    pl.lastMoveY = input.moveY;
    applyInput(pl, input, dt);
    {
      const moving = Math.hypot(pl.lastMoveX, pl.lastMoveY) > 0.08 && pl.stunT <= 0;
      const dx = moving ? pl.lastMoveX : pl.facing.x;
      const dy = moving ? pl.lastMoveY : pl.facing.y;
      const frames = getSpriteMeta()?.frames ?? 4;
      tickAnim(pl.anim, moving, dx, dy, pl.isHuman && hungerFail ? dt * 0.4 : dt, frames);
    }
    refillAtOwnStand(pl, dt);
    trySteal(pl);
    deliverTowAtBooth(pl);

    // Nobody conversation (any player)
    if (pl.stunT <= 0 && pl.immuneT <= 0) {
      for (const p of persons) {
        if (p.kind !== 'nobody' || p.bubbleT > 0) continue;
        if (p.pest && p.pest.cd > 0) continue;
        if (pl.isHuman && giftOn('tarnweste')) break;
        if (Math.hypot(p.x - pl.x, p.y - pl.y) < pl.r + p.r - 2 + (p.pest ? 6 : 0)) {
          if (pl.isHuman && giftShield) {
            giftShield = false;
            pl.immuneT = IMMUNE_TIME * 2;
            spawnFloat(pl.x, pl.y - 34, 'Formular B7 vorgelegt!', '#b0ffb8');
            break;
          }
          const tt = p.pest ? PEST_TALK_TIME : TALK_TIME;
          pl.stunT = tt;
          pl.talk = p.pest
            ? PEST_LINES[(Math.random() * PEST_LINES.length) | 0] ?? '…'
            : NOBODY_LINES[(Math.random() * NOBODY_LINES.length) | 0]!;
          pl.talkT = bubbleDur(pl.talk, tt);
          pl.immuneT = tt + IMMUNE_TIME;
          if (p.pest) {
            p.pest.cd = PEST_TALK_TIME + PEST_COOLDOWN;
            p.pest.follow = false;
            npcStats.pestTalks++;
            if (pl.isHuman) {
              npcStats.pestHumanTalks++;
              npcStats.pestLastStun = tt;
              modeStreakReset();
            }
          }
          break;
        }
      }
    }
  }
  // Debug für Smoke/Tests
  const w = window as Window & { __messeDebug?: unknown };
  const rivalBooths = booths
    .map((b, index) => ({ b, index }))
    .filter(({ b }) => isRivalBooth(b))
    .map(({ b, index }) => ({
      index,
      x: b.x,
      y: b.y,
      w: b.w,
      h: b.h,
      stockPens: b.stockPens ?? 0,
      stockFlyers: b.stockFlyers ?? 0,
      dispPens: b.dispPens ?? b.stockPens ?? 0,
      dispFlyers: b.dispFlyers ?? b.stockFlyers ?? 0,
      ownerPlayerId: b.ownerPlayerId,
    }));
  w.__messeDebug = {
    wetSpawnHere: () => { const hh = human(); if (hh) spawnWet(hh.x, hh.y, true, 25); return wetZones.length; },
    wetInfo: () => ({ n: wetZones.length, signs: wetZones.filter((z) => z.sign).length, onWet: !!human() && wetAt(human()!.x, human()!.y), lines: REINIGUNG_SPRUECHE.length, ...wetStats }),
    wetTick: (sec: number) => { for (let k = 0; k < sec * 10; k++) tickWet(0.1); return wetZones.length; },
    wetMopNow: () => { wetNext = 0; tickWet(0.01); return cleaners.some((c) => !!c.mop); },
    collDebug: (on: boolean) => { collDebug = on; return collDebug; },
    /** Spieler diagonal gegen jede Exponat-Box schieben: muss entlanggleiten, nicht hängen */
    slideProbe: (id: string) => {
      const b = booths.find((x) => x.featured === id);
      const hh = human();
      if (!b || !hh) return null;
      const ex = b.exhibits ?? [];
      const res: { i: number; slid: number }[] = [];
      for (let i = 0; i < ex.length; i++) {
        const e = ex[i]!;
        const ent = { x: e.x - PLAYER_R - 1, y: e.y + e.h / 2 };
        if (blocked(ent.x, ent.y, PLAYER_R)) { res.push({ i, slid: -1 }); continue; }
        const y0 = ent.y;
        for (let k = 0; k < 30; k++) tryMoveCircle(ent, ent.x + 3, ent.y + 3, PLAYER_R);
        const down = Math.abs(ent.y - y0);
        ent.x = e.x - PLAYER_R - 1;
        ent.y = y0;
        for (let k = 0; k < 30; k++) tryMoveCircle(ent, ent.x + 3, ent.y - 3, PLAYER_R);
        res.push({ i, slid: Math.round(Math.max(down, Math.abs(ent.y - y0))) });
      }
      let minGap = 1e9;
      let tightPair: number[] = [];
      for (let i = 0; i < ex.length; i++) for (let j = i + 1; j < ex.length; j++) {
        const a = ex[i]!, c = ex[j]!;
        const gx = Math.max(c.x - (a.x + a.w), a.x - (c.x + c.w));
        const gy = Math.max(c.y - (a.y + a.h), a.y - (c.y + c.h));
        const g = gx > 0 && gy < 0 ? gx : gy > 0 && gx < 0 ? gy : -1;
        const mx = gx > 0 ? (gx === c.x - (a.x + a.w) ? a.x + a.w + gx / 2 : c.x + c.w + gx / 2) : (Math.max(a.x, c.x) + Math.min(a.x + a.w, c.x + c.w)) / 2;
        const my = gy > 0 ? (gy === c.y - (a.y + a.h) ? a.y + a.h + gy / 2 : c.y + c.h + gy / 2) : (Math.max(a.y, c.y) + Math.min(a.y + a.h, c.y + c.h)) / 2;
        if (g > 0 && g < minGap && !blocked(mx, my, 1)) { minGap = g; tightPair = [i, j]; }
      }
      return { n: ex.length, w: b.w, h: b.h, tightPair, res, minGap: Math.round(minGap), need: Math.round(PLAYER_R * 2 * 1.2) };
    },
    placeSizes: () => hallPlaces.map((p) => ({ kind: p.kind, x: p.x, y: p.y, w: p.w, h: p.h })),
    standInfo: (id: string) => { const b = booths.find((x) => x.featured === id); return b ? { x: b.x, y: b.y, w: b.w, h: b.h, img: !!standImage(id), coll: (b.exhibits ?? []).map((e) => [e.x, e.y, e.w, e.h]) } : null; },
    teleport: (x: number, y: number) => { const hh = human(); if (hh) { hh.x = x; hh.y = y; } return !!hh; },
    kaffeeInfo: () => hallPlaces.filter((p) => p.kind === 'coffee').map((p) => { const k = kaffeeStandOf(p); const a = k ? KAFFEE_ART[k.id] : undefined; return { label: p.label, id: k?.id ?? null, img: !!(a && placeImage(a.img)), x: p.x + p.w / 2, y: p.y + p.h / 2 }; }),
    demoStart: () => { demoNext = 0; tickDemo(0); return demoDebugInfo(); },
    demoInfo: () => demoDebugInfo(),
    demoTick: (sec: number) => { for (let k = 0; k < sec * 20; k++) tickDemo(0.05); return demoDebugInfo(); },
    demoPoofAll: () => { for (const h of hippies) demoPoof(h); return demoDebugInfo(); },
    demoHitOne: () => { const h = hippies.find((q) => !q.leaving && !q.gone); if (h) demoHit(h); return { hp: h?.hp ?? -1, ...demoDebugInfo() }; },
    demoShape: () => hippies.map((h) => ({ r: h.r, gear: h.gear, hp: h.hp })),
    demoFocus: () => { const hh = human(); const h0 = hippies[0]; if (hh && h0) { hh.x = demoGoal.x; hh.y = demoGoal.y + 120; } return demoDebugInfo(); },
    modeSelect: (m: GameMode, dur?: number) => modeSelect(m, dur),
    modeInfo: () => modeDebugInfo(),
    modeAdvance: (sec: number) => { for (let k = 0; k < sec * 10; k++) tickMode(0.1); return modeDebugInfo(); },
    modeDeliver: (n: number, vip = false) => { const h = human(); if (!h) return null; for (let k = 0; k < n; k++) { const p = persons.find((q) => q.kind === (vip ? 'vip' : 'customer')) ?? persons[0]!; h.score += p.points; modeOnDeliver(h, p); } return modeDebugInfo(); },
    modeStreakReset: () => { modeStreakReset(); return ms.streak; },
    durchsage: (k?: string) => { durchsageJetzt(k); return dsCur?.text ?? ''; },
    durchsageKontext,
    giftById: (id: string) => { const h = human(); const d = GIFTS.find((g) => g.id === id); return !!(h && d && giveGift(h, d)); },
    audioInfo,
    playAllSfx,
    playing,
    frameT: performance.now(),
    camZoom,
    camW: CAM_W,
    camH: CAM_H,
    rivalStockBarsDrawn,
    nearRivalSteal,
    rivalBooths,
    sprites: spriteLoadStatus(),
    places: hallPlaces.map((pl) => ({
      kind: pl.kind,
      x: pl.x,
      y: pl.y,
      w: pl.w,
      h: pl.h,
    })),
    placeOverlaps: cachedPlaceOverlaps,
    boothLayout: cachedBoothLayout,
    exhibits: exhibitStatus(),
    featuredExhibits: booths
      .filter((b) => b.featured)
      .map((b) => ({
        id: b.featured,
        kind: b.kind,
        exhibits: (b.exhibits ?? []).map((e) => ({ key: e.key, w: e.w, h: e.h, sprite: !!exhibitImage(e.key) })),
      })),
    setStamina(id: number | null, v: number) {
      const pl = id == null ? human() : players.find((x) => x.id === id);
      if (!pl) return { ok: false };
      pl.stamina = v;
      if (!pl.isHuman) {
        pl.stunT = 0;
        pl.immuneT = Math.max(pl.immuneT, 3);
      }
      return { ok: true, id: pl.id, stamina: pl.stamina };
    },
    speedFactor(id?: number) {
      const pl = id == null ? human() : players.find((x) => x.id === id);
      return pl ? staminaSpeedFactor(pl.stamina) : null;
    },
    /** Mensch an Kaffeestand, trinken */
    forceDrink() {
      const hum = human();
      const st = hallPlaces.find((p) => p.kind === 'coffee');
      if (!hum || !st) return { ok: false, reason: 'no stand' };
      if (st.drinkCd) st.drinkCd[hum.id] = 0;
      const f = findFreeNear(st.x + st.w / 2, st.y + st.h / 2, hum.r + 2, 200) ?? { x: st.x + st.w / 2, y: st.y + st.h / 2 };
      hum.x = f.x;
      hum.y = f.y;
      const before = hum.stamina;
      const drank = tryDrinkCoffee(hum);
      return { ok: drank && hum.stamina === STAMINA_MAX, before, after: hum.stamina, cd: st.drinkCd?.[hum.id] ?? 0 };
    },
    coffeeStandsPos: hallPlaces.filter((p) => p.kind === 'coffee').map((p) => ({ x: p.x + p.w / 2, y: p.y + p.h / 2 })),
    /** Kamera/Mensch an einen Aussteller-Stand setzen (Screenshots) */
    serviceInfo() {
      const r = hallPlaces.find((p) => p.kind === 'restaurant');
      const w = wcPlace();
      return {
        resto: r ? { x: r.x, y: r.y, w: r.w, h: r.h } : null,
        wc: w ? { x: w.x, y: w.y, w: w.w, h: w.h } : null,
        imgs: { resto: !!placeImage(RESTO.image), wc: !!placeImage(WC.image) },
        stages: [...svcStats.stagesSeen],
        restoDone: svcStats.restoDone,
        wcDone: svcStats.wcDone,
        cleared: svcStats.cleared,
        litter: litter.map((l) => l.kind),
        cleaners: cleaners.length,
        qTalks: qTalks.length,
        active: [...svcOf.values()].map((v) => v.place.kind + ':' + v.stage),
      };
    },
    /** n Besucher direkt in die Restaurant-/WC-Schlange schicken */
    forceService(kind: 'restaurant' | 'toilet', n = 1) {
      const pl = hallPlaces.find((p) => p.kind === kind);
      if (!pl) return { ok: false, reason: 'no place' };
      const ids: number[] = [];
      for (const p of persons) {
        if (ids.length >= n) break;
        if (svcOf.has(p) || !canVisitorBrowse(p)) continue;
        const f = findFreeNear(pl.x + pl.w / 2, pl.y + pl.h + 30, p.r + 1, 200);
        if (!f) continue;
        p.x = f.x;
        p.y = f.y;
        p.bubbleT = 0;
        p.bubble = null;
        if (startSvc(p, pl)) ids.push(p.id);
      }
      return { ok: ids.length > 0, ids };
    },
    svcOfPerson(id: number) {
      const p = persons.find((q) => q.id === id);
      const s = p ? svcOf.get(p) : undefined;
      return p ? { stage: s?.stage ?? null, kind: s?.place.kind ?? null, hidden: !!p.hidden } : null;
    },
    forceQueueTalk() {
      for (const [pl, q] of queues) if (q.length >= 2 && startQTalk(pl, q[0]!, q[1]!)) return true;
      return false;
    },
    forceLitter() {
      const r = hallPlaces.find((p) => p.kind === 'restaurant');
      if (!r) return false;
      dropLitter(r.x + r.w * 0.4, r.y + r.h * 0.6);
      return true;
    },
    forcePipi() {
      return startPipi() ? { ok: true, t: pipiMax } : { ok: false };
    },
    kundeWegInfo() {
      const hum = human();
      return { count: kundeWegCount, last: kundeWegLast, streak: ms.streak, tow: hum ? towList(hum.id).length : -1 };
    },
    pipiFailInfo() {
      const hum = human();
      return { count: pipiFailCount, parts: pipiParts.length, shake: pipiShakeT, stunT: hum?.stunT ?? 0, talk: hum?.talk ?? null, lines: PIPI_FAIL_BUBBLES.length };
    },
    securityInfo() {
      return { count: guards.length, lines: SECURITY_LINES.length, guards: guards.map((g) => ({ phase: g.phase, x: Math.round(g.x), y: Math.round(g.y), i: g.i, n: g.route.length, bubble: g.bubbleT > 0 ? g.bubble : null })) };
    },
    securityShow() {
      const hum = human();
      if (!hum) return false;
      for (const g of guards) {
        const f = findFreeNear(hum.x + 90 * (guards.indexOf(g) - 1), hum.y - 70, g.r + 1, 200);
        if (f) { g.x = f.x; g.y = f.y; }
        g.phase = 'scan';
        g.t = 6;
        g.bubble = SECURITY_LINES[0] ?? '';
        g.bubbleT = guards.indexOf(g) === 0 ? bubbleDur(g.bubble, 6) : 0;
      }
      return true;
    },
    giftInfo() {
      return { active: gift ? { id: gift.def.id, name: gift.def.name, t: gift.t } : null, reveal: giftReveal?.name ?? null, desk: infoDesk, given: giftsGiven, count: GIFTS.length, bubble: giftBubbleT > 0 ? giftBubble : null };
    },
    humanToInfo() {
      const hum = human();
      if (!hum || !infoDesk) return false;
      const f = findFreeNear(infoDesk.x, infoDesk.y + 30, hum.r + 1, 50);
      if (!f) return false;
      hum.x = f.x;
      hum.y = f.y;
      return true;
    },
    forceGift(id: string) {
      const hum = human();
      const d = GIFTS.find((g) => g.id === id);
      if (!hum || !d) return false;
      return giveGift(hum, d);
    },
    clearGift() {
      gift = null;
      giftInZone = false;
    },
    testRefill() {
      const hum = human();
      const b = hum ? booths[hum.standIndex] : null;
      if (!hum || !b) return null;
      hum.x = b.x + b.w / 2;
      hum.y = b.y + b.h + 20;
      b.refillCd = 0;
      hum.pensAmmo = 0;
      hum.flyersAmmo = 0;
      refillAtOwnStand(hum, 0.016);
      const first = { pens: hum.pensAmmo, flyers: hum.flyersAmmo, cd: b.refillCd ?? 0 };
      hum.pensAmmo = 0;
      refillAtOwnStand(hum, 0.016);
      const second = { pens: hum.pensAmmo, cd: b.refillCd ?? 0 };
      hum.pensAmmo = MAX_PENS;
      hum.flyersAmmo = MAX_FLYERS;
      return { first, second, maxPens: MAX_PENS, maxFlyers: MAX_FLYERS, cdMax: STAND_REFILL_CD };
    },
    refillInfo() {
      const hum = human();
      const b = hum ? booths[hum.standIndex] : null;
      return { cd: b?.refillCd ?? 0, cdMax: STAND_REFILL_CD, refills: standRefills, pens: hum?.pensAmmo ?? 0, flyers: hum?.flyersAmmo ?? 0, maxPens: MAX_PENS, maxFlyers: MAX_FLYERS };
    },
    hungerInfo() {
      const hum = human();
      return { t: hungerT, max: hungerMax, eat: hungerEat, slowT: hungerSlowT, fail: hungerFail, speed: pipiSpeedFactor(), bubble: hungerBubbleT > 0 ? hungerBubble : null, msg: hungerMsgT > 0 ? hungerMsg : null, score: hum?.score ?? 0, pipiT, next: hungerNext };
    },
    forceHunger() {
      pipiT = 0;
      return startHunger();
    },
    setHungerT(v: number) {
      hungerT = v;
    },
    humanToResto() {
      const hum = human();
      const r = restoPlace();
      if (!hum || !r) return false;
      const f = findFreeNear(r.x + r.w / 2, r.y + r.h / 2, hum.r + 1, 300);
      if (!f || !inResto(f.x, f.y)) return false;
      hum.x = f.x;
      hum.y = f.y;
      return true;
    },
    pestInfo() {
      const hum = human();
      return { count: persons.filter((p) => p.pest).length, following: persons.filter((p) => p.pest?.follow).length, talks: npcStats.pestTalks, humanTalks: npcStats.pestHumanTalks, lastStun: npcStats.pestLastStun, stunT: hum?.stunT ?? 0, talk: hum?.talk ?? null };
    },
    forcePest() {
      const hum = human();
      const p = persons.find((q) => q.pest);
      if (!hum || !p) return false;
      const f = findFreeNear(hum.x + 40, hum.y, p.r + 2, 120);
      if (!f) return false;
      p.x = f.x;
      p.y = f.y;
      p.pest!.cd = 0;
      p.bubbleT = 0;
      hum.immuneT = 0;
      hum.stunT = 0;
      hum.talk = null;
      pipiShakeT = 0;
      return true;
    },
    towMax() {
      return TOW_MAX;
    },
    pipiInfo() {
      const hum = human();
      return { t: pipiT, max: pipiMax, slowT: pipiSlowT, msg: pipiMsgT > 0 ? pipiMsg : null, score: hum?.score ?? 0, puddles: litter.filter((l) => l.kind === 'puddle').length };
    },
    svcSkip() {
      for (const v of svcOf.values()) v.t = Math.min(v.t, 0.2);
    },
    setHumanScore(v: number) {
      const hum = human();
      if (hum) hum.score = v;
    },
    setPipiT(v: number) {
      pipiT = v;
    },
    humanToWc() {
      const hum = human();
      const w = wcPlace();
      if (!hum || !w) return false;
      const st = wcState(w);
      let i = st.spots.findIndex((_, k) => !wcSpotBlocked(st, k));
      if (i < 0) {
        st.spots[0]!.occT = 0;
        i = 0;
        wcCollDirty = true;
      }
      const c = wcUV(w, st.spots[i]!.u, st.spots[i]!.v);
      hum.x = c.x;
      hum.y = c.y;
      clampCam();
      return inWcZone(hum.x, hum.y);
    },
    humanToWcFoyer() {
      const hum = human();
      const w = wcPlace();
      if (!hum || !w) return false;
      const z = imgRect(w, WC.alarmZone);
      hum.x = z.x + z.w / 2;
      hum.y = z.y + z.h / 2;
      clampCam();
      return inWcZone(hum.x, hum.y);
    },
    wcInnerInfo() {
      const w = wcPlace();
      if (!w) return null;
      const st = wcState(w);
      const ir = wcInnerRects(w);
      const inColl = ir.filter((r) => colliders.some((c) => c.x === r.x && c.y === r.y && c.w === r.w && c.h === r.h)).length;
      return { spots: st.spots.length, busy: st.spots.filter((_, k) => wcSpotBlocked(st, k)).length, nutz: st.nutz.length, lines: WC_NUTZ_LINES.length, talking: st.nutz.filter((z) => z.talkT > 0).length, roles: Object.fromEntries([...WC_NUTZ_BY_ROLE].map(([k, v]) => [k, v.length])), roleOk: st.nutz.every((z) => wcLineRole(wcLine(z)) === z.role || !WC_NUTZ_BY_ROLE.get(z.role)?.length), rects: ir.length, inColl, ...wcStats };
    },
    focusCrowd() {
      const hum = human();
      if (!hum) return null;
      let best: Person | null = null;
      let bn = -1;
      for (const p of persons) {
        if (p.hidden) continue;
        let n = 0;
        for (const q of persons) if (!q.hidden && Math.abs(q.x - p.x) < CAM_W / 2 && Math.abs(q.y - p.y) < CAM_H / 2) n++;
        if (n > bn) {
          bn = n;
          best = p;
        }
      }
      if (!best) return null;
      const f = findFreeNear(best.x, best.y + 30, hum.r + 2, 200);
      if (f) {
        hum.x = f.x;
        hum.y = f.y;
      }
      clampCam();
      return bn;
    },
    reinknallInfo() {
      const b = booths.find((x) => x.featured === 'reinknall');
      if (!b) return null;
      const rs = boothColliderRects(b);
      const inColl = rs.filter((r) => colliders.some((c) => c.x === r.x && c.y === r.y && c.w === r.w && c.h === r.h)).length;
      const lines = AUSSTELLER.find((a) => a.id === 'reinknall')?.personal_sprueche?.length ?? 0;
      return { x: b.x, y: b.y, w: b.w, h: b.h, kind: b.kind, walls: b.walls?.length ?? -1, ex: (b.exhibits ?? []).map((e) => ({ k: e.key, x: e.x, y: e.y, w: e.w, h: e.h, img: !!exhibitImage(e.key) })), rects: rs.length, inColl, lines, said: staffSoloStats.said, staff: staffList.filter((s) => s.booth === b).length };
    },
    focusReinknall() {
      const hum = human();
      const b = booths.find((x) => x.featured === 'reinknall');
      if (!hum || !b) return false;
      const f = findFreeNear(b.x + b.w * 0.5, b.y + b.h * 0.5, PLAYER_R + 2, 200);
      if (f) { hum.x = f.x; hum.y = f.y; }
      clampCam();
      return true;
    },
    focusPlace(kind: string) {
      const hum = human();
      const pl = hallPlaces.find((p) => p.kind === kind);
      if (!hum || !pl) return false;
      hum.x = pl.x + pl.w / 2;
      hum.y = pl.y + pl.h * 0.55;
      clampCam();
      return true;
    },
    standImages() {
      return standImageStatus();
    },
    staffInfo() {
      return {
        staff: staffList.length,
        booths: new Set(staffList.map((x) => x.booth)).size,
        talks: staffTalks.map((k) => ({
          booth: k.staff.booth.featured,
          idx: k.idx,
          n: k.lines.length,
          personId: k.person.id,
          line: k.lines[k.idx]?.t ?? null,
        })),
      };
    },
    /** Standgespräch erzwingen: Besucher neben Personal setzen */
    perf() {
      return perfSummary();
    },
    perfReset() {
      perfUpd.length = 0;
      perfDraw.length = 0;
      perfIv.length = 0;
      perfPrev = 0;
      pathCalls = 0;
    },
    setLod(on: boolean) {
      npcLod = on;
    },
    npcInfo() {
      const personas: Record<string, number> = {};
      let leads = 0;
      let followers = 0;
      let waiting = 0;
      let withGoals = 0;
      const needs = { hunger: 0, bladder: 0, tired: 0 };
      const spreads: number[] = [];
      let npcs = 0;
      for (const p of persons) {
        const n = p.npc;
        if (!n) continue;
        npcs++;
        personas[n.persona.id] = (personas[n.persona.id] ?? 0) + 1;
        if (n.lead != null) {
          followers++;
          const l = npcById.get(n.lead);
          if (l && !l.hidden && !p.hidden && !svcOf.has(l)) spreads.push(Math.hypot(l.x - p.x, l.y - p.y));
        } else if (persons.some((q) => q.npc?.lead === p.id)) leads++;
        if (n.waitBooth >= 0) waiting++;
        if (n.goals.length) withGoals++;
        needs.hunger += n.hunger;
        needs.bladder += n.bladder;
        needs.tired += n.tired;
      }
      let overlaps = 0;
      const vis = persons.filter((p) => !p.hidden && p.towedBy == null);
      for (let i = 0; i < vis.length; i++)
        for (let j = i + 1; j < vis.length; j++) {
          const a = vis[i]!;
          const c = vis[j]!;
          if (Math.hypot(a.x - c.x, a.y - c.y) < (a.r + c.r) * 0.5) overlaps++;
        }
      spreads.sort((a, b) => a - b);
      const groupSpread = spreads.length ? spreads[Math.floor(spreads.length / 2)]! : 0;
      return { npcs, personas, leads, followers, waiting, withGoals, groupSpread, overlaps, needs, stats: { ...npcStats }, lod: npcLod };
    },
    npcSetNeed(id: number, need: 'hunger' | 'bladder' | 'tired', v: number) {
      const p = persons.find((q) => q.id === id);
      if (!p?.npc) return false;
      p.npc[need] = v;
      p.npc.decideCd = 0;
      return true;
    },
    npcList() {
      return persons.filter((p) => p.npc).map((p) => ({ id: p.id, persona: p.npc!.persona.id, lead: p.npc!.lead, goals: [...p.npc!.goals], x: p.x, y: p.y, mode: p.visitMode, svc: svcOf.get(p)?.place.kind ?? null }));
    },
    forceStaffTalk(id?: string) {
      const st = staffList.find((x) => !id || x.booth.featured === id);
      if (!st) return { ok: false, reason: 'no staff' };
      const p = persons.find((q) => canVisitorBrowse(q) && q.boothChatT <= 0 && q.visitMode !== 'chat');
      if (!p) return { ok: false, reason: 'no visitor' };
      const f = findFreeNear(st.x + 40, st.y, p.r + 1, 120) ?? { x: st.x + 40, y: st.y };
      p.x = f.x;
      p.y = f.y;
      boothTalkCd.set(st.booth, 0);
      const ok = startStaffTalk(st, p);
      return { ok, personId: p.id, booth: st.booth.featured, n: staffTalks[staffTalks.length - 1]?.lines.length ?? 0 };
    },
    personPos(id: number) {
      const p = persons.find((q) => q.id === id);
      return p ? { x: p.x, y: p.y, chatT: p.boothChatT, mode: p.visitMode, hp: p.hp } : null;
    },
    focusFeatured(id: string) {
      const hum = human();
      const b = booths.find((x) => x.featured === id);
      if (!hum || !b) return { ok: false };
      const f = findFreeNear(b.x + b.w / 2, b.y + b.h * 0.6, hum.r + 2, 200) ?? { x: b.x + b.w / 2, y: b.y + b.h + 40 };
      hum.x = f.x;
      hum.y = f.y;
      hum.stunT = 0;
      clampCam();
      return { ok: true, x: f.x, y: f.y, booth: { x: b.x, y: b.y, w: b.w, h: b.h }, exhibits: b.exhibits };
    },
    /** Insel durchlaufen: West → Ost über die freie Randspur */
    forceIslandWalk() {
      const hum = human();
      const b = booths.find((x) => x.featured && x.kind === 'island' && x.walls && x.walls.length === 0)
        ?? booths.find((x) => x.kind === 'island' && x.walls && x.walls.length === 0);
      if (!hum || !b) return { ok: false, reason: 'no island' };
      const ent = { x: b.x - 30, y: b.y + 30 };
      if (blocked(ent.x, ent.y, hum.r)) return { ok: false, reason: 'start blocked', ent };
      for (let i = 0; i < 400 && ent.x < b.x + b.w + 30; i++) tryMoveCircle(ent, ent.x + 4, ent.y, hum.r);
      hum.x = ent.x;
      hum.y = ent.y;
      return { ok: ent.x >= b.x + b.w + 30, endX: ent.x, boothRight: b.x + b.w, label: b.label };
    },
    /** Exponat blockiert: frontal dagegen laufen */
    forceExhibitBlock() {
      const hum = human();
      const b = booths.find((x) => x.featured && (x.exhibits?.length ?? 0) > 0);
      const e = b?.exhibits?.[0];
      if (!hum || !b || !e) return { ok: false, reason: 'no exhibit' };
      const ent = { x: e.x - hum.r - 8, y: e.y + e.h / 2 };
      if (blocked(ent.x, ent.y, hum.r)) return { ok: false, reason: 'start blocked' };
      for (let i = 0; i < 100; i++) tryMoveCircle(ent, ent.x + 4, ent.y, hum.r);
      return { ok: ent.x + hum.r <= e.x + 1, endX: ent.x, exhibitX: e.x, key: e.key };
    },
    aisleReach: cachedAisleReach,
    visitorSpread: cachedVisitorSpread,
    players: players.map((pl) => ({
      id: pl.id,
      name: pl.name,
      isHuman: pl.isHuman,
      x: pl.x,
      y: pl.y,
      stunT: pl.stunT,
      immuneT: pl.immuneT,
      standIndex: pl.standIndex,
      lastMoveX: pl.lastMoveX,
      lastMoveY: pl.lastMoveY,
      blockedHere: blocked(pl.x, pl.y, pl.r),
      pensAmmo: pl.pensAmmo,
      flyersAmmo: pl.flyersAmmo,
      score: pl.score,
      stamina: pl.stamina,
      botMode: (pl.controller as { debugMode?: string }).debugMode ?? null,
      tow: towStats(pl.id),
    })),
    towedPersons: persons
      .filter((p) => p.towedBy != null)
      .map((p) => ({
        id: p.id,
        kind: p.kind,
        towedBy: p.towedBy,
        towSeq: p.towSeq,
        hp: p.hp,
        points: p.points,
        x: p.x,
        y: p.y,
      })),
    visitorChats: persons
      .filter((p) => p.boothChatT > 0)
      .map((p) => ({
        id: p.id,
        kind: p.kind,
        visitMode: p.visitMode,
        boothChatT: p.boothChatT,
        line: p.boothChatLine,
        x: p.x,
        y: p.y,
      })),
    visitorModes: {
      roam: persons.filter((p) => canVisitorBrowse(p) && p.visitMode === 'roam').length,
      seek: persons.filter((p) => canVisitorBrowse(p) && p.visitMode === 'seek').length,
      chat: persons.filter((p) => p.boothChatT > 0).length,
    },
    /** Test-Hook: Besucher an Stand setzen, chatten, danach weiterlaufen lassen */
    forceVisitorBoothChat() {
      if (!playing || !booths.length) return { ok: false, reason: 'no game' };
      let p =
        persons.find(
          (x) => canVisitorBrowse(x) && x.kind === 'customer' && !x.becomeNobody,
        ) ?? persons.find((x) => canVisitorBrowse(x));
      if (!p) return { ok: false, reason: 'no visitor' };
      const bi = booths.findIndex((b) => b.w >= 40);
      if (bi < 0) return { ok: false, reason: 'no booth' };
      const b = booths[bi]!;
      const goal = boothApproachPoint(b);
      const free = findFreeNear(goal.x, goal.y, p.r + 2, 160) ?? goal;
      p.x = free.x;
      p.y = free.y;
      p.targetBooth = bi;
      p.leaving = false;
      p.restT = 0;
      startBoothChat(p);
      // kurze Chat-Zeit für Smoke
      p.boothChatT = Math.min(p.boothChatT, 1.2);
      return {
        ok: true,
        id: p.id,
        visitMode: p.visitMode,
        boothChatT: p.boothChatT,
        line: p.boothChatLine,
        x: p.x,
        y: p.y,
      };
    },
    /** Capture → Schlepptau, Score unverändert */
    forceCaptureCustomer() {
      const hum = human();
      if (!hum || !playing) return { ok: false, reason: 'no human' };
      let p = persons.find(
        (x) =>
          x.kind === 'customer' &&
          !x.becomeNobody &&
          x.towedBy == null &&
          x.towImmuneT <= 0 &&
          x.hp > 0,
      );
      if (!p) {
        p = makePerson('customer', true);
        persons.push(p);
      }
      const free =
        findFreeNear(hum.x, hum.y + 50, p.r + 2, 200) ?? { x: hum.x, y: hum.y + 40 };
      p.x = free.x;
      p.y = free.y;
      p.towImmuneT = 0;
      p.bubbleT = 0;
      p.bubble = null;
      const scoreBefore = hum.score;
      p.hp = 0;
      captureIntoTow(p, hum.id);
      return {
        ok: p.towedBy === hum.id,
        id: p.id,
        scoreBefore,
        scoreAfter: hum.score,
        tow: towStats(hum.id),
      };
    },
    /** Abliefern am eigenen Stand → Score steigt */
    forceDeliverTow() {
      const hum = human();
      if (!hum || !playing) return { ok: false, reason: 'no human' };
      const stand = booths[hum.standIndex];
      if (!stand) return { ok: false, reason: 'no stand' };
      let list = towList(hum.id);
      if (!list.length) {
        let p = persons.find(
          (x) =>
            x.kind === 'customer' &&
            !x.becomeNobody &&
            x.towedBy == null &&
            x.towImmuneT <= 0,
        );
        if (!p) {
          p = makePerson('customer', true);
          persons.push(p);
        }
        p.towImmuneT = 0;
        p.bubbleT = 0;
        captureIntoTow(p, hum.id);
        list = towList(hum.id);
      }
      if (!list.length) return { ok: false, reason: 'no tow' };
      const scoreBefore = hum.score;
      const goal = { x: stand.x + stand.w / 2, y: stand.y + stand.h + 36 };
      const free = findFreeNear(goal.x, goal.y, PLAYER_R + 2, 200) ?? goal;
      hum.x = free.x;
      hum.y = free.y;
      for (const p of list) {
        p.x = free.x + (Math.random() - 0.5) * 20;
        p.y = free.y + 10 + Math.random() * 20;
      }
      deliverTowAtBooth(hum);
      return {
        ok: hum.score > scoreBefore,
        scoreBefore,
        scoreAfter: hum.score,
        tow: towStats(hum.id),
      };
    },
    /** Treffer auf Schlepptau → Kunde frei */
    forceHitTowed() {
      const hum = human();
      if (!hum || !playing) return { ok: false, reason: 'no human' };
      let list = towList(hum.id);
      if (!list.length) {
        let p0 = persons.find(
          (x) => x.kind === 'customer' && !x.becomeNobody && x.towedBy == null,
        );
        if (!p0) {
          p0 = makePerson('customer', true);
          persons.push(p0);
        }
        p0.towImmuneT = 0;
        captureIntoTow(p0, hum.id);
        list = towList(hum.id);
      }
      const p = list[0];
      if (!p) return { ok: false, reason: 'no tow' };
      const other = players.find((x) => x.id !== hum.id) ?? hum;
      freeFromTow(p);
      return {
        ok: p.towedBy == null && p.hp > 0,
        id: p.id,
        hp: p.hp,
        towImmuneT: p.towImmuneT,
        tow: towStats(hum.id),
        by: other.id,
      };
    },
    /** Test-Hook: Mensch zum Rivalenstand, CD löschen, einmal klauen */
    forceStealRival(boothIndex?: number) {
      const hum = human();
      if (!hum || !playing) return { ok: false, reason: 'no human' };
      const list = booths
        .map((b, index) => ({ b, index }))
        .filter(({ b }) => isRivalBooth(b));
      const pick =
        boothIndex != null
          ? list.find((x) => x.index === boothIndex)
          : list[0];
      if (!pick) return { ok: false, reason: 'no rival booth' };
      const { b, index } = pick;
      const before = { pens: b.stockPens ?? 0, flyers: b.stockFlyers ?? 0 };
      const free =
        findFreeNear(b.x + b.w / 2, b.y + b.h + 36, PLAYER_R + 2, 200) ?? {
          x: b.x + b.w / 2,
          y: b.y + b.h + 40,
        };
      hum.x = free.x;
      hum.y = free.y;
      if (!b.stealCd) b.stealCd = {};
      delete b.stealCd[hum.id];
      const ok = trySteal(hum);
      return {
        ok,
        index,
        before,
        after: { pens: b.stockPens ?? 0, flyers: b.stockFlyers ?? 0 },
      };
    },
  };

  clampCam();
  tickBoothStockDisplay(dt);
  tickFloatFx(dt);
  tickHallPlaces(dt);
  // HUD-Hinweis / Touch-Label für Klauen
  updateStealHintUi();

  tickSpawner(dt);
  npcUpdateAll(dt);
  updateStaff(dt);
  tickService(dt);
  tickPipi(dt);
  tickHunger(dt);
  tickGift(dt);
  tickMode(dt);
  tickSecurity(dt);
  tickDemo(dt);
  tickWet(dt);
  tickWcInner(dt);
  tickPipiFx(dt);
  {
    const frames = getSpriteMeta()?.frames ?? 4;
    for (const p of persons) {
      const spd = Math.hypot(p.vx, p.vy);
      if (npcLod && !npcOnScreen(p.x, p.y)) continue;
      const moving = spd > 12 && p.boothChatT <= 0 && p.restT <= 0 && p.bubbleT <= 0;
      tickAnim(p.anim, moving, p.vx, p.vy, dt, frames);
    }
  }

  for (let i = projectiles.length - 1; i >= 0; i--) {
    const pr = projectiles[i]!;
    pr.x += pr.vx * dt;
    pr.y += pr.vy * dt;
    pr.life -= dt;
    if (
      pr.life <= 0 ||
      pr.x < 0 ||
      pr.x > WORLD_W ||
      pr.y < 0 ||
      pr.y > WORLD_H ||
      pointInCollider(pr.x, pr.y)
    ) {
      projectiles.splice(i, 1);
      continue;
    }
    let hit = false;
    // PvP: pen stuns other players
    if (pr.kind === 'pen') {
      for (const pl of players) {
        if (pl.id === pr.ownerId) continue;
        if (Math.hypot(pr.x - pl.x, pr.y - pl.y) < pl.r + pr.hitR) {
          stunPlayer(pl, pr.ownerId);
          hit = true;
          break;
        }
      }
    }
    if (!hit && demoHitTest(pr.x, pr.y, pr.hitR)) hit = true;
    if (!hit) {
      for (const p of persons) {
        if (p.leaving) continue;
        const dist = Math.hypot(pr.x - p.x, pr.y - p.y);
        if (dist >= p.r + pr.hitR) continue;
        if (p.kind === 'vip') {
          if (p.towedBy != null) {
            damageVip(p, pr.dmg, pr.ownerId);
            hit = true;
            break;
          }
          if (pr.kind === 'pen') {
            p.bubble = VIP_PEN_LINES[(Math.random() * VIP_PEN_LINES.length) | 0]!;
            p.bubbleT = bubbleDur(p.bubble, 1.1);
            hit = true;
            break;
          }
          damageVip(p, pr.dmg, pr.ownerId);
          hit = true;
          break;
        }
        if (p.kind === 'customer' && !p.becomeNobody) {
          if (p.towedBy != null) {
            damageCustomer(p, pr.dmg, pr.ownerId);
            hit = true;
            break;
          }
          if (p.bubbleT > 0) continue;
          if (inRestaurant(p.x, p.y)) {
            p.bubble = RESTAURANT_LINES[(Math.random() * RESTAURANT_LINES.length) | 0]!;
            p.bubbleT = bubbleDur(p.bubble, 1.0);
            hit = true;
            break;
          }
          if (p.restT > 0 || inSmoke(p.x, p.y)) {
            p.bubble = p.bubble ?? 'Pause …';
            p.bubbleT = bubbleDur(p.bubble, Math.max(p.bubbleT, 0.6));
            hit = true;
            break;
          }
          damageCustomer(p, pr.dmg, pr.ownerId);
          hit = true;
          break;
        }
      }
    }
    if (hit) projectiles.splice(i, 1);
  }
}

function drawBubble(cx: number, cy: number, text: string, r: number): void {
  ctx.font = 'bold 13px system-ui, sans-serif';
  const padX = 8;
  const padY = 5;
  const maxW = 200;
  const words = text.split(' ');
  const lines: string[] = [];
  let line = '';
  for (const w of words) {
    const t = line ? `${line} ${w}` : w;
    if (ctx.measureText(t).width > maxW && line) {
      lines.push(line);
      line = w;
    } else line = t;
  }
  if (line) lines.push(line);
  const tw = Math.max(...lines.map((l) => ctx.measureText(l).width));
  const lineH = 15;
  const bw = tw + padX * 2;
  const bh = lines.length * lineH + padY * 2;
  const bx = cx - bw / 2;
  const by = cy - r - bh - 10;
  ctx.fillStyle = '#f5f0e0';
  ctx.strokeStyle = '#333';
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.roundRect(bx, by, bw, bh, 6);
  ctx.fill();
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(cx - 6, by + bh);
  ctx.lineTo(cx, by + bh + 8);
  ctx.lineTo(cx + 6, by + bh);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();
  ctx.fillStyle = '#222';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  lines.forEach((l, i) => ctx.fillText(l, cx, by + padY + lineH / 2 + i * lineH));
}

function drawProp(p: Prop): void {
  if (!inView(p.x, p.y, p.w, p.h)) return;
  if (p.shape === 'barrier') {
    // Absperrband: dezente Pfosten + Kordel
    const horiz = p.w >= p.h;
    ctx.strokeStyle = 'rgba(150,150,170,0.8)';
    ctx.lineWidth = 2;
    ctx.beginPath();
    if (horiz) {
      ctx.moveTo(p.x, p.y + p.h / 2);
      ctx.lineTo(p.x + p.w, p.y + p.h / 2);
    } else {
      ctx.moveTo(p.x + p.w / 2, p.y);
      ctx.lineTo(p.x + p.w / 2, p.y + p.h);
    }
    ctx.stroke();
    ctx.fillStyle = '#9a9aa8';
    const posts = horiz ? [p.x, p.x + p.w] : [p.y, p.y + p.h];
    for (const v of posts) {
      ctx.beginPath();
      if (horiz) ctx.arc(v, p.y + p.h / 2, 4, 0, Math.PI * 2);
      else ctx.arc(p.x + p.w / 2, v, 4, 0, Math.PI * 2);
      ctx.fill();
    }
    return;
  }
  if (p.shape === 'tank') {
    ctx.fillStyle = '#4a5a3a';
    ctx.fillRect(p.x, p.y + 8, p.w, p.h - 12);
    ctx.fillRect(p.x + p.w * 0.2, p.y, p.w * 0.45, 14);
    ctx.fillRect(p.x + p.w * 0.55, p.y + 4, p.w * 0.4, 6);
    ctx.strokeStyle = '#8a9a6a';
    ctx.strokeRect(p.x, p.y + 8, p.w, p.h - 12);
    return;
  }
  if (p.shape === 'truck') {
    ctx.fillStyle = '#3a4a5a';
    ctx.fillRect(p.x, p.y + 6, p.w * 0.65, p.h - 10);
    ctx.fillRect(p.x + p.w * 0.6, p.y, p.w * 0.4, p.h - 4);
    ctx.fillStyle = '#222';
    ctx.beginPath();
    ctx.arc(p.x + 12, p.y + p.h - 2, 6, 0, Math.PI * 2);
    ctx.arc(p.x + p.w - 12, p.y + p.h - 2, 6, 0, Math.PI * 2);
    ctx.fill();
    return;
  }
  ctx.fillStyle = '#555';
  ctx.fillRect(p.x, p.y, p.w, p.h);
}


function drawHallPlaces(): void {
  for (const pl of hallPlaces) {
    if (!inView(pl.x, pl.y, pl.w, pl.h, 40)) continue;
    if (pl.kind === 'restaurant') {
      drawRestaurant(pl);
      continue;
    }
    if (pl.kind === 'toilet') {
      drawToilet(pl);
      continue;
    }
    ctx.save();
    if (pl.kind === 'smoke') {
      ctx.fillStyle = 'rgba(90, 90, 70, 0.35)';
      ctx.strokeStyle = 'rgba(180, 160, 100, 0.7)';
    } else if (pl.kind === 'coffee') {
      ctx.fillStyle = 'rgba(90, 55, 30, 0.4)';
      ctx.strokeStyle = 'rgba(220, 160, 90, 0.85)';
    } else {
      ctx.fillStyle = 'rgba(50, 80, 60, 0.4)';
      ctx.strokeStyle = 'rgba(120, 200, 140, 0.75)';
    }
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.roundRect(pl.x, pl.y, pl.w, pl.h, 10);
    ctx.fill();
    ctx.stroke();
    // Icon / Schild
    ctx.font = 'bold 14px system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    const cx = pl.x + pl.w / 2;
    const cy = pl.y + pl.h / 2;
    if (pl.kind === 'smoke') {
      ctx.fillStyle = '#e8d9a8';
      ctx.fillText('🚬', cx, cy - 10);
      ctx.font = '11px system-ui, sans-serif';
      ctx.fillText('Raucher', cx, cy + 14);
    } else if (pl.kind === 'coffee') {
      if (!drawKaffeeArt(pl)) {
        ctx.fillStyle = '#ffe0b0';
        ctx.fillText('☕', cx, cy - 14);
        ctx.font = '11px system-ui, sans-serif';
        ctx.fillText(pl.label, cx, cy + 8);
      }
      const hum = human();
      const dcd = hum ? (pl.drinkCd?.[hum.id] ?? 0) : 0;
      if (dcd > 0) {
        const bw = pl.w - 20;
        const bx = pl.x + 10;
        const by = pl.y + 6;
        ctx.fillStyle = 'rgba(0,0,0,0.6)';
        ctx.fillRect(bx, by, bw, 7);
        ctx.fillStyle = '#7fd0ff';
        ctx.fillRect(bx, by, bw * (1 - dcd / DRINK_COOLDOWN), 7);
      }
      if (pl.cooldown > 0) {
        const bw = pl.w - 20;
        const bh = 8;
        const bx = pl.x + 10;
        const by = pl.y + pl.h - 18;
        const prog = 1 - pl.cooldown / COFFEE_COOLDOWN;
        ctx.fillStyle = 'rgba(0,0,0,0.55)';
        ctx.fillRect(bx, by, bw, bh);
        ctx.fillStyle = '#c8a060';
        ctx.fillRect(bx, by, bw * prog, bh);
      }
    } else {
      ctx.fillStyle = '#c8ffd0';
      ctx.fillText('🍽', cx, cy - 10);
      ctx.font = '11px system-ui, sans-serif';
      ctx.fillText('Restaurant', cx, cy + 14);
    }
    ctx.restore();
  }
}


let stealHintArmed = false;
function updateStealHintUi(): void {
  const hum = human();
  let near = false;
  let canSteal = false;
  if (hum && playing) {
    for (const b of booths) {
      if (!isRivalBooth(b)) continue;
      if (!nearStand(hum.x, hum.y, b)) continue;
      near = true;
      const cd = b.stealCd?.[hum.id] ?? 0;
      const has =
        (b.stockPens ?? 0) >= STEAL_PENS || (b.stockFlyers ?? 0) >= STEAL_FLYERS;
      if (cd <= 0 && has) canSteal = true;
      break;
    }
  }
  stealHintArmed = near && canSteal;
  const hint = document.getElementById('hint');
  if (hint) {
    if (near) {
      hint.textContent = isTouchDevice
        ? 'Rivalenstand: In der Zone klauen (automatisch) — Button zeigt „Klauen!“'
        : 'Rivalenstand: In der Zone klauen (Taste E oder automatisch)';
    } else if (playing) {
      hint.textContent =
        'Hallenplan · Kuli/Flyer · Rivalenstand plündern · Orange/Rot = Kunden, Grau = Nobody';
    }
  }
  if (isTouchDevice && playing) {
    if (stealHintArmed) {
      fireBtn.innerHTML = '👜<br />Klauen!';
      fireBtn.style.borderColor = '#ff80c0';
    } else if (hum) {
      fireBtn.style.borderColor = '';
      fireBtn.innerHTML =
        hum.activeAmmo === 'pen' ? 'Kuli<br />werfen' : 'Flyer<br />werfen';
    }
  }
}

function drawExhibit(e: Exhibit): void {
  const img = exhibitImage(e.key);
  if (img) {
    ctx.drawImage(img, e.x, e.y, e.w, e.h);
  } else {
    // Platzhalter, bis die Exponat-Art da ist (sichtbar = Kollision)
    ctx.fillStyle = 'rgba(70,74,82,0.82)';
    ctx.fillRect(e.x, e.y, e.w, e.h);
    ctx.strokeStyle = 'rgba(235,235,240,0.85)';
    ctx.lineWidth = 2;
    ctx.setLineDash([6, 4]);
    ctx.strokeRect(e.x + 1, e.y + 1, e.w - 2, e.h - 2);
    ctx.setLineDash([]);
  }
  drawExhibitLabel(e);
}

function drawExhibitLabel(e: Exhibit): void {
  if (e.label && !e.furniture) {
    ctx.font = '11px system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    const tw = Math.min(260, ctx.measureText(e.label).width + 8);
    ctx.fillStyle = 'rgba(0,0,0,0.7)';
    ctx.fillRect(e.x + e.w / 2 - tw / 2, e.y + e.h + 2, tw, 15);
    ctx.fillStyle = '#fff';
    ctx.fillText(e.label, e.x + e.w / 2, e.y + e.h + 4, 254);
  }
}

function drawFeaturedSign(b: Booth): void {
  // Stände mit Bild haben ein eigenes Banner im Bild → kein Schild
  if (standImage(b.featured)) return;
  const sw = Math.min(b.w - 16, 320);
  const sx = b.x + (b.w - sw) / 2;
  const sy = b.y - 70;
  ctx.fillStyle = '#f4ead0';
  ctx.fillRect(sx, sy, sw, 62);
  ctx.strokeStyle = b.color ?? '#333';
  ctx.lineWidth = 5;
  ctx.strokeRect(sx, sy, sw, 62);
  ctx.fillStyle = '#1a1408';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.font = 'bold 28px system-ui, sans-serif';
  ctx.fillText(b.label, sx + sw / 2, sy + 22, sw - 10);
  ctx.font = 'italic 15px system-ui, sans-serif';
  ctx.fillText(b.slogan ?? '', sx + sw / 2, sy + 48, sw - 10);
}

function drawBooth(b: Booth): void {
  if (!inView(b.x, b.y, b.w, b.h)) return;
  if (b.own) {
    ctx.fillStyle = '#1a6a5a';
    ctx.strokeStyle = '#5eefc8';
  } else if (b.ownerPlayerId != null) {
    const op = players.find((x) => x.id === b.ownerPlayerId);
    ctx.fillStyle = op ? op.color : '#6a3a5a';
    ctx.strokeStyle = '#f0c0e0';
  } else if (b.color) {
    ctx.fillStyle = b.color;
    ctx.strokeStyle = '#e0e0c0';
  } else if (b.kind === 'island') {
    ctx.fillStyle = '#3a4558';
    ctx.strokeStyle = '#8aa0c0';
  } else if (b.kind === 'corner') {
    ctx.fillStyle = '#5a4838';
    ctx.strokeStyle = '#a08860';
  } else {
    ctx.fillStyle = '#524838';
    ctx.strokeStyle = '#8a7a5a';
  }
  if (b.walls) {
    // Begehbarer Stand: Boden halbtransparent, Wände an geschlossenen Seiten
    ctx.globalAlpha = 0.8;
    ctx.fillRect(b.x, b.y, b.w, b.h);
    ctx.globalAlpha = 1;
    // Teppichkante
    ctx.strokeStyle = 'rgba(255,255,255,0.25)';
    ctx.lineWidth = 1;
    ctx.strokeRect(b.x + 0.5, b.y + 0.5, b.w - 1, b.h - 1);
    // niedrige Wand
    ctx.fillStyle = '#ece6d6';
    for (const wr of b.walls) ctx.fillRect(wr.x, wr.y, wr.w, wr.h);
    const simg = standImage(b.featured);
    if (simg) {
      ctx.drawImage(simg, b.x, b.y, b.w, b.h);
      drawStandImgTexts(b);
      // Exponate im Bild nur bei eingebackener Kollision
      if (!STAND_COLL[b.featured ?? '']) for (const e of b.exhibits ?? []) drawExhibit(e);
    } else {
      for (const e of b.exhibits ?? []) drawExhibit(e);
    }
  } else {
    ctx.fillRect(b.x, b.y, b.w, b.h);
    ctx.lineWidth = b.own || b.kind === 'island' ? 3 : 1.5;
    ctx.strokeRect(b.x + 1, b.y + 1, b.w - 2, b.h - 2);
  }

  // Open-side hint (lighter edge)
  ctx.strokeStyle = 'rgba(200,220,255,0.35)';
  ctx.lineWidth = 3;
  if (b.openN) {
    ctx.beginPath();
    ctx.moveTo(b.x + 4, b.y);
    ctx.lineTo(b.x + b.w - 4, b.y);
    ctx.stroke();
  }
  if (b.openS) {
    ctx.beginPath();
    ctx.moveTo(b.x + 4, b.y + b.h);
    ctx.lineTo(b.x + b.w - 4, b.y + b.h);
    ctx.stroke();
  }
  if (b.openE) {
    ctx.beginPath();
    ctx.moveTo(b.x + b.w, b.y + 4);
    ctx.lineTo(b.x + b.w, b.y + b.h - 4);
    ctx.stroke();
  }
  if (b.openW) {
    ctx.beginPath();
    ctx.moveTo(b.x, b.y + 4);
    ctx.lineTo(b.x, b.y + b.h - 4);
    ctx.stroke();
  }

  if (b.featured) {
    drawFeaturedSign(b);
    return;
  }
  const signH = b.own || b.kind === 'island' ? 28 : 22;
  ctx.fillStyle = b.own ? '#c8ffe8' : '#d8c8a0';
  ctx.fillRect(b.x + 4, b.y + 4, b.w - 8, signH);
  ctx.fillStyle = b.own ? '#0a3a30' : '#2a2010';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  if (b.own || b.ownerPlayerId != null) {
    ctx.font = 'bold 10px system-ui, sans-serif';
    ctx.fillText(b.standNo, b.x + b.w / 2, b.y + 11, b.w - 10);
    ctx.font = '9px system-ui, sans-serif';
    ctx.fillText(b.label, b.x + b.w / 2, b.y + 22, b.w - 10);
  } else {
    ctx.font = 'bold 9px system-ui, sans-serif';
    ctx.fillText(b.standNo, b.x + b.w / 2, b.y + 10, b.w - 10);
    ctx.font = '9px system-ui, sans-serif';
    ctx.fillText(b.label, b.x + b.w / 2, b.y + 20, b.w - 10);
  }

  const owner = b.ownerPlayerId != null ? players.find((x) => x.id === b.ownerPlayerId) : null;
  const atStand = owner ? nearStand(owner.x, owner.y, b) : false;
  if (owner && (b.own || owner.isHuman) && atStand) {
    ctx.fillStyle = 'rgba(94, 239, 200, 0.28)';
    ctx.fillRect(b.x - 3, b.y - 3, b.w + 6, b.h + 6);
  }
  // Nachfüll-Ladeleiste über dem eigenen Stand (Mensch)
  if (b.own && owner && owner.isHuman) {
    const barW = b.w;
    const barH = 7;
    const bx = b.x;
    const by = b.y - 28;
    const penProg =
      owner.pensAmmo >= MAX_PENS ? 1 : Math.min(1, owner.penRefillAcc / PEN_REFILL_EVERY);
    const flyProg =
      owner.flyersAmmo >= MAX_FLYERS ? 1 : Math.min(1, owner.flyerRefillAcc / FLYER_REFILL_EVERY);
    ctx.fillStyle = 'rgba(0,0,0,0.55)';
    ctx.fillRect(bx, by, barW, barH);
    ctx.fillStyle = '#7dffc8';
    ctx.fillRect(bx, by, barW * penProg, barH);
    ctx.fillStyle = 'rgba(0,0,0,0.55)';
    ctx.fillRect(bx, by + 9, barW, barH);
    ctx.fillStyle = '#ffe08a';
    ctx.fillRect(bx, by + 9, barW * flyProg, barH);
    ctx.fillStyle = '#e8fff6';
    ctx.font = '9px system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText(
      `Kulis ${owner.pensAmmo}/${MAX_PENS} · Stand ${b.stockPens ?? 0}`,
      b.x + b.w / 2,
      by - 8,
      b.w + 40,
    );
    ctx.fillText(
      `Flyer ${owner.flyersAmmo}/${MAX_FLYERS} · Stand ${b.stockFlyers ?? 0}`,
      b.x + b.w / 2,
      by + 24,
      b.w + 40,
    );
  }

  // Auffüll-Cooldown (eigener Stand / Bot-Stände)
  if ((b.refillCd ?? 0) > 0) {
    const frac = 1 - (b.refillCd ?? 0) / STAND_REFILL_CD;
    const cx = b.x + b.w - 16;
    const cy = b.y + 16;
    ctx.save();
    ctx.fillStyle = 'rgba(0,0,0,0.65)';
    ctx.beginPath();
    ctx.arc(cx, cy, 14, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = '#7dffc8';
    ctx.lineWidth = 4;
    ctx.beginPath();
    ctx.arc(cx, cy, 10, -Math.PI / 2, -Math.PI / 2 + Math.PI * 2 * frac);
    ctx.stroke();
    ctx.fillStyle = '#fff';
    ctx.font = 'bold 10px system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(String(Math.ceil(b.refillCd ?? 0)), cx, cy);
    ctx.restore();
  }
  // Rivalen-Vorrat + Klauen-Zone
  if (isRivalBooth(b)) {
    ensureBoothDisp(b);
    rivalStockBarsDrawn++;
    const barW = Math.max(b.w, 110);
    const barH = 8;
    const bx = b.x + b.w / 2 - barW / 2;
    const by = b.y - 36;
    const pens = b.dispPens ?? b.stockPens ?? 0;
    const flyers = b.dispFlyers ?? b.stockFlyers ?? 0;
    const penFrac = Math.max(0, Math.min(1, pens / STAND_STOCK_PENS));
    const flyFrac = Math.max(0, Math.min(1, flyers / STAND_STOCK_FLYERS));
    ctx.fillStyle = 'rgba(0,0,0,0.62)';
    ctx.fillRect(bx - 2, by - 14, barW + 4, 34);
    ctx.fillStyle = '#f0d0e8';
    ctx.font = 'bold 11px system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'bottom';
    ctx.fillText('Vorrat Rivalenstand', b.x + b.w / 2, by - 2, barW);
    ctx.fillStyle = 'rgba(20,10,18,0.85)';
    ctx.fillRect(bx, by, barW, barH);
    ctx.fillStyle = '#7dffc8';
    ctx.fillRect(bx, by, barW * penFrac, barH);
    ctx.fillStyle = 'rgba(20,10,18,0.85)';
    ctx.fillRect(bx, by + 11, barW, barH);
    ctx.fillStyle = '#ffe08a';
    ctx.fillRect(bx, by + 11, barW * flyFrac, barH);
    ctx.fillStyle = '#e8e0f0';
    ctx.font = '10px system-ui, sans-serif';
    ctx.textBaseline = 'top';
    ctx.fillText(
      `Kulis ${Math.round(pens)}/${STAND_STOCK_PENS}  Flyer ${Math.round(flyers)}/${STAND_STOCK_FLYERS}`,
      b.x + b.w / 2,
      by + 22,
      barW + 20,
    );

    const hum = human();
    if (hum && nearStand(hum.x, hum.y, b)) {
      nearRivalSteal = true;
      const cd = b.stealCd?.[hum.id] ?? 0;
      const pulse = 0.55 + 0.45 * Math.sin(performance.now() / 180);
      const cx = b.x + b.w / 2;
      const cy = b.y + b.h + 28;
      const rr = 36 + pulse * 8;
      ctx.save();
      ctx.strokeStyle = `rgba(255, 120, 180, ${0.35 + pulse * 0.45})`;
      ctx.lineWidth = 3;
      ctx.setLineDash([8, 6]);
      ctx.beginPath();
      ctx.arc(cx, cy, rr, 0, Math.PI * 2);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.fillStyle = `rgba(255, 80, 140, ${0.2 + pulse * 0.25})`;
      ctx.beginPath();
      ctx.arc(cx, cy, rr * 0.55, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = '#fff0f8';
      ctx.font = 'bold 14px system-ui, sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('👜 Klauen!', cx, cy - 6);
      ctx.font = '10px system-ui, sans-serif';
      ctx.fillStyle = '#ffd0e8';
      if (cd > 0) {
        ctx.fillText(`Pause ${cd.toFixed(1)} s`, cx, cy + 12);
      } else if (isTouchDevice) {
        ctx.fillText('In Reichweite — Button: Klauen', cx, cy + 12);
      } else {
        ctx.fillText('In Reichweite — Taste E / automatisch', cx, cy + 12);
      }
      ctx.restore();
    }
  }
}

function drawFloatFx(): void {
  for (const f of floatFx) {
    const a = Math.max(0, f.life / f.maxLife);
    ctx.save();
    ctx.globalAlpha = a;
    ctx.font = 'bold 14px system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = f.color;
    ctx.strokeStyle = 'rgba(0,0,0,0.65)';
    ctx.lineWidth = 3;
    ctx.strokeText(f.text, f.x, f.y);
    ctx.fillText(f.text, f.x, f.y);
    ctx.restore();
  }
}


function minimapSize(): { mw: number; mh: number } {
  const shortSide = Math.min(view.cssW, view.cssH) || 400;
  const target = Math.max(72, Math.round(shortSide * 0.22));
  const aspect = WORLD_W / WORLD_H;
  if (aspect >= 1) {
    return { mw: target, mh: Math.max(48, Math.round(target / aspect)) };
  }
  return { mw: Math.max(48, Math.round(target * aspect)), mh: target };
}

/** Render hall outline + booths once into an offscreen canvas. */
function rebuildMinimapStatic(): void {
  let { mw, mh } = minimapSize();
  mw = Math.max(32, Math.min(512, mw | 0));
  mh = Math.max(32, Math.min(512, mh | 0));
  if (!Number.isFinite(mw) || !Number.isFinite(mh)) return;
  const c = document.createElement('canvas');
  c.width = mw;
  c.height = mh;
  const m = c.getContext('2d');
  if (!m) return;
  const sx = mw / WORLD_W;
  const sy = mh / WORLD_H;

  m.fillStyle = '#1e2028';
  m.fillRect(0, 0, mw, mh);
  m.fillStyle = '#2a2c38';
  m.fillRect(0, (WORLD_H - ENTRANCE_H) * sy, mw, ENTRANCE_H * sy);

  for (const b of booths) {
    if (b.own) m.fillStyle = '#2a9a7a';
    else if (b.ownerPlayerId != null) m.fillStyle = '#8a5080';
    else if (b.label) m.fillStyle = '#5a4e3c';
    else m.fillStyle = '#3a3a46';
    m.fillRect(b.x * sx, b.y * sy, Math.max(1, b.w * sx), Math.max(1, b.h * sy));
  }

  m.strokeStyle = 'rgba(170, 180, 200, 0.55)';
  m.lineWidth = 1;
  m.strokeRect(0.5, 0.5, mw - 1, mh - 1);
  miniStatic = c;
}

function drawMinimap(): void {
  if (!playing) return;
  const { mw, mh } = minimapSize();
  if (!miniStatic || miniStatic.width !== mw || miniStatic.height !== mh) {
    rebuildMinimapStatic();
  }
  if (!miniStatic) return;

  const pad = 10;
  const x = view.cssW - mw - pad;
  const y = pad;
  const sx = mw / WORLD_W;
  const sy = mh / WORLD_H;

  ctx.save();
  ctx.globalAlpha = 0.78;
  ctx.drawImage(miniStatic, x, y);
  ctx.globalAlpha = 1;

  for (const e of entrancePoints) {
    ctx.fillStyle = 'rgba(150, 170, 210, 0.95)';
    ctx.beginPath();
    ctx.arc(x + e.x * sx, y + e.y * sy, 2.2, 0, Math.PI * 2);
    ctx.fill();
  }

  if (ownBooth) {
    ctx.fillStyle = 'rgba(94, 239, 200, 0.95)';
    ctx.fillRect(
      x + ownBooth.x * sx,
      y + ownBooth.y * sy,
      Math.max(2, ownBooth.w * sx),
      Math.max(2, ownBooth.h * sy),
    );
  }

  // Rivalenstände: Vorrats-Hinweis (pinkes Rechteck + Punkt)
  for (const b of booths) {
    if (!isRivalBooth(b)) continue;
    const pens = b.stockPens ?? 0;
    const low = pens < STAND_STOCK_PENS * 0.35;
    ctx.fillStyle = low ? 'rgba(255, 140, 180, 0.95)' : 'rgba(200, 80, 140, 0.85)';
    ctx.fillRect(
      x + b.x * sx,
      y + b.y * sy,
      Math.max(2, b.w * sx),
      Math.max(2, b.h * sy),
    );
    ctx.fillStyle = '#fff';
    ctx.beginPath();
    ctx.arc(
      x + (b.x + b.w / 2) * sx,
      y + (b.y + b.h / 2) * sy,
      1.6,
      0,
      Math.PI * 2,
    );
    ctx.fill();
  }

  // Orte-Icons
  for (const pl of hallPlaces) {
    const px = x + (pl.x + pl.w / 2) * sx;
    const py = y + (pl.y + pl.h / 2) * sy;
    if (pl.kind === 'smoke') {
      ctx.fillStyle = '#c8b070';
      ctx.beginPath();
      ctx.arc(px, py, 2.5, 0, Math.PI * 2);
      ctx.fill();
    } else if (pl.kind === 'coffee') {
      const ir = Math.max(3.5, mw * 0.03);
      ctx.fillStyle = '#d4894a';
      ctx.strokeStyle = '#3a2210';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.arc(px, py, ir, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
      ctx.fillStyle = '#fff3e0';
      ctx.fillRect(px - ir * 0.45, py - ir * 0.35, ir * 0.7, ir * 0.75);
      ctx.fillRect(px + ir * 0.25, py - ir * 0.15, ir * 0.3, ir * 0.3);
    } else if (pl.kind === 'toilet') {
      const ir = Math.max(4.5, mw * 0.04);
      ctx.fillStyle = '#3d8be0';
      ctx.strokeStyle = '#fff';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.roundRect(px - ir * 1.2, py - ir * 0.8, ir * 2.4, ir * 1.6, 2);
      ctx.fill();
      ctx.stroke();
      ctx.fillStyle = '#fff';
      ctx.font = `bold ${Math.max(6, Math.round(ir * 1.2))}px system-ui, sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('WC', px, py + 0.5);
    } else {
      ctx.fillStyle = '#6dcf8a';
      ctx.beginPath();
      ctx.moveTo(px, py - 3);
      ctx.lineTo(px + 3, py + 2);
      ctx.lineTo(px - 3, py + 2);
      ctx.closePath();
      ctx.fill();
    }
  }

  // VIP-Routen als gestrichelte Linien
  for (const p of persons) {
    if (p.kind !== 'vip' || p.leaving || p.route.length < 2) continue;
    ctx.save();
    ctx.strokeStyle = 'rgba(255, 210, 74, 0.55)';
    ctx.lineWidth = 1;
    ctx.setLineDash([3, 3]);
    ctx.beginPath();
    for (let i = 0; i < p.route.length; i++) {
      const pt = p.route[i]!;
      const px = x + pt.x * sx;
      const py = y + pt.y * sy;
      if (i === 0) ctx.moveTo(px, py);
      else ctx.lineTo(px, py);
    }
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.restore();
  }

  for (const p of persons) {
    if (p.kind === 'customer') {
      ctx.fillStyle = 'rgba(230, 160, 120, 0.4)';
      ctx.fillRect(x + p.x * sx - 0.5, y + p.y * sy - 0.5, 1.2, 1.2);
    } else if (p.kind === 'vip' && !p.leaving) {
      ctx.fillStyle = '#ffd24a';
      ctx.beginPath();
      ctx.arc(x + p.x * sx, y + p.y * sy, 2.4, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  ctx.strokeStyle = 'rgba(160, 200, 255, 0.55)';
  ctx.lineWidth = 1;
  ctx.strokeRect(x + camX * sx, y + camY * sy, CAM_W * sx, CAM_H * sy);

  for (const pl of players) {
    if (!pl.isHuman) {
      ctx.fillStyle = pl.color;
      ctx.beginPath();
      ctx.arc(x + pl.x * sx, y + pl.y * sy, 2.2, 0, Math.PI * 2);
      ctx.fill();
    }
  }
  const h = human();
  if (!h) {
    ctx.restore();
    ctx.strokeStyle = 'rgba(200, 200, 220, 0.45)';
    ctx.lineWidth = 1;
    ctx.strokeRect(x + 0.5, y + 0.5, mw - 1, mh - 1);
    return;
  }
  const px = x + h.x * sx;
  const py = y + h.y * sy;
  const ang = Math.atan2(h.facing.y, h.facing.x);
  ctx.translate(px, py);
  ctx.rotate(ang);
  ctx.fillStyle = '#b8e4ff';
  ctx.strokeStyle = '#3a6a90';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(5, 0);
  ctx.lineTo(-3.5, 3.2);
  ctx.lineTo(-2, 0);
  ctx.lineTo(-3.5, -3.2);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();

  ctx.restore();
  ctx.strokeStyle = 'rgba(200, 200, 220, 0.45)';
  ctx.lineWidth = 1;
  ctx.strokeRect(x + 0.5, y + 0.5, mw - 1, mh - 1);
}

function drawHud(): void {
  if (!playing) return;
  const h = human();
  if (!h) return;
  const pad = 10;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'top';

  // Ammo boxes (links, nicht Minimap)
  const boxY = pad;
  const drawAmmoBox = (
    x: number,
    title: string,
    cur: number,
    max: number,
    active: boolean,
  ) => {
    const label = `${title}: ${cur}/${max}`;
    ctx.font = 'bold 15px system-ui, sans-serif';
    const tw = ctx.measureText(label).width;
    ctx.fillStyle = active ? 'rgba(40, 70, 40, 0.85)' : 'rgba(10, 12, 18, 0.7)';
    ctx.strokeStyle = active ? '#9dff9a' : 'rgba(100,100,120,0.5)';
    ctx.lineWidth = active ? 2 : 1;
    ctx.fillRect(x, boxY, tw + 18, 28);
    ctx.strokeRect(x + 0.5, boxY + 0.5, tw + 17, 27);
    ctx.fillStyle = cur === 0 ? '#ff8080' : active ? '#c8ffc0' : '#e8e8ec';
    ctx.fillText(label, x + 8, boxY + 6);
    return tw + 18;
  };
  const w1 = drawAmmoBox(pad, 'Kulis', h.pensAmmo, MAX_PENS, h.activeAmmo === 'pen');
  const w2 = drawAmmoBox(pad + w1 + 8, 'Flyer', h.flyersAmmo, MAX_FLYERS, h.activeAmmo === 'flyer');
  // Kaffee-Becher
  {
    const barW = 54;
    const boxW = 26 + barW + (h.hasCoffee ? 22 : 6);
    // Schmal (Phone hoch): in die zweite Zeile neben das Schlepptau, nicht unter die Minimap
    const mmW = minimapSize().mw;
    let x0 = pad + w1 + 8 + w2 + 8;
    let boxYc = boxY;
    if (x0 + boxW > view.cssW - mmW - pad * 2) {
      ctx.font = 'bold 13px system-ui, sans-serif';
      const ts0 = towStats(h.id);
      x0 = pad + ctx.measureText(`Schlepptau: ${ts0.count} · bereit +${ts0.value}`).width + 14 + 8;
      boxYc = boxY + 32;
    }
    const frac = h.stamina / STAMINA_MAX;
    const empty = h.stamina <= 0;
    ctx.fillStyle = 'rgba(10, 12, 18, 0.75)';
    ctx.strokeStyle = empty ? '#ff7070' : h.hasCoffee ? '#ffc878' : 'rgba(100,100,120,0.5)';
    ctx.lineWidth = empty || h.hasCoffee ? 2 : 1;
    ctx.fillRect(x0, boxYc, boxW, 28);
    ctx.strokeRect(x0 + 0.5, boxYc + 0.5, boxW - 1, 27);
    ctx.font = '16px system-ui, sans-serif';
    ctx.fillStyle = '#ffe0b0';
    ctx.fillText('☕', x0 + 4, boxYc + 5);
    ctx.fillStyle = 'rgba(255,255,255,0.15)';
    ctx.fillRect(x0 + 24, boxYc + 9, barW, 10);
    ctx.fillStyle = frac < 0.25 ? '#ff8060' : '#c8904a';
    ctx.fillRect(x0 + 24, boxYc + 9, barW * frac, 10);
    if (h.hasCoffee) {
      ctx.font = 'bold 12px system-ui, sans-serif';
      ctx.fillStyle = '#ffe0b0';
      ctx.fillText('+1', x0 + 26 + barW, boxYc + 7);
    }
  }
  // Schlepptau
  {
    const ts = towStats(h.id);
    const x0 = pad;
    const y0 = boxY + 32;
    const label = `Schlepptau: ${ts.count} · bereit +${ts.value}`;
    ctx.font = 'bold 13px system-ui, sans-serif';
    const tw = ctx.measureText(label).width;
    ctx.fillStyle = ts.count > 0 ? 'rgba(60, 40, 90, 0.88)' : 'rgba(10, 12, 18, 0.55)';
    ctx.strokeStyle = ts.count > 0 ? '#c8a0ff' : 'rgba(100,100,120,0.4)';
    ctx.lineWidth = 1;
    ctx.fillRect(x0, y0, tw + 14, 22);
    ctx.strokeRect(x0 + 0.5, y0 + 0.5, tw + 13, 21);
    ctx.fillStyle = ts.count > 0 ? '#e8d0ff' : '#888898';
    ctx.fillText(label, x0 + 7, y0 + 4);
  }

  const stand = booths[h.standIndex];
  const atOwn = stand ? nearStand(h.x, h.y, stand) : false;
  if (atOwn && stand) {
    ctx.font = '11px system-ui, sans-serif';
    ctx.fillStyle = '#7dffc8';
    const penP = h.pensAmmo >= MAX_PENS ? 1 : h.penRefillAcc / PEN_REFILL_EVERY;
    const flyP = h.flyersAmmo >= MAX_FLYERS ? 1 : h.flyerRefillAcc / FLYER_REFILL_EVERY;
    ctx.fillText(
      `Nachfüllen Kuli ${(penP * 100) | 0}% · Flyer ${(flyP * 100) | 0}%`,
      pad,
      boxY + 56,
    );
    ctx.fillText(
      `Vorrat Stand: Kulis ${stand.stockPens ?? 0} · Flyer ${stand.stockFlyers ?? 0}`,
      pad,
      boxY + 70,
    );
  }

  // Rangliste: oben rechts unter der Minimap, kompakt, einklappbar (Tap / Taste R)
  {
    const ranked = [...players].sort((a, b) => b.score - a.score);
    const { mh } = minimapSize();
    const bw = scoreboardOpen ? 150 : 74;
    const bx = view.cssW - pad - bw;
    const by = pad + mh + 6;
    const bh = scoreboardOpen ? 16 + ranked.length * 13 : 18;
    scoreboardRect = { x: bx, y: by, w: bw, h: bh };
    ctx.fillStyle = 'rgba(10,12,18,0.65)';
    ctx.fillRect(bx, by, bw, bh);
    ctx.font = 'bold 11px system-ui, sans-serif';
    ctx.fillStyle = '#ffe08a';
    const myRank = ranked.findIndex((p) => p.isHuman) + 1;
    ctx.fillText(scoreboardOpen ? '🏆 Rangliste ▴' : `🏆 ${myRank}/${ranked.length} ▾`, bx + 5, by + 3);
    if (scoreboardOpen) {
      ctx.font = '10px system-ui, sans-serif';
      ranked.forEach((pl, i) => {
        ctx.fillStyle = pl.color;
        const mark = pl.isHuman ? '★' : '';
        ctx.fillText(`${i + 1}.${mark} ${pl.name}: ${pl.score}`, bx + 5, by + 16 + i * 13, bw - 10);
      });
    }
  }

  drawGiftHud(view.cssH - 70);
  drawDurchsage(HUD_TOP_FREE + drawModeHud(HUD_TOP_FREE));
  if (emptyMsgT > 0) {
    ctx.font = 'bold 20px system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    const msg = emptyMsg || 'Keine Munition!';
    const mw = ctx.measureText(msg).width;
    const mx = view.cssW / 2;
    const my = view.cssH * 0.2;
    ctx.fillStyle = 'rgba(40, 10, 10, 0.8)';
    ctx.fillRect(mx - mw / 2 - 14, my - 16, mw + 28, 32);
    ctx.fillStyle = '#ffb0b0';
    ctx.fillText(msg, mx, my);
  }

  drawMinimap();
}

function draw(): void {
  rivalStockBarsDrawn = 0;
  nearRivalSteal = false;
  ctx.clearRect(0, 0, view.cssW, view.cssH);
  if (!playing) {
    ctx.fillStyle = '#121218';
    ctx.fillRect(0, 0, view.cssW, view.cssH);
    return;
  }

  ctx.save();
  ctx.translate(view.offsetX, view.offsetY);
  ctx.scale(view.scale, view.scale);
  ctx.translate(-camX, -camY);

  // Floor
  ctx.fillStyle = '#2c2c34';
  ctx.fillRect(camX - 20, camY - 20, CAM_W + 40, CAM_H + 40);

  // Aisle tint (lighter strips) — only visible mains approx via marks density skip
  ctx.fillStyle = '#343440';
  ctx.fillRect(camX, camY, CAM_W, CAM_H);

  // Entrance tint
  if (camY + CAM_H > WORLD_H - ENTRANCE_H) {
    ctx.fillStyle = '#3a3a48';
    ctx.fillRect(0, WORLD_H - ENTRANCE_H, WORLD_W, ENTRANCE_H);
  }

  // Floor marks
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  for (const m of floorMarks) {
    if (!inView(m.x - 60, m.y - 20, 120, 40, 100)) continue;
    ctx.fillStyle = m.big ? 'rgba(180,190,210,0.35)' : 'rgba(140,150,170,0.28)';
    ctx.font = m.big ? 'bold 28px system-ui, sans-serif' : 'bold 16px system-ui, sans-serif';
    ctx.fillText(m.text, m.x, m.y);
  }

  drawHallPlaces();
  drawLitter();
  drawWet();

  for (const b of booths) drawBooth(b);
  for (const p of props) drawProp(p);
  drawFloatFx();

  for (const pr of projectiles) {
    if (!inView(pr.x - 16, pr.y - 16, 32, 32)) continue;
    const ang = Math.atan2(pr.vy, pr.vx);
    ctx.save();
    ctx.translate(pr.x, pr.y);
    ctx.rotate(ang);
    if (pr.kind === 'pen') {
      ctx.fillStyle = '#c0c8d8';
      ctx.fillRect(-PEN_LEN / 2, -PEN_W / 2, PEN_LEN, PEN_W);
      ctx.fillStyle = '#4060a0';
      ctx.fillRect(PEN_LEN / 2 - 3, -PEN_W / 2, 3, PEN_W);
    } else {
      // flat flyer sheet
      ctx.fillStyle = '#f0e8c0';
      ctx.strokeStyle = '#806040';
      ctx.lineWidth = 1;
      ctx.fillRect(-12, -8, 24, 16);
      ctx.strokeRect(-12, -8, 24, 16);
      ctx.beginPath();
      ctx.moveTo(-4, -8);
      ctx.lineTo(2, 0);
      ctx.lineTo(-4, 8);
      ctx.stroke();
    }
    ctx.restore();
  }

  // Schlepptau-Leinen
  for (const pl of players) {
    const chain = towList(pl.id);
    if (!chain.length) continue;
    ctx.strokeStyle = pl.color;
    ctx.globalAlpha = 0.55;
    ctx.lineWidth = 2;
    ctx.setLineDash([5, 4]);
    ctx.beginPath();
    ctx.moveTo(pl.x, pl.y);
    for (const c of chain) ctx.lineTo(c.x, c.y);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.globalAlpha = 1;
  }

  for (const p of persons) {
    if (p.hidden) continue;
    if (!inView(p.x - p.r, p.y - p.r, p.r * 2, p.r * 2)) continue;
    const sk = spriteKeyForPerson(p.kind, p.tier);
    const drawSize = Math.max(36, p.r * 2.6);
    const ok = drawSprite(ctx, sk, p.x, p.y, drawSize, p.anim.row, p.anim.frame);
    if (!ok) {
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
      if (p.kind === 'vip') {
        ctx.fillStyle = '#d4a820';
        ctx.strokeStyle = '#ffe680';
      } else if (p.kind === 'nobody') {
        ctx.fillStyle = p.followVipId !== null ? '#6a6a88' : '#7a7a90';
        ctx.strokeStyle = '#b0b0c8';
      } else if (p.tier === 'gross') {
        ctx.fillStyle = '#c04050';
        ctx.strokeStyle = '#ff90a0';
      } else if (p.tier === 'hart') {
        ctx.fillStyle = '#c87830';
        ctx.strokeStyle = '#ffd080';
      } else {
        ctx.fillStyle = '#e07840';
        ctx.strokeStyle = '#ffc090';
      }
      ctx.fill();
      ctx.lineWidth = 2;
      ctx.stroke();
      if (p.kind === 'vip') {
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r + 5, 0, Math.PI * 2);
        ctx.strokeStyle = 'rgba(255, 220, 100, 0.85)';
        ctx.lineWidth = 3;
        ctx.stroke();
        ctx.fillStyle = '#1a1408';
        ctx.font = 'bold 10px system-ui, sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText('VIP', p.x, p.y);
      }
    } else if (p.kind === 'vip') {
      ctx.beginPath();
      ctx.arc(p.x, p.y, drawSize * 0.42, 0, Math.PI * 2);
      ctx.strokeStyle = 'rgba(255, 220, 100, 0.75)';
      ctx.lineWidth = 2;
      ctx.stroke();
    }
    if (p.towedBy != null) {
      const owner = players.find((x) => x.id === p.towedBy);
      ctx.beginPath();
      ctx.arc(p.x, p.y - drawSize * 0.42, 5, 0, Math.PI * 2);
      ctx.fillStyle = owner?.color ?? '#fff';
      ctx.fill();
      ctx.strokeStyle = '#fff';
      ctx.lineWidth = 1;
      ctx.stroke();
    }

    if (
      (p.kind === 'customer' || p.kind === 'vip') &&
      p.maxHp > 0 &&
      !p.becomeNobody &&
      p.hp > 0 &&
      p.towedBy == null
    ) {
      const bw = Math.max(28, p.r * 2.2);
      const bh = 5;
      const bx = p.x - bw / 2;
      const by = p.y - p.r - (p.kind === 'vip' ? 18 : 12);
      ctx.fillStyle = 'rgba(0,0,0,0.55)';
      ctx.fillRect(bx, by, bw, bh);
      ctx.fillStyle = p.kind === 'vip' ? '#ffd24a' : '#6dff7a';
      ctx.fillRect(bx, by, bw * (p.hp / p.maxHp), bh);
    }
    let chatOrBubble = p.bubble;
    if (p.boothChatT > 0 && p.boothChatLine) {
      // Culling: nur die ersten MAX_BOOTH_CHATS Chat-Bubbles (nach id)
      const chatters = persons
        .filter((q) => q.boothChatT > 0 && q.boothChatLine)
        .sort((a, b) => a.id - b.id);
      const rank = chatters.findIndex((q) => q.id === p.id);
      if (rank >= 0 && rank < MAX_BOOTH_CHATS) chatOrBubble = p.boothChatLine;
    }
    if (chatOrBubble) {
      drawBubble(p.x, p.y, chatOrBubble, p.r);
      voiceObserve(p.id + 1000, chatOrBubble, p.x, p.y);
    }
  }

  drawPestMarks();
  drawInfoDesk();
  drawSecurity();
  drawDemo();
  drawCollDebug();
  drawPipiFx();
  drawStaff();
  drawCleanersAndQTalks();
  drawStaffTalks();

  for (const pl of players) {
    if (!inView(pl.x - pl.r, pl.y - pl.r, pl.r * 2, pl.r * 2)) continue;
    const sk = spriteKeyForPlayer(pl.isHuman, pl.rivalIndex > 0 ? pl.rivalIndex - 1 : 0);
    const drawSize = Math.max(40, pl.r * 2.8);
    if (pl.stunT > 0) ctx.globalAlpha = 0.7;
    const wob = pl.isHuman && hungerFail;
    if (wob) {
      ctx.save();
      ctx.translate(pl.x, pl.y + drawSize * 0.4);
      ctx.rotate(Math.sin(simTime * 3.2) * 0.09);
      ctx.translate(-pl.x, -pl.y - drawSize * 0.4);
    }
    const ok = drawSprite(ctx, sk, pl.x + pipiShakeX(pl), pl.y, drawSize, pl.anim.row, pl.anim.frame);
    if (wob) ctx.restore();
    if (wob && hungerIconT > 0) {
      ctx.font = '16px system-ui, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('🌀 grrr', pl.x + drawSize * 0.45, pl.y - drawSize * 0.55 - (1.6 - hungerIconT) * 8);
    }
    if (pl.isHuman && hungerBubbleT > 0) drawBubble(pl.x, pl.y, hungerBubble, pl.r);
    ctx.globalAlpha = 1;
    drawGiftCosmetics(pl, sk, drawSize);
    if (!ok) {
      ctx.beginPath();
      ctx.arc(pl.x, pl.y, pl.r, 0, Math.PI * 2);
      ctx.fillStyle = pl.stunT > 0 ? '#6a90b0' : pl.color;
      ctx.fill();
      ctx.strokeStyle = pl.isHuman ? '#a0d0f0' : '#fff';
      ctx.lineWidth = 2;
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(pl.x, pl.y);
      ctx.lineTo(pl.x + pl.facing.x * (pl.r + 8), pl.y + pl.facing.y * (pl.r + 8));
      ctx.strokeStyle = '#e8f0ff';
      ctx.lineWidth = 2;
      ctx.stroke();
    } else if (pl.stunT > 0) {
      ctx.beginPath();
      ctx.arc(pl.x, pl.y, drawSize * 0.38, 0, Math.PI * 2);
      ctx.strokeStyle = 'rgba(120, 180, 255, 0.7)';
      ctx.lineWidth = 3;
      ctx.stroke();
    }
    // Namensschild
    ctx.font = 'bold 10px system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'bottom';
    ctx.fillStyle = 'rgba(0,0,0,0.55)';
    const nw = ctx.measureText(pl.name).width + 8;
    const nameY = pl.y - drawSize * 0.45 - 6;
    ctx.fillRect(pl.x - nw / 2, nameY - 10, nw, 12);
    ctx.fillStyle = '#fff';
    ctx.fillText(pl.name, pl.x, nameY, 120);
    if (pl.talk) {
      drawBubble(pl.x, pl.y, pl.talk, pl.r);
      voiceObserve(pl.id, pl.talk, pl.x, pl.y);
    }
  }

  const hum = human();
  if (hum && mouseWorld) {
    ctx.beginPath();
    ctx.moveTo(hum.x, hum.y);
    ctx.lineTo(mouseWorld.x, mouseWorld.y);
    ctx.strokeStyle = 'rgba(200,200,220,0.25)';
    ctx.lineWidth = 1;
    ctx.stroke();
  }

  ctx.restore();
  drawPipiOverlay();
  drawHungerOverlay();
  drawHud();
}

let last = performance.now();
let loopDead = false;
let perfPrev = 0;
function frame(now: number): void {
  if (loopDead) return;
  try {
    const dt = Math.min(0.05, (now - last) / 1000);
    if (perfPrev > 0) perfPush(perfIv, now - perfPrev);
    perfPrev = now;
    last = now;
    const t0 = performance.now();
    update(dt);
    const t1 = performance.now();
    draw();
    perfPush(perfUpd, t1 - t0);
    perfPush(perfDraw, performance.now() - t1);
    requestAnimationFrame(frame);
  } catch (err) {
    loopDead = true;
    playing = false;
    showFatalError(err);
  }
}
try {
  resize();
} catch (err) {
  showFatalError(err);
}
void loadSprites();
void loadExhibits();
requestAnimationFrame(frame);
